/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ChatSidebar from './sidebar/ChatSidebar';
import ChatMain from './ChatMain';
import ChatRightPanel from './ChatRightPanel';
import OverviewSection from './OverviewSection';
import CandidateDetail from './CandidateDetail';
import BulkReplyDraftModal from './BulkReplyDraftModal';
import InterviewWorkspace from '../../components/InterviewScheduler/InterviewWorkspace';
import MeetingAnalysisModal from '../../components/MeetingAnalysisModal';
import RejectCandidateModal from '../../components/InterviewScheduler/RejectCandidateModal';
import ChatHeaderControls from '../../components/ChatHeaderControls';
import JobPostingPreview from '../../components/JobPostingPreview';
import JobPostingConfirmationModal from '../../components/JobPostingConfirmationModal';
import DynamicNaukriFieldsModal from '../../components/DynamicNaukriFieldsModal';
import OtpModal from '../../components/OtpModal';
import ScoreDetailsInfo from '../../components/ScoreDetailsInfo';
import JDScoreConfigModal from '../../components/JDScoreConfigModal';
import InterviewScorecardModal from '../../components/InterviewScorecardModal';
import { PageLoadingOverlay } from '../../components/SkeletonLoader';
import { cleanLogMessage } from '../../components/ProcessLogSummary';
import {
  buildJobPostingCollectedDetails,
  collectedDetailsToPostingDetails,
  getMissingJobPostingFields,
  normalizeJobPostingFieldKey,
} from './jobPostingHelpers';
import {
  createEmptySessionState,
  getPersistedJobPostingState,
  mergeJobPostingState,
  normalizeJobPostingState,
} from './chatPageSessionStateUtils';
import {
  ALL_POST_CHANNELS,
  formatLastUpdated,
  getLatestSessionTimestamp,
  getPostingChannelsFromSourcingPreferences,
  hasLatestRunPostingFailure,
  hasLatestRunPostingSuccess,
  isLocalUiNavigationHint,
  isLocalUiOnlyChatCommand,
  parseExperienceRange,
  resolvePostingProcessingFlag,
} from './chatPageViewUtils';
import { formatJobPostingErrorMessage } from '../../utils/jobPostingErrors';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSyncContext } from '../../contexts/SyncContext';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { getAuthToken, getCid, getMetadata } from '../../utils/encryptedStorage';
import {
  isMailProviderAuthError,
  handleMailProviderReconnect,
  getGooglePermissionError,
  openMailOAuthUrlPopup,
  confirmGmailAccountSwitch,
  cancelGmailAccountSwitch,
} from '../../utils/gmailOAuth';
import Toast from '../../components/Toast';
import { RESUME_LIBRARY_LABEL } from '../../utils/uxLabels';
import { getFeatureBlockedMessage, useCompanyFeatureAccess } from '../../utils/companyFeatureAccess';

const JOB_POSTING_INTENT_PATTERN = /^(?:post|publish|repost|retry)$/i;
const JOB_POSTING_COMMAND_PATTERN = /\b(?:post|publish|repost|retry|start)\b(?:\s+[\w-]+){0,5}\s+\b(?:job|jd|posting)\b|\bjob\s+(?:post|posting)\b/i;
const SOURCING_COMMAND_PATTERN = /\b(?:search|source|find|get|fetch|pull|scrape|scout|discover|collect|bring)\b[\s\S]{0,80}\b(?:candidates?|profiles?|talent)\b|\b(?:candidates?|profiles?|talent)\b[\s\S]{0,80}\b(?:from|on|via|using)\b[\s\S]{0,40}\b(?:linkedin|google|online|resumebox|resume\s*box)\b|\b(?:linkedin|google|online|resumebox|resume\s*box)\b[\s\S]{0,80}\b(?:candidates?|profiles?|talent)\b|\b(?:sourcing|talent\s+scouting|scout\s+candidates?)\b/i;
const SOURCING_VIEW_COMMAND_PATTERN = /\b(?:show|view|open|see|list)\b[\s\S]{0,40}\b(?:candidates?|profiles?|candidate\s+list)\b|\bcandidate\s+list\b/i;
const INTERVIEW_SCHEDULING_INTENT_PATTERN = /\b(?:schedule|book|arrange|set\s*up|setup|create)\b[\s\S]{0,80}\b(?:interviews?|meets?|meetings?|calls?)\b|\b(?:interviews?|meets?|meetings?|calls?)\b[\s\S]{0,80}\b(?:schedule|booking|slot|slots)\b/i;
const AI_INTERVIEW_INTENT_PATTERN = /\b(?:ai interview|interview with wiza|wiza interview|generate interview)\b/i;
const MANUAL_INTERVIEW_INTENT_PATTERN = /\b(?:manual interview|human interview|in[-\s]?person interview)\b/i;

const isSourcingRelatedIntent = (message, { includeViewCandidates = false } = {}) => {
  const text = String(message || '').trim();
  if (!text || text.length > 200) return false;
  return SOURCING_COMMAND_PATTERN.test(text) || (includeViewCandidates && SOURCING_VIEW_COMMAND_PATTERN.test(text));
};

const isInterviewSchedulingIntent = (message) => {
  const text = String(message || '').trim();
  if (!text || text.length > 200) return false;
  return INTERVIEW_SCHEDULING_INTENT_PATTERN.test(text) || AI_INTERVIEW_INTENT_PATTERN.test(text) || MANUAL_INTERVIEW_INTENT_PATTERN.test(text);
};

const resolveAiAutomationAssetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const configured = process.env.REACT_APP_AI_AUTOMATION_URL || '';
  if (configured && typeof window !== 'undefined') {
    try {
      const base = new URL(configured, window.location.origin);
      const currentHostname = window.location.hostname;
      const localHosts = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);
      if (currentHostname && !localHosts.has(currentHostname) && localHosts.has(base.hostname)) {
        return `${window.location.origin}${cleanPath}`;
      }
      const apiPrefix = base.pathname.replace(/\/api\/v\d+\/?$/i, '').replace(/\/$/, '');
      if (apiPrefix && !cleanPath.startsWith(apiPrefix)) {
        return `${base.origin}${apiPrefix}${cleanPath}`;
      }
      return `${base.origin}${cleanPath}`;
    } catch {
      // Fall through to same-origin below.
    }
  }
  return `${typeof window !== 'undefined' ? window.location.origin : ''}${cleanPath}`;
};

const ChatPage = ({ embedded = false } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id') || searchParams.get('id');
  const fromDashboard = searchParams.get('from_dashboard') === 'true';
  const suppressFullPageRestoreOverlay =
    fromDashboard && !searchParams.get('candidate') && !searchParams.get('candidateId');
  console.log('[ChatPage] fromDashboard:', fromDashboard, 'embedded:', embedded, 'session_id:', sessionId);
  const [dashboardInitialQuery, setDashboardInitialQuery] = useState('');
  const [dashboardInitialQueryToken, setDashboardInitialQueryToken] = useState(0);
  const [dashboardInitialQuerySessionId, setDashboardInitialQuerySessionId] = useState('');
  const forceNewSessionFromDashboardRef = useRef(false);
  const initialGreetingSavedRef = useRef(false);
  const newChatPendingRef = useRef(null); // session ID of a "New Chat" that has no user messages yet
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < 1536 : false)
  );
  const [isStackedPanels, setIsStackedPanels] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < 1280 : false)
  );
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < 768 : false)
  );
  const [mobilePanelView, setMobilePanelView] = useState('chat');
  const [centerWidth, setCenterWidth] = useState(null); // null means calculate from available space
  const [isResizing, setIsResizing] = useState(false);
  const centerRef = useRef(null);
  const containerRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const mailOAuthResumeHandledRef = useRef(new Set());
  const mailOAuthResumeInFlightRef = useRef(new Set());
  const collapsedSidebarRailWidth = 56;
  const collapsedSidebarWidth = isCompactLayout ? 56 : 64;
  const expandedSidebarWidth = isCompactLayout ? 276 : 296;
  const sidebarWidthPx = sidebarCollapsed ? collapsedSidebarWidth : expandedSidebarWidth;
  const effectiveSidebarWidthPx = embedded ? 0 : sidebarVisible ? expandedSidebarWidth : isMobileLayout ? 0 : collapsedSidebarRailWidth;
  const centerMinWidthPx = isCompactLayout ? 320 : 400;
  const rightPanelMinWidthPx = isCompactLayout ? 290 : 340;
  
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { features: companyFeatureAccess, canUseFeature } = useCompanyFeatureAccess();
  const { getSyncState, startSync, connectToExistingSync, refreshSyncState, updateSyncState, setCurrentSearchId } = useSyncContext();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  const appName = settings.app?.name || 'Wizbox';
  const appLogo = settings.app?.logo || '/config/logo.svg';
  const [activeHeaderTab, _setActiveHeaderTab] = useState(() => {
    try {
      if (fromDashboard) {
        sessionStorage.removeItem('activeHeaderTab');
      }
      return 'status';
    } catch { return 'status'; }
  });
  const campaignHeaderHeightPx = isMobileLayout && activeHeaderTab !== 'overview' ? 96 : 58;
  const [pipelineCandidateStatusFilter, setPipelineCandidateStatusFilter] = useState([]);
  const setActiveHeaderTab = (tab) => {
    // Note: Removed the fromDashboard guard that was blocking all tab changes.
    // The initial state is already set to 'status' on first load,
    // so subsequent user-initiated tab changes should be allowed.
    _setActiveHeaderTab(tab);
    try { sessionStorage.setItem('activeHeaderTab', tab); } catch {}
  };
  const handleOpenPipelineFilter = (statusFilter = []) => {
    if (blockDisabledFeature('filter')) return;
    setPipelineCandidateStatusFilter(Array.isArray(statusFilter) ? statusFilter.filter(Boolean) : []);
    setActiveHeaderTab('sourcing');
  };

  const ArrowRightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
      <path d="M13.0001 17C12.8685 17.0008 12.738 16.9756 12.6162 16.9258C12.4943 16.876 12.3835 16.8027 12.2901 16.71L8.29006 12.71C8.10381 12.5227 7.99927 12.2692 7.99927 12.005C7.99927 11.7408 8.10381 11.4874 8.29006 11.3L12.2901 7.30003C12.4814 7.1362 12.7274 7.05059 12.9791 7.06032C13.2308 7.07004 13.4695 7.17437 13.6476 7.35246C13.8257 7.53056 13.9301 7.7693 13.9398 8.02097C13.9495 8.27265 13.8639 8.51873 13.7001 8.71003L10.4101 12L13.7001 15.29C13.8405 15.4293 13.9365 15.6071 13.9758 15.8009C14.0152 15.9947 13.9963 16.1958 13.9213 16.3789C13.8464 16.5619 13.7189 16.7186 13.5549 16.8292C13.3909 16.9397 13.1978 16.9992 13.0001 17Z" fill="currentColor"/>
      <path d="M19 13H9C8.73478 13 8.48043 12.8946 8.29289 12.7071C8.10536 12.5196 8 12.2652 8 12C8 11.7348 8.10536 11.4804 8.29289 11.2929C8.48043 11.1054 8.73478 11 9 11H19C19.2652 11 19.5196 11.1054 19.7071 11.2929C19.8946 11.4804 20 11.7348 20 12C20 12.2652 19.8946 12.5196 19.7071 12.7071C19.5196 12.8946 19.2652 13 19 13ZM5 20C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V5C4 4.73478 4.10536 4.48043 4.29289 4.29289C4.48043 4.10536 4.73478 4 5 4C5.26522 4 5.51957 4.10536 5.70711 4.29289C5.89464 4.48043 6 4.73478 6 5V19C6 19.2652 5.89464 19.5196 5.70711 19.7071C5.51957 19.8946 5.26522 20 5 20Z" fill="currentColor"/>
    </svg>
  );

  const CalendarIcon = () => (
    <svg className="block h-[15px] w-[15px] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M8 3v3M16 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
    </svg>
  );
  // Job postings loaded from AI Automation sessions API
  const [jobPostings, setJobPostings] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [newChatCreating, setNewChatCreating] = useState(false);
  const newChatCreatingRef = useRef(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  // Track whether we've attempted to restore the session from URL
  const hasCandidateInUrl = searchParams.get('candidate');
  const [sessionRestored, setSessionRestored] = useState(!sessionId); // true immediately if no session_id in URL
  const [candidateRestored, setCandidateRestored] = useState(!hasCandidateInUrl); // true immediately if no candidate in URL
  const [selectedJobId, setSelectedJobId] = useState(null);
  // AI Automation session + JD state
  const [automationSessionId, setAutomationSessionId] = useState(null);
  const [automationSearchId, setAutomationSearchId] = useState(null);
  // Sync the current search_id to SyncContext so chat-level sync UI has a stable binding
  useEffect(() => {
    const currentSessionId = automationSessionId || selectedJobId || sessionId;
    const storedSearchId = currentSessionId
      ? sessionStateStoreRef.current.get(currentSessionId)?.searchId
      : '';
    const resolvedSearchId = [automationSearchId, storedSearchId]
      .find((value) => typeof value === 'string' && value.startsWith('search_')) || null;
    setCurrentSearchId(resolvedSearchId);
  }, [automationSearchId, automationSessionId, selectedJobId, sessionId, setCurrentSearchId]);
  useEffect(() => () => setCurrentSearchId(null), [setCurrentSearchId]);
  // Keep stable mapping to avoid creating new preliminary search_id repeatedly per session.
  const searchIdBySessionRef = useRef({});
  // Helper: return a valid search_id (must start with "search_") or empty string
  const getSafeSearchId = (sid) => {
    const id = sid ? (searchIdBySessionRef.current[sid] || '') : '';
    return id.startsWith('search_') ? id : '';
  };
  const getCurrentSessionSearchId = useCallback((targetSessionId = null) => {
    const sessionToUse = targetSessionId || automationSessionId || selectedJobId || sessionId;
    const mappedSearchId = getSafeSearchId(sessionToUse);
    if (mappedSearchId) return mappedSearchId;
    const storedSearchId = sessionToUse
      ? sessionStateStoreRef.current.get(sessionToUse)?.searchId
      : '';
    if (storedSearchId?.startsWith('search_')) return storedSearchId;
    return automationSearchId?.startsWith('search_') ? automationSearchId : '';
  }, [automationSearchId, automationSessionId, selectedJobId, sessionId]);
  // Track previous session for saving/restoring job posting state
  const previousSessionIdRef = useRef(null);
  // Track which session initiated the JD loading - for session-isolated loader state
  const jdLoadingSessionRef = useRef(null);
  const [isJDLoading, setIsJDLoading] = useState(false);
  const [jdText, setJdText] = useState('');
  const [jdError, setJdError] = useState('');
  const [systemMessage, setSystemMessage] = useState('');
  const [systemMessages, setSystemMessages] = useState([]);
  const [systemMessageToken, setSystemMessageToken] = useState(0); // Token to force message re-add
  const [lifecycleActions, setLifecycleActions] = useState([]); // Quick action buttons from lifecycle agent
  const [scorecardSetupStep, setScorecardSetupStep] = useState('');
  const [scorecardSetupSessionId, setScorecardSetupSessionId] = useState('');
  const [scorecardEditorOpen, setScorecardEditorOpen] = useState(false);
  const [scorecardSelectedSetupId, setScorecardSelectedSetupId] = useState(null);
  const [scorecardEditorSessionId, setScorecardEditorSessionId] = useState('');
  const [scorecardSavedSummary, setScorecardSavedSummary] = useState('');
  const [pipelineSnapshot, setPipelineSnapshot] = useState(null); // Structured DB state for button logic
  const [filterBreadcrumbs, setFilterBreadcrumbs] = useState([]); // Ask-mode filter trail: ["3+ yrs", "React"]
  const [filterActions, setFilterActions] = useState([]); // Ask-mode action buttons with handoff
  const [workingCandidates, setWorkingCandidates] = useState([]); // Ask-mode filtered candidate set
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]); // Ask-mode follow-up chips for the latest turn
  const [newCandidatesCount, setNewCandidatesCount] = useState(0); // Ask-mode hint: candidates added since last filter run that match the chain
  const [showFilterClearModal, setShowFilterClearModal] = useState(false); // Confirm clear filters on mode switch
  const lastFilterPillRef = useRef(''); // Tracks last filter pill to prevent duplicates
  const [filterPillText, setFilterPillText] = useState(''); // Current transition pill text
  const [filterPillToken, setFilterPillToken] = useState(0); // Increment to inject pill
  const [lastResponseMode, setLastResponseMode] = useState(null); // "agent" | "ask" — mode of last lifecycle response
  const [chatMode, setChatMode] = useState('agent'); // Current chat mode toggle: "agent" | "ask"
  const [lifecycleLoading, setLifecycleLoading] = useState(false); // Show typing dots during lifecycle API call
  const [chatApiLoading, setChatApiLoading] = useState(false); // Show typing dots during any API call (sourcing, etc.)
  const [agentStageLabel, setAgentStageLabel] = useState(''); // LangGraph active agent stage label
  const [agentStageCancellable, setAgentStageCancellable] = useState(false); // Whether active stage can be cancelled
  // Agent transition pills are now driven by backend's agent_pill field (no client-side tracking needed)
  const [lastResponseType, setLastResponseType] = useState(''); // Last /chat response type for right panel step tracking
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);

  // Close header menu on outside click
  useEffect(() => {
    if (!headerMenuOpen) return;
    const handler = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [headerMenuOpen]);
  const [lastQueryResults, setLastQueryResults] = useState(null); // Store query results for follow-up actions
  const [draftEditActions, setDraftEditActions] = useState([]); // Edit/Send/Cancel buttons for draft previews
  const [bulkReplyDraftModal, setBulkReplyDraftModal] = useState({ isOpen: false, drafts: [] });
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [workspaceSelectionRequired, setWorkspaceSelectionRequired] = useState(false);
  const [lifecycleAwaitingConfirmation, setLifecycleAwaitingConfirmation] = useState(false); // Route ALL messages to lifecycle when awaiting draft confirmation
  const [lifecycleScheduleActive, setLifecycleScheduleActive] = useState(false); // Route ALL messages to lifecycle during interview scheduling flow
  const [interviewRefreshKey, setInterviewRefreshKey] = useState(0); // Bump to reload the Interview tab workspace after a setup is created/changed in chat
  const [interviewSetupInProgress, setInterviewSetupInProgress] = useState(false); // True while an interview setup is being created in chat — blocks starting a second creation
  const [interviewSetupDraft, setInterviewSetupDraft] = useState(null); // Live in-progress blueprint shown in the Interview panel while building it conversationally
  const lastLifecycleInteractionRef = useRef(0); // Timestamp of last lifecycle interaction for follow-up routing

  // Toast notification state
  const [toastState, setToastState] = useState({ visible: false, message: '', type: 'info' });
  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    setToastState({ visible: true, message, type, duration });
  }, []);

  const blockDisabledFeature = useCallback((feature) => {
    if (canUseFeature(feature)) return false;
    const message = feature === 'job_posting'
      ? 'Job Post feature is disabled.'
      : getFeatureBlockedMessage(feature);
    setSystemMessages([message]);
    setSystemMessageToken((prev) => prev + 1);
    showToast(message, 'warning', 5000);
    return true;
  }, [canUseFeature, showToast]);

  const blockDisabledSchedulingFlow = useCallback(() => {
    if (canUseFeature('manual_interview') || canUseFeature('ai_interview')) return false;
    const message = getFeatureBlockedMessage('scheduling');
    setSystemMessages([message]);
    setSystemMessageToken((prev) => prev + 1);
    showToast(message, 'warning', 5000);
    return true;
  }, [canUseFeature, showToast]);

  const getDisabledFeatureForCommand = useCallback((message, mode = 'agent') => {
    const text = String(message || '').toLowerCase();
    if ((JOB_POSTING_INTENT_PATTERN.test(text) || JOB_POSTING_COMMAND_PATTERN.test(text)) && !canUseFeature('job_posting')) return 'job_posting';
    if (mode === 'ask' && !canUseFeature('filter')) return 'filter';
    if (/\b(filter|pipeline|clear filters?)\b/.test(text) && !canUseFeature('filter')) return 'filter';
    if (isSourcingRelatedIntent(text, { includeViewCandidates: true }) && !canUseFeature('sourcing')) return 'sourcing';
    if (/\b(outreach|send outreach|send email|send message|contact candidates?|follow-?up)\b/.test(text) && !canUseFeature('outreach')) return 'outreach';
    if (/\b(sync replies|email sync|mail sync|sync mail|sync email)\b/.test(text) && !(canUseFeature('outreach') || canUseFeature('email_sync'))) return 'email_sync';
    if (/\b(applicant sync|sync applicants?|get posted candidates?|posted applicants?)\b/.test(text) && !canUseFeature('job_posting')) return 'job_posting';
    if (isInterviewSchedulingIntent(text)) {
      if (!canUseFeature('manual_interview') && !canUseFeature('ai_interview')) return 'scheduling';
      if (AI_INTERVIEW_INTENT_PATTERN.test(text) && !canUseFeature('ai_interview')) return 'ai_interview';
      if (MANUAL_INTERVIEW_INTENT_PATTERN.test(text) && !canUseFeature('manual_interview')) return 'manual_interview';
    }
    return null;
  }, [canUseFeature]);

  const getOnlyEnabledInterviewType = useCallback(() => {
    const canManual = canUseFeature('manual_interview');
    const canAi = canUseFeature('ai_interview');
    if (canAi && !canManual) return 'ai';
    if (canManual && !canAi) return 'manual';
    return '';
  }, [canUseFeature]);

  // Listen for resume parsing started events to show toast notification
  useEffect(() => {
    const handler = (e) => {
      const total = e.detail?.total || 0;
      if (total > 0) {
        showToast(`${total} resume(s) received and currently parsing...`, 'info', 6000);
      }
    };
    window.addEventListener('resumeParsingStarted', handler);
    return () => window.removeEventListener('resumeParsingStarted', handler);
  }, [showToast]);

  // -- Cross-session context leak guards --
  // Ref that always holds the latest automationSessionId (accessible in async closures)
  const currentSessionRef = useRef(null);
  // AbortController for lifecycle/outreach in-flight API calls — aborted on session switch
  const lifecycleAbortRef = useRef(null);
  // Track in-flight lifecycle requests in sessionStorage so typing loader survives page navigation
  const lifecycleInflightRef = useRef(false);

  const updateJobPostingLastUpdated = (id, value = new Date()) => {
    if (!id) return;
    const formatted = formatLastUpdated(value);
    const raw = value instanceof Date ? value.toISOString() : value;
    setJobPostings((prev) =>
      prev.map((job) =>
        job.id === id
          ? {
              ...job,
              lastUpdated: formatted,
              rawTimestamp: raw,
            }
          : job
      )
    );
  };
  
  // Helper function to add system messages with deduplication
  const addSystemMessage = (message, options = {}) => {
    if (!message) return;
    
    const {
      removeSimilar = false, // Remove similar messages (e.g., same prefix)
      removeKeywords = [], // Remove messages containing these keywords
      checkExact = true // Check for exact duplicates
    } = options;
    
    setSystemMessages(prev => {
      // Check for exact duplicate
      if (checkExact && prev.includes(message)) {
        return prev; // Already exists, don't add
      }
      
      // Remove similar messages if requested
      let filtered = prev;
      if (removeSimilar) {
        // Extract key part of message (first 30 chars) for similarity check
        const messageKey = message.substring(0, 30).toLowerCase();
        filtered = prev.filter(msg => {
          if (typeof msg !== 'string') return true;
          const msgKey = msg.substring(0, 30).toLowerCase();
          return msgKey !== messageKey;
        });
      }
      
      // Remove messages containing specified keywords
      if (removeKeywords.length > 0) {
        filtered = filtered.filter(msg => {
          if (typeof msg !== 'string') return true;
          const msgLower = msg.toLowerCase();
          return !removeKeywords.some(keyword => msgLower.includes(keyword.toLowerCase()));
        });
      }
      
      return [...filtered, message];
    });
  };

  // Helper: persist a system/assistant message to the DB via the sessions API
  // This ensures chat messages are stored for history even when they are set locally via setSystemMessages
  const persistSystemMessage = useCallback((content, options = {}) => {
    if (!content) return;
    const sessionIdToUse = options.sessionId || automationSessionId || selectedJobId || sessionId;
    if (!sessionIdToUse) return;
    const phase = options.phase || 'system';
    const metadata = options.metadata || {};
    // Fire-and-forget — do NOT block UI
    aiAutomationApi.post(`/sessions/${sessionIdToUse}/messages`, {
      message_type: 'assistant',
      content: typeof content === 'string' ? content : content.join('\n'),
      message_role: 'assistant',
      phase,
      metadata: { ...metadata, source: 'system_message' },
    }).catch((e) => {
      console.warn('[persistSystemMessage] Failed to persist system message:', e);
    });
  }, [automationSessionId, selectedJobId, sessionId]);

  // Combined helper: set system message locally AND persist to DB
  // Pass { skipPersist: true } when the backend /chat endpoint already saves the message
  // to avoid duplicate DB entries (e.g., lifecycle responses via /chat)
  const setAndPersistSystemMessage = useCallback((messages, options = {}) => {
    if (!messages) return;
    const msgArray = Array.isArray(messages) ? messages : [messages];
    const targetSessionId = options.forSessionId || null;
    const effectiveSessionId = targetSessionId || currentViewedSessionRef.current || automationSessionId || null;
    const sessionState = effectiveSessionId
      ? sessionStateStoreRef.current.get(effectiveSessionId)
      : null;
    const hasActiveJobPostingPrompt = Boolean(
      (effectiveSessionId === currentViewedSessionRef.current && pendingNaukriField) ||
      (effectiveSessionId === currentViewedSessionRef.current && waitingForBackendQuestion) ||
      (effectiveSessionId === currentViewedSessionRef.current && fieldCollectionStateRef.current?.isActive) ||
      sessionState?.jobPosting?.pendingField ||
      sessionState?.jobPosting?.fieldCollectionActive,
    );
    const hasOnlySourcingCompletionMessages = msgArray.every((msg) => {
      const text = String(msg || '').trim().toLowerCase();
      return (
        text.startsWith('candidate sourcing completed') ||
        text === 'exited candidate sourcing' ||
        text.startsWith('__agent_transition__exited candidate sourcing')
      );
    });

    if (hasActiveJobPostingPrompt && hasOnlySourcingCompletionMessages) {
      if (!options.skipPersist) {
        const combinedContent = msgArray.join('\n');
        if (combinedContent.trim()) {
          persistSystemMessage(combinedContent, targetSessionId ? { ...options, sessionId: targetSessionId } : options);
        }
      }
      return;
    }

    // SESSION GUARD: If a target session is specified, only update React state
    // if the user is currently viewing that session. Otherwise just store it.
    if (targetSessionId && currentViewedSessionRef.current !== targetSessionId) {
      updateSessionState(targetSessionId, (state) => {
        state.chat.systemMessages = msgArray;
        return state;
      });
      if (!options.skipPersist) {
        const combinedContent = msgArray.join('\n');
        if (combinedContent.trim()) {
          persistSystemMessage(combinedContent, { ...options, sessionId: targetSessionId });
        }
      }
      return;
    }

    setSystemMessages(msgArray);
    if (options.token !== false) {
      setSystemMessageToken((prev) => prev + 1);
    }
    // Also update session store for the current session
    if (targetSessionId) {
      updateSessionState(targetSessionId, (state) => {
        state.chat.systemMessages = msgArray;
        return state;
      });
    }
    // Persist the combined content to DB (skip if backend already handles persistence)
    if (!options.skipPersist) {
      const combinedContent = msgArray.join('\n');
      if (combinedContent.trim()) {
        persistSystemMessage(combinedContent, options);
      }
    }
  }, [persistSystemMessage]);

  const normalizePlatformName = (rawPlatform) => {
    if (!rawPlatform || typeof rawPlatform !== 'string') return null;
    const value = rawPlatform.trim().toLowerCase();
    if (!value) return null;
    if (value.includes('linkedin')) return 'LinkedIn';
    if (value.includes('naukri')) return 'Naukri';
    if (value.includes('google') || value === 'search' || value.includes('searxng')) return 'Google';
    if (value.includes('resumebox') || value.includes('resume box') || value.includes('resume_box')) return 'ResumeBox';
    return rawPlatform;
  };

  const normalizeSourcingChannelValue = (channel) =>
    String(channel || '').trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');

  const normalizeRequirementSkillsForQuery = (skills) => {
    if (Array.isArray(skills)) {
      return skills.map((skill) => String(skill || '').trim()).filter(Boolean);
    }
    if (typeof skills === 'string') {
      const trimmed = skills.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((skill) => String(skill || '').trim()).filter(Boolean);
        }
      } catch (_) {
        // Not JSON; fall back to comma/newline splitting below.
      }

      return trimmed
        .split(/[\n,]+/)
        .map((skill) => skill.trim())
        .filter(Boolean);
    }
    return [];
  };

  const hasSourcingChannel = (channels, matcher) =>
    Array.isArray(channels) && channels.some((channel) => matcher(normalizeSourcingChannelValue(channel)));

  const shouldRunGoogleForChannels = (channels = []) =>
    channels.length === 0 || hasSourcingChannel(channels, (channel) =>
      channel.includes('google') || channel.includes('online sourcing') || channel.includes('web search')
    );

  const shouldRunLinkedinForChannels = (channels = []) =>
    channels.length === 0 || hasSourcingChannel(channels, (channel) => channel.includes('linkedin'));

  const shouldRunResumeBoxForChannels = (channels = []) =>
    channels.length === 0 ||
    hasSourcingChannel(channels, (channel) =>
      channel.includes('resumebox') || channel.includes('resume box') || channel.includes('resume database')
    );

  const getEffectiveSourcingState = (sourcing = {}) => {
    const googleProcessing = Boolean(sourcing.googleProcessing);
    const linkedinProcessing = Boolean(sourcing.linkedinProcessing);
    const resumeBoxProcessing = Boolean(sourcing.resumeBoxProcessing);
    const anyProcessing = googleProcessing || linkedinProcessing || resumeBoxProcessing;
    const hasLogs =
      (sourcing.googleLogs?.length > 0) ||
      (sourcing.linkedinLogs?.length > 0) ||
      (sourcing.resumeBoxLogs?.length > 0);
    const complete = Boolean(sourcing.complete) && !anyProcessing;
    const started = Boolean(sourcing.started) || anyProcessing || (complete && hasLogs);

    return {
      ...sourcing,
      googleProcessing,
      linkedinProcessing,
      resumeBoxProcessing,
      anyProcessing,
      hasLogs,
      complete,
      started,
    };
  };
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [accumulatedRequirements, setAccumulatedRequirements] = useState({});
  const [backendMissingFields, setBackendMissingFields] = useState([]);
  const [analysisUxPhase, setAnalysisUxPhase] = useState('idle'); // idle | analysing | analysed | building | done
  const [showResultsPanel, setShowResultsPanel] = useState(false); // true when sourcing strategy cards visible
  const [finalRequirements, setFinalRequirements] = useState({});
  const [talentScoutingStarted, setTalentScoutingStarted] = useState(false); // true when user confirms to start talent scouting
  const [sourcingComplete, setSourcingComplete] = useState(false); // true when sourcing process is complete
  const [userTriggeredProcess, setUserTriggeredProcess] = useState(null); // Track which process user explicitly triggered ('sourcing' | 'job_posting' | null)
  const [candidates, setCandidates] = useState([]); // List of sourced candidates
  const [sourcingStats, setSourcingStats] = useState({}); // Stats about sourcing (total profiles, emails, LinkedIn)
  const [sessionCandidateCount, setSessionCandidateCount] = useState(0);
  // Job posting flow state
  const [jobPostingPhase, setJobPostingPhase] = useState('idle'); // idle | channel_selection | collecting_details | posting | posted
  const [selectedChannels, setSelectedChannels] = useState([]); // ['LinkedIn', 'Naukri']
  const [postedChannels, setPostedChannels] = useState([]); // channels already posted for current session
  const [postingDetails, setPostingDetails] = useState({}); // Employment Type, Workplace Type, Job Location, etc.
  const [pendingNaukriField, setPendingNaukriField] = useState(null); // field requested via WebSocket (e.g., companyName)
  const [pendingNaukriFieldOptions, setPendingNaukriFieldOptions] = useState([]); // dropdown options for the pending field
  const [dynamicNaukriFieldRequest, setDynamicNaukriFieldRequest] = useState(null);
  const [naukriFieldResponses, setNaukriFieldResponses] = useState({}); // Track field responses for validation
  const [showJobPostingPreview, setShowJobPostingPreview] = useState(false); // Show preview modal before posting
  const [jobPostingPreviewData, setJobPostingPreviewData] = useState({}); // Data to show in preview modal
  const [waitingForJobPostingConfirmation, setWaitingForJobPostingConfirmation] = useState(false); // Waiting for user to confirm job posting details
  const [showJobPostingConfirmationModal, setShowJobPostingConfirmationModal] = useState(false);
  const [collectedJobPostingDetails, setCollectedJobPostingDetails] = useState(null);
  const [jobPostingIntroShown, setJobPostingIntroShown] = useState(false); // whether we've already shown the JD posting instructions
  const [activeJobPostingSessions, setActiveJobPostingSessions] = useState(new Set()); // Track active background job posting sessions (supports multiple)
  const [activeSourcingSessions, setActiveSourcingSessions] = useState(new Set()); // Track active background sourcing sessions (supports multiple)
  const [pendingFieldsQueue, setPendingFieldsQueue] = useState([]); // Queue of pending field requests when user is away
  const [pendingJobPostingFieldsQueue, setPendingJobPostingFieldsQueue] = useState([]); // Queue of job posting fields to ask one by one
  const [pendingJobPostingChannels, setPendingJobPostingChannels] = useState([]); // Channels selected for job posting
  const [pendingJobPostingSessionId, setPendingJobPostingSessionId] = useState(null); // Session that initiated job posting - to ensure posting happens for correct session
  const pendingJobPostingSessionRef = useRef(null); // Ref for async access to pending job posting session
  const jobPostingSocketRef = useRef(null);
  const processedWebSocketEventsRef = useRef(new Map());
  const dismissedDynamicNaukriRequestsRef = useRef(new Map());
  const handledPostingTerminalEventsRef = useRef(new Set());
  const [jobPostingSocketReconnectKey, setJobPostingSocketReconnectKey] = useState(0);
  const backgroundSourcingSocketRef = useRef(new Map()); // Map<sessionId, WebSocket> for background sourcing sessions
  const backgroundJobPostingSocketRef = useRef(null); // Separate WebSocket for background job posting session
  const logsInitializedRef = useRef({ linkedin: false, naukri: false }); // Track log reset per platform for current posting session
  const manualTabOverrideRef = useRef(false);
  // Per-session job posting state storage (Map: sessionId -> { systemMessages, pendingField, pendingOptions, phase, ... })
  const jobPostingStatePerSessionRef = useRef(new Map());
  // Per-session agent state storage (Map: sessionId -> { jd_agent, job_post_agent, sourcing_agent })
  // This stores the three-agent state from backend for session-isolated state management
  const agentStatePerSessionRef = useRef(new Map());

  // ===================== ACTIVE SESSION TRACKING HELPERS =====================
  // Helper functions to add/remove sessions from active Sets (supports multiple concurrent operations)
  
  /**
   * Add a session to active job posting sessions
   * @param {string} sessionId - The session to add
   */
  const addActiveJobPostingSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setActiveJobPostingSessions(prev => {
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
  }, []);
  
  /**
   * Remove a session from active job posting sessions
   * @param {string} sessionId - The session to remove. If null, does nothing.
   */
  const removeActiveJobPostingSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setActiveJobPostingSessions(prev => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  }, []);
  
  /**
   * Check if a session has active job posting
   * @param {string} sessionId - The session to check
   * @returns {boolean} True if session has active job posting
   */
  const hasActiveJobPosting = useCallback((sessionId) => {
    return sessionId && activeJobPostingSessions.has(sessionId);
  }, [activeJobPostingSessions]);
  
  /**
   * Add a session to active sourcing sessions
   * @param {string} sessionId - The session to add
   */
  const addActiveSourcingSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setActiveSourcingSessions(prev => {
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
  }, []);
  
  /**
   * Remove a session from active sourcing sessions
   * @param {string} sessionId - The session to remove. If null, does nothing.
   */
  const removeActiveSourcingSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setActiveSourcingSessions(prev => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  }, []);
  
  /**
   * Check if a session has active sourcing
   * @param {string} sessionId - The session to check
   * @returns {boolean} True if session has active sourcing
   */
  const hasActiveSourcing = useCallback((sessionId) => {
    return sessionId && activeSourcingSessions.has(sessionId);
  }, [activeSourcingSessions]);
  
  // ===================== CENTRALIZED SESSION STATE STORE =====================
  // Stores ALL session-specific UI state to ensure strict session isolation
  // Structure: sessionId -> { jd, chat, jobPosting, sourcing }
  const sessionStateStoreRef = useRef(new Map());
  // Track the currently viewed session for response validation
  const currentViewedSessionRef = useRef(null);
  useEffect(() => {
    if (scorecardSetupStep || !['lifecycle_setup_collect', 'lifecycle_setup_review'].includes(lastResponseType)) return;
    const targetSessionId = currentViewedSessionRef.current || automationSessionId || selectedJobId || sessionId;
    if (!targetSessionId) return;
    let live = true;
    aiAutomationApi.get(`/chat/setup-scorecard/${encodeURIComponent(targetSessionId)}`)
      .then((editor) => {
        if (live) {
          setScorecardSetupStep(editor?.setup_step || '');
          setScorecardSetupSessionId(editor?.setup_step ? targetSessionId : '');
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, [automationSessionId, lastResponseType, lifecycleActions, scorecardSetupStep, selectedJobId, sessionId]);
  // Force update counter for re-rendering when session state changes
  const [, forceUpdate] = useState({});
  
  /**
   * Cleanup old localStorage entries to prevent quota exceeded errors
   * Removes sourcing_state and job_posting_state entries older than 1 hour
   */
  const cleanupOldLocalStorageEntries = () => {
    try {
      const ONE_HOUR = 60 * 60 * 1000;
      const NOW = Date.now();
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sourcing_state_') || key.startsWith('job_posting_state_') || key.startsWith('chat_history_'))) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data?.savedAt && (NOW - data.savedAt) > ONE_HOUR) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid JSON, mark for removal
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log('[localStorage] Cleaned up old entry:', key);
        } catch {}
      });
      
      if (keysToRemove.length > 0) {
        console.log(`[localStorage] Cleaned up ${keysToRemove.length} old entries`);
      }
    } catch (e) {
      console.warn('[localStorage] Failed to cleanup old entries:', e);
    }
  };
  
  // Run localStorage cleanup on component mount
  useEffect(() => {
    cleanupOldLocalStorageEntries();
  }, []);
  
  /**
   * Get session state from the centralized store
   * @param {string} sessionId - The session ID
   * @returns {object} The session state or empty defaults
   */
  const getSessionState = (sessionId) => {
    if (!sessionId) return createEmptySessionState();
    if (!sessionStateStoreRef.current.has(sessionId)) {
      // Initialize with empty state
      const state = createEmptySessionState();
      
      // Try to restore JD state from localStorage
      try {
        const savedJd = localStorage.getItem(`jd_state_${sessionId}`);
        if (savedJd) {
          const jd = JSON.parse(savedJd);
          state.jd = {
            loading: jd.loading || false,
            analysisPhase: jd.analysisPhase || 'idle',
            text: jd.text || '',
            error: null
          };
        }
      } catch (e) {
        console.warn('[localStorage] Failed to restore JD state:', e);
      }
      
      // Restore only field-collection state from localStorage (logs/processing come from DB/file API)
      try {
        const savedJp = localStorage.getItem(`job_posting_state_${sessionId}`);
        if (savedJp) {
          const jp = normalizeJobPostingState(JSON.parse(savedJp));
          // Only restore field collection progress — logs, processing and completion come from DB/file API
          state.jobPosting = {
            phase: jp.phase || 'idle',
            pendingField: jp.pendingField || null,
            pendingFieldsQueue: jp.pendingFieldsQueue || [],
            collectedDetails: jp.collectedDetails || {},
            fieldCollectionActive: jp.fieldCollectionActive || false,
            selectedChannels: jp.selectedChannels || [],
            processing: false,
            postedChannels: jp.postedChannels || [],
            complete: false,
            failed: false,
            linkedinProcessing: false,
            naukriProcessing: false,
            linkedinLogs: [],
            naukriLogs: []
          };
        }
      } catch (e) {
        console.warn('[localStorage] Failed to restore job posting state:', e);
      }
      // NOTE: sourcing state (processing, logs, completion) is loaded from DB/file API — not localStorage

      // Try to restore chatHistory from localStorage (preserves candidate cards)
      try {
        const savedChat = localStorage.getItem(`chat_history_${sessionId}`);
        if (savedChat) {
          const chat = JSON.parse(savedChat);
          state.chat = {
            ...state.chat,
            chatHistory: sanitizeRestoredChatHistory(chat.chatHistory || [])
          };
        }
      } catch (e) {
        console.warn('[localStorage] Failed to restore chat history:', e);
      }
      
      sessionStateStoreRef.current.set(sessionId, state);
    }
    return sessionStateStoreRef.current.get(sessionId);
  };
  
  /**
   * Update session state in the centralized store
   * Supports both object merge and function updater patterns
   * Also persists critical state to localStorage for refresh resilience
   * @param {string} sessionId - The session ID
   * @param {object|function} updater - State updates object or function that receives current state and returns new state
   */
  const updateSessionState = (sessionId, updater) => {
    if (!sessionId) return;
    
    const state = getSessionState(sessionId);
    
    let newState;
    if (typeof updater === 'function') {
      // Function updater pattern - pass clone to allow mutation
      newState = updater(structuredClone(state));
    } else {
      // Object merge pattern
      newState = { ...state, ...updater };
    }
    
    sessionStateStoreRef.current.set(sessionId, newState);
    
    // Persist JD state if loading/analyzing
    const jd = newState.jd;
    if (jd && jd.loading) {
      try {
        const persistState = {
          loading: jd.loading,
          analysisPhase: jd.analysisPhase,
          text: jd.text,
          savedAt: Date.now()
        };
        localStorage.setItem(`jd_state_${sessionId}`, JSON.stringify(persistState));
      } catch (e) {
        console.warn('[localStorage] Failed to persist JD state:', e);
      }
    } else {
      // Clear JD state when not loading
      try {
        localStorage.removeItem(`jd_state_${sessionId}`);
      } catch (e) {
        // Ignore
      }
    }
    
    // Persist critical job posting state to localStorage — field collection progress only
    // Logs, processing, and completion come from DB/file API and are NOT persisted to localStorage
    const jp = newState.jobPosting;
    if (jp && (jp.phase === 'collecting_details' || jp.pendingField || jp.pendingFieldsQueue?.length > 0)) {
      try {
        const normalizedJp = normalizeJobPostingState(jp);
        const persistState = {
          phase: normalizedJp.phase,
          pendingField: normalizedJp.pendingField,
          pendingFieldsQueue: normalizedJp.pendingFieldsQueue || [],
          collectedDetails: normalizedJp.collectedDetails,
          fieldCollectionActive: normalizedJp.fieldCollectionActive,
          selectedChannels: normalizedJp.selectedChannels,
          postedChannels: normalizedJp.postedChannels || [],
          savedAt: Date.now()
        };
        localStorage.setItem(`job_posting_state_${sessionId}`, JSON.stringify(persistState));
      } catch (e) {
        console.warn('[localStorage] Failed to persist job posting state:', e);
      }
    }
    
    // NOTE: sourcing state (processing, logs, completion) is NOT persisted to localStorage — comes from DB/file API

    // Persist chatHistory to preserve candidate cards on refresh
    const chat = newState.chat;
    if (chat && Array.isArray(chat.chatHistory) && chat.chatHistory.length > 0) {
      try {
        // Limit to last 50 messages to prevent localStorage quota issues
        const MAX_CHAT_MESSAGES = 50;
        const persistState = {
          chatHistory: sanitizeRestoredChatHistory(chat.chatHistory).slice(-MAX_CHAT_MESSAGES),
          savedAt: Date.now()
        };
        localStorage.setItem(`chat_history_${sessionId}`, JSON.stringify(persistState));
      } catch (e) {
        console.warn('[localStorage] Failed to persist chat history:', e);
        if (e.name === 'QuotaExceededError') {
          cleanupOldLocalStorageEntries();
        }
      }
    } else {
      // Clear chat history when empty
      try {
        localStorage.removeItem(`chat_history_${sessionId}`);
      } catch (e) {
        // Ignore
      }
    }
    
    // Only trigger UI update if this is the currently viewed session
    if (sessionId === currentViewedSessionRef.current) {
      forceUpdate({});
    }
  };
  
  /**
   * Apply session state to React state (for syncing)
   * Call this after switching sessions to update React state from session store
   * @param {string} sessionId - The session to sync
   */
  const syncReactStateFromSession = useCallback((sessionId) => {
    if (!sessionId) return;
    const state = getSessionState(sessionId);
    const mergedJobPostingState = mergeJobPostingState(
      state.jobPosting,
      getPersistedJobPostingState(sessionId),
    );
    
    // JD State
    if (state.jd) {
      setJdText(state.jd.text || '');
      setJdError(state.jd.error || null);
      setIsJDLoading(state.jd.loading || false);
      setAnalysisUxPhase(state.jd.analysisPhase || 'initial');
      setWaitingForConfirmation(state.jd.waitingForConfirmation || false);
      setAccumulatedRequirements(state.jd.requirements || {});
      setBackendMissingFields(state.jd.missingFields || []);
    }
    
    // Chat State
    if (state.chat) {
      setSystemMessages(sanitizeSystemMessages(state.chat.systemMessages || []));
      setSystemMessage(state.chat.systemMessage || '');
      setChatApiLoading(state.chat.apiLoading || false);
      setLifecycleLoading(state.chat.lifecycleLoading || false);
      if (Array.isArray(state.chat.chatHistory)) {
        setChatHistory(sanitizeRestoredChatHistory(state.chat.chatHistory));
      }
    }

    // Lifecycle State
    if (state.lifecycle) {
      setLifecycleAwaitingConfirmation(state.lifecycle.awaitingConfirmation || false);
      setLifecycleScheduleActive(state.lifecycle.scheduleActive || false);
      setLifecycleActions(state.lifecycle.actions || []);
      lastLifecycleInteractionRef.current = state.lifecycle.lastInteraction || 0;
      if (state.lifecycle.pipelineSnapshot) setPipelineSnapshot(state.lifecycle.pipelineSnapshot);
    }

    // Job Posting State
    if (state.jobPosting) {
      setLinkedinLogs(mergedJobPostingState.linkedinLogs || []);
      setNaukriLogs(mergedJobPostingState.naukriLogs || []);
      setLinkedinProcessing(mergedJobPostingState.linkedinProcessing || false);
      setNaukriProcessing(mergedJobPostingState.naukriProcessing || false);
      setJobPostingPhase(mergedJobPostingState.phase || 'idle');
      setSelectedChannels(mergedJobPostingState.selectedChannels || []);
      setPostedChannels(mergedJobPostingState.postedChannels || []);
      setJobPostingProcessing(mergedJobPostingState.processing || false);
      setJobPostingComplete(mergedJobPostingState.complete || false);
      setJobPostingFailed(mergedJobPostingState.failed || false);
      setWaitingForBackendQuestion(mergedJobPostingState.waitingForQuestion || false);
      setPendingNaukriField(mergedJobPostingState.pendingField || null);
      setPendingNaukriFieldOptions(mergedJobPostingState.pendingFieldOptions || []);
      setCollectedJobPostingDetails(mergedJobPostingState.collectedDetails || null);
    }
    
    // Auto-determine right panel based on what's processing
    const isSourcingActive = state.sourcing && (
      state.sourcing.googleProcessing || state.sourcing.linkedinProcessing || 
      state.sourcing.resumeBoxProcessing || (state.sourcing.started && !state.sourcing.complete)
    );
    const isJobPostingActive = state.jobPosting && (
      mergedJobPostingState.phase === 'posting' || mergedJobPostingState.linkedinProcessing || 
      mergedJobPostingState.naukriProcessing || 
      (mergedJobPostingState.phase === 'posted' && (mergedJobPostingState.linkedinLogs?.length > 0 || mergedJobPostingState.naukriLogs?.length > 0))
    );
    
    if (isSourcingActive || isJobPostingActive) {
      // Priority: job posting wins when posting logs are active; sourcing should not steal the panel.
      if (isJobPostingActive) {
        const explicitMode = state.rightPanelMode === 'candidates' || state.rightPanelMode === 'jd'
          ? state.rightPanelMode
          : 'auto';
        setRightPanelMode(explicitMode);
        setAutoActiveHeaderTab('job_posting');
      } else if (isSourcingActive) {
        setRightPanelMode(state.sourcing?.candidates?.length > 0 ? 'candidates' : 'sourcing');
        setAutoActiveHeaderTab('sourcing');
      }
    }
    
    // Sourcing State
    if (state.sourcing) {
      const effectiveSourcing = getEffectiveSourcingState(state.sourcing);
      setGoogleSourcingLogs(effectiveSourcing.googleLogs || []);
      setLinkedinSourcingLogs(effectiveSourcing.linkedinLogs || []);
      setResumeBoxSourcingLogs(effectiveSourcing.resumeBoxLogs || []);
      setGoogleSourcingProcessing(effectiveSourcing.googleProcessing);
      setLinkedinSourcingProcessing(effectiveSourcing.linkedinProcessing);
      setResumeBoxSourcingProcessing(effectiveSourcing.resumeBoxProcessing);
      setGoogleCandidateCount(effectiveSourcing.googleCandidateCount || 0);
      setLinkedinCandidateCount(effectiveSourcing.linkedinCandidateCount || 0);
      setResumeBoxCandidateCount(effectiveSourcing.resumeBoxCandidateCount || 0);
      setTalentScoutingStarted(effectiveSourcing.started);
      setSourcingComplete(effectiveSourcing.complete);
      setCandidates(effectiveSourcing.candidates || []);
      setSourcingStats(effectiveSourcing.stats || {});
    }
    
    // Outreach State
    if (state.outreach) {
      setOutreachLogs(state.outreach.logs || []);
      setOutreachPhase(state.outreach.phase || null);
      setOutreachSending(state.outreach.sending || false);
      setOutreachCompleted(state.outreach.completed || false);
    }
    
    // Metadata
    if (state.searchId) {
      setAutomationSearchId(state.searchId);
      if (sessionId && String(state.searchId).startsWith('search_')) {
        searchIdBySessionRef.current[sessionId] = state.searchId;
      }
    }
    
    // Right panel mode
    if (state.rightPanelMode && !isJobPostingActive) {
      setRightPanelMode(state.rightPanelMode);
    }
  }, []);
  
  /**
   * Save current React state to session store
   * @param {string} sessionId - The session to save state for
   */
  const saveCurrentSessionState = (sessionId) => {
    if (!sessionId) return;
    
    console.log('[Session State] Saving state for session:', sessionId);
    updateSessionState(sessionId, {
      jd: {
        text: jdText,
        loading: isJDLoading,
        error: jdError,
        analysisPhase: analysisUxPhase,
        waitingForConfirmation,
        requirements: { ...accumulatedRequirements },
        missingFields: [...backendMissingFields],
      },
      chat: {
        systemMessages: [...systemMessages],
        systemMessage,
        chatHistory: Array.isArray(chatHistory) ? [...chatHistory] : [],
        apiLoading: chatApiLoading, // Save session-scoped chat API loading state
        lifecycleLoading, // Session-scoped lifecycle typing indicator
      },
      lifecycle: {
        awaitingConfirmation: lifecycleAwaitingConfirmation,
        scheduleActive: lifecycleScheduleActive,
        actions: [...lifecycleActions],
        lastInteraction: lastLifecycleInteractionRef.current,
        pipelineSnapshot: pipelineSnapshot,
      },
      jobPosting: {
        linkedinLogs: [...linkedinLogs],
        naukriLogs: [...naukriLogs],
        linkedinProcessing,
        naukriProcessing,
        phase: jobPostingPhase,
        selectedChannels: [...selectedChannels],
        postedChannels: [...postedChannels],
        processing: jobPostingProcessing,
        complete: jobPostingComplete,
        failed: jobPostingFailed,
        waitingForQuestion: waitingForBackendQuestion,
        pendingField: pendingNaukriField,
        pendingFieldOptions: [...pendingNaukriFieldOptions],
        collectedDetails: collectedJobPostingDetails,
        pendingFieldsQueue: [...pendingJobPostingFieldsQueue],
        fieldCollectionActive: fieldCollectionStateRef.current?.isActive || false,
      },
      sourcing: {
        googleLogs: [...googleSourcingLogs],
        linkedinLogs: [...linkedinSourcingLogs],
        resumeBoxLogs: [...resumeBoxSourcingLogs],
        googleProcessing: googleSourcingProcessing,
        linkedinProcessing: linkedinSourcingProcessing,
        resumeBoxProcessing: resumeBoxSourcingProcessing,
        googleCandidateCount,
        linkedinCandidateCount,
        resumeBoxCandidateCount,
        started: talentScoutingStarted,
        complete: sourcingComplete,
        candidates: [...candidates],
        stats: { ...sourcingStats },
        selectedChannels: [...selectedSourcingChannels],
      },
      rightPanelMode,
      outreach: {
        logs: [...outreachLogs],
        phase: outreachPhase,
        sending: outreachSending,
        completed: outreachCompleted,
      },
      searchId: automationSearchId,
      savedAt: Date.now(),
    });
  };
  
  /**
   * Restore session state from the store
   * @param {string} sessionId - The session to restore state for
   * @returns {boolean} Whether state was restored
   */
  const restoreSessionState = (sessionId) => {
    if (!sessionId) return false;
    
    // First, ensure session state is initialized (this will load from localStorage if needed)
    const savedState = getSessionState(sessionId);
    if (!savedState) {
      console.log('[Session State] No saved state for session:', sessionId);
      return false;
    }
    
    // Check if there's any meaningful state to restore
    const hasMeaningfulState = savedState.sourcing?.started || 
                               savedState.sourcing?.googleLogs?.length > 0 ||
                               savedState.sourcing?.linkedinLogs?.length > 0 ||
                               savedState.sourcing?.resumeBoxLogs?.length > 0 ||
                               savedState.sourcing?.candidates?.length > 0;
    
    console.log('[Session State] Restoring state for session:', sessionId, 
                'hasMeaningfulState:', hasMeaningfulState,
                'logs: google=', savedState.sourcing?.googleLogs?.length,
                'linkedin=', savedState.sourcing?.linkedinLogs?.length,
                'resumeBox=', savedState.sourcing?.resumeBoxLogs?.length);
    
    // Use the new sync function that understands the new nested structure
    syncReactStateFromSession(sessionId);
    
    return true;
  };
  
  /**
   * Validate that an API response belongs to the current session
   * @param {string} responseSessionId - The session_id from API response
   * @returns {boolean} Whether the response should be rendered
   */
  const validateResponseSession = (responseSessionId) => {
    const currentSession = currentViewedSessionRef.current;
    if (!responseSessionId || !currentSession) return true; // Allow if can't determine
    return responseSessionId === currentSession;
  };
  
  /**
   * Route any event (API response or WebSocket) to the correct session
   * Updates session store and only applies to UI if current session
   * @param {string} sessionId - Target session ID
   * @param {object} event - The event data
   * @param {string} eventType - Type of event for logging
   * @returns {boolean} - true if event was applied to UI, false if stored only
   */
  const routeEventToSession = (sessionId, event, eventType = 'unknown') => {
    if (!sessionId) {
      console.warn('[EventRouter] No sessionId provided for event:', eventType);
      return false;
    }
    
    // Get or create session state
    const existingState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
    
    // Merge event data into session state based on event type
    const newState = { ...existingState, lastEvent: event, lastEventType: eventType };
    sessionStateStoreRef.current.set(sessionId, newState);
    
    // Check if this is the current session
    const isCurrentSession = currentViewedSessionRef.current === sessionId;
    
    console.log(`[EventRouter] ${eventType} -> Session ${sessionId.slice(0, 8)}... | Current: ${isCurrentSession}`);
    
    return isCurrentSession;
  };
  
  /**
   * Session-aware log appender - updates session store and React state if current session
   * @param {string} sessionId - Target session ID
   * @param {string} logType - 'linkedin' | 'naukri' | 'googleSourcing' | 'linkedinSourcing' | 'resumeBoxSourcing' | 'outreach'
   * @param {object} logEntry - The log entry to append
   */
  const appendSessionLog = (sessionId, logType, logEntry) => {
    if (!sessionId) return;
    
    updateSessionState(sessionId, (state) => {
      switch (logType) {
        case 'linkedin':
          state.jobPosting.linkedinLogs = [...state.jobPosting.linkedinLogs, logEntry];
          break;
        case 'naukri':
          state.jobPosting.naukriLogs = [...state.jobPosting.naukriLogs, logEntry];
          break;
        case 'googleSourcing':
          state.sourcing.googleLogs = [...state.sourcing.googleLogs, logEntry];
          break;
        case 'linkedinSourcing':
          state.sourcing.linkedinLogs = [...state.sourcing.linkedinLogs, logEntry];
          break;
        case 'resumeBoxSourcing':
          state.sourcing.resumeBoxLogs = [...state.sourcing.resumeBoxLogs, logEntry];
          break;
        case 'outreach':
          state.outreach.logs = [...state.outreach.logs, logEntry];
          break;
        default:
          console.warn('[appendSessionLog] Unknown log type:', logType);
      }
      return state;
    });
    
    // Also update React state if this is the current session
    if (currentViewedSessionRef.current === sessionId) {
      const state = getSessionState(sessionId);
      switch (logType) {
        case 'linkedin':
          setLinkedinLogs(state.jobPosting.linkedinLogs);
          break;
        case 'naukri':
          setNaukriLogs(state.jobPosting.naukriLogs);
          break;
        case 'googleSourcing':
          setGoogleSourcingLogs(state.sourcing.googleLogs);
          break;
        case 'linkedinSourcing':
          setLinkedinSourcingLogs(state.sourcing.linkedinLogs);
          break;
        case 'resumeBoxSourcing':
          setResumeBoxSourcingLogs(state.sourcing.resumeBoxLogs);
          break;
        case 'outreach':
          setOutreachLogs(state.outreach.logs);
          break;
        default:
          break;
      }
    }
  };

  const persistJobPostingStateSnapshot = (sessionId, jobPostingState) => {
    if (!sessionId || !jobPostingState) return;
    // Only persist field collection state — logs/processing come from DB/file API
    try {
      const normalizedJp = normalizeJobPostingState(jobPostingState);
      if (normalizedJp.phase === 'collecting_details' || normalizedJp.pendingField || normalizedJp.pendingFieldsQueue?.length > 0) {
        localStorage.setItem(`job_posting_state_${sessionId}`, JSON.stringify({
          phase: normalizedJp.phase || 'collecting_details',
          pendingField: normalizedJp.pendingField || null,
          pendingFieldsQueue: normalizedJp.pendingFieldsQueue || [],
          collectedDetails: normalizedJp.collectedDetails || null,
          fieldCollectionActive: normalizedJp.fieldCollectionActive || false,
          selectedChannels: normalizedJp.selectedChannels || [],
          postedChannels: normalizedJp.postedChannels || [],
          savedAt: Date.now(),
        }));
      }
    } catch (e) {
      console.warn('Failed to persist job posting state:', e);
    }
  };
  
  /**
   * Session-aware loader state updater
   * @param {string} sessionId - Target session ID
   * @param {string} loaderType - 'jd' | 'linkedin' | 'naukri' | 'googleSourcing' | 'linkedinSourcing' | 'resumeBoxSourcing'
   * @param {boolean} isLoading - Loading state
   */
  const setSessionLoader = (sessionId, loaderType, isLoading) => {
    if (!sessionId) return;
    
    updateSessionState(sessionId, (state) => {
      switch (loaderType) {
        case 'jd':
          state.jd.loading = isLoading;
          break;
        case 'linkedin':
          state.jobPosting.linkedinProcessing = isLoading;
          break;
        case 'naukri':
          state.jobPosting.naukriProcessing = isLoading;
          break;
        case 'googleSourcing':
          state.sourcing.googleProcessing = isLoading;
          break;
        case 'linkedinSourcing':
          state.sourcing.linkedinProcessing = isLoading;
          break;
        case 'resumeBoxSourcing':
          state.sourcing.resumeBoxProcessing = isLoading;
          break;
        case 'chatApi':
          state.chat.apiLoading = isLoading;
          break;
        case 'lifecycle':
          state.chat.lifecycleLoading = isLoading;
          break;
        default:
          break;
      }
      return state;
    });

    // Also update React state if this is the current session
    if (currentViewedSessionRef.current === sessionId) {
      switch (loaderType) {
        case 'jd':
          setIsJDLoading(isLoading);
          break;
        case 'linkedin':
          setLinkedinProcessing(isLoading);
          break;
        case 'naukri':
          setNaukriProcessing(isLoading);
          break;
        case 'googleSourcing':
          setGoogleSourcingProcessing(isLoading);
          break;
        case 'linkedinSourcing':
          setLinkedinSourcingProcessing(isLoading);
          break;
        case 'resumeBoxSourcing':
          setResumeBoxSourcingProcessing(isLoading);
          break;
        case 'chatApi':
          setChatApiLoading(isLoading);
          break;
        case 'lifecycle':
          setLifecycleLoading(isLoading);
          break;
        default:
          break;
      }
    }
  };
  
  /**
   * Session-aware system messages updater
   * @param {string} sessionId - Target session ID  
   * @param {string[]} messages - New messages array
   */
  const setSessionMessages = (sessionId, messages) => {
    if (!sessionId) return;
    
    updateSessionState(sessionId, (state) => {
      state.chat.systemMessages = messages;
      return state;
    });
    
    if (currentViewedSessionRef.current === sessionId) {
      setSystemMessages(messages);
    }
  };
  
  /**
   * Set session JD text
   * @param {string} sessionId - Target session ID
   * @param {string} text - JD text
   */
  const setSessionJdText = (sessionId, text) => {
    if (!sessionId) return;
    
    updateSessionState(sessionId, (state) => {
      state.jd.text = text;
      return state;
    });
    
    const isCurrentSession = currentViewedSessionRef.current === sessionId;
    if (isCurrentSession) {
      setJdText(text);
    }
  };
  
  /**
   * Set session accumulated requirements
   * @param {string} sessionId - Target session ID
   * @param {object} requirements - Requirements object
   */
  const setSessionRequirements = (sessionId, requirements) => {
    if (!sessionId) return;
    
    const sessionState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
    sessionState.accumulatedRequirements = requirements;
    sessionStateStoreRef.current.set(sessionId, sessionState);
    
    const isCurrentSession = currentViewedSessionRef.current === sessionId;
    if (isCurrentSession) {
      setAccumulatedRequirements(requirements);
    }
  };
  
  /**
   * Set session missing fields
   * @param {string} sessionId - Target session ID
   * @param {string[]} fields - Missing fields array
   */
  const setSessionMissingFields = (sessionId, fields) => {
    if (!sessionId) return;
    
    const sessionState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
    sessionState.backendMissingFields = fields;
    sessionStateStoreRef.current.set(sessionId, sessionState);
    
    const isCurrentSession = currentViewedSessionRef.current === sessionId;
    if (isCurrentSession) {
      setBackendMissingFields(fields);
    }
  };
  
  /**
   * Set session analysis phase
   * @param {string} sessionId - Target session ID
   * @param {string} phase - Phase value
   */
  const setSessionAnalysisPhase = (sessionId, phase) => {
    if (!sessionId) return;
    
    const sessionState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
    sessionState.analysisUxPhase = phase;
    sessionStateStoreRef.current.set(sessionId, sessionState);
    
    const isCurrentSession = currentViewedSessionRef.current === sessionId;
    if (isCurrentSession) {
      setAnalysisUxPhase(phase);
    }
  };
  
  /**
   * CRITICAL: Hydrate UI from session store
   * This function MUST be called when switching sessions or when updating UI
   * It ensures UI always reflects the session store (single source of truth)
   * @param {string} sessionId - The session ID to hydrate from
   */
  const hydrateUIFromSession = (sessionId) => {
    if (!sessionId) return;

    // CRITICAL: Use getSessionState instead of direct ref access to load from localStorage if needed
    const sessionState = getSessionState(sessionId);
    if (!sessionState) {
      console.log('[HydrateUI] No saved state for session, using defaults:', sessionId);
      // Reset to defaults
      setSystemMessages([]);
      setJdText('');
      setJdError('');
      setIsJDLoading(false);
      setLinkedinLogs([]);
      setNaukriLogs([]);
      setLinkedinProcessing(false);
      setNaukriProcessing(false);
      setGoogleSourcingLogs([]);
      setLinkedinSourcingLogs([]);
      setResumeBoxSourcingLogs([]);
      setGoogleSourcingProcessing(false);
      setLinkedinSourcingProcessing(false);
      setResumeBoxSourcingProcessing(false);
      setGoogleCandidateCount(0);
      setLinkedinCandidateCount(0);
      setResumeBoxCandidateCount(0);
      setAccumulatedRequirements({});
      setBackendMissingFields([]);
      setAnalysisUxPhase('idle');
      setJobPostingPhase('idle');
      setTalentScoutingStarted(false);
      setSourcingComplete(false);
      setCandidates([]);
      setRightPanelMode('auto'); // Reset right panel to prevent cross-session leak
      setSelectedSourcingChannels([]); // Reset sourcing channels
      // CRITICAL: Reset finalRequirements to prevent job posting triggering in wrong session
      setFinalRequirements({});
      setSelectedChannels([]);
      setPostedChannels([]);
      // Clear pending job posting session tracking when switching to a fresh session
      setPendingJobPostingSessionId(null);
      pendingJobPostingSessionRef.current = null;
      setPendingSourcingSessionId(null);
      pendingSourcingSessionRef.current = null;
      // CRITICAL: Clear field collection state to prevent questions from appearing in wrong session
      setPendingNaukriField(null);
      setPendingNaukriFieldOptions([]);
      setPendingJobPostingFieldsQueue([]);
      setCollectedJobPostingDetails(null);
      fieldCollectionStateRef.current = { isActive: false, currentField: null, queue: [] };
      // Clear job posting processing state
      setJobPostingProcessing(false);
      // CRITICAL: Clear chat history when no saved state
      setChatHistory([]);
      return;
    }

    // Restore chat history from session state
    if (sessionState.chat && Array.isArray(sessionState.chat.chatHistory)) {
      setChatHistory(sessionState.chat.chatHistory);
    }

    setJobPostingComplete(false);
    setJobPostingFailed(false);
    setWaitingForBackendQuestion(false);
    // Reset lifecycle state
    setLifecycleLoading(false);
    setLifecycleAwaitingConfirmation(false);
    setLifecycleScheduleActive(false);
    setLifecycleActions([]);
    setPipelineSnapshot(null);
    lastLifecycleInteractionRef.current = 0;

    // Reset agent stage banner (per-session)
    setAgentStageLabel('');
    setAgentStageCancellable(false);
    setLastResponseType('');
    setScorecardSetupStep('');
    setScorecardSetupSessionId('');
    setScorecardEditorOpen(false);
    setScorecardSavedSummary('');
    // Reset filter state to prevent cross-session leak
    setChatMode('agent');
    setFilterBreadcrumbs([]);
    setFilterActions([]);
    setWorkingCandidates([]);
    setFollowUpSuggestions([]);
    // Reset outreach state
    setOutreachLogs([]);
    setOutreachPhase(null);
    setOutreachSending(false);
    setOutreachCompleted(false);

    console.log('[HydrateUI] Hydrating UI from session store:', sessionId, sessionState);
    
    // ===== NEW NESTED STRUCTURE =====
    // JD State
    if (sessionState.jd) {
      setJdText(sessionState.jd.text || '');
      setJdError(sessionState.jd.error || null);
      setIsJDLoading(sessionState.jd.loading || false);
      setAnalysisUxPhase(sessionState.jd.analysisPhase || 'idle');
      setWaitingForConfirmation(sessionState.jd.waitingForConfirmation || false);
      setAccumulatedRequirements(sessionState.jd.requirements || {});
      setBackendMissingFields(sessionState.jd.missingFields || []);
      setFinalRequirements(sessionState.jd.requirements || {});
    }
    
    // Chat State
    if (sessionState.chat) {
      setSystemMessages(sanitizeSystemMessages(sessionState.chat.systemMessages || []));
      setSystemMessage(sessionState.chat.systemMessage || '');
      setChatApiLoading(sessionState.chat.apiLoading || false);
      setLifecycleLoading(sessionState.chat.lifecycleLoading || false);
      // CRITICAL: Restore chatHistory so messages show correctly when switching back
      if (Array.isArray(sessionState.chat.chatHistory)) {
        setChatHistory(sanitizeRestoredChatHistory(sessionState.chat.chatHistory));
      }
    }

    // Lifecycle State
    if (sessionState.lifecycle) {
      setLifecycleAwaitingConfirmation(sessionState.lifecycle.awaitingConfirmation || false);
      setLifecycleScheduleActive(sessionState.lifecycle.scheduleActive || false);
      setLifecycleActions(sessionState.lifecycle.actions || []);
      lastLifecycleInteractionRef.current = sessionState.lifecycle.lastInteraction || 0;
      if (sessionState.lifecycle.pipelineSnapshot) setPipelineSnapshot(sessionState.lifecycle.pipelineSnapshot);
    }

    // Agent Stage Banner (per-session isolation)
    setAgentStageLabel(sessionState.agentStage?.label || '');
    setAgentStageCancellable(sessionState.agentStage?.cancellable || false);

    // Ask-mode filtering state (per-session)
    if (sessionState.filtering) {
      setChatMode(sessionState.filtering.chatMode || 'agent');
      setFilterBreadcrumbs(sessionState.filtering.breadcrumbs || []);
      setFilterActions(sessionState.filtering.actions || []);
      setWorkingCandidates(sessionState.filtering.workingCandidates || []);
      setFollowUpSuggestions(sessionState.filtering.followUpSuggestions || []);
    } else {
      // No saved filter state — reset to defaults to prevent cross-session leak
      setChatMode('agent');
      setFilterBreadcrumbs([]);
      setFilterActions([]);
      setWorkingCandidates([]);
      setFollowUpSuggestions([]);
    }

    if (sessionState.searchId) {
      setAutomationSearchId(sessionState.searchId);
      if (sessionId && String(sessionState.searchId).startsWith('search_')) {
        searchIdBySessionRef.current[sessionId] = sessionState.searchId;
      }
    }

    // Job Posting State
    let restoredJobPostingActive = false;
    if (sessionState.jobPosting) {
      const mergedJobPostingState = mergeJobPostingState(
        sessionState.jobPosting,
        getPersistedJobPostingState(sessionId),
      );
      setLinkedinLogs(mergedJobPostingState.linkedinLogs || []);
      setNaukriLogs(mergedJobPostingState.naukriLogs || []);
      setLinkedinProcessing(mergedJobPostingState.linkedinProcessing || false);
      setNaukriProcessing(mergedJobPostingState.naukriProcessing || false);
      setJobPostingPhase(mergedJobPostingState.phase || 'idle');
      setSelectedChannels(mergedJobPostingState.selectedChannels || []);
      setPostedChannels(mergedJobPostingState.postedChannels || []);
      setJobPostingProcessing(mergedJobPostingState.processing || false);
      setJobPostingComplete(mergedJobPostingState.complete || false);
      setJobPostingFailed(mergedJobPostingState.failed || false);
      setWaitingForBackendQuestion(mergedJobPostingState.waitingForQuestion || false);
      setPendingNaukriField(mergedJobPostingState.pendingField || null);
      setPendingNaukriFieldOptions(mergedJobPostingState.pendingFieldOptions || []);
      setCollectedJobPostingDetails(mergedJobPostingState.collectedDetails || null);
      setPendingJobPostingFieldsQueue(mergedJobPostingState.pendingFieldsQueue || []);
      setPendingJobPostingChannels(mergedJobPostingState.pendingChannels || []);
      // Sync fieldCollectionStateRef with restored session state
      const restoredQueue = mergedJobPostingState.pendingFieldsQueue || [];
      const restoredField = mergedJobPostingState.pendingField || null;
      const restoredActive = mergedJobPostingState.fieldCollectionActive || (restoredQueue.length > 0);
      fieldCollectionStateRef.current = {
        isActive: restoredActive,
        currentField: restoredField,
        queue: restoredQueue,
      };
      
      // CRITICAL: If field collection is active, show the systemMessages from session store
      // NOT a hardcoded question - use what was stored to maintain exact position in flow
      if (restoredActive && sessionState.chat?.systemMessages?.length > 0) {
        // Use the exact system messages that were stored for this session
        setSystemMessages(sanitizeSystemMessages(sessionState.chat.systemMessages));
        setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
      } else if (restoredActive && restoredField) {
        // Fallback: If no stored messages, show the current field question
        const fieldQuestions = {
          'experienceMin': '📊 What is the minimum experience required? (Enter years, e.g., 0 2)',
          'experienceMax': '📊 What is the maximum experience required? (Enter years, e.g., 5)',
          'salaryMin': '💰 What is the minimum salary for this position? (Enter a number, e.g., 50000)',
          'salaryMax': '💰 What is the maximum salary for this position? (Enter a number, e.g., 100000)',
          'companyName': '🏢 What is the company name for this job posting?',
          'company_name': '🏢 What is the company name for this job posting?',
          'allowCandidatesToCall': '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
          'allow_call': '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
          'recruiterName': '👤 What is the recruiter name?',
          'recruiter_name': '👤 What is the recruiter name?',
          'recruiterNumber': '📱 What is the recruiter phone number? (10 digits)',
          'recruiter_number': '📱 What is the recruiter phone number? (10 digits)',
        };
        const currentQuestion = fieldQuestions[restoredField] || `Please provide: ${restoredField.replace(/_/g, ' ')}`;
        // Show the current question for this field
        setSystemMessages([currentQuestion]);
        setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
      }
      
	      // CRITICAL: Set activeHeaderTab based on job posting state so the correct panel shows
	      const jobPhase = mergedJobPostingState.phase || 'idle';
	      const hasPostingLogs =
	        (mergedJobPostingState.linkedinLogs?.length > 0) ||
	        (mergedJobPostingState.naukriLogs?.length > 0) ||
	        mergedJobPostingState.linkedinProcessing ||
	        mergedJobPostingState.naukriProcessing;
	      restoredJobPostingActive =
	        jobPhase === 'posting' ||
	        jobPhase === 'posted' ||
	        jobPhase === 'channel_selection' ||
	        jobPhase === 'collecting_details' ||
	        hasPostingLogs;
	      if (restoredJobPostingActive) {
	        setAutoActiveHeaderTab('job_posting');
	        setRightPanelMode('auto');
	      }
	    }
    
    // Sourcing State — always clear first to prevent cross-session contamination
    setCandidates([]);
    setSourcingStats({});
    setTalentScoutingStarted(false);
    setSourcingComplete(false);
    setSelectedSourcingChannels([]);
    setGoogleSourcingLogs([]);
    setLinkedinSourcingLogs([]);
    setResumeBoxSourcingLogs([]);
    setGoogleSourcingProcessing(false);
    setLinkedinSourcingProcessing(false);
    setResumeBoxSourcingProcessing(false);
    setGoogleCandidateCount(0);
    setLinkedinCandidateCount(0);
    setResumeBoxCandidateCount(0);
    
    // Helper to filter out promotional messages from logs
    const filterPromotionalLogsSourcingRestore = (logs) => {
      if (!Array.isArray(logs)) return [];
      return logs.filter(log => {
        const msg = String(log?.message || '').toLowerCase();
        const content = log?.message || '';
        if (content.includes('**') && (
          msg.includes('moving to') ||
          msg.includes('ready to source') ||
          msg.includes('say **') ||
          msg.includes('sourcing platforms:') ||
          msg.includes('this will search') ||
          msg.includes("let's find")
        )) {
          return false;
        }
        return true;
      });
    };
    
    if (sessionState.sourcing) {
      const effectiveSourcing = getEffectiveSourcingState(sessionState.sourcing);
      setGoogleSourcingLogs(filterPromotionalLogsSourcingRestore(effectiveSourcing.googleLogs || []));
      setLinkedinSourcingLogs(filterPromotionalLogsSourcingRestore(effectiveSourcing.linkedinLogs || []));
      setResumeBoxSourcingLogs(filterPromotionalLogsSourcingRestore(effectiveSourcing.resumeBoxLogs || []));
      setGoogleSourcingProcessing(effectiveSourcing.googleProcessing);
      setLinkedinSourcingProcessing(effectiveSourcing.linkedinProcessing);
      setResumeBoxSourcingProcessing(effectiveSourcing.resumeBoxProcessing);
      setGoogleCandidateCount(effectiveSourcing.googleCandidateCount || 0);
      setLinkedinCandidateCount(effectiveSourcing.linkedinCandidateCount || 0);
      setResumeBoxCandidateCount(effectiveSourcing.resumeBoxCandidateCount || 0);
      setTalentScoutingStarted(effectiveSourcing.started);
      setSourcingComplete(effectiveSourcing.complete);
      setCandidates(effectiveSourcing.candidates || []);
      setSourcingStats(effectiveSourcing.stats || {});
      setSelectedSourcingChannels(effectiveSourcing.selectedChannels || []);
      
      // CRITICAL: Set activeHeaderTab to 'sourcing' if sourcing was active or has logs
      const hasAnyLogs = effectiveSourcing.hasLogs;
      const isProcessing = effectiveSourcing.anyProcessing;
	      if (!restoredJobPostingActive && (hasAnyLogs || isProcessing || effectiveSourcing.started)) {
	        // Only change tab to sourcing if NOT coming from dashboard
	        if (!fromDashboard) {
          setAutoActiveHeaderTab('sourcing');
        }
        // Also set rightPanelMode to 'sourcing' to show the sourcing logs panel
        setRightPanelMode('sourcing');
      }
    }

    // Restore rightPanelMode per session (overrides the sourcing panel mode if explicitly saved)
	    if (sessionState.rightPanelMode && !restoredJobPostingActive) {
	      setRightPanelMode(sessionState.rightPanelMode);
	    }
    
    // Outreach State
    if (sessionState.outreach) {
      setOutreachLogs(sessionState.outreach.logs || []);
      setOutreachPhase(sessionState.outreach.phase || null);
      setOutreachSending(sessionState.outreach.sending || false);
      setOutreachCompleted(sessionState.outreach.completed || false);
    }
    
    // Metadata
    if (sessionState.searchId) {
      setAutomationSearchId(sessionState.searchId);
    }
    
    // ===== LEGACY FALLBACK STRUCTURE (for backwards compatibility) =====
    // This handles old session states that might still be in the store
    if (!sessionState.jd && !sessionState.jobPosting && !sessionState.sourcing) {
      // Legacy flat structure
      setSystemMessages(sessionState.systemMessages || []);
      setJdText(sessionState.jdText || '');
      setJdError(sessionState.jdError || '');
      setAccumulatedRequirements(sessionState.accumulatedRequirements || {});
      setBackendMissingFields(sessionState.backendMissingFields || []);
      setAnalysisUxPhase(sessionState.analysisUxPhase || 'idle');
      
      // Legacy Loaders
      if (sessionState.loaders) {
        setIsJDLoading(sessionState.loaders.jd || false);
        setLinkedinProcessing(sessionState.loaders.linkedin || false);
        setNaukriProcessing(sessionState.loaders.naukri || false);
        setGoogleSourcingProcessing(sessionState.loaders.googleSourcing || false);
        setLinkedinSourcingProcessing(sessionState.loaders.linkedinSourcing || false);
      } else {
        setIsJDLoading(sessionState.isJDLoading || false);
        setLinkedinProcessing(sessionState.linkedinProcessing || false);
        setNaukriProcessing(sessionState.naukriProcessing || false);
        setGoogleSourcingProcessing(sessionState.googleSourcingProcessing || false);
        setLinkedinSourcingProcessing(sessionState.linkedinSourcingProcessing || false);
      }
      
      // Helper to filter promotional messages from legacy logs restoration
      const filterPromoLegacy = (logs) => {
        if (!Array.isArray(logs)) return [];
        return logs.filter(log => {
          const msg = String(log?.message || '').toLowerCase();
          const content = log?.message || '';
          if (content.includes('**') && (
            msg.includes('moving to') ||
            msg.includes('ready to source') ||
            msg.includes('say **') ||
            msg.includes('sourcing platforms:') ||
            msg.includes('this will search') ||
            msg.includes("let's find")
          )) {
            return false;
          }
          return true;
        });
      };
      
      // Legacy Logs (filtered)
      setLinkedinLogs(sessionState.linkedinLogs || []);
      setNaukriLogs(sessionState.naukriLogs || []);
      setGoogleSourcingLogs(filterPromoLegacy(sessionState.googleSourcingLogs || []));
      setLinkedinSourcingLogs(filterPromoLegacy(sessionState.linkedinSourcingLogs || []));
      
      // Legacy Job Posting
      setJobPostingPhase(sessionState.jobPostingPhase || 'idle');
      setSelectedChannels(sessionState.selectedChannels || []);
      setPostedChannels(sessionState.postedChannels || []);
      setJobPostingComplete(sessionState.jobPostingComplete || false);
      setJobPostingFailed(sessionState.jobPostingFailed || false);
      
      // Legacy Sourcing
      setTalentScoutingStarted(sessionState.talentScoutingStarted || false);
      setSourcingComplete(sessionState.sourcingComplete || false);
      setCandidates(sessionState.candidates || []);
      setSourcingStats(sessionState.sourcingStats || {});
      setSelectedSourcingChannels(sessionState.selectedSourcingChannels || []);
      
      // Legacy Requirements
      setFinalRequirements(sessionState.finalRequirements || {});
      
      // Legacy Other states
      if (sessionState.waitingForConfirmation !== undefined) setWaitingForConfirmation(sessionState.waitingForConfirmation);
      if (sessionState.pendingNaukriField !== undefined) setPendingNaukriField(sessionState.pendingNaukriField);
      if (sessionState.pendingNaukriFieldOptions !== undefined) setPendingNaukriFieldOptions(sessionState.pendingNaukriFieldOptions);
    }
  };
  // ===================== END SESSION STATE STORE =====================
  
  // Ref for immediate field collection state tracking (avoids React state timing issues)
  const fieldCollectionStateRef = useRef({ isActive: false, currentField: null, queue: [] });
  const [selectedCandidate, setSelectedCandidate] = useState(null); // Candidate detail view (Job Posting tab)
  const [quickProfileCandidate, setQuickProfileCandidate] = useState(null); // Simple profile modal for Job Posting tab
  // Job posting logs for LinkedIn and Naukri
  const [linkedinLogs, setLinkedinLogs] = useState([]); // Array of log messages from LinkedIn automation
  const [naukriLogs, setNaukriLogs] = useState([]); // Array of log messages from Naukri automation
  // Track if each platform is currently processing (for loading animation)
  const [linkedinProcessing, setLinkedinProcessing] = useState(false);
  const [naukriProcessing, setNaukriProcessing] = useState(false);
  
  // Sourcing logs state (for Google, LinkedIn, and Resume Box during candidate sourcing)
  const [googleSourcingLogs, setGoogleSourcingLogs] = useState([]);
  const [linkedinSourcingLogs, setLinkedinSourcingLogs] = useState([]);
  const [resumeBoxSourcingLogs, setResumeBoxSourcingLogs] = useState([]);
  const [googleSourcingProcessing, setGoogleSourcingProcessing] = useState(false);
  const [linkedinSourcingProcessing, setLinkedinSourcingProcessing] = useState(false);
  const [resumeBoxSourcingProcessing, setResumeBoxSourcingProcessing] = useState(false);
  const [googleCandidateCount, setGoogleCandidateCount] = useState(0);
  const [linkedinCandidateCount, setLinkedinCandidateCount] = useState(0);
  const [resumeBoxCandidateCount, setResumeBoxCandidateCount] = useState(0);
  // Sourcing phase counts for Early Signals display
  const [googleSearchFoundCount, setGoogleSearchFoundCount] = useState(0); // Raw count from Google search
  const [linkedinSearchFoundCount, setLinkedinSearchFoundCount] = useState(0); // Raw count from LinkedIn search
  const [enrichedCandidateCount, setEnrichedCandidateCount] = useState(0); // Count after enrichment
  const [matchedCandidateCount, setMatchedCandidateCount] = useState(0); // Count after JD matching/filtering
  const [talentPoolChannelFilter, setTalentPoolChannelFilter] = useState([]);
  // Re-sourcing confirmation state
  const [showResourcingConfirm, setShowResourcingConfirm] = useState(false); // Show dialog to confirm re-sourcing
  const [pendingResourcingChannels, setPendingResourcingChannels] = useState([]); // Channels to use when user confirms re-sourcing
  // Sourcing OTP UX gating (we only want: OTP prompt once, then OTP submitted once)
  const sourcingOtpUiRef = useRef({ promptShown: false, submitted: false });
  const [jobPostingProcessing, setJobPostingProcessing] = useState(false); // overall job posting in progress
  const [jobPostingComplete, setJobPostingComplete] = useState(false); // true when job posting is done
  const [jobPostingFailed, setJobPostingFailed] = useState(false); // true if job posting failed
  const [waitingForBackendQuestion, setWaitingForBackendQuestion] = useState(false); // true when waiting for backend to ask questions
  // Cross-session notification modal state for OTP/questions from background sessions
  const [crossSessionNotification, setCrossSessionNotification] = useState(null); // { sessionId, jobTitle, type, message, options, platform }
  // Google Calendar connect URL surfaced by the agent. We try to auto-open the popup, but browsers
  // block window.open that runs AFTER an await (the /chat response), so the first attempt silently
  // fails. When that happens we keep the URL here and show a Connect button — clicking it is a fresh
  // user gesture, so the popup opens reliably the first time.
  const [googleCalendarAuthUrl, setGoogleCalendarAuthUrl] = useState(null);
  const [mailAuthPrompt, setMailAuthPrompt] = useState(null);
  // WebSocket connection ID to track which system triggered OTP
  const [wsConnectionId, setWsConnectionId] = useState(null);
  // OTP Modal state - shows when OTP is required for any process
  const [otpModal, setOtpModal] = useState({
    isOpen: false,
    sessionId: null,
    process: 'verification', // 'linkedin', 'naukri', 'sourcing', 'job_posting'
    platform: '',
    message: '',
    jobTitle: '',
  });
  const [linkedinAppApprovalModal, setLinkedinAppApprovalModal] = useState({
    isOpen: false,
    sessionId: null,
    platform: 'LinkedIn',
    message: '',
    screenshotUrl: '',
    attempt: 1,
    maxAttempts: 3,
    timeoutSeconds: 60,
    status: 'waiting',
  });
  // Credentials modal state for platform login (LinkedIn/Naukri)
  const [credentialsModal, setCredentialsModal] = useState(null); // { sessionId, platform, attempt, maxAttempts, attemptMessage, isRetry, errorMessage, prompt }
  
  // Work email modal state for LinkedIn company email verification
  const [workEmailModal, setWorkEmailModal] = useState(null); // { sessionId, companyName, attempt, maxAttempts, attemptMessage, isRetry, errorMessage, prompt }
  
  // Site error recovery modal state (Naukri "Something Went Wrong" page)
  const [siteErrorRecoveryModal, setSiteErrorRecoveryModal] = useState({
    open: false,
    sessionId: null,
    message: '',
    prompt: '',
    clearLogsOnRetry: false,
    onRetry: null,
    onCancel: null,
  });

  // Resume View Modal state
  const [resumeViewModal, setResumeViewModal] = useState({
    isOpen: false,
    loading: false,
    data: null,
    error: null,
    attachmentUrl: null,
  });

  // Generate AI Interview Modal state
  const [generateInterviewModal, setGenerateInterviewModal] = useState({
    isOpen: false,
    loading: false,
    candidate: null,
    questions: [],
    error: null,
  });

  // Meeting Analysis Modal state
  const [meetingAnalysisModal, setMeetingAnalysisModal] = useState({
    isOpen: false,
    loading: false,
    candidate: null,
    analysis: null,
    aiResult: null,
    error: null,
  });
  const [rejectCandidate, setRejectCandidate] = useState(null);
  const meetingReportRequestRef = useRef(0);
  useEffect(() => () => { meetingReportRequestRef.current += 1; }, []);

  const [chatHistory, setChatHistory] = useState([]); // full chat for selected job/session
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false); // loader while fetching chat from backend
  const [chatResetToken, setChatResetToken] = useState(0); // bump to force fresh chat (New Job Posting)
  const [jdCriteriaModalOpen, setJdCriteriaModalOpen] = useState(false);
  const [jdCriteriaModalContext, setJdCriteriaModalContext] = useState({ jdId: '', jobTitle: '' });

  // Channel preferences modal state (for sourcing and outreach)
  const [showChannelSelectionModal, setShowChannelSelectionModal] = useState(false);
  const [channelSelectionType, setChannelSelectionType] = useState(null); // 'sourcing' | 'outreach'
  const [selectedSourcingChannels, setSelectedSourcingChannels] = useState([]); // ['google', 'linkedin']
  const [selectedOutreachChannels, setSelectedOutreachChannels] = useState([]); // ['gmail', 'linkedin', 'whatsapp']
  const [channelPreferencesLoaded, setChannelPreferencesLoaded] = useState(false);
  const [configuredPostingChannels, setConfiguredPostingChannels] = useState(ALL_POST_CHANNELS);
  const [saveChannelPreferences, setSaveChannelPreferences] = useState(false); // whether to save to DB
  const [pendingOutreachData, setPendingOutreachData] = useState(null); // { candidateIds, candidates } - stored when waiting for channel selection
  const [pendingSourcingSessionId, setPendingSourcingSessionId] = useState(null); // session_id that initiated sourcing - to ensure sourcing happens for the correct session even if user switches
  const pendingSourcingSessionRef = useRef(null); // Ref for async access to pending sourcing session

  // Outreach states
  const [outreachPhase, setOutreachPhase] = useState('idle'); // 'idle', 'template', 'sending', 'done'
  const [outreachTemplate, setOutreachTemplate] = useState('');
  const [outreachWorkspaceName, setOutreachWorkspaceName] = useState('');
  const [outreachCandidates, setOutreachCandidates] = useState([]);
  const [outreachCandidateIds, setOutreachCandidateIds] = useState([]);
  const [isLoadingOutreachTemplate, setIsLoadingOutreachTemplate] = useState(false);
  const [outreachTemplatesByChannel, setOutreachTemplatesByChannel] = useState(null); // { call, email, linkedin }
  const [outreachLogs, setOutreachLogs] = useState([]); // activity feed for outreach sending
  const [outreachSummary, setOutreachSummary] = useState(null); // total/sent/failed per channel
  const [outreachSending, setOutreachSending] = useState(false);
  const [outreachCompleted, setOutreachCompleted] = useState(false);
  const [syncingPostedApplicants, setSyncingPostedApplicants] = useState(false);
  const [postedApplicantsLastSyncTime, setPostedApplicantsLastSyncTime] = useState(null);
  const [rightPanelMode, setRightPanelMode] = useState('auto'); // auto | jd | candidates | sourcing | job_posting
  const [rightPanelTransitioning, setRightPanelTransitioning] = useState(false); // brief loader on panel mode switch
  const [candidatesRenderToken, setCandidatesRenderToken] = useState(0); // force CandidateResults re-render
  const [overviewScrollToCandidatesToken, setOverviewScrollToCandidatesToken] = useState(0);
  const [jobPostingExpandToken, setJobPostingExpandToken] = useState(0);
  const [sourcingExpandToken, setSourcingExpandToken] = useState(0);
  const [sourcingRefreshToken, setSourcingRefreshToken] = useState(0);
  const lastNotificationDeepLinkRef = useRef('');

  const getSourcingNotificationChannels = (sessionId, fallbackChannels = []) => {
    const storedChannels = sessionStateStoreRef.current.get(sessionId)?.sourcing?.selectedChannels;
    if (Array.isArray(storedChannels) && storedChannels.length > 0) return storedChannels;
    if (Array.isArray(fallbackChannels) && fallbackChannels.length > 0) return fallbackChannels;
    return selectedSourcingChannels || [];
  };

  const buildSourcingSourcesLabel = (channels = [], { includeLinkedin = false, fallbackMessage = '' } = {}) => {
    const hasExplicitChannels = Array.isArray(channels) && channels.length > 0;
    const labels = [];
    if (hasExplicitChannels) {
      if (shouldRunResumeBoxForChannels(channels)) labels.push('Resume Box');
      if (shouldRunGoogleForChannels(channels)) labels.push('Online Sourcing');
      if (includeLinkedin && shouldRunLinkedinForChannels(channels)) labels.push('LinkedIn');
    } else {
      const message = String(fallbackMessage || '').toLowerCase();
      if (message.includes('resume box') || message.includes('resumebox')) labels.push('Resume Box');
      if (message.includes('google') || message.includes('online sourcing')) labels.push('Online Sourcing');
      if (includeLinkedin && message.includes('linkedin')) labels.push('LinkedIn');
    }
    return labels.join(' + ');
  };

  const addUniqueSourceLabel = (labels, label) => {
    if (label && !labels.includes(label)) labels.push(label);
  };

  const inferSourcingSourcesLabel = ({ candidates = [], stats = {}, fallbackMessage = '', includeLinkedin = true } = {}) => {
    const labels = [];
    const addFromText = (raw) => {
      const text = String(raw || '').toLowerCase();
      if (!text) return;
      if (text.includes('resume_box') || text.includes('resume box') || text.includes('resumebox')) {
        addUniqueSourceLabel(labels, 'Resume Box');
      }
      if (text.includes('google') || text.includes('online sourcing') || text.includes('web search')) {
        addUniqueSourceLabel(labels, 'Online Sourcing');
      }
      if (includeLinkedin && text.includes('linkedin')) {
        addUniqueSourceLabel(labels, 'LinkedIn');
      }
    };

    (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
      addFromText(candidate?.source_type);
      addFromText(candidate?.source);
      addFromText(candidate?.platform);
      if (Array.isArray(candidate?.platforms)) {
        candidate.platforms.forEach(addFromText);
      }
    });

    if (Number(stats?.google_count || stats?.totalGoogleCandidates || stats?.total_google_candidates || 0) > 0) {
      addUniqueSourceLabel(labels, 'Online Sourcing');
    }
    if (includeLinkedin && Number(stats?.linkedin_count || stats?.totalLinkedinCandidates || stats?.total_linkedin_candidates || 0) > 0) {
      addUniqueSourceLabel(labels, 'LinkedIn');
    }
    if (Number(stats?.resume_box_count || stats?.totalResumeBoxCandidates || stats?.total_resume_box_candidates || 0) > 0) {
      addUniqueSourceLabel(labels, 'Resume Box');
    }
    addFromText(fallbackMessage);

    return labels.join(' + ');
  };

  const getCompletedSourcingSourcesLabel = (sessionId, options = {}) => {
    const channels = getSourcingNotificationChannels(sessionId, options.fallbackChannels || []);
    return (
      buildSourcingSourcesLabel(channels, {
        includeLinkedin: options.includeLinkedin !== false,
        fallbackMessage: options.fallbackMessage,
      }) ||
      inferSourcingSourcesLabel(options) ||
      'Candidate sourcing'
    );
  };

  const buildSourcingCompletedMessage = (sessionId, count, options = {}) => {
    const sourceLabel = getCompletedSourcingSourcesLabel(sessionId, options);
    const candidateText = `${count} candidate${count === 1 ? '' : 's'}`;
    const suffix = options.suffix || '';
    return `${sourceLabel} completed! Found ${candidateText}.${suffix}`;
  };

  const buildSearchParams = useCallback((params = {}) => {
    const nextParams = { ...params };
    if (fromDashboard) {
      nextParams.from_dashboard = 'true';
    }
    return nextParams;
  }, [fromDashboard]);

  // -- Helper: select a candidate and persist in URL --
  const selectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    if (candidate) {
      const currentSession = selectedJobId || automationSessionId || sessionId;
      const params = { session_id: currentSession };
      const candidateId = candidate.id || candidate.candidate_id;
      if (candidateId) params.candidateId = String(candidateId);
      if (candidate.email) params.candidate = candidate.email;
      if (candidate._searchId) params.searchId = candidate._searchId;
      setSearchParams(buildSearchParams(params), { replace: true });
    }
  };

  // -- Helper: clear candidate and remove from URL --
  const clearCandidate = ({ restoreInlineCandidate = true } = {}) => {
    const candidateSearchId = selectedCandidate?._searchId || searchParams.get('searchId') || automationSearchId;
    setSelectedCandidate(null);
    const currentSession = selectedJobId || automationSessionId || sessionId;
    if (currentSession) {
      const params = { session_id: currentSession };
      if (candidateSearchId) params.searchId = candidateSearchId;
      setSearchParams(buildSearchParams(params), { replace: true });
      if (restoreInlineCandidate) {
        loadSessionData(currentSession).catch((err) => {
          console.warn('[ChatPage] Failed to reload chat after closing candidate details:', err);
        });
      }
    } else {
      setSearchParams(buildSearchParams({}), { replace: true });
    }
  };

  // -- Helper: update a candidate's data in the candidates array + selectedCandidate --
  const updateCandidateData = (email, updates) => {
    setCandidates((prev) =>
      prev.map((c) =>
        (c.email || '').toLowerCase() === (email || '').toLowerCase()
          ? { ...c, ...updates }
          : c
      )
    );
    setSelectedCandidate((prev) =>
      prev && (prev.email || '').toLowerCase() === (email || '').toLowerCase()
        ? { ...prev, ...updates }
        : prev
    );
  };

  // -- Helper: a candidate was rejected → drop them from the sourcing list (mirrors what a full
  // refresh does) and clear the transient candidate view that's currently open. Accepts a candidate
  // object or a raw email.
  const handleCandidateRejected = (rejected) => {
    const email = String(
      (rejected && (rejected.email || rejected.candidate_email)) ||
      (typeof rejected === 'string' ? rejected : '') ||
      ''
    ).toLowerCase();
    const rid = rejected && (rejected.id ?? rejected.candidate_id);
    if (email || rid != null) {
      setCandidates((prev) => prev.filter((c) => {
        if (email && String(c.email || '').toLowerCase() === email) return false;
        if (rid != null && (c.id === rid || c.candidate_id === rid)) return false;
        return true;
      }));
    }
    // Force the sourcing panel (own server-fetched list) to re-fetch so the rejected candidate
    // disappears immediately, and clear the transient candidate view that's currently open.
    setSourcingRefreshToken((prev) => prev + 1);
    clearCandidate({ restoreInlineCandidate: false });
  };

  const handleOpenOverviewCandidates = () => {
    setUserActiveHeaderTab('overview');
    setOverviewScrollToCandidatesToken((prev) => prev + 1);
  };

  const setUserActiveHeaderTab = useCallback((tab) => {
    manualTabOverrideRef.current = true;
    setActiveHeaderTab(tab);
  }, []);

  const handleHeaderTabChange = (tab) => {
    if (tab === 'job_posting' && blockDisabledFeature('job_posting')) return;
    if (tab === 'sourcing' && blockDisabledFeature('sourcing')) return;
    if (tab === 'interview') {
      if (blockDisabledSchedulingFlow()) return;
    }
    setUserActiveHeaderTab(tab);
    if (tab === 'job_posting') {
      if (rightPanelMode !== 'jd') {
        setRightPanelMode('auto');
      }
      if (shouldForceJobPostingPanel || linkedinLogs.length > 0 || naukriLogs.length > 0) {
        setJobPostingExpandToken((prev) => prev + 1);
      }
    } else if (tab === 'sourcing') {
      setRightPanelMode('sourcing');
      setSourcingExpandToken((prev) => prev + 1);
    }
    clearCandidate({ restoreInlineCandidate: false });
  };

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    const requestedPanel = searchParams.get('panel');
    const allowedTabs = new Set(['status', 'job_posting', 'sourcing', 'interview', 'overview']);
    const allowedPanels = new Set(['auto', 'jd', 'candidates', 'sourcing', 'job_posting']);
    const deepLinkKey = [
      requestedTab || '',
      requestedPanel || '',
      searchParams.get('session_id') || '',
      searchParams.get('searchId') || '',
      searchParams.get('candidateId') || '',
      searchParams.get('candidate') || '',
      searchParams.get('interview_id') || '',
    ].join('|');

    if (!requestedTab && !requestedPanel) return;

    if (requestedTab && allowedTabs.has(requestedTab) && activeHeaderTab !== requestedTab) {
      setUserActiveHeaderTab(requestedTab);
    }

    if (requestedPanel && allowedPanels.has(requestedPanel) && rightPanelMode !== requestedPanel) {
      setRightPanelMode(requestedPanel);
    }

    if (lastNotificationDeepLinkRef.current !== deepLinkKey) {
      lastNotificationDeepLinkRef.current = deepLinkKey;
      if (requestedTab === 'sourcing' || requestedPanel === 'sourcing') {
        setSourcingExpandToken((prev) => prev + 1);
      }
      if (requestedTab === 'job_posting' || requestedPanel === 'job_posting') {
        setJobPostingExpandToken((prev) => prev + 1);
      }
      if (requestedTab === 'interview' || requestedPanel === 'candidates') {
        setMobilePanelView('results');
      }
    }
  }, [activeHeaderTab, rightPanelMode, searchParams, setUserActiveHeaderTab]);

  const setAutoActiveHeaderTab = useCallback((tab, { force = false } = {}) => {
    if (force || !manualTabOverrideRef.current || activeHeaderTab === tab) {
      setActiveHeaderTab(tab);
    }
  }, [activeHeaderTab]);

  const handleViewCandidateDetailsFromChat = (candidate) => {
    if (!candidate) return;
    selectCandidate({ ...candidate, _searchId: candidate._searchId || automationSearchId });
  };

  const handleViewResume = useCallback(async (docId, candidate) => {
    if (!docId) return;
    const metadata = getCid();
    const cid = metadata;
    const authToken = getAuthToken();
    const CORE_URL = process.env.REACT_APP_CORE_URL || '';
    const pdfUrl = cid
      ? `${CORE_URL}/api/resume-parsing/file/${docId}?cid=${cid}${authToken ? `&token=${encodeURIComponent(authToken)}` : ''}`
      : null;
    const searchId = candidate?._searchId || automationSearchId;
    setResumeViewModal({ isOpen: true, loading: true, data: null, error: null, attachmentUrl: pdfUrl });
    try {
      const searchParam = searchId ? `?search_id=${searchId}` : '';
      const data = await aiAutomationApi.get(`/resume/view/${docId}${searchParam}`);
      if (data?.success) {
        setResumeViewModal({ isOpen: true, loading: false, data, error: null, attachmentUrl: pdfUrl });
      } else {
        setResumeViewModal({ isOpen: true, loading: false, data: null, error: 'Resume data not found.', attachmentUrl: pdfUrl });
      }
    } catch (err) {
      setResumeViewModal({ isOpen: true, loading: false, data: null, error: err?.message || 'Failed to load resume data.', attachmentUrl: pdfUrl });
    }
  }, [automationSearchId]);

  const currentPostedApplicantsSessionId = automationSessionId || selectedJobId || sessionId;
  const syncApplicantsStartedByUserRef = useRef(false);
  const syncingPostedApplicantsRef = useRef(false);

  // Stable refs so polling interval never needs to be rebuilt when these change
  const _syncSessionRef = useRef(null);
  _syncSessionRef.current = currentPostedApplicantsSessionId;

  const fetchPostedApplicantsSyncMeta = useCallback(async (targetSessionId = null) => {
    const sessionToUse = targetSessionId || _syncSessionRef.current;
    if (!sessionToUse) {
      setPostedApplicantsLastSyncTime(null);
      setSyncingPostedApplicants(false);
      syncingPostedApplicantsRef.current = false;
      return null;
    }

    try {
      const data = await aiAutomationApi.get(`/job-postings/by-session/${sessionToUse}`);
      const jobPosting = data?.job_posting;
      const lastSync = jobPosting?.last_sync ? new Date(jobPosting.last_sync) : null;
      const syncing = Boolean(jobPosting?.sync_process);

      setPostedApplicantsLastSyncTime(lastSync);
      setSyncingPostedApplicants(syncing);
      syncingPostedApplicantsRef.current = syncing;

      return { lastSync, syncing };
    } catch (error) {
      setPostedApplicantsLastSyncTime(null);
      setSyncingPostedApplicants(false);
      syncingPostedApplicantsRef.current = false;
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable — reads session from ref, never needs to be rebuilt

  useEffect(() => {
    setPostedApplicantsLastSyncTime(null);
    setSyncingPostedApplicants(false);
    syncingPostedApplicantsRef.current = false;
    syncApplicantsStartedByUserRef.current = false;

    if (currentPostedApplicantsSessionId) {
      fetchPostedApplicantsSyncMeta(currentPostedApplicantsSessionId);
    }
  }, [currentPostedApplicantsSessionId, fetchPostedApplicantsSyncMeta]);

  useEffect(() => {
    // Use ref so interval is started once and never restarted due to state changes
    if (!syncingPostedApplicantsRef.current || !_syncSessionRef.current) return;

    let pollCount = 0;
    const interval = setInterval(async () => {
      if (pollCount >= 60) {
        clearInterval(interval);
        setSyncingPostedApplicants(false);
        syncingPostedApplicantsRef.current = false;
        syncApplicantsStartedByUserRef.current = false;
        return;
      }
      pollCount += 1;

      const syncMeta = await fetchPostedApplicantsSyncMeta(_syncSessionRef.current);
      if (!syncMeta?.syncing) {
        clearInterval(interval);
        if (syncApplicantsStartedByUserRef.current) {
          showToast('Applicant sync completed.', 'success', 4000);
        }
        syncApplicantsStartedByUserRef.current = false;
      }
    }, 5000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncingPostedApplicants]); // only restart interval when syncing transitions false→true

  const handleSyncPostedApplicants = useCallback(async () => {
    if (blockDisabledFeature('job_posting')) return;
    const currentSession = automationSessionId || selectedJobId || sessionId;
    if (!currentSession || syncingPostedApplicants) return;
    const preserveJobPostingView =
      activeHeaderTab === 'job_posting' &&
      (
        jobPostingPhase === 'posting' ||
        jobPostingPhase === 'collecting_details' ||
        jobPostingPhase === 'channel_selection' ||
        jobPostingProcessing ||
        linkedinProcessing ||
        naukriProcessing ||
        waitingForBackendQuestion ||
        pendingNaukriField ||
        fieldCollectionStateRef.current?.isActive
      );
    syncApplicantsStartedByUserRef.current = true;
    setSyncingPostedApplicants(true);
    try {
      await aiAutomationApi.post('/get-postedjob-candidates', {
        session_id: currentSession,
        cid: getCid?.() || undefined,
        connection_id: wsConnectionId,  // Send connection_id for targeted OTP messaging
      }, { timeout: 300000 });
      await fetchPostedApplicantsSyncMeta(currentSession);
      setRightPanelMode('candidates');
      if (!preserveJobPostingView) {
        setActiveHeaderTab('sourcing');
      }
      showToast('Applicant sync started. Results will appear in candidates.', 'success', 4000);
    } catch (error) {
      const isGatewayTimeout =
        error?.status === 504 ||
        /504|gateway time-?out/i.test(String(error?.message || ''));
      if (isGatewayTimeout) {
        const syncMeta = await fetchPostedApplicantsSyncMeta(currentSession);
        if (syncMeta?.syncing) {
          setRightPanelMode('candidates');
          if (!preserveJobPostingView) {
            setActiveHeaderTab('sourcing');
          }
          showToast('Applicant sync is still running in the background. Check the sourcing panel for progress.', 'success', 5000);
          return;
        }
      }
      syncApplicantsStartedByUserRef.current = false;
      setSyncingPostedApplicants(false);
      showToast(error?.message || 'Failed to start applicant sync.', 'error', 5000);
    }
  }, [
    automationSessionId,
    activeHeaderTab,
    fetchPostedApplicantsSyncMeta,
    jobPostingPhase,
    jobPostingProcessing,
    linkedinProcessing,
    naukriProcessing,
    pendingNaukriField,
    selectedJobId,
    sessionId,
    showToast,
    syncingPostedApplicants,
    blockDisabledFeature,
    waitingForBackendQuestion,
  ]);

  // -- Right panel transition loader: show foggy overlay while panel content changes --
  const switchRightPanelWithLoader = useCallback((newMode) => {
    if (newMode === rightPanelMode) return; // no-op if same mode
    setRightPanelTransitioning(true);
    // Switch content immediately — the foggy overlay covers the transition
    setRightPanelMode(newMode);
    // Hide the overlay once the new content has rendered
    setTimeout(() => setRightPanelTransitioning(false), 350);
  }, [rightPanelMode]);

  // Safety net: never leave the right panel loader stuck for more than 2 seconds
  useEffect(() => {
    if (!rightPanelTransitioning) return;
    const safetyTimer = setTimeout(() => setRightPanelTransitioning(false), 2000);
    return () => clearTimeout(safetyTimer);
  }, [rightPanelTransitioning]);

  // Clear agent banner when sourcing or job posting completes
  // These processes complete via WebSocket/background, not via /chat response,
  // so the banner isn't cleared by the normal response handler.
  useEffect(() => {
    if (sourcingComplete && agentStageLabel === 'Candidate Sourcing') {
      setAgentStageLabel('');
      setAgentStageCancellable(false);
      const sid = automationSessionId || selectedJobId;
      if (sid) {
        updateSessionState(sid, (s) => {
          s.agentStage = { label: '', cancellable: false };
          return s;
        });
      }
    }
  }, [sourcingComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (jobPostingComplete && agentStageLabel === 'Job Posting') {
      setAgentStageLabel('');
      setAgentStageCancellable(false);
      const sid = automationSessionId || selectedJobId;
      if (sid) {
        updateSessionState(sid, (s) => {
          s.agentStage = { label: '', cancellable: false };
          return s;
        });
      }
    }
  }, [jobPostingComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const computeMissingFieldsFromAccumulated = (reqs) => {
    const missing = [];
    const title = reqs?.title || reqs?.role;
    const location = reqs?.location;
    const experience = reqs?.experience;
    const skills = Array.isArray(reqs?.skills) ? reqs.skills : [];

    if (!title) missing.push('title');
    if (!location) missing.push('location');
    if (!experience) missing.push('experience');
    if (!skills || skills.length === 0) missing.push('skills');

    return missing;
  };

  const parseMessageMetadata = (metadata) => {
    if (!metadata) return {};
    if (typeof metadata === 'object') return metadata;
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return {};
  };

  const normalizeAssistantChatContent = useCallback((content, metadata = {}, phase = '') => {
    const message = String(content || '').trim();
    if (!message) return message;

    const normalized = message.toLowerCase();
    const normalizedPhase = String(phase || metadata?.phase || '').toLowerCase();
    const platform = metadata?.platform || metadata?.channel || '';
    const detail =
      metadata?.error_details ||
      metadata?.error_detail ||
      metadata?.details ||
      metadata?.reason ||
      '';

    if (
      normalized === 'buttons_not_found' ||
      normalized === 'posting_failed' ||
      normalized === 'button_not_found' ||
      normalizedPhase === 'job_posting_failed' ||
      metadata?.type === 'job_posting_error' ||
      metadata?.type === 'job_posting_update_failure'
    ) {
      return `❌ ${formatJobPostingErrorMessage(message, { platform, detail })}`;
    }

    return message;
  }, []);

  const isTransientJobPostingChatMessage = useCallback((message) => {
    if (!message) return false;

    const metadata = parseMessageMetadata(message?.metadata);
    const content = String(message?.message || message?.content || '').trim().toLowerCase();
    const messageType = String(message?.message_type || '').toLowerCase();
    const phase = String(message?.phase || metadata?.phase || '').toLowerCase();
    const metadataType = String(metadata?.type || '').toLowerCase();

    if (metadataType === 'job_posting_success') return true;

    if (content.includes('posted successfully on naukri') || content.includes('posted successfully on linkedin')) {
      return true;
    }

    if (phase !== 'job_posting_completed') return false;

    return messageType === 'assistant' || messageType === 'system' || message?.sender === 'agent';
  }, []);

  const sanitizeRestoredChatHistory = useCallback((messages) => {
    if (!Array.isArray(messages)) return [];
    return messages.filter((message) => {
      if (isTransientJobPostingChatMessage(message)) return false;
      const content = String(message?.message || message?.content || '').trim();
      const sender = String(message?.sender || message?.message_role || message?.message_type || '').toLowerCase();
      if (!content) return false;
      if ((sender === 'user') && isLocalUiOnlyChatCommand(content)) return false;
      if ((sender === 'agent' || sender === 'assistant' || sender === 'system') && isLocalUiNavigationHint(content)) return false;
      return true;
    });
  }, [isTransientJobPostingChatMessage]);

  const sanitizeSystemMessages = useCallback((messages) => {
    if (!Array.isArray(messages)) return [];
    return messages.filter((message) => {
      const text = String(message || '').trim().toLowerCase();
      return !(
        text.includes('posted successfully on naukri') ||
        text.includes('posted successfully on linkedin') ||
        isLocalUiNavigationHint(text)
      );
    });
  }, []);

  const mergePersistedChatArtifacts = useCallback((baseMessages = [], persistedMessages = []) => {
    const safeBase = Array.isArray(baseMessages) ? baseMessages : [];
    const safePersisted = Array.isArray(persistedMessages) ? persistedMessages : [];

    const syntheticArtifacts = safePersisted.filter((message) => {
      const type = String(message?.metadata?.type || '').toLowerCase();
      return type === 'jd_card' || type === 'candidate_card';
    });

    if (syntheticArtifacts.length === 0) return safeBase;

    const merged = [...safeBase];
    syntheticArtifacts.forEach((artifact) => {
      const artifactType = String(artifact?.metadata?.type || '').toLowerCase();
      const alreadyExists = merged.some((existing) => {
        const existingType = String(existing?.metadata?.type || '').toLowerCase();
        if (existingType !== artifactType) return false;
        if (artifactType === 'jd_card') {
          return String(existing?.metadata?.jdText || '') === String(artifact?.metadata?.jdText || '');
        }
        if (artifactType === 'candidate_card') {
          const existingCandidate = existing?.metadata?.candidate || {};
          const artifactCandidate = artifact?.metadata?.candidate || {};
          return String(existingCandidate?.email || '') === String(artifactCandidate?.email || '')
            && String(existingCandidate?.name || '') === String(artifactCandidate?.name || '');
        }
        return false;
      });

      if (!alreadyExists) merged.push(artifact);
    });

    return merged.sort((a, b) => {
      const aTime = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });
  }, []);

  const detectRemoteActiveJobPosting = async (sessionToCheck) => {
    if (!sessionToCheck) return false;
    try {
      // First check the database-backed process status (most reliable source)
      try {
        const processStatus = await aiAutomationApi.get(`/process-status/${sessionToCheck}`);
        if (processStatus?.success && processStatus?.job_posting === true) {
          console.log('[detectRemoteActiveJobPosting] Database says job_posting=true for', sessionToCheck);
          return true;
        }
        if (processStatus?.success && processStatus?.job_posting === false) {
          console.log('[detectRemoteActiveJobPosting] Database says job_posting=false for', sessionToCheck);
          // Database explicitly says no job posting in progress - trust this
          return false;
        }
      } catch (processStatusError) {
        console.warn('[detectRemoteActiveJobPosting] Could not check process status table:', processStatusError);
        // Fall back to existing logic if process status endpoint fails
      }
      
      // Fallback: check session data and chat logs (existing logic)
      const sessionData = await aiAutomationApi.get(`/sessions/${sessionToCheck}`);
      const postings = Array.isArray(sessionData?.job_postings) ? sessionData.job_postings : [];
      const latestPosting = postings.length > 0 ? postings[0] : null;
      const postingStatus = String(latestPosting?.posting_status || '').toLowerCase();
      const inProgressStatuses = new Set([
        'posting',
        'processing',
        'in_progress',
        'in-progress',
        'pending',
        'running',
        'started',
      ]);
      if (inProgressStatuses.has(postingStatus)) return true;
      if (postingStatus === 'posted' || postingStatus === 'partial' || postingStatus === 'failed') return false;

      // Fallback: infer from chat logs when posting_status is not updated yet.
      const historyResponse = await aiAutomationApi.get(
        `/sessions/${sessionToCheck}/chat-messages`
      );
      const messages = Array.isArray(historyResponse?.messages) ? historyResponse.messages : [];
      if (messages.length === 0) return false;

      let hasPostingActivity = false;
      let hasPostingFinal = false;

      for (const m of messages) {
        const content = String(m?.content || '').toLowerCase();
        if (!content) continue;
        const metadata = parseMessageMetadata(m?.metadata);
        const phaseValue = String(m?.phase || metadata?.phase || '').toLowerCase();
        const platformValue = normalizePlatformName(
          m?.platform || metadata?.platform || metadata?.source || ''
        );
        const messageType = String(m?.message_type || '').toLowerCase();
        const statusValue = String(metadata?.status || metadata?.state || '').toLowerCase();
        const metadataType = String(metadata?.type || '').toLowerCase();
        const isJobPostingPreStartPhase =
          phaseValue === 'job_posting_field_collection' ||
          phaseValue === 'job_posting_channel_selection';
        const isJobPostingStartSignal =
          phaseValue === 'job_posting_started' ||
          phaseValue === 'job_posting_start' ||
          metadataType === 'job_posting_start' ||
          metadata?._internal === 'job_posting_started_signal';
        const isJobPostingTerminalSignal =
          phaseValue === 'job_posting_completed' ||
          phaseValue === 'job_posting_complete' ||
          phaseValue === 'job_posting_failed' ||
          phaseValue === 'job_posting_error';

        const postingPhaseDetected =
          !isJobPostingPreStartPhase &&
          (isJobPostingStartSignal ||
            isJobPostingTerminalSignal ||
            phaseValue === 'linkedin' ||
            phaseValue === 'naukri' ||
            (messageType === 'log' &&
              (platformValue === 'LinkedIn' || platformValue === 'Naukri') &&
              !phaseValue.includes('source_candidates') &&
              !phaseValue.includes('sourcing') &&
              !phaseValue.includes('outreach')) ||
            ((messageType === 'log' || messageType === 'system') &&
              (content.includes('job posting started') ||
                content.includes('job posting completed') ||
                content.includes('job posting failed') ||
                content.includes('posted successfully'))));

        if (!postingPhaseDetected) continue;
        hasPostingActivity = true;

        if (
          statusValue === 'completed' ||
          statusValue === 'failed' ||
          statusValue === 'success' ||
          content.includes('all job postings complete') ||
          content.includes('job posting completed') ||
          content.includes('posted successfully')
        ) {
          hasPostingFinal = true;
        }
      }

      return hasPostingActivity && !hasPostingFinal;
    } catch (error) {
      console.warn('Failed to detect remote active job posting state', error);
      return false;
    }
  };

  const detectRemoteActiveSourcing = async (sessionToCheck) => {
    if (!sessionToCheck) return false;
    try {
      // First check the database-backed process status (most reliable source)
      try {
        const processStatus = await aiAutomationApi.get(`/process-status/${sessionToCheck}`);
        if (processStatus?.success && processStatus?.searching === true) {
          console.log('[detectRemoteActiveSourcing] Database says searching=true for', sessionToCheck);
          return true;
        }
        if (processStatus?.success && processStatus?.searching === false) {
          console.log('[detectRemoteActiveSourcing] Database says searching=false for', sessionToCheck);
          // Database explicitly says no searching in progress - trust this
          return false;
        }
      } catch (processStatusError) {
        console.warn('[detectRemoteActiveSourcing] Could not check process status table:', processStatusError);
        // Fall back to existing logic if process status endpoint fails
      }
      
      // Fallback: check session data and chat logs (existing logic)
      const sessionData = await aiAutomationApi.get(`/sessions/${sessionToCheck}`);
      const searches = Array.isArray(sessionData?.searches) ? sessionData.searches : [];
      const totalCandidates = searches
        .flatMap((s) => (Array.isArray(s?.candidates) ? s.candidates : []))
        .filter(Boolean).length;
      if (totalCandidates > 0) return false;

      const historyResponse = await aiAutomationApi.get(
        `/sessions/${sessionToCheck}/chat-messages`
      );
      const messages = Array.isArray(historyResponse?.messages) ? historyResponse.messages : [];

      let hasSourcingActivity = false;
      let hasSourcingCompleted = false;
      for (const m of messages) {
        const content = String(m?.content || '').toLowerCase();
        const metadata = parseMessageMetadata(m?.metadata);
        const phaseValue = String(m?.phase || metadata?.phase || '').toLowerCase();
        const statusValue = String(metadata?.status || metadata?.state || '').toLowerCase();

        if (
          phaseValue.includes('source_candidates') ||
          phaseValue.includes('sourcing') ||
          content.includes('searching across linkedin') ||
          content.includes('candidate sourcing')
        ) {
          hasSourcingActivity = true;
        }
        if (
          statusValue === 'completed' ||
          statusValue === 'success' ||
          content.includes('talent scouting is completed') ||
          content.includes('sourcing complete') ||
          content.includes('sourcing completed')
        ) {
          hasSourcingCompleted = true;
        }
      }
      return hasSourcingActivity && !hasSourcingCompleted;
    } catch (error) {
      console.warn('Failed to detect remote active sourcing state', error);
      return false;
    }
  };

  const detectRemoteActiveOutreach = async (sessionToCheck) => {
    if (!sessionToCheck) return false;
    try {
      // First check the database-backed process status (most reliable source)
      try {
        const processStatus = await aiAutomationApi.get(`/process-status/${sessionToCheck}`);
        if (processStatus?.success && processStatus?.outreach === true) {
          console.log('[detectRemoteActiveOutreach] Database says outreach=true for', sessionToCheck);
          return true;
        }
        if (processStatus?.success && processStatus?.outreach === false) {
          console.log('[detectRemoteActiveOutreach] Database says outreach=false for', sessionToCheck);
          // Database explicitly says no outreach in progress - trust this
          return false;
        }
      } catch (processStatusError) {
        console.warn('[detectRemoteActiveOutreach] Could not check process status table:', processStatusError);
        // Fall back to existing logic if process status endpoint fails
      }
      
      // Fallback: check chat logs (existing logic)
      const historyResponse = await aiAutomationApi.get(
        `/sessions/${sessionToCheck}/chat-messages`
      );
      const messages = Array.isArray(historyResponse?.messages) ? historyResponse.messages : [];

      let hasOutreachActivity = false;
      let hasOutreachCompleted = false;
      for (const m of messages) {
        const content = String(m?.content || '').toLowerCase();
        const metadata = parseMessageMetadata(m?.metadata);
        const phaseValue = String(m?.phase || metadata?.phase || '').toLowerCase();
        const platformValue = String(m?.platform || metadata?.platform || '').toLowerCase();
        const statusValue = String(metadata?.status || metadata?.state || '').toLowerCase();

        if (
          phaseValue.includes('outreach') ||
          platformValue.includes('outreach') ||
          content.includes('outreach')
        ) {
          hasOutreachActivity = true;
        }
        if (
          statusValue === 'completed' ||
          statusValue === 'success' ||
          content.includes('outreach completed') ||
          content.includes('all outreach messages')
        ) {
          hasOutreachCompleted = true;
        }
      }

      return hasOutreachActivity && !hasOutreachCompleted;
    } catch (error) {
      console.warn('Failed to detect remote active outreach state', error);
      return false;
    }
  };

  const getOutreachWorkflowBlockMessage = useCallback(async (targetSessionId = null) => {
    const sessionToCheck = targetSessionId || selectedJobId || automationSessionId || sessionId;
    if (!sessionToCheck) return null;

    try {
      const processStatus = await aiAutomationApi.get(`/process-status/${sessionToCheck}`);
      if (processStatus?.success) {
        const jobPostingActive =
          processStatus?.job_posting === true ||
          processStatus?.job_post_linkedin === 'processing' ||
          processStatus?.job_post_naukri === 'processing';
        if (jobPostingActive) {
          return 'Job posting is in progress. Please wait until posting completes before sending outreach.';
        }

        const sourcingActive =
          processStatus?.searching === true ||
          processStatus?.sourcing_google === 'processing' ||
          processStatus?.sourcing_linkedin === 'processing' ||
          processStatus?.sourcing_resumebox === 'processing';
        if (sourcingActive) {
          return 'Candidate sourcing is in progress. Please wait until sourcing completes before sending outreach.';
        }

        if (processStatus?.outreach === true) {
          return 'Outreach is already in progress. Please wait until it completes before starting another outreach.';
        }
      }
    } catch (error) {
      console.warn('[getOutreachWorkflowBlockMessage] Failed to read process status, falling back to local/session state.', error);
    }

    const [remoteJobPostingActive, remoteSourcingActive, remoteOutreachActive] = await Promise.all([
      detectRemoteActiveJobPosting(sessionToCheck),
      detectRemoteActiveSourcing(sessionToCheck),
      detectRemoteActiveOutreach(sessionToCheck),
    ]);

    if (remoteJobPostingActive) {
      return 'Job posting is in progress. Please wait until posting completes before sending outreach.';
    }
    if (remoteSourcingActive) {
      return 'Candidate sourcing is in progress. Please wait until sourcing completes before sending outreach.';
    }
    if (remoteOutreachActive) {
      return 'Outreach is already in progress. Please wait until it completes before starting another outreach.';
    }

    const persistedSessionState = sessionStateStoreRef.current.get(sessionToCheck) || createEmptySessionState();
    const localJobPostingActive =
      jobPostingProcessing ||
      linkedinProcessing ||
      naukriProcessing ||
      persistedSessionState?.jobPosting?.processing ||
      persistedSessionState?.jobPosting?.linkedinProcessing ||
      persistedSessionState?.jobPosting?.naukriProcessing;

    if (localJobPostingActive) {
      return 'Job posting is in progress. Please wait until posting completes before sending outreach.';
    }

    const localSourcingActive =
      (talentScoutingStarted && !sourcingComplete) ||
      googleSourcingProcessing ||
      linkedinSourcingProcessing ||
      resumeBoxSourcingProcessing ||
      persistedSessionState?.sourcing?.started ||
      persistedSessionState?.sourcing?.googleProcessing ||
      persistedSessionState?.sourcing?.linkedinProcessing ||
      persistedSessionState?.sourcing?.resumeBoxProcessing;

    if (localSourcingActive) {
      return 'Candidate sourcing is in progress. Please wait until sourcing completes before sending outreach.';
    }

    if (outreachSending || persistedSessionState?.outreach?.sending) {
      return 'Outreach is already in progress. Please wait until it completes before starting another outreach.';
    }

    return null;
  }, [
    automationSessionId,
    detectRemoteActiveJobPosting,
    detectRemoteActiveOutreach,
    detectRemoteActiveSourcing,
    googleSourcingProcessing,
    jobPostingProcessing,
    linkedinProcessing,
    linkedinSourcingProcessing,
    naukriProcessing,
    outreachSending,
    resumeBoxSourcingProcessing,
    selectedJobId,
    sessionId,
    sourcingComplete,
    talentScoutingStarted,
  ]);

  const effectiveLinkedinPostingProcessing = resolvePostingProcessingFlag(linkedinLogs, linkedinProcessing);
  const effectiveNaukriPostingProcessing = resolvePostingProcessingFlag(naukriLogs, naukriProcessing);
  const effectivePostedChannels = Array.from(new Set([
    ...postedChannels,
    ...(hasLatestRunPostingSuccess(linkedinLogs) ? ['LinkedIn'] : []),
    ...(hasLatestRunPostingSuccess(naukriLogs) ? ['Naukri'] : []),
  ]));

  const postingChannelUniverse = Array.from(new Set([
    ...configuredPostingChannels,
    ...selectedChannels,
    ...effectivePostedChannels,
    ...(linkedinLogs.length > 0 || effectiveLinkedinPostingProcessing ? ['LinkedIn'] : []),
    ...(naukriLogs.length > 0 || effectiveNaukriPostingProcessing ? ['Naukri'] : []),
  ]));

  const derivedRetryablePostingChannels = postingChannelUniverse.filter((channel) => {
    if (effectivePostedChannels.includes(channel)) return false;
    const logs = channel === 'LinkedIn' ? linkedinLogs : naukriLogs;
    return hasLatestRunPostingFailure(logs);
  });
  const availablePostingChannels = postingChannelUniverse.filter(
    (channel) => !effectivePostedChannels.includes(channel)
  );
  const retryablePostingChannels = postingChannelUniverse.filter((channel) => {
    if (effectivePostedChannels.includes(channel)) return false;
    const logs = channel === 'LinkedIn' ? linkedinLogs : naukriLogs;
    return logs.some((log) => {
      const content = String(log?.message || log?.logMessage || '').toLowerCase();
      const status = String(log?.status || log?.logType || log?.type || '').toLowerCase();
      return (
        status === 'error' ||
        status === 'failed' ||
        content.includes('failed') ||
        content.includes('error') ||
        content.includes('payment_required') ||
        content.includes('free_job_limit_reached')
      );
    });
  });
  const actionablePostingChannels = Array.from(new Set([
    ...availablePostingChannels,
    ...retryablePostingChannels,
    ...derivedRetryablePostingChannels,
  ]));
  const getPostJobButtonLabel = () => {
    if (actionablePostingChannels.length === 1 && retryablePostingChannels.includes(actionablePostingChannels[0])) {
      return 'Repost Job';
    }
    if (actionablePostingChannels.length === 1 && derivedRetryablePostingChannels.includes(actionablePostingChannels[0])) {
      return 'Repost Job';
    }
    return `Post Job (${actionablePostingChannels.length} left)`;
  };
  const getPostJobCommand = (channels = actionablePostingChannels) =>
    channels.length === 1 ? `post jd to ${channels[0]}` : 'post job';

  const appendJdCardToChat = useCallback((targetSessionId = null) => {
    const sessionForJd = targetSessionId || selectedJobId || automationSessionId || sessionId;
    if (!jdText || !sessionForJd) return;

    const jdMessage = {
      id: Date.now(),
      sender: 'agent',
      message: '',
      timestamp: new Date().toISOString(),
      metadata: {
        type: 'jd_card',
        jdText,
        requirements: finalRequirements || {},
      },
    };

    setChatHistory((prev) => [...prev, jdMessage]);
    updateSessionState(sessionForJd, (state) => {
      state.chat.chatHistory = [...(state.chat.chatHistory || []), jdMessage];
      return state;
    });
  }, [automationSessionId, finalRequirements, jdText, selectedJobId, sessionId, updateSessionState]);

  const appendUserChatActivity = useCallback((message, targetSessionId = null, mode = 'agent') => {
    const sessionForMessage = targetSessionId || selectedJobId || automationSessionId || sessionId;
    const trimmedMessage = String(message || '').trim();
    if (!trimmedMessage || !sessionForMessage) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      message: trimmedMessage,
      timestamp: new Date().toISOString(),
      mode,
    };

    setChatHistory((prev) => [...prev, userMessage]);
    updateSessionState(sessionForMessage, (state) => {
      state.chat.chatHistory = [...(state.chat.chatHistory || []), userMessage];
      return state;
    });
  }, [automationSessionId, selectedJobId, sessionId, updateSessionState]);

  const appendAgentChatActivity = useCallback((message, targetSessionId = null) => {
    const sessionForMessage = targetSessionId || selectedJobId || automationSessionId || sessionId;
    const trimmedMessage = String(message || '').trim();
    if (!trimmedMessage || !sessionForMessage) return;

    const agentMessage = {
      id: Date.now(),
      sender: 'agent',
      message: trimmedMessage,
      timestamp: new Date().toISOString(),
      metadata: {
        type: 'transient_status',
      },
    };

    setChatHistory((prev) => {
      const recentAgentMessages = prev
        .filter((entry) => entry?.sender === 'agent' && typeof entry?.message === 'string')
        .slice(-6);
      if (recentAgentMessages.some((entry) => entry.message.trim() === trimmedMessage)) {
        return prev;
      }
      return [...prev, agentMessage];
    });
    updateSessionState(sessionForMessage, (state) => {
      const existingHistory = Array.isArray(state.chat.chatHistory) ? state.chat.chatHistory : [];
      const recentAgentMessages = existingHistory
        .filter((entry) => entry?.sender === 'agent' && typeof entry?.message === 'string')
        .slice(-6);
      if (recentAgentMessages.some((entry) => entry.message.trim() === trimmedMessage)) {
        return state;
      }
      state.chat.chatHistory = [...existingHistory, agentMessage];
      return state;
    });
  }, [automationSessionId, selectedJobId, sessionId, updateSessionState]);

  // -- Keep currentSessionRef in sync with automationSessionId --
  // This ref is readable inside async closures to detect stale responses.
  useEffect(() => {
    currentSessionRef.current = automationSessionId;
  }, [automationSessionId]);

  // Bucket-staleness strategy: eager refresh on every user interaction.
  //
  // Every endpoint the chat page hits during a user action
  // (/chat, /chat/mode-switch, /sessions/{id}, /filter/refresh) calls
  // refresh_working_candidates server-side, so the bucket is always
  // fresh at the moment the user does anything. There is no live
  // polling or WebSocket push for state changes — idle users see the
  // last-rendered snapshot until they next interact, at which point
  // the server returns fresh data. This matches how the rest of the
  // app behaves and avoids persistent connections / periodic probes.

  // -- Save/restore job posting state when switching sessions --
  // This ensures that when user switches away from a session with active job posting,
  // the state (questions, pending fields, etc.) is preserved and restored when they return.
  // Also handles restoring from localStorage after page refresh.
  useEffect(() => {
    const currentSession = automationSessionId;
    const prevSession = previousSessionIdRef.current;
    
    // If session changed — NOTE: Do NOT save prevSession state here because
    // hydrateUIFromSession(newSession) has already reset React state to the new session's
    // defaults by this point. Saving here would overwrite prevSession's correct state
    // (already saved by saveCurrentSessionState in the click handler) with the new session's
    // reset defaults. The click handler at line 10654 already handles saving correctly.
    
    // Update previous session ref
    previousSessionIdRef.current = currentSession;
    
    // Restore state for the new session - check both ref and session store (for after refresh)
    if (currentSession) {
      // First check if saved in ref (in-memory, survives session switch but not refresh)
      if (jobPostingStatePerSessionRef.current.has(currentSession)) {
        const savedState = jobPostingStatePerSessionRef.current.get(currentSession);
        const normalizedSavedJp = normalizeJobPostingState({
          phase: savedState.jobPostingPhase || 'idle',
          pendingField: savedState.pendingNaukriField || null,
          pendingFieldsQueue: savedState.pendingJobPostingFieldsQueue || [],
          collectedDetails: savedState.collectedJobPostingDetails || null,
          fieldCollectionActive: savedState.waitingForBackendQuestion || false,
          selectedChannels: savedState.selectedChannels || [],
          processing: savedState.jobPostingProcessing || false,
          complete: savedState.jobPostingComplete || false,
          failed: savedState.jobPostingFailed || false,
          linkedinProcessing: savedState.linkedinProcessing || false,
          naukriProcessing: savedState.naukriProcessing || false,
          linkedinLogs: savedState.linkedinLogs || [],
          naukriLogs: savedState.naukriLogs || [],
        });
        console.log('[Session State] Restoring job posting state from REF for session:', currentSession);
        
        // Restore the saved state
        setSystemMessages(sanitizeSystemMessages(savedState.systemMessages || []));
        setPendingNaukriField(savedState.pendingNaukriField || null);
        setPendingNaukriFieldOptions(savedState.pendingNaukriFieldOptions || []);
        setJobPostingPhase(normalizedSavedJp.phase || 'idle');
        setSelectedChannels(savedState.selectedChannels || []);
        setCollectedJobPostingDetails(savedState.collectedJobPostingDetails || null);
        setWaitingForBackendQuestion(savedState.waitingForBackendQuestion || false);
        setJobPostingProcessing(normalizedSavedJp.processing || false);
        setLinkedinLogs(savedState.linkedinLogs || []);
        setNaukriLogs(savedState.naukriLogs || []);
        setLinkedinProcessing(normalizedSavedJp.linkedinProcessing || false);
        setNaukriProcessing(normalizedSavedJp.naukriProcessing || false);
        setJobPostingComplete(normalizedSavedJp.complete || false);
        setJobPostingFailed(normalizedSavedJp.failed || false);
        
        // Clear the saved state since we've restored it
        jobPostingStatePerSessionRef.current.delete(currentSession);
      } else {
        // Check session store (which loads from localStorage after refresh)
        const sessionState = getSessionState(currentSession);
        const jp = normalizeJobPostingState(sessionState?.jobPosting || {});
        
        // Only restore if there's active field collection (persisted from localStorage)
        if (jp && (jp.fieldCollectionActive || jp.pendingField || jp.pendingFieldsQueue?.length > 0)) {
          console.log('[Session State] Restoring job posting state from LOCALSTORAGE for session:', currentSession, jp);
          
          // Restore job posting state from session store
          setPendingNaukriField(jp.pendingField || null);
          setPendingJobPostingFieldsQueue(jp.pendingFieldsQueue || []);
          setCollectedJobPostingDetails(jp.collectedDetails || null);
          setJobPostingPhase(jp.phase || 'collecting_details');
          setSelectedChannels(jp.selectedChannels || []);
          setJobPostingProcessing(jp.processing || false);
          
          // Update ref
          fieldCollectionStateRef.current = {
            isActive: jp.fieldCollectionActive || (jp.pendingFieldsQueue?.length > 0) || false,
            currentField: jp.pendingField || null,
            queue: jp.pendingFieldsQueue || [],
          };
          
          // Show the current question for this field
          if (jp.pendingField) {
            const fieldQuestions = {
              'experienceMin': '📊 What is the minimum experience required? (Enter years, e.g., 0 for fresher, 2)',
              'experienceMax': '📊 What is the maximum experience required? (Enter years, e.g., 5)',
              'experience_min': '📊 What is the minimum experience required? (Enter years, e.g., 0 for fresher, 2)',
              'experience_max': '📊 What is the maximum experience required? (Enter years, e.g., 5)',
              'salaryMin': '💰 What is the minimum salary for this position? (Enter a number, e.g., 50000)',
              'salaryMax': '💰 What is the maximum salary for this position? (Enter a number, e.g., 100000)',
              'salary_min': '💰 What is the minimum salary for this position? (Enter a number, e.g., 50000)',
              'salary_max': '💰 What is the maximum salary for this position? (Enter a number, e.g., 100000)',
              'companyName': '🏢 What is the company name for this job posting?',
              'company_name': '🏢 What is the company name for this job posting?',
              'allowCandidatesToCall': '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
              'allow_call': '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
              'recruiterName': '👤 What is the recruiter name?',
              'recruiter_name': '👤 What is the recruiter name?',
              'recruiterNumber': '📱 What is the recruiter phone number? (10 digits)',
              'recruiter_number': '📱 What is the recruiter phone number? (10 digits)',
            };
            const currentQuestion = fieldQuestions[jp.pendingField] || `Please provide: ${jp.pendingField.replace(/_/g, ' ')}`;
            setSystemMessages([currentQuestion]);
            setSystemMessageToken((prev) => prev + 1);
          }
        } else if (jp && jp.processing) {
          // NOTE: Processing/failed states are NOT restored from localStorage.
          // They are exclusively derived from DB per-channel columns (job_post_linkedin, etc.)
          // via loadSessionData → restoreSessionState which runs right after this block.
          // Restoring from localStorage here caused stale 'failed' flash when repost was triggered.
          console.log('[Session State] Skipping processing state restore from localStorage - will be set from DB per-channel status');
        }
        
        // Check for active JD analysis from localStorage
        const jd = sessionState?.jd;
        if (jd && jd.loading) {
          console.log('[Session State] Restoring JD loading state from LOCALSTORAGE for session:', currentSession, jd);
          
          setIsJDLoading(true);
          setAnalysisUxPhase(jd.analysisPhase || 'analysing');
          if (jd.text) {
            setJdText(jd.text);
          }
        }
        
        // Also check for active sourcing OR completed sourcing with logs from localStorage
        const src = sessionState?.sourcing;
        // Restore if sourcing is in progress OR if sourcing is complete with logs to display
        const hasLogsToRestore = (src?.googleLogs?.length > 0) || (src?.linkedinLogs?.length > 0) || (src?.resumeBoxLogs?.length > 0);
        if (src && (src.started || src.googleProcessing || src.linkedinProcessing || src.resumeBoxProcessing || (src.complete && hasLogsToRestore))) {
          console.log('[Session State] Restoring sourcing state from LOCALSTORAGE for session:', currentSession, src, 'hasLogs:', hasLogsToRestore);

          // CRITICAL: Also populate the in-memory session store so loadSessionData can read
          // selectedChannels and other sourcing state (store is empty after page refresh)
          updateSessionState(currentSession, (state) => {
            state.sourcing.started = src.started || false;
            state.sourcing.complete = src.complete || false;
            state.sourcing.googleProcessing = src.googleProcessing || false;
            state.sourcing.linkedinProcessing = src.linkedinProcessing || false;
            state.sourcing.resumeBoxProcessing = src.resumeBoxProcessing || false;
            state.sourcing.selectedChannels = src.selectedChannels || [];
            if (src.googleLogs?.length > 0) state.sourcing.googleLogs = src.googleLogs;
            if (src.linkedinLogs?.length > 0) state.sourcing.linkedinLogs = src.linkedinLogs;
            if (src.resumeBoxLogs?.length > 0) state.sourcing.resumeBoxLogs = src.resumeBoxLogs;
            if (src.candidates?.length > 0) state.sourcing.candidates = src.candidates;
            return state;
          });

          // Restore sourcing state
          // If any channel is still processing, treat as NOT complete regardless of complete flag
          const effectiveSrcComplete = (src.complete || false) && !src.googleProcessing && !src.linkedinProcessing && !src.resumeBoxProcessing;
          setTalentScoutingStarted(src.started || src.googleProcessing || src.linkedinProcessing || src.resumeBoxProcessing || false);
          setGoogleSourcingProcessing(src.googleProcessing || false);
          setLinkedinSourcingProcessing(src.linkedinProcessing || false);
          setResumeBoxSourcingProcessing(src.resumeBoxProcessing || false);
          setSourcingComplete(effectiveSrcComplete);

          // Helper to filter out promotional messages from logs
          const filterPromotionalLogs = (logs) => {
            if (!Array.isArray(logs)) return [];
            return logs.filter(log => {
              const msg = String(log?.message || '').toLowerCase();
              const content = log?.message || '';
              // Skip user-facing chat messages (contain markdown **bold** or promotional phrases)
              if (content.includes('**') && (
                msg.includes('moving to') ||
                msg.includes('ready to source') ||
                msg.includes('say **') ||
                msg.includes('sourcing platforms:') ||
                msg.includes('this will search') ||
                msg.includes("let's find")
              )) {
                return false; // Filter out this promotional message
              }
              return true;
            });
          };

          // Restore logs (filtered to remove promotional messages)
          if (src.googleLogs?.length > 0) {
            const filteredGoogleLogs = filterPromotionalLogs(src.googleLogs);
            if (filteredGoogleLogs.length > 0) {
              setGoogleSourcingLogs(filteredGoogleLogs);
            }
          }
          if (src.linkedinLogs?.length > 0) {
            const filteredLinkedinLogs = filterPromotionalLogs(src.linkedinLogs);
            if (filteredLinkedinLogs.length > 0) {
              setLinkedinSourcingLogs(filteredLinkedinLogs);
            }
          }
          if (src.resumeBoxLogs?.length > 0) {
            const filteredResumeBoxLogs = filterPromotionalLogs(src.resumeBoxLogs);
            if (filteredResumeBoxLogs.length > 0) {
              setResumeBoxSourcingLogs(filteredResumeBoxLogs);
            }
          }

          // Restore candidates and count
          if (src.candidates?.length > 0) {
            setCandidates(src.candidates);
            setMatchedCandidateCount(src.candidates.length);
          }

          // Restore selected sourcing channels
          if (src.selectedChannels?.length > 0) {
            setSelectedSourcingChannels(src.selectedChannels);
          }

          // Set appropriate panel mode based on sourcing state
          // Stay on sourcing/logs page if any channel is still processing
          if (src.googleProcessing || src.linkedinProcessing || src.resumeBoxProcessing) {
            if (!shouldPreserveJobPostingView) {
              setRightPanelMode('sourcing');
              setActiveHeaderTab('sourcing');
            }
          } else if (effectiveSrcComplete && src.candidates?.length > 0) {
            setRightPanelMode('candidates');
          } else if (hasLogsToRestore) {
            // If we have logs but no candidates and not processing, show sourcing logs
            if (!shouldPreserveJobPostingView) {
              setRightPanelMode('sourcing');
              setActiveHeaderTab('sourcing');
            }
          }
        }
      }
    }
  }, [automationSessionId]);

  // Load previous jobs from AI Automation sessions API
  useEffect(() => {
    const fetchSessionsAsJobPostings = async () => {
      setSidebarLoading(true);
      try {
        const cid = getCid();
        const sessionsEndpoint = cid
          ? `/sessions?limit=100&cid=${encodeURIComponent(String(cid))}`
          : '/sessions?limit=100';
        const data = await aiAutomationApi.get(sessionsEndpoint);
        if (!Array.isArray(data) || data.length === 0) {
          console.info(
            'Sessions API responded with empty list. No previous job postings to display.'
          );
          setJobPostings([]);
          setSelectedJobId(null);
          return;
        }

        const currentCid = getCid() != null ? String(getCid()) : null;
        const mappedPostings = data.map((session) => {
          const parsedReq = session.parsed_requirements || {};

          // Build a short, clean title without long summary details
          // Priority: custom_title (user-edited) > parsed JD role > query > fallback
          let rawTitle =
            session.custom_title ||
            parsedReq.role ||
            parsedReq.title ||
            session.query ||
            session.initial_query ||
            'New Chat';

          if (typeof rawTitle !== 'string') {
            rawTitle = String(rawTitle || 'Untitled Job');
          }

          // Remove newlines and collapse extra spaces
          let title = rawTitle.replace(/\s+/g, ' ').trim();

          // Truncate very long titles so sidebar only shows a concise label
          const MAX_TITLE_LENGTH = 80;
          if (title.length > MAX_TITLE_LENGTH) {
            title = `${title.slice(0, MAX_TITLE_LENGTH - 1)}…`;
          }

          const latestActivityAt = getLatestSessionTimestamp(session);
          let lastUpdated = 'Recently';
          try {
            if (latestActivityAt) {
              const date = new Date(latestActivityAt);
              if (!isNaN(date.getTime())) {
                lastUpdated = formatLastUpdated(date);
              }
            }
          } catch (e) {
            // Keep default label on parse error
          }

          return {
            id: session.id,
            title,
            lastUpdated,
            rawTimestamp: latestActivityAt || null,
            duration: session.search_count
              ? `${session.search_count} searches`
              : 'Session',
            // For delete eligibility — deletable only if no candidates and no posted job
            candidateCount: session.candidate_count || 0,
            hasPostedJob: session.has_posted_job || false,
            cid: session.cid != null ? String(session.cid) : currentCid,
          };
        });

        // Merge fetched sessions with any locally-created sessions that may not
        // be returned yet (e.g., dashboard auto-send race on initial load).
        setJobPostings((prev) => {
          const fetchedById = new Map(mappedPostings.map((job) => [job.id, job]));
          const localOnly = (Array.isArray(prev) ? prev : []).filter((job) => {
            if (!job?.id || fetchedById.has(job.id)) return false;
            const jobCid = job?.cid != null ? String(job.cid) : currentCid;
            return jobCid === currentCid;
          });
          return [...localOnly, ...mappedPostings];
        });
        // Don't auto-select first job - start with new chat
        // setSelectedJobId will be set when user sends first message
      } catch (error) {
        console.error('Error fetching sessions from AI Automation API:', error);
      } finally {
        setSidebarLoading(false);
      }

      // Fetch archived count (non-blocking)
      try {
        const archived = await aiAutomationApi.get('/sessions/archived?limit=1');
        if (Array.isArray(archived)) {
          setArchivedCount(archived.length);
          // Re-fetch full count with higher limit
          const allArchived = await aiAutomationApi.get('/sessions/archived');
          if (Array.isArray(allArchived)) setArchivedCount(allArchived.length);
        }
      } catch (_) { /* non-critical */ }
    };

    fetchSessionsAsJobPostings();
  }, []);

  const selectedJob = selectedJobId
    ? jobPostings.find((job) => job.id === selectedJobId)
    : null;
  const activeSessionForCandidateCount = selectedJobId || automationSessionId || sessionId;
  const activeSessionCandidateCount = Number(sessionCandidateCount || selectedJob?.candidateCount || 0);

  const refreshSessionCandidateCount = useCallback(async (targetSessionId = null) => {
    const sid = targetSessionId || selectedJobId || automationSessionId || sessionId;
    if (!sid) {
      setSessionCandidateCount(0);
      return 0;
    }
    try {
      const response = await aiAutomationApi.get(`/automation_candidates/all?session_id=${encodeURIComponent(sid)}&limit=1&exclude_rejected=true`);
      const count = Number(
        response?.total_records
        || response?.total
        || response?.count
        || response?.total_count
        || 0
      );
      setSessionCandidateCount(count);
      setJobPostings((prev) => prev.map((job) => (job.id === sid ? { ...job, candidateCount: count } : job)));
      return count;
    } catch (error) {
      console.warn('[CandidateCount] Failed to refresh session candidate count:', error);
      return 0;
    }
  }, [automationSessionId, selectedJobId, sessionId]);

  useEffect(() => {
    setSessionCandidateCount(0);
    refreshSessionCandidateCount(activeSessionForCandidateCount);
  }, [activeSessionForCandidateCount, refreshSessionCandidateCount, candidatesRenderToken]);

  const currentInterviewJobTitle = selectedJob?.title
    || jdText?.split('\n')[0]?.replace(/^(job\s*title:?\s*|position:?\s*)/i, '').trim()
    || 'Current Job';

  const openJdCriteriaReview = useCallback((targetJdId = null, targetJobTitle = null) => {
    const resolvedJdId = targetJdId || automationSessionId || selectedJobId || sessionId;
    if (!resolvedJdId) return;
    const resolvedTitle = targetJobTitle || currentInterviewJobTitle;
    setJdCriteriaModalContext({
      jdId: resolvedJdId,
      jobTitle: resolvedTitle || 'Current Job',
    });
    localStorage.setItem(`jd_score_criteria_reviewed_${resolvedJdId}`, 'true');
    setJdCriteriaModalOpen(true);
  }, [automationSessionId, currentInterviewJobTitle, selectedJobId, sessionId]);

  useEffect(() => {
    const activeSessionId = automationSessionId || selectedJobId || sessionId;
    if (!activeSessionId || !Array.isArray(chatHistory) || chatHistory.length === 0) return;

    const storageKey = `jd_score_criteria_reviewed_${activeSessionId}`;
    if (localStorage.getItem(storageKey) === 'true') return;

    const hasJdCreatedMessage = chatHistory.some((message) => {
      const text = String(message?.message || message?.content || message?.text || '').toLowerCase();
      return text.includes('jd for') && text.includes('has been created') && text.includes('successfully');
    });

    if (hasJdCreatedMessage) {
      openJdCriteriaReview(activeSessionId, currentInterviewJobTitle);
    }
  }, [automationSessionId, selectedJobId, sessionId, chatHistory, currentInterviewJobTitle, openJdCriteriaReview]);

  // Load persistent session logs from file storage
  const loadSessionLogs = async (sessionId, sessionType = 'job_post') => {
    try {
      const response = await aiAutomationApi.get(
        `/session-logs/${sessionType}/${sessionId}`
      );
      if (response?.success && response?.logs) {
        const logs = response.logs;
        const formattedLogs = logs.map((log) => ({
          message: log.message,
          timestamp: log.timestamp,
          type: log.level?.toLowerCase() === 'error' ? 'error' : 'info',
          logType: log.level?.toLowerCase() === 'error' ? 'error' : 'info',
          platform: log.platform || null,
        }));
        const hasPostingPlatformInfo = sessionType === 'job_post' && formattedLogs.some((log) => log.platform);
        const linkedinPostingLogs = hasPostingPlatformInfo
          ? formattedLogs.filter((log) => String(log.platform || '').toLowerCase() === 'linkedin')
          : [];
        const naukriPostingLogs = hasPostingPlatformInfo
          ? formattedLogs.filter((log) => String(log.platform || '').toLowerCase() === 'naukri' || !log.platform)
          : formattedLogs;

        // CRITICAL: Always update session store first (merge with existing localStorage data)
        updateSessionState(sessionId, (state) => {
          if (sessionType === 'job_post') {
            state.jobPosting = state.jobPosting || {};
            if (linkedinPostingLogs.length > 0) {
              state.jobPosting.linkedinLogs = linkedinPostingLogs;
            }
            if (naukriPostingLogs.length > 0 || (!hasPostingPlatformInfo && formattedLogs.length > 0)) {
              state.jobPosting.naukriLogs = naukriPostingLogs;
            }
          } else if (sessionType === 'linkedin_search') {
            state.sourcing = state.sourcing || {};
            const existingLogs = state.sourcing.linkedinLogs || [];
            if (existingLogs.length > 0) {
              const existing = new Set(existingLogs.map(l => l.message));
              state.sourcing.linkedinLogs = [...existingLogs, ...formattedLogs.filter(l => !existing.has(l.message))];
            } else {
              state.sourcing.linkedinLogs = formattedLogs;
            }
          } else if (sessionType === 'google_search') {
            state.sourcing = state.sourcing || {};
            const existingLogs = state.sourcing.googleLogs || [];
            if (existingLogs.length > 0) {
              const existing = new Set(existingLogs.map(l => l.message));
              state.sourcing.googleLogs = [...existingLogs, ...formattedLogs.filter(l => !existing.has(l.message))];
            } else {
              state.sourcing.googleLogs = formattedLogs;
            }
          } else if (sessionType === 'outreach') {
            state.outreach = state.outreach || {};
            state.outreach.logs = formattedLogs;
          }
          return state;
        });

        // Only update React state if still viewing this session
        // MERGE with existing logs (from localStorage restoration) instead of replacing
        if (currentViewedSessionRef.current === sessionId) {
          if (sessionType === 'job_post') {
            if (linkedinPostingLogs.length > 0) {
              setLinkedinLogs(linkedinPostingLogs);
            }
            if (naukriPostingLogs.length > 0 || (!hasPostingPlatformInfo && formattedLogs.length > 0)) {
              setNaukriLogs(naukriPostingLogs);
            }
          } else if (sessionType === 'linkedin_search') {
            setLinkedinSourcingLogs((prev) => {
              if (!prev || prev.length === 0) return formattedLogs;
              // Merge: keep localStorage logs + add any file logs not already present
              const existing = new Set(prev.map(l => l.message));
              const merged = [...prev, ...formattedLogs.filter(l => !existing.has(l.message))];
              return merged;
            });
          } else if (sessionType === 'google_search') {
            setGoogleSourcingLogs((prev) => {
              if (!prev || prev.length === 0) return formattedLogs;
              const existing = new Set(prev.map(l => l.message));
              const merged = [...prev, ...formattedLogs.filter(l => !existing.has(l.message))];
              return merged;
            });
          } else if (sessionType === 'outreach') {
            setOutreachLogs(formattedLogs);
          }
        }
        
        return formattedLogs;
      }
    } catch (error) {
      // Silently ignore 404 errors - expected when session has no logs yet
    }
    return [];
  };

  // Load full session data (JD, candidates, requirements) from API when selecting a previous job
  const loadSessionData = async (sessionId) => {
    if (!sessionId) return;
    manualTabOverrideRef.current = false;
    setSessionLoading(true);
    setTalentPoolChannelFilter([]);
    
    // CRITICAL: Capture which session started this load.
    // All React state setters below must check this before rendering.
    const loadingForSession = sessionId;
    
    // Helper: only apply React state if the user is still viewing the session this load was started for
    const applyIfCurrentSession = (fn) => {
      if (currentViewedSessionRef.current === loadingForSession) {
        fn();
      }
    };

    const getLastAssistantMetadata = (messages = []) => {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        if (msg?.sender === 'agent' && msg?.metadata && typeof msg.metadata === 'object') {
          return msg.metadata;
        }
      }
      return null;
    };

    const getLastJdMetadata = (messages = []) => {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        const metadata = msg?.metadata && typeof msg.metadata === 'object' ? msg.metadata : null;
        if (!metadata) continue;
        const responseType = String(metadata?.type || '').toLowerCase();
        const frontendAction = String(metadata?.frontend_action || '').toLowerCase();
        if (metadata?.jd_text || responseType === 'jd_saved' || frontendAction === 'jd_created') {
          return metadata;
        }
      }
      return null;
    };

    const restoreRecoveredJdUiState = async (assistantMetadata = null, fallbackJdText = '') => {
      const metadata = assistantMetadata && typeof assistantMetadata === 'object' ? assistantMetadata : {};
      const responseType = String(metadata?.type || '').toLowerCase();
      const frontendAction = String(metadata?.frontend_action || '').toLowerCase();
      if (!fallbackJdText && frontendAction !== 'jd_created' && responseType !== 'jd_saved' && !metadata?.jd_text) return;

      let recoveredJdText = fallbackJdText || metadata?.jd_text || '';
      let recoveredSearchId = metadata?.search_id || '';

      if (!recoveredJdText || !recoveredSearchId) {
        try {
          const refreshedSessionData = await aiAutomationApi.get(`/searches/${sessionId}`);
          const refreshedSearches = Array.isArray(refreshedSessionData?.searches)
            ? refreshedSessionData.searches
            : [];

          if (!recoveredSearchId && refreshedSearches.length > 0) {
            const latestSearch = refreshedSearches[refreshedSearches.length - 1];
            recoveredSearchId = latestSearch?.search_id || latestSearch?.id || '';
          }

          if (!recoveredJdText) {
            for (let i = refreshedSearches.length - 1; i >= 0 && !recoveredJdText; i -= 1) {
              const jdList = Array.isArray(refreshedSearches[i]?.job_descriptions)
                ? refreshedSearches[i].job_descriptions
                : [];
              if (jdList.length === 0) continue;
              const latestJd = jdList[jdList.length - 1];
              recoveredJdText =
                latestJd?.jd_text ||
                latestJd?.job_description_text ||
                latestJd?.job_description ||
                latestJd?.full_job_description ||
                latestJd?.jobDescription?.content ||
                latestJd?.description ||
                latestJd?.content ||
                '';
            }
          }
        } catch (restoreErr) {
          console.warn('[restoreSessionState] Failed to refresh JD state after recovery:', restoreErr);
        }
      }

      if (recoveredSearchId) {
        setAutomationSearchId(recoveredSearchId);
        searchIdBySessionRef.current[sessionId] = recoveredSearchId;
      }

      updateSessionState(sessionId, (state) => {
        if (recoveredJdText) {
          state.jd.text = recoveredJdText;
        }
        state.jd.analysisPhase = 'done';
        return state;
      });

      applyIfCurrentSession(() => {
        if (recoveredJdText) {
          setJdText(recoveredJdText);
        }
        setAnalysisUxPhase('done');
        setShowResultsPanel(true);
        setActiveHeaderTab('status');
        setRightPanelMode('jd');
        setLastResponseType('jd_saved');
        setAgentStageLabel('');
        setAgentStageCancellable(false);
      });
    };
    
    // Check if session store already has data for this session
    // If so, skip resetting state - hydrateUIFromSession already restored it
    const hasStoredState = sessionStateStoreRef.current.has(sessionId);
    
    // Reset confirmation/requirements state at the start - will be properly restored from API data
    // Only reset if no stored state (avoid undoing hydrateUIFromSession)
    if (!hasStoredState) {
      applyIfCurrentSession(() => {
        setWaitingForConfirmation(false);
        setAccumulatedRequirements({});
      });
    }
    
    try {
      applyIfCurrentSession(() => {
        setChatHistoryLoading(true);
        // Reset logs when loading session data to prevent cross-session contamination
        // But skip reset if session already has stored state (hydrateUIFromSession already restored)
        setRightPanelMode('auto');
        if (!hasStoredState) {
          setLinkedinLogs([]);
          setNaukriLogs([]);
          setGoogleSourcingLogs([]);
          setLinkedinSourcingLogs([]);
          setResumeBoxSourcingLogs([]);
          setOutreachLogs([]);
          setCandidates([]);
          setTalentScoutingStarted(false);
          setSourcingComplete(false);
          setOutreachSummary(null);
          setOutreachSending(false);
          setOutreachCompleted(false);
          setOutreachPhase('idle');
        }
        // Clear stale lifecycle draft state so buttons don't carry over between sessions
        setDraftEditActions([]);
        setLifecycleAwaitingConfirmation(false);
        setLifecycleScheduleActive(false);
      });

      // NOTE: Sourcing and job posting state is loaded from DB/file API — no immediate localStorage restore

      // Load persistent session logs from file storage for all session types
      try {
        await Promise.all([
          loadSessionLogs(sessionId, 'job_post'),
          loadSessionLogs(sessionId, 'google_search'),
          loadSessionLogs(sessionId, 'linkedin_search'),
          loadSessionLogs(sessionId, 'outreach'),
        ]);
      } catch (logError) {
        console.log('Could not load session logs:', logError);
      }
      
      let sessionData;
      try {
        sessionData = await aiAutomationApi.get(`/sessions/${sessionId}`);
      } catch (sessionErr) {
        // 403 = session belongs to another user, 404 = session doesn't exist
        const status = sessionErr?.status;
        if (status === 403 || status === 404) {
          console.warn(`[loadSessionData] Session access denied or not found (${status}):`, sessionId);
          navigate('/dashboard', { replace: true });
          return;
        }
        throw sessionErr;
      }
      if (!sessionData) return;

      let backendJobPosting = null;
      try {
        const postingResponse = await aiAutomationApi.get(`/job-postings/by-session/${sessionId}`);
        backendJobPosting = postingResponse?.job_posting || null;
      } catch (postingErr) {
        console.warn('[loadSessionData] Failed to fetch dedicated job posting state:', postingErr);
      }

      // Fetch agent state from backend for session-isolated state management
      // This ensures agent states (JD agent, Job Post agent, Sourcing agent) are restored correctly
      try {
        if (!agentStatePerSessionRef.current.has(sessionId)) {
          const agentStateResponse = await aiAutomationApi.get(`/session-agent-state/${sessionId}`);
          if (agentStateResponse?.success && agentStateResponse.agent_state) {
            agentStatePerSessionRef.current.set(sessionId, agentStateResponse.agent_state);
            console.log('[loadSessionData] Fetched agent state from backend:', sessionId, agentStateResponse.agent_state);
          }
        } else {
          console.log('[loadSessionData] Using cached agent state for session:', sessionId);
        }
      } catch (agentErr) {
        console.log('[loadSessionData] Could not fetch agent state, may be a new session:', agentErr);
      }

      // Restore Filter (Ask) mode state from LangGraph persisted state.
      //
      // The hardened filter pipeline persists `filter_breadcrumbs` and
      // `working_candidates` per (cid, session_id, search_id) on every
      // turn. On session reload we read them back so the user's funnel
      // survives a refresh — they had filtered to 47 candidates, they
      // come back, those 47 candidates are still there.
      //
      // Filter mode hydration. `filter_actions` and `follow_up_suggestions`
      // live inside langgraph_state.response_extra (the filter agent's
      // last-turn payload), so chips survive a hard refresh instead of
      // disappearing until the next turn re-derives them.
      try {
        let lgState = sessionData?.langgraph_state;
        if (typeof lgState === 'string') {
          try { lgState = JSON.parse(lgState); } catch (_) { lgState = null; }
        }
        const lgExtra = (lgState && typeof lgState.response_extra === 'object' && lgState.response_extra) || {};
        const restoredWorkspaceOptions = Array.isArray(lgExtra.workspace_options)
          ? lgExtra.workspace_options
          : [];
        applyIfCurrentSession(() => {
          setWorkspaceOptions(restoredWorkspaceOptions);
          setWorkspaceSelectionRequired(lgExtra.workspace_selection_required === true);
        });
        console.log('[loadSessionData] langgraph_state:', lgState ? {
          mode: lgState.mode,
          agent: lgState.current_agent,
          breadcrumbs: (lgState.filter_breadcrumbs || []).length,
          working: (lgState.working_candidates || []).length,
          actions: (lgExtra.filter_actions || []).length,
          suggestions: (lgExtra.follow_up_suggestions || []).length,
        } : 'null');
        if (lgState) {
          const restoredMode = lgState.mode || 'agent';
          if (restoredMode === 'ask') {
            const restoredBreadcrumbs = Array.isArray(lgState.filter_breadcrumbs)
              ? lgState.filter_breadcrumbs
              : [];
            const restoredWorking = Array.isArray(lgState.working_candidates)
              ? lgState.working_candidates
              : [];
            const restoredActions = Array.isArray(lgExtra.filter_actions)
              ? lgExtra.filter_actions
              : [];
            const restoredSuggestions = Array.isArray(lgExtra.follow_up_suggestions)
              ? lgExtra.follow_up_suggestions
              : [];
            const restoredNewCount = Number.isInteger(lgExtra.new_candidates_count)
              ? lgExtra.new_candidates_count
              : 0;
            applyIfCurrentSession(() => {
              setChatMode('ask');
              setFilterBreadcrumbs(restoredBreadcrumbs);
              setWorkingCandidates(restoredWorking);
              setFilterActions(restoredActions);
              setFollowUpSuggestions(restoredSuggestions);
              setNewCandidatesCount(restoredNewCount);
              console.log(
                '[loadSessionData] Restored Filter mode',
                {
                  breadcrumbs: restoredBreadcrumbs.length,
                  working: restoredWorking.length,
                  actions: restoredActions.length,
                  suggestions: restoredSuggestions.length,
                  newCandidates: restoredNewCount,
                },
              );
            });
            updateSessionState(sessionId, (s) => {
              s.filtering = {
                chatMode: 'ask',
                breadcrumbs: restoredBreadcrumbs,
                actions: restoredActions,
                workingCandidates: restoredWorking,
                followUpSuggestions: restoredSuggestions,
              };
              return s;
            });
          }
        }
      } catch (filterRestoreErr) {
        console.log('[loadSessionData] Filter state restore failed (non-critical):', filterRestoreErr);
      }

      // Check process tracking status first to determine active processes
      const processStatus = sessionData.process_status || {};
      console.log('[restoreSessionState] Process tracking status:', processStatus);

      // CRITICAL: If a channel is ACTIVELY PROCESSING in a new run, clear any stale logs
      // loaded from the old session-level JSON file (session_logs/job_post/*.json).
      // The JSONL file API will populate fresh logs asynchronously below.
      if (processStatus.job_post_linkedin === 'processing') {
        applyIfCurrentSession(() => setLinkedinLogs([]));
        updateSessionState(sessionId, (state) => { state.jobPosting.linkedinLogs = []; return state; });
      }
      if (processStatus.job_post_naukri === 'processing') {
        applyIfCurrentSession(() => setNaukriLogs([]));
        updateSessionState(sessionId, (state) => { state.jobPosting.naukriLogs = []; return state; });
      }
      
      // CRITICAL: After every await, re-check that user is still viewing this session.
      // If they switched away, store state in session store but don't update React UI.
      if (currentViewedSessionRef.current !== loadingForSession) {
        console.log('[loadSessionData] User switched away during load, aborting UI updates for:', loadingForSession);
        setChatHistoryLoading(false);
        setSessionLoading(false);
        return;
      }
      
      // Set processing states based on per-channel DB columns (most reliable source).
      // NOTE: job_post_linkedin/naukri values: 'processing' | 'complete' | 'failed' | 'not_started'
      // The legacy job_posting boolean is only used as a fallback.
      const hasProcessingJobPostChannel =
        processStatus.job_post_linkedin === 'processing' ||
        processStatus.job_post_naukri === 'processing';
      // Terminal states (failed/complete) must win — do NOT let legacy boolean override them.
      const hasTerminalJobPostChannel =
        processStatus.job_post_linkedin === 'failed' || processStatus.job_post_linkedin === 'complete' ||
        processStatus.job_post_naukri === 'failed' || processStatus.job_post_naukri === 'complete';
      const legacyJobPostingActive = processStatus.job_posting === true && !hasTerminalJobPostChannel;
      if (legacyJobPostingActive || hasProcessingJobPostChannel) {
        console.log('[restoreSessionState] Job posting is active according to process tracking (per-channel or legacy flag)');
        applyIfCurrentSession(() => {
          setJobPostingPhase('posting');
          setJobPostingProcessing(true);
          setJobPostingComplete(false);
          setAutoActiveHeaderTab('job_posting');
          setRightPanelMode('auto');
          // Reset outreach phase so job posting logs can show in right panel
          setOutreachPhase('idle');
        });
        
        // Try to fetch pending field responses from backend
        try {
          const fieldResponsesData = await aiAutomationApi.get(`/post-job/field-responses/${sessionId}`);
          if (fieldResponsesData?.success && fieldResponsesData?.data) {
            const responses = fieldResponsesData.data;
            console.log('[restoreSessionState] Fetched field responses:', responses);
            // If there are any pending fields stored, the backend will re-ask them via WebSocket
            // The WebSocket reconnection below will handle receiving new questions
          }
        } catch (fieldErr) {
          // Session might not have active field responses - this is normal
          console.log('[restoreSessionState] No active field responses for job posting');
        }
        
        // Check if there's saved job posting state in the ref
        if (jobPostingStatePerSessionRef.current.has(sessionId)) {
          const savedState = jobPostingStatePerSessionRef.current.get(sessionId);
          console.log('[restoreSessionState] Found saved job posting state:', savedState);
          
          // Also persist into session store so it's available after session switches
          updateSessionState(sessionId, (state) => {
            if (savedState.systemMessages?.length > 0) state.chat.systemMessages = sanitizeSystemMessages(savedState.systemMessages);
            if (savedState.pendingNaukriField) state.jobPosting.pendingField = savedState.pendingNaukriField;
            if (savedState.pendingNaukriFieldOptions?.length > 0) state.jobPosting.pendingFieldOptions = savedState.pendingNaukriFieldOptions;
            if (savedState.selectedChannels?.length > 0) state.jobPosting.selectedChannels = savedState.selectedChannels;
            if (savedState.linkedinLogs?.length > 0) state.jobPosting.linkedinLogs = savedState.linkedinLogs;
            if (savedState.naukriLogs?.length > 0) state.jobPosting.naukriLogs = savedState.naukriLogs;
            if (savedState.linkedinProcessing) state.jobPosting.linkedinProcessing = true;
            if (savedState.naukriProcessing) state.jobPosting.naukriProcessing = true;
            return state;
          });
          
          // Only restore React state if still viewing this session
          applyIfCurrentSession(() => {
            if (savedState.systemMessages?.length > 0) setSystemMessages(sanitizeSystemMessages(savedState.systemMessages));
            if (savedState.pendingNaukriField) setPendingNaukriField(savedState.pendingNaukriField);
            if (savedState.pendingNaukriFieldOptions?.length > 0) setPendingNaukriFieldOptions(savedState.pendingNaukriFieldOptions);
            if (savedState.selectedChannels?.length > 0) setSelectedChannels(savedState.selectedChannels);
            if (savedState.linkedinLogs?.length > 0) setLinkedinLogs(savedState.linkedinLogs);
            if (savedState.naukriLogs?.length > 0) setNaukriLogs(savedState.naukriLogs);
            if (savedState.linkedinProcessing) setLinkedinProcessing(true);
            if (savedState.naukriProcessing) setNaukriProcessing(true);
          });
          
          // Clear the saved state since we've restored it
          jobPostingStatePerSessionRef.current.delete(sessionId);
        }
      } else {
        console.log('[restoreSessionState] Job posting is NOT active according to process tracking');
        // Will be overridden later if job posting status indicates otherwise
      }
      
      // Set sourcing state from per-channel DB columns (most reliable source).
      const hasProcessingSourcingChannel =
        processStatus.sourcing_google === 'processing' ||
        processStatus.sourcing_linkedin === 'processing' ||
        processStatus.sourcing_resumebox === 'processing';
      if (processStatus.searching === true || hasProcessingSourcingChannel) {
        console.log('[restoreSessionState] Searching is active according to process tracking database');
        // Check if sourcing already completed via WebSocket while we were loading (race condition)
        // Only treat as truly complete if complete flag is true AND no channels are still processing
        const storedState = sessionStateStoreRef.current.get(sessionId);
        const storedSourcing = storedState?.sourcing || {};
        const alreadyCompletedViaWs = storedSourcing.complete === true && 
          !storedSourcing.googleProcessing && !storedSourcing.linkedinProcessing && !storedSourcing.resumeBoxProcessing;
        if (!alreadyCompletedViaWs) {
          // Restore selectedSourcingChannels from session store or localStorage so the UI
          // only shows the channels that were actually selected (not all channels)
          let restoredSourcingChannels = storedState?.sourcing?.selectedChannels || [];
          // Fallback: read from session store (in case channels were set during this session)
          if (restoredSourcingChannels.length === 0) {
            // No localStorage fallback — sourcing channels should come from session store only
            console.log('[restoreSessionState] No stored sourcing channels, using defaults (all channels)');
          }
          const channelGoogle = shouldRunGoogleForChannels(restoredSourcingChannels);
          const channelLinkedin = shouldRunLinkedinForChannels(restoredSourcingChannels);
          const channelResumeBox = shouldRunResumeBoxForChannels(restoredSourcingChannels);
          // Per-channel DB status from hr_talent_gpt_process_tracking (most precise)
          const dbGoogleSourcing = processStatus.sourcing_google || 'not_started';     // 'processing'|'complete'|'not_started'
          const dbLinkedinSourcing = processStatus.sourcing_linkedin || 'not_started';
          const dbResumeboxSourcing = processStatus.sourcing_resumebox || 'not_started';
          const hasPerChannelSourcingData = dbGoogleSourcing !== 'not_started' ||
            dbLinkedinSourcing !== 'not_started' || dbResumeboxSourcing !== 'not_started';
          // If DB has per-channel data, use it for precision; otherwise assume all selected channels are processing
          const googleProcessingFinal = hasPerChannelSourcingData ? dbGoogleSourcing === 'processing' : channelGoogle;
          const linkedinProcessingFinal = hasPerChannelSourcingData ? dbLinkedinSourcing === 'processing' : channelLinkedin;
          const resumeBoxProcessingFinal = hasPerChannelSourcingData ? dbResumeboxSourcing === 'processing' : channelResumeBox;
          console.log('[restoreSessionState] Per-channel processing from DB/session store:', { googleProcessingFinal, linkedinProcessingFinal, resumeBoxProcessingFinal });
          applyIfCurrentSession(() => {
            setTalentScoutingStarted(true);
            setSourcingComplete(false);
            if (!(processStatus.job_posting === true || hasProcessingJobPostChannel) && !shouldPreserveJobPostingView) {
              setRightPanelMode('sourcing');
              setActiveHeaderTab('sourcing');
            }
            setSystemMessages(['Candidate sourcing is in progress. Please wait...']);
            setGoogleSourcingProcessing(googleProcessingFinal);
            setLinkedinSourcingProcessing(linkedinProcessingFinal);
            setResumeBoxSourcingProcessing(resumeBoxProcessingFinal);
            if (restoredSourcingChannels.length > 0) {
              setSelectedSourcingChannels(restoredSourcingChannels);
            }
          });
          // Register session for WebSocket routing so incoming sourcing messages after
          // page refresh are not silently dropped (activeSourcingSessions resets on mount).
          addActiveSourcingSession(sessionId);
          pendingSourcingSessionRef.current = sessionId;
          // Fetch historical file logs for all channels that have DB activity so users
          // can see past progress; new WebSocket messages will then append to these.
          const sourcingPlatformsToFetch = [
            dbGoogleSourcing !== 'not_started' && 'google',
            dbLinkedinSourcing !== 'not_started' && 'linkedin',
            dbResumeboxSourcing !== 'not_started' && 'resumebox',
          ].filter(Boolean);
          if (sourcingPlatformsToFetch.length > 0) {
            Promise.all(
              sourcingPlatformsToFetch.map((platform) =>
                aiAutomationApi
                  .get(`/logs/session/${sessionId}/sourcing/${platform}`)
                  .then((data) => ({ platform, logs: data?.logs || [] }))
                  .catch(() => ({ platform, logs: [] }))
              )
            ).then((results) => {
              applyIfCurrentSession(() => {
                results.forEach(({ platform, logs }) => {
                  const chStatus = processStatus[`sourcing_${platform}`];
                  const formatted =
                    logs.length > 0
                      ? logs.map((e) => ({
                          message: e.message || '',
                          timestamp: e.timestamp || '',
                          type: e.log_type === 'error' ? 'error' : e.log_type === 'success' ? 'completed' : 'info',
                          logType: e.log_type === 'error' ? 'error' : e.log_type === 'success' ? 'completed' : 'info',
                          status: e.log_type === 'error' ? 'error' : e.log_type === 'success' ? 'completed' : 'info',
                          platform: e.platform || null,
                        }))
                      : chStatus && chStatus !== 'not_started' ? [{
                          message:
                            chStatus === 'complete' ? 'Sourcing completed successfully.' :
                            chStatus === 'failed'   ? 'Sourcing encountered an issue.'   :
                                                     'Sourcing is in progress.',
                          timestamp: new Date().toISOString(),
                          type:    chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          logType: chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          status:  chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          platform: null,
                        }] : [];
                  if (platform === 'google')    setGoogleSourcingLogs(formatted);
                  if (platform === 'linkedin')  setLinkedinSourcingLogs(formatted);
                  if (platform === 'resumebox') setResumeBoxSourcingLogs(formatted);
                });
              });
            });
          }
        } else {
          console.log('[restoreSessionState] Sourcing already completed via WebSocket, skipping in-progress restoration');
          // No localStorage logs to restore — logs come from DB chat messages or file API
        }
      } else {
        // processStatus.searching is not true — no active sourcing to restore.
        // But check hr_talent_gpt_process_tracking per-channel columns directly:
        // if any channel has activity (complete/failed/processing), restore sourcing state.
        console.log('[restoreSessionState] Searching is NOT active — checking per-channel DB columns for completed sourcing');
        const anySourcingActivity =
          processStatus.sourcing_google !== 'not_started' ||
          processStatus.sourcing_linkedin !== 'not_started' ||
          processStatus.sourcing_resumebox !== 'not_started';
        if (anySourcingActivity) {
          const gsProcessing  = processStatus.sourcing_google    === 'processing';
          const lnProcessing  = processStatus.sourcing_linkedin  === 'processing';
          const rbProcessing  = processStatus.sourcing_resumebox === 'processing';
          const anyStillProcessing = gsProcessing || lnProcessing || rbProcessing;
          console.log('[restoreSessionState] Sourcing activity found in DB — restoring as', anyStillProcessing ? 'in-progress' : 'complete');
          applyIfCurrentSession(() => {
            setTalentScoutingStarted(true);
            setSourcingComplete(!anyStillProcessing);
            setGoogleSourcingProcessing(gsProcessing);
            setLinkedinSourcingProcessing(lnProcessing);
            setResumeBoxSourcingProcessing(rbProcessing);
          });
          updateSessionState(sessionId, (state) => {
            state.sourcing.started = true;
            state.sourcing.complete = !anyStillProcessing;
            return state;
          });
          // Fetch file-based logs for each channel that has activity.
          // If the file API returns nothing, inject a synthetic entry so ProcessStatusCard
          // shows the correct completion/failure tone instead of the default idle state.
          const platformsWithActivity = [
            processStatus.sourcing_google    !== 'not_started' && 'google',
            processStatus.sourcing_linkedin  !== 'not_started' && 'linkedin',
            processStatus.sourcing_resumebox !== 'not_started' && 'resumebox',
          ].filter(Boolean);
          if (platformsWithActivity.length > 0) {
            Promise.all(
              platformsWithActivity.map((platform) =>
                aiAutomationApi
                  .get(`/logs/session/${sessionId}/sourcing/${platform}`)
                  .then((data) => ({ platform, logs: data?.logs || [] }))
                  .catch(() => ({ platform, logs: [] }))
              )
            ).then((results) => {
              applyIfCurrentSession(() => {
                results.forEach(({ platform, logs }) => {
                  const chStatus = processStatus[`sourcing_${platform}`];
                  const formatted =
                    logs.length > 0
                      ? logs.map((e) => ({
                          message: e.message || '',
                          timestamp: e.timestamp || '',
                          type: e.log_type === 'error' ? 'error' : 'info',
                          logType: e.log_type === 'error' ? 'error' : 'info',
                          platform: e.platform || null,
                        }))
                      : [{
                          message:
                            chStatus === 'complete' ? 'Sourcing completed successfully.' :
                            chStatus === 'failed'   ? 'Sourcing encountered an issue.'   :
                                                     'Sourcing is in progress.',
                          timestamp: new Date().toISOString(),
                          type:    chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          logType: chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          status:  chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
                          platform: null,
                        }];
                  if (platform === 'google')    setGoogleSourcingLogs(formatted);
                  if (platform === 'linkedin')  setLinkedinSourcingLogs(formatted);
                  if (platform === 'resumebox') setResumeBoxSourcingLogs(formatted);
                });
              });
            });
          }
        }
      }
      
      if (processStatus.outreach === true) {
        console.log('[restoreSessionState] Outreach is active according to process tracking');
        applyIfCurrentSession(() => {
          setOutreachPhase('sending');
          setOutreachSending(true);
          setOutreachCompleted(false);
        });
      }

      // Restore search_id from session searches
      const searches = sessionData.searches || [];
      let restoredJdText = '';
      if (searches.length > 0) {
        const latestSearch = searches[searches.length - 1];
        const restoredSearchId = latestSearch.search_id || latestSearch.id;
        setAutomationSearchId(restoredSearchId);
        if (restoredSearchId) {
          searchIdBySessionRef.current[sessionId] = restoredSearchId;
          updateSessionState(sessionId, (state) => {
            state.searchId = restoredSearchId;
            return state;
          });
        }

        // Restore JD text from search's job descriptions
        // Search through ALL searches (in reverse) to find one with job_descriptions, 
        // not just the latest (which might be empty if created during job posting)
        for (let i = searches.length - 1; i >= 0 && !restoredJdText; i--) {
          const search = searches[i];
          const jdList = search.job_descriptions || [];
          if (jdList.length > 0) {
            const latestJd = jdList[jdList.length - 1];
            restoredJdText =
              latestJd.jd_text ||
              latestJd.job_description_text ||
              latestJd.job_description ||
              latestJd.full_job_description ||
              latestJd.jobDescription?.content ||
              latestJd.description ||
              latestJd.content ||
              '';
          }
        }

        // Restore parsed requirements
        const parsedReq = latestSearch.parsed_requirements || sessionData.parsed_requirements || {};
        if (parsedReq && Object.keys(parsedReq).length > 0) {
          // Always persist to session store
          updateSessionState(sessionId, (state) => {
            state.jd.requirements = parsedReq;
            return state;
          });
          applyIfCurrentSession(() => {
            setAccumulatedRequirements(parsedReq);
            setFinalRequirements(parsedReq);
            setBackendMissingFields(computeMissingFieldsFromAccumulated(parsedReq));
          });
          
          // Check if JD exists - if no JD but we have requirements, show confirmation buttons
          if (!restoredJdText) {
            // Check if we have complete requirements (ready for confirmation)
            const hasTitle = !!(parsedReq.title || parsedReq.role);
            const hasLocation = !!parsedReq.location;
            const hasExperience = !!parsedReq.experience;
            const hasSkills = Array.isArray(parsedReq.skills) && parsedReq.skills.length > 0;
            
            if (hasTitle && hasLocation && hasExperience && hasSkills) {
              // All fields present - show confirmation buttons
              const confirmationParts = [];
              if (parsedReq.title || parsedReq.role) confirmationParts.push(`Role: ${parsedReq.title || parsedReq.role}`);
              if (parsedReq.experience) confirmationParts.push(`Experience: ${parsedReq.experience}`);
              if (parsedReq.location) confirmationParts.push(`Location: ${parsedReq.location}`);
              if (parsedReq.skills && parsedReq.skills.length > 0) confirmationParts.push(`Skills: ${parsedReq.skills.join(', ')}`);
              const detailsMsg = `Got it - here's what I understood:\n\n${confirmationParts.join('\n')}`;
              const questionMsg = "Do you want to make any changes or should I proceed with the Sourcing Strategy and JD?";
              
              updateSessionState(sessionId, (state) => {
                state.jd.analysisPhase = 'analysed';
                state.jd.waitingForConfirmation = true;
                state.chat.systemMessages = [detailsMsg, questionMsg];
                return state;
              });
              applyIfCurrentSession(() => {
                setAnalysisUxPhase('analysed');
                setWaitingForConfirmation(true);
                setSystemMessages([detailsMsg, questionMsg]);
                setSystemMessage('');
              });
            } else {
              // Missing some fields - stay in analysing phase
              updateSessionState(sessionId, (state) => { state.jd.analysisPhase = 'analysing'; return state; });
              applyIfCurrentSession(() => setAnalysisUxPhase('analysing'));
            }
          } else {
            // JD exists - mark as done
            updateSessionState(sessionId, (state) => { state.jd.analysisPhase = 'done'; return state; });
            applyIfCurrentSession(() => {
              setAnalysisUxPhase('done');
              setShowResultsPanel(true);
            });
          }
        }

        // Restore candidates from ALL searches in this session
        const allCandidates = searches
          .flatMap((s) => (Array.isArray(s?.candidates) ? s.candidates : []))
          .filter(Boolean);
        const uniqueCandidates = allCandidates.filter(
          (candidate, idx, arr) =>
            idx === arr.findIndex((row) => String(row?.id || '') === String(candidate?.id || ''))
        );

        if (uniqueCandidates.length > 0) {
          const emailCount = uniqueCandidates.filter(c => c.email && c.email.trim() !== '').length;
          const linkedinCount = uniqueCandidates.filter(c =>
            (c.profile_url && c.profile_url.includes('linkedin')) ||
            (c.linkedin_profile_url && c.linkedin_profile_url.trim() !== '') ||
            (c.platforms && Array.isArray(c.platforms) && c.platforms.includes('LinkedIn'))
          ).length;
          const restoredStats = { total_profiles: uniqueCandidates.length, email_count: emailCount, linkedin_count: linkedinCount };
          // Compute per-channel counts from `platforms` field (DB source of truth)
          const computedGoogleCount = uniqueCandidates.filter(c => {
            const plats = Array.isArray(c.platforms) ? c.platforms : [];
            return plats.some(p => typeof p === 'string' && p.toLowerCase() === 'google');
          }).length;
          const computedLinkedinCount = uniqueCandidates.filter(c => {
            const plats = Array.isArray(c.platforms) ? c.platforms : [];
            return plats.some(p => typeof p === 'string' && p.toLowerCase() === 'linkedin');
          }).length;
          const computedResumeBoxCount = uniqueCandidates.filter(c => {
            const plats = Array.isArray(c.platforms) ? c.platforms : [];
            return plats.some(p => typeof p === 'string' && p.toLowerCase() === 'resume_box');
          }).length;
          
          // Also restore logs from localStorage if available 
          let restoredGoogleLogs = [];
          let restoredLinkedinLogs = [];
          let restoredResumeBoxLogs = [];
          let lsGoogleCandidateCount = 0;
          let lsLinkedinCandidateCount = 0;
          let lsResumeBoxCandidateCount = 0;
          try {
            const lsKey = `sourcing_state_${sessionId}`;
            const lsData = JSON.parse(localStorage.getItem(lsKey) || '{}');
            if (Array.isArray(lsData.googleLogs) && lsData.googleLogs.length > 0) {
              restoredGoogleLogs = lsData.googleLogs;
            }
            if (Array.isArray(lsData.linkedinLogs) && lsData.linkedinLogs.length > 0) {
              restoredLinkedinLogs = lsData.linkedinLogs;
            }
            if (Array.isArray(lsData.resumeBoxLogs) && lsData.resumeBoxLogs.length > 0) {
              restoredResumeBoxLogs = lsData.resumeBoxLogs;
            }
            lsGoogleCandidateCount = lsData.googleCandidateCount || 0;
            lsLinkedinCandidateCount = lsData.linkedinCandidateCount || 0;
            lsResumeBoxCandidateCount = lsData.resumeBoxCandidateCount || 0;
          } catch (_) { /* ignore */ }
          
          // DB per-channel processing status is the authoritative source (localStorage may lag)
          const dbGoogleChannelProcessing = processStatus.sourcing_google === 'processing';
          const dbLinkedinChannelProcessing = processStatus.sourcing_linkedin === 'processing';
          const dbResumeBoxChannelProcessing = processStatus.sourcing_resumebox === 'processing';
          // Only mark sourcing as truly complete if the process tracking table says no channel is processing.
          // LocalStorage/log state can lag after refresh and must not override hr_talent_gpt_process_tracking.
          const anyChannelStillProcessing = dbGoogleChannelProcessing || dbLinkedinChannelProcessing || dbResumeBoxChannelProcessing
            || (processStatus.searching === true);
          const isSourcingTrulyComplete = !anyChannelStillProcessing;
          // Per-channel processing is restored from hr_talent_gpt_process_tracking only.
          const searchingBranchActive = processStatus.searching === true || dbGoogleChannelProcessing || dbLinkedinChannelProcessing || dbResumeBoxChannelProcessing;
          const effectiveGoogleProcessingCandidates = searchingBranchActive && dbGoogleChannelProcessing;
          const effectiveLinkedinProcessingCandidates = searchingBranchActive && dbLinkedinChannelProcessing;
          const effectiveResumeBoxProcessingCandidates = searchingBranchActive && dbResumeBoxChannelProcessing;
          
          // Always store in session state
          updateSessionState(sessionId, (state) => {
            state.sourcing.candidates = uniqueCandidates;
            state.sourcing.started = true;
            state.sourcing.complete = isSourcingTrulyComplete;
            state.sourcing.stats = restoredStats;
            // Preserve per-channel processing flags from the process tracking table.
            state.sourcing.googleProcessing = Boolean(effectiveGoogleProcessingCandidates);
            state.sourcing.linkedinProcessing = Boolean(effectiveLinkedinProcessingCandidates);
            state.sourcing.resumeBoxProcessing = Boolean(effectiveResumeBoxProcessingCandidates);
            // Also restore logs to session state
            if (restoredGoogleLogs.length > 0) state.sourcing.googleLogs = restoredGoogleLogs;
            if (restoredLinkedinLogs.length > 0) state.sourcing.linkedinLogs = restoredLinkedinLogs;
            if (restoredResumeBoxLogs.length > 0) state.sourcing.resumeBoxLogs = restoredResumeBoxLogs;
            return state;
          });
          if (isSourcingTrulyComplete) {
            removeActiveSourcingSession(sessionId); // Clear active sourcing session when complete
          }
          
          // NOTE: Do NOT remove localStorage here - it contains logs that users want to see
          // after page refresh. The logs will be restored from localStorage on next load.
          
          applyIfCurrentSession(() => {
            setCandidates(uniqueCandidates);
            setTalentScoutingStarted(true);
            setSourcingComplete(isSourcingTrulyComplete);
            setRightPanelMode('candidates');
            setSourcingStats(restoredStats);
            // Restore per-channel processing flags: DB is authoritative, LS is fallback
            setGoogleSourcingProcessing(effectiveGoogleProcessingCandidates);
            setLinkedinSourcingProcessing(effectiveLinkedinProcessingCandidates);
            setResumeBoxSourcingProcessing(effectiveResumeBoxProcessingCandidates);
            // Restore candidate counts: prefer localStorage → DB-computed → DB tracking counts
            const dbGoogleCount = processStatus.sourcing_google_count || 0;
            const dbLinkedinCount = processStatus.sourcing_linkedin_count || 0;
            const dbResumeBoxCount = processStatus.sourcing_resumebox_count || 0;
            setGoogleCandidateCount(lsGoogleCandidateCount || computedGoogleCount || dbGoogleCount);
            setLinkedinCandidateCount(lsLinkedinCandidateCount || computedLinkedinCount || dbLinkedinCount);
            setResumeBoxCandidateCount(lsResumeBoxCandidateCount || computedResumeBoxCount || dbResumeBoxCount);
            // Restore logs to React state too
            if (restoredGoogleLogs.length > 0) setGoogleSourcingLogs(restoredGoogleLogs);
            if (restoredLinkedinLogs.length > 0) setLinkedinSourcingLogs(restoredLinkedinLogs);
            if (restoredResumeBoxLogs.length > 0) setResumeBoxSourcingLogs(restoredResumeBoxLogs);
          });
        }
      }

      // Restore job posting details/status if available.
      // Use the dedicated backend endpoint as the primary status source.
      const jobPostings = Array.isArray(sessionData.job_postings) ? sessionData.job_postings : [];
      const latestSessionPosting = jobPostings[0] || null;
      const latestPosting = backendJobPosting || latestSessionPosting;
      if (latestPosting) {
        let additional = latestSessionPosting?.additional_data || latestPosting.additional_data;
        if (typeof additional === 'string') {
          try {
            additional = JSON.parse(additional);
          } catch (e) {
            additional = {};
          }
        }
        additional = additional || {};
        const backendChannelStatuses = latestPosting?.channel_statuses || {};
        const inferredChannelsFromBackend = Object.entries(backendChannelStatuses)
          .filter(([, status]) => status && status !== 'not_started')
          .map(([channel]) => (channel === 'linkedin' ? 'LinkedIn' : 'Naukri'));
        const channels = Array.isArray(additional.channels) && additional.channels.length > 0
          ? additional.channels
          : inferredChannelsFromBackend;

        const postingStatus = (latestPosting.posting_status || '').toLowerCase();
        const postingInProgressStatuses = [
          'posting', 'processing', 'in_progress', 'in-progress', 'pending', 'running', 'started',
        ];
        // Per-channel DB status from hr_talent_gpt_process_tracking (most reliable source)
        const dbLinkedinStatus = processStatus.job_post_linkedin || 'not_started'; // 'processing'|'complete'|'failed'|'not_started'
        const dbNaukriStatus = processStatus.job_post_naukri || 'not_started';
        const hasFailedChannel = Object.values(backendChannelStatuses).includes('failed') ||
          processStatus.failed_job_post_linkedin === true || processStatus.job_post_linkedin === 'failed' ||
          processStatus.failed_job_post_naukri === true || processStatus.job_post_naukri === 'failed';
        const hasProcessingChannel = Object.values(backendChannelStatuses).includes('processing') ||
          dbLinkedinStatus === 'processing' || dbNaukriStatus === 'processing';

        // Determine job posting state
        let restoredJobPostingPhase = 'idle';
        let restoredJobPostingComplete = false;
        let restoredJobPostingProcessing = false;
        let restoredJobPostingFailed = false;

        // DB channel-complete flag is the most authoritative — if complete, treat as completed
        const dbAnyComplete = dbLinkedinStatus === 'complete' || dbNaukriStatus === 'complete';
        // Terminal channels take priority over legacy job_posting boolean (which is never cleared)
        const hasTerminalJobPostChannelLP =
          dbLinkedinStatus === 'failed' || dbLinkedinStatus === 'complete' ||
          dbNaukriStatus === 'failed' || dbNaukriStatus === 'complete';
        const legacyJobPostActiveLP = processStatus.job_posting === true && !hasTerminalJobPostChannelLP;

        if (legacyJobPostActiveLP || hasProcessingChannel) {
          restoredJobPostingPhase = 'posting';
          restoredJobPostingProcessing = true;
        } else if (postingStatus === 'posted' || postingStatus === 'partial' || postingStatus === 'failed' || hasFailedChannel || dbAnyComplete) {
          restoredJobPostingPhase = 'posted';
          restoredJobPostingComplete = true;
          restoredJobPostingFailed = (postingStatus === 'failed' || hasFailedChannel) && !dbAnyComplete;
        } else if (postingInProgressStatuses.includes(postingStatus)) {
          restoredJobPostingPhase = 'posting';
          restoredJobPostingProcessing = true;
        }

        const posted = new Set();
        if (Array.isArray(latestPosting?.posted_channels)) {
          latestPosting.posted_channels.forEach((channel) => posted.add(channel));
        }
        if (latestPosting?.linkedin_success === true) posted.add('LinkedIn');
        if (latestPosting?.naukri_success === true) posted.add('Naukri');
        // hr_talent_gpt_process_tracking _complete=true is the most reliable completion signal
        if (dbLinkedinStatus === 'complete') posted.add('LinkedIn');
        if (dbNaukriStatus === 'complete') posted.add('Naukri');
        const postedChannelsArr = Array.from(posted);
        const selectedOrInferredChannels = channels.length > 0 ? channels : postedChannelsArr;

        // DB terminal states (failed/complete) must suppress processing — never show "processing"
        // after page refresh when hr_talent_gpt_process_tracking already has a final status.
        const restoredLinkedinProcessing = dbLinkedinStatus === 'processing' ||
          (backendChannelStatuses.linkedin === 'processing' && dbLinkedinStatus !== 'failed' && dbLinkedinStatus !== 'complete') ||
          (postingInProgressStatuses.includes(postingStatus) &&
            selectedOrInferredChannels.includes('LinkedIn') && !posted.has('LinkedIn') &&
            dbLinkedinStatus !== 'failed' && dbLinkedinStatus !== 'complete');
        const restoredNaukriProcessing = dbNaukriStatus === 'processing' ||
          (backendChannelStatuses.naukri === 'processing' && dbNaukriStatus !== 'failed' && dbNaukriStatus !== 'complete') ||
          (postingInProgressStatuses.includes(postingStatus) &&
            selectedOrInferredChannels.includes('Naukri') && !posted.has('Naukri') &&
            dbNaukriStatus !== 'failed' && dbNaukriStatus !== 'complete');

        const sessionLogState = getSessionState(sessionId);
        const restoredCollectedDetails = sessionLogState?.jobPosting?.collectedDetails || {};
        const restoredPostingDetails = {
          ...collectedDetailsToPostingDetails(restoredCollectedDetails),
          employmentType: restoredCollectedDetails.employment_type || additional.employment_type || latestSessionPosting?.employment_type || latestPosting.employment_type || '',
          workplaceType: restoredCollectedDetails.workplace_type || additional.workplace_type || latestSessionPosting?.workplace_type || latestPosting.workplace_type || '',
          jobLocation: restoredCollectedDetails.location || additional.job_location || latestSessionPosting?.location || latestPosting.location || '',
          companyName: restoredCollectedDetails.company_name || latestSessionPosting?.company_name || latestPosting.company_name || '',
          salaryMin: restoredCollectedDetails.salary_min ?? latestSessionPosting?.salary_min ?? latestPosting.salary_min ?? '',
          salaryMax: restoredCollectedDetails.salary_max ?? latestSessionPosting?.salary_max ?? latestPosting.salary_max ?? '',
          recruiterName: restoredCollectedDetails.recruiter_name || latestSessionPosting?.recruiter_name || latestPosting.recruiter_name || '',
          recruiterNumber: restoredCollectedDetails.recruiter_number || latestSessionPosting?.recruiter_number || latestPosting.recruiter_number || '',
        };

        // Fallback: if JD text was not found in search records, restore from job posting record
        if (!restoredJdText && (latestSessionPosting?.full_job_description || latestPosting.full_job_description)) {
          restoredJdText = latestSessionPosting?.full_job_description || latestPosting.full_job_description;
        }

        // Always persist to session store
        updateSessionState(sessionId, (state) => {
          state.jobPosting.phase = restoredJobPostingPhase;
          state.jobPosting.complete = restoredJobPostingComplete;
          state.jobPosting.processing = restoredJobPostingProcessing;
          state.jobPosting.failed = restoredJobPostingFailed;
          state.jobPosting.selectedChannels = selectedOrInferredChannels;
          state.jobPosting.postedChannels = postedChannelsArr;
          state.jobPosting.linkedinProcessing = restoredLinkedinProcessing;
          state.jobPosting.naukriProcessing = restoredNaukriProcessing;
          return state;
        });

        applyIfCurrentSession(() => {
          setJobPostingPhase(restoredJobPostingPhase);
          setJobPostingComplete(restoredJobPostingComplete);
          setJobPostingProcessing(restoredJobPostingProcessing);
          setJobPostingFailed(restoredJobPostingFailed);
          if (channels.length > 0) setSelectedChannels(channels);
          else if (postedChannelsArr.length > 0) setSelectedChannels(postedChannelsArr);
          setPostedChannels(postedChannelsArr);
          setPostingDetails(restoredPostingDetails);
          setLinkedinProcessing(restoredLinkedinProcessing);
          setNaukriProcessing(restoredNaukriProcessing);
          
          if (Object.keys(restoredCollectedDetails).length > 0) {
            setCollectedJobPostingDetails((prev) => ({ ...(prev || {}), ...restoredCollectedDetails }));
          }

          // Also restore logs from session state (includes localStorage-restored logs)
          if (sessionLogState?.jobPosting?.linkedinLogs?.length > 0) {
            setLinkedinLogs(sessionLogState.jobPosting.linkedinLogs);
          }
          if (sessionLogState?.jobPosting?.naukriLogs?.length > 0) {
            setNaukriLogs(sessionLogState.jobPosting.naukriLogs);
          }
          
          if (restoredJobPostingPhase === 'posting' || restoredJobPostingProcessing) {
            setAutoActiveHeaderTab('job_posting');
            // Only override rightPanelMode if sourcing is NOT complete — candidates panel takes priority
            const sourcingState = sessionStateStoreRef.current.get(sessionId)?.sourcing;
            if (!sourcingState?.complete) {
              setRightPanelMode('auto');
            }
          }
        });
      } else {
        // No job posting data for this session
        const hasAnyDbJobPostingProcessing =
          processStatus.job_posting === true ||
          processStatus.job_post_linkedin === 'processing' ||
          processStatus.job_post_naukri === 'processing';
        if (hasAnyDbJobPostingProcessing) {
          console.log('[restoreSessionState] No job posting data but process tracking shows an active run, restoring in-progress state');
          updateSessionState(sessionId, (state) => {
            state.jobPosting.phase = 'posting';
            state.jobPosting.processing = true;
            state.jobPosting.complete = false;
            state.jobPosting.linkedinProcessing = processStatus.job_post_linkedin === 'processing';
            state.jobPosting.naukriProcessing = processStatus.job_post_naukri === 'processing';
            return state;
          });
          applyIfCurrentSession(() => {
            setJobPostingPhase('posting');
            setJobPostingProcessing(true);
            setJobPostingComplete(false);
            setJobPostingFailed(false);
            setLinkedinProcessing(processStatus.job_post_linkedin === 'processing');
            setNaukriProcessing(processStatus.job_post_naukri === 'processing');
            setAutoActiveHeaderTab('job_posting');
            // Only override rightPanelMode if sourcing is NOT complete
            const sourcingState = sessionStateStoreRef.current.get(sessionId)?.sourcing;
            if (!sourcingState?.complete) {
              setRightPanelMode('auto');
            }
          });
        } else {
          // Process tracking confirms no job posting - reset states
          updateSessionState(sessionId, (state) => {
            state.jobPosting.phase = 'idle';
            state.jobPosting.processing = false;
            state.jobPosting.complete = false;
            return state;
          });
          applyIfCurrentSession(() => {
            setJobPostingPhase('idle');
            setJobPostingProcessing(false);
            setJobPostingComplete(false);
            setJobPostingFailed(false);
            setSelectedChannels([]);
            setPostedChannels([]);
            setPostingDetails({});
            setLinkedinProcessing(false);
            setNaukriProcessing(false);
            setPendingNaukriField(null);
            setPendingNaukriFieldOptions([]);
          });
        }
      }

      if (restoredJdText) {
        updateSessionState(sessionId, (state) => {
          state.jd.text = restoredJdText;
          state.jd.analysisPhase = 'done';
          return state;
        });
        applyIfCurrentSession(() => {
          setJdText(restoredJdText);
          setAnalysisUxPhase('done');
          setShowResultsPanel(true);
        });
      }

      // Fetch full chat history (user + assistant + system) from hr_automation_chat_message
      try {
        const historyResponse = await aiAutomationApi.get(
          `/sessions/${sessionId}/chat-messages`
        );
        let rawMessages = Array.isArray(historyResponse?.messages)
          ? historyResponse.messages
          : [];
        
        // Sort messages by sequence_number (stable) → created_at (fallback) → array index (last resort)
        rawMessages = rawMessages.sort((a, b) => {
          const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : Infinity;
          const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : Infinity;
          if (aSeq !== bSeq) return aSeq - bSeq;
          // Fallback to created_at (ISO string comparison works for UTC timestamps)
          const aTime = a.created_at || '';
          const bTime = b.created_at || '';
          if (aTime !== bTime) return aTime < bTime ? -1 : 1;
          return 0; // Preserve original order
        });

        if (rawMessages.length > 0) {
          const restoredCandidateCount = Array.isArray(searches)
            ? searches
                .flatMap((s) => (Array.isArray(s?.candidates) ? s.candidates : []))
                .filter(Boolean).length
            : 0;
          const restoredLinkedinPostingLogs = [];
          const restoredNaukriPostingLogs = [];
          const restoredGoogleSourcingLogs = [];
          const restoredLinkedinSourcingLogs = [];
          const restoredOutreachLogs = [];

          let hasSourcingStartedSignal = false;
          let hasSourcingCompletedSignal = false;
          let hasGoogleSourcingStarted = false;
          let hasLinkedinSourcingStarted = false;
          let hasLinkedinSourcingCompleted = false;
          let hasOutreachStartedSignal = false;
          let hasOutreachCompletedSignal = false;
          let hasJobPostingStartedSignal = false;
          let hasJobPostingCompletedSignal = false;
          const startedJobPostingChannels = new Set();
          const persistedJobPostingState = getPersistedJobPostingState(sessionId);
          const persistedLinkedinPostingProcessing = Boolean(persistedJobPostingState?.linkedinProcessing);
          const persistedNaukriPostingProcessing = Boolean(persistedJobPostingState?.naukriProcessing);
          const persistedJobPostingInProgress = Boolean(
            persistedJobPostingState?.processing ||
            persistedLinkedinPostingProcessing ||
            persistedNaukriPostingProcessing
          );
          
          // Track field collection state for session persistence
          const fieldCollectionAskedFields = new Set(); // Fields that have been asked
          const fieldCollectionAnsweredFields = new Set(); // Fields that have been answered
          const collectedDetailsFromDB = {}; // Collected details from answered questions
          let lastAskedField = null; // The last field that was asked (may be unanswered)
          let hasJobPostingCancelledSignal = false; // Track if job posting was cancelled

          rawMessages.forEach((m) => {
            const content = String(m?.content || '').trim();
            if (!content) return;
            const metadata = parseMessageMetadata(m?.metadata);
            const phaseValue = String(m?.phase || metadata?.phase || '').toLowerCase();
            const platformValue = normalizePlatformName(
              m?.platform || metadata?.platform || metadata?.source || ''
            );
            const statusValue = String(
              metadata?.status || metadata?.state || m?.status || ''
            ).toLowerCase();
            const lower = content.toLowerCase();
            const messageType = String(m?.message_type || '').toLowerCase();
            const metadataType = String(metadata?.type || '').toLowerCase();
            const isJobPostingFieldCollectionPhase = phaseValue === 'job_posting_field_collection';
            const isJobPostingChannelSelectionPhase = phaseValue === 'job_posting_channel_selection';
            const isJobPostingPreStartPhase =
              isJobPostingFieldCollectionPhase || isJobPostingChannelSelectionPhase;
            const isJobPostingStartSignal =
              phaseValue === 'job_posting_started' ||
              phaseValue === 'job_posting_start' ||
              metadataType === 'job_posting_start' ||
              metadata?._internal === 'job_posting_started_signal';
            const isJobPostingTerminalSignal =
              phaseValue === 'job_posting_completed' ||
              phaseValue === 'job_posting_complete' ||
              phaseValue === 'job_posting_failed' ||
              phaseValue === 'job_posting_error';

            // Check for job posting cancellation - this takes precedence over field collection
            if (phaseValue === 'job_posting_cancelled' || lower.includes('job posting process cancelled')) {
              hasJobPostingCancelledSignal = true;
              // Clear asked fields on cancel to prevent restoring field collection
              fieldCollectionAskedFields.clear();
              fieldCollectionAnsweredFields.clear();
            }

            // Track job posting field collection state for session persistence
            // But only if not cancelled
            if (!hasJobPostingCancelledSignal && phaseValue === 'job_posting_field_collection' && metadata?.field_name) {
              const fieldName = metadata.field_name;
              if (messageType === 'assistant') {
                // This is a question being asked
                fieldCollectionAskedFields.add(fieldName);
                lastAskedField = fieldName;
              } else if (messageType === 'user') {
                // This is an answer to a question
                fieldCollectionAnsweredFields.add(fieldName);
                // Store parsed values if available
                if (metadata?.parsed_values) {
                  Object.assign(collectedDetailsFromDB, metadata.parsed_values);
                } else {
                  // Try to map the value based on field name
                  const modalKey = fieldName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
                  collectedDetailsFromDB[modalKey] = content;
                }
              }
            }

            const isError =
              statusValue === 'failed' ||
              statusValue === 'error' ||
              lower.includes(' failed') ||
              lower.includes(' error');
            const isSuccess =
              statusValue === 'completed' ||
              statusValue === 'success' ||
              lower.includes('completed') ||
              lower.includes('posted successfully');

            if (
              phaseValue.includes('source_candidates') ||
              phaseValue.includes('sourcing') ||
              lower.includes('searching across linkedin') ||
              lower.includes('candidate sourcing') ||
              // Also catch messages with platform='LinkedIn' or 'Google' that might have different phase values
              ((platformValue === 'LinkedIn' || platformValue === 'Google') && 
               (messageType === 'log' || phaseValue === 'source_candidates' || lower.includes('search')))
            ) {
              hasSourcingStartedSignal = true;
              if (
                lower.includes('talent scouting is completed') ||
                lower.includes('sourcing complete') ||
                lower.includes('sourcing completed')
              ) {
                hasSourcingCompletedSignal = true;
              }

              // Only add actual WebSocket log messages to sourcing logs, NOT agent/assistant promotional messages
              // Agent messages like "Moving to Candidate Sourcing..." contain markdown and promotional text
              const isWebSocketLog = messageType === 'log' || messageType === 'system';
              const isPromotionalMessage = content.includes('**') && (
                lower.includes('moving to') ||
                lower.includes('ready to source') ||
                lower.includes('say **') ||
                lower.includes('sourcing platforms:') ||
                lower.includes('this will search') ||
                lower.includes("let's find")
              );
              
              if (isWebSocketLog || (!isPromotionalMessage && messageType !== 'agent' && messageType !== 'assistant')) {
                const entry = {
                  message: content,
                  timestamp: m?.created_at || m?.timestamp || new Date().toISOString(),
                  logType: isError ? 'error' : 'info',
                  type: isError ? 'error' : 'info',
                };
                if (platformValue === 'Google' || lower.includes('google')) {
                  restoredGoogleSourcingLogs.push(entry);
                  hasGoogleSourcingStarted = true;
                } else if (platformValue === 'LinkedIn' || lower.includes('linkedin')) {
                  restoredLinkedinSourcingLogs.push(entry);
                  hasLinkedinSourcingStarted = true;
                  // Check for LinkedIn-specific completion messages
                  if (
                    lower.includes('linkedin search completed') ||
                    lower.includes('linkedin profile search completed') ||
                    lower.includes('profile search automation completed')
                  ) {
                    hasLinkedinSourcingCompleted = true;
                  }
                }
              }
            }

            let postingPhaseDetected =
              !isJobPostingPreStartPhase &&
              (isJobPostingStartSignal ||
                isJobPostingTerminalSignal ||
                phaseValue === 'linkedin' ||
                phaseValue === 'naukri' ||
                ((messageType === 'log' || messageType === 'system') &&
                  phaseValue.includes('job_post')) ||
                ((messageType === 'log' || messageType === 'system') &&
                  (lower.includes('job posting started') ||
                    lower.includes('job posting completed') ||
                    lower.includes('job posting failed') ||
                    lower.includes('job post failed') ||
                    lower.includes('posted successfully'))));
            // Fallback: many backend posting logs are saved as generic 'log' entries
            // with platform LinkedIn/Naukri and without explicit phase.
            if (
              !postingPhaseDetected &&
              !isJobPostingPreStartPhase &&
              messageType === 'log' &&
              (platformValue === 'LinkedIn' || platformValue === 'Naukri') &&
              !phaseValue.includes('source_candidates') &&
              !phaseValue.includes('sourcing') &&
              !phaseValue.includes('outreach')
            ) {
              postingPhaseDetected = true;
            }
            if (postingPhaseDetected) {
              // Skip JD creation / promotional messages that mention platform names
              const isJDCreationMsg = lower.includes('has been created') || 
                (lower.includes('next step:') && (lower.includes('source candidates') || lower.includes('post job'))) ||
                lower.includes('view it in the job posting tab') ||
                (lower.includes('starting **job posting**') && (lower.includes('available platforms') || lower.includes('guide you through'))) ||
                (lower.includes('starting job posting') && (lower.includes('available platforms') || lower.includes('guide you through'))) ||
                (lower.includes('ready to post the job for') && lower.includes('available platforms'));
              if (isJDCreationMsg) {
                // Don't add JD creation messages to job posting logs
              } else {
              const metadataChannels = Array.isArray(metadata?.channels) ? metadata.channels : [];
              metadataChannels.forEach((channel) => {
                const normalizedChannel = normalizePlatformName(channel);
                if (normalizedChannel === 'LinkedIn' || normalizedChannel === 'Naukri') {
                  startedJobPostingChannels.add(normalizedChannel);
                }
              });
              if (platformValue === 'LinkedIn' || platformValue === 'Naukri') {
                startedJobPostingChannels.add(platformValue);
              }
              hasJobPostingStartedSignal = true;
              // Check for completion - either success OR failure
              if (isSuccess || isError || lower.includes('all job postings complete') || lower.includes('job posting failed') || lower.includes('job post failed')) {
                hasJobPostingCompletedSignal = true;
              }
              const entry = {
                message: content,
                timestamp: m?.created_at || m?.timestamp || new Date().toISOString(),
                status: isError ? 'error' : isSuccess ? 'success' : 'info',
              };
              if (platformValue === 'Naukri' || phaseValue.includes('naukri') || lower.includes('naukri')) {
                restoredNaukriPostingLogs.push(entry);
              } else if (platformValue === 'LinkedIn' || phaseValue.includes('linkedin') || lower.includes('linkedin')) {
                restoredLinkedinPostingLogs.push(entry);
              }
              } // close else for isJDCreationMsg
            }

            // Outreach detection — only match actual outreach system/log messages,
            // NOT general chat messages that mention the word "outreach" in feature lists etc.
            const isOutreachSystemMsg = (
              phaseValue.includes('outreach') ||
              platformValue === 'Outreach' ||
              (messageType === 'log' && lower.includes('outreach'))
            );
            // For regular agent messages, require specific outreach action phrases
            // (not just the word "outreach" which appears in greetings/feature lists)
            const isOutreachActionMsg = !isOutreachSystemMsg && (
              lower.includes('sending outreach') ||
              lower.includes('outreach sent') ||
              lower.includes('outreach completed') ||
              lower.includes('outreach started') ||
              lower.includes('outreach in progress') ||
              lower.includes('all outreach messages') ||
              lower.includes('sending personalized')
            );
            if (isOutreachSystemMsg || isOutreachActionMsg) {
              hasOutreachStartedSignal = true;
              if (
                isSuccess ||
                lower.includes('outreach completed') ||
                lower.includes('all outreach messages')
              ) {
                hasOutreachCompletedSignal = true;
              }
              restoredOutreachLogs.push({
                message: content,
                success: !isError,
                error: isError ? content : null,
                timestamp: m?.created_at || m?.timestamp || new Date().toISOString(),
              });
            }
          });

          // Merge restored logs with any previously loaded session logs (from file storage)
          // Use a Map to deduplicate by message content
          // IMPORTANT: Only deduplicate within the session's own logs - don't merge with prev React state
          // because prev may contain logs from a different session
          const dedupeWithinSession = (newLogs) => {
            const unique = new Map();
            newLogs.forEach((log) => {
              const key = `${log.message || ''}_${log.timestamp || ''}`;
              if (!unique.has(key)) {
                unique.set(key, log);
              }
            });
            return Array.from(unique.values()).sort((a, b) => 
              new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
            );
          };

          // Load job posting logs from file API (source of truth) so Posting Activity shows after refresh
          // This runs async so it doesn't block the main DB restore flow
          if (hasJobPostingStartedSignal) {
            Promise.allSettled([
              aiAutomationApi.get(`/logs/session/${sessionId}/jobposting/linkedin`).catch(() => null),
              aiAutomationApi.get(`/logs/session/${sessionId}/jobposting/naukri`).catch(() => null),
            ]).then(([liResult, nkResult]) => {
              const liLogs = liResult?.status === 'fulfilled' && liResult?.value?.logs
                ? liResult.value.logs.map(e => ({ message: e.message || '', timestamp: e.timestamp || '', status: e.log_type === 'error' ? 'error' : e.log_type === 'success' ? 'success' : 'info' }))
                : [];
              const nkLogs = nkResult?.status === 'fulfilled' && nkResult?.value?.logs
                ? nkResult.value.logs.map(e => ({ message: e.message || '', timestamp: e.timestamp || '', status: e.log_type === 'error' ? 'error' : e.log_type === 'success' ? 'success' : 'info' }))
                : [];
              if (liLogs.length > 0) {
                liLogs.forEach(log => appendSessionLog(sessionId, 'linkedin', log));
                const s = getSessionState(sessionId);
                applyIfCurrentSession(() => setLinkedinLogs(s.jobPosting?.linkedinLogs || liLogs));
              }
              if (nkLogs.length > 0) {
                nkLogs.forEach(log => appendSessionLog(sessionId, 'naukri', log));
                const s = getSessionState(sessionId);
                applyIfCurrentSession(() => setNaukriLogs(s.jobPosting?.naukriLogs || nkLogs));
              }
            }).catch(() => {});
          }
          if (restoredGoogleSourcingLogs.length > 0) {
            updateSessionState(sessionId, (state) => {
              // Merge: only add DB logs whose message doesn't already exist in localStorage logs
              const existing = new Set((state.sourcing.googleLogs || []).map(l => l.message || ''));
              const newOnly = restoredGoogleSourcingLogs.filter(l => !existing.has(l.message || ''));
              if (newOnly.length > 0) {
                state.sourcing.googleLogs = [...(state.sourcing.googleLogs || []), ...newOnly];
              } else if (!state.sourcing.googleLogs || state.sourcing.googleLogs.length === 0) {
                state.sourcing.googleLogs = restoredGoogleSourcingLogs;
              }
              return state;
            });
            const updatedState = getSessionState(sessionId);
            applyIfCurrentSession(() => setGoogleSourcingLogs(dedupeWithinSession(updatedState.sourcing?.googleLogs || restoredGoogleSourcingLogs)));
          }
          if (restoredLinkedinSourcingLogs.length > 0) {
            updateSessionState(sessionId, (state) => {
              const existing = new Set((state.sourcing.linkedinLogs || []).map(l => l.message || ''));
              const newOnly = restoredLinkedinSourcingLogs.filter(l => !existing.has(l.message || ''));
              if (newOnly.length > 0) {
                state.sourcing.linkedinLogs = [...(state.sourcing.linkedinLogs || []), ...newOnly];
              } else if (!state.sourcing.linkedinLogs || state.sourcing.linkedinLogs.length === 0) {
                state.sourcing.linkedinLogs = restoredLinkedinSourcingLogs;
              }
              return state;
            });
            const updatedState = getSessionState(sessionId);
            applyIfCurrentSession(() => setLinkedinSourcingLogs(dedupeWithinSession(updatedState.sourcing?.linkedinLogs || restoredLinkedinSourcingLogs)));
          }
          if (restoredOutreachLogs.length > 0) {
            updateSessionState(sessionId, (state) => {
              state.outreach.logs = [...(state.outreach.logs || []), ...restoredOutreachLogs];
              return state;
            });
            const updatedState = getSessionState(sessionId);
            applyIfCurrentSession(() => setOutreachLogs(dedupeWithinSession(updatedState.outreach?.logs || restoredOutreachLogs)));
          }

          // Track if field collection is restored (to prevent job phase override)
          let fieldCollectionRestored = false;
          
          // Restore job posting field collection state if there are unanswered questions
          // BUT only if job posting wasn't cancelled AND hasn't already started (processing)!
          if (fieldCollectionAskedFields.size > 0 && !hasJobPostingCancelledSignal && !hasJobPostingStartedSignal) {
            const persistedCollectedDetails =
              sessionStateStoreRef.current.get(sessionId)?.jobPosting?.collectedDetails || {};
            const mergedCollectedDetails = buildJobPostingCollectedDetails({
              collectedDetails: {
                ...persistedCollectedDetails,
                ...collectedDetailsFromDB,
              },
              postingValues: {
                ...postingDetails,
                ...collectedDetailsToPostingDetails({
                  ...persistedCollectedDetails,
                  ...collectedDetailsFromDB,
                }),
              },
              channels: sessionStateStoreRef.current.get(sessionId)?.jobPosting?.selectedChannels || [],
              finalRequirements,
            });
            const remainingFieldsToAsk = getMissingJobPostingFields(mergedCollectedDetails);
            
            console.log('[restoreFieldCollection] Asked fields:', Array.from(fieldCollectionAskedFields));
            console.log('[restoreFieldCollection] Answered fields:', Array.from(fieldCollectionAnsweredFields));
            console.log('[restoreFieldCollection] Collected details from DB:', collectedDetailsFromDB);
            console.log('[restoreFieldCollection] Merged collected details:', mergedCollectedDetails);
            console.log('[restoreFieldCollection] Remaining fields to ask:', remainingFieldsToAsk);
            
            if (remainingFieldsToAsk.length > 0) {
              // There are still fields to ask - restore the collection state
              const nextFieldToAsk = remainingFieldsToAsk[0];
              const question = getFieldQuestion(nextFieldToAsk);
              console.log('[restoreFieldCollection] Restoring field collection state with next field:', nextFieldToAsk);
              fieldCollectionRestored = true;
              
              // CRITICAL: Update ref synchronously for immediate availability in handlers
              fieldCollectionStateRef.current = {
                isActive: true,
                currentField: nextFieldToAsk,
                queue: remainingFieldsToAsk,
              };
              
              // CRITICAL: Always persist to session store first
              updateSessionState(sessionId, (state) => {
                state.jobPosting.phase = 'collecting_details';
                state.jobPosting.processing = false;
                state.jobPosting.complete = false;
                state.jobPosting.linkedinProcessing = false;
                state.jobPosting.naukriProcessing = false;
                state.jobPosting.pendingField = nextFieldToAsk;
                state.jobPosting.pendingFieldOptions = [];
                state.jobPosting.pendingFieldsQueue = remainingFieldsToAsk;  // CRITICAL: Store queue for session isolation
                state.jobPosting.collectedDetails = mergedCollectedDetails;
                state.jobPosting.fieldCollectionActive = true;  // Mark field collection active
                state.chat.systemMessages = [question];
                return state;
              });
              
              applyIfCurrentSession(() => {
                if (Object.keys(mergedCollectedDetails).length > 0) {
                  setCollectedJobPostingDetails(prev => ({ ...prev, ...mergedCollectedDetails }));
                  setPostingDetails(prev => ({
                    ...prev,
                    ...collectedDetailsToPostingDetails(mergedCollectedDetails),
                  }));
                }
                setJobPostingPhase('collecting_details');
                setJobPostingProcessing(false);
                setJobPostingComplete(false);
                setLinkedinProcessing(false);
                setNaukriProcessing(false);
                setPendingJobPostingFieldsQueue(remainingFieldsToAsk);
                setPendingNaukriField(nextFieldToAsk);
                setSystemMessages([question]);
                setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
              });
            } else {
              // All required fields are answered
              if (Object.keys(mergedCollectedDetails).length > 0) {
                updateSessionState(sessionId, (state) => {
                  state.jobPosting.collectedDetails = mergedCollectedDetails;
                  return state;
                });
                applyIfCurrentSession(() => {
                  setCollectedJobPostingDetails(prev => ({ ...prev, ...mergedCollectedDetails }));
                  setPostingDetails(prev => ({
                    ...prev,
                    ...collectedDetailsToPostingDetails(mergedCollectedDetails),
                  }));
                });
              }
              if (lastAskedField && fieldCollectionAnsweredFields.has(lastAskedField) && !hasJobPostingStartedSignal) {
                applyIfCurrentSession(() => setShowJobPostingConfirmationModal(true));
              }
            }
          }

          // Restore active progress views so users can continue watching logs.
          // CRITICAL: Always check process_status table first - it's the source of truth!
          // IMPORTANT: Skip if field collection phase is being restored (fieldCollectionRestored is true)
          // Terminal per-channel states (failed/complete) must win over legacy job_posting boolean
          const linkedinIsTerminalP3 = processStatus.job_post_linkedin === 'failed' || processStatus.job_post_linkedin === 'complete';
          const naukriIsTerminalP3 = processStatus.job_post_naukri === 'failed' || processStatus.job_post_naukri === 'complete';
          const hasTerminalJobPostChannelP3 = linkedinIsTerminalP3 || naukriIsTerminalP3;
          const hasDbChannelPostingInProgressP3 =
            processStatus.job_post_linkedin === 'processing' ||
            processStatus.job_post_naukri === 'processing';
          const hasDbPostingInProgressP3 =
            hasDbChannelPostingInProgressP3 ||
            (processStatus.job_posting === true && hasJobPostingStartedSignal && !fieldCollectionRestored);
          const shouldForcePersistedPostingState =
            persistedJobPostingInProgress &&
            !fieldCollectionRestored &&
            (hasDbChannelPostingInProgressP3 || hasJobPostingStartedSignal) &&
            !hasTerminalJobPostChannelP3;
          const shouldKeepPostingInProgressFromChat =
            hasJobPostingStartedSignal &&
            !hasJobPostingCompletedSignal &&
            !fieldCollectionRestored &&
            !hasTerminalJobPostChannelP3;

          if (shouldKeepPostingInProgressFromChat || shouldForcePersistedPostingState) {
            if (hasDbPostingInProgressP3 || shouldKeepPostingInProgressFromChat) {
              const restoredPostingChannelsP3 = (
                getSessionState(sessionId)?.jobPosting?.selectedChannels ||
                []
              );
              const inferredPostingChannelsP3 =
                restoredPostingChannelsP3.length > 0
                  ? restoredPostingChannelsP3
                  : Array.from(startedJobPostingChannels);
              const shouldRestoreLinkedinProcessingP3 =
                !linkedinIsTerminalP3 && (
                  processStatus.job_post_linkedin === 'processing' ||
                  persistedLinkedinPostingProcessing ||
                  restoredLinkedinPostingLogs.length > 0 ||
                  (shouldKeepPostingInProgressFromChat && inferredPostingChannelsP3.includes('LinkedIn'))
                );
              const shouldRestoreNaukriProcessingP3 =
                !naukriIsTerminalP3 && (
                  processStatus.job_post_naukri === 'processing' ||
                  persistedNaukriPostingProcessing ||
                  restoredNaukriPostingLogs.length > 0 ||
                  (shouldKeepPostingInProgressFromChat && inferredPostingChannelsP3.includes('Naukri'))
                );
              console.log('[restoreSessionState] Restoring active job posting state from DB/chat history');
              updateSessionState(sessionId, (state) => {
                state.jobPosting.phase = 'posting';
                state.jobPosting.processing = true;
                state.jobPosting.complete = false;
                if (inferredPostingChannelsP3.length > 0) {
                  state.jobPosting.selectedChannels = inferredPostingChannelsP3;
                }
                state.jobPosting.linkedinProcessing = shouldRestoreLinkedinProcessingP3;
                state.jobPosting.naukriProcessing = shouldRestoreNaukriProcessingP3;
                return state;
              });
              applyIfCurrentSession(() => {
                setJobPostingPhase('posting');
                setJobPostingProcessing(true);
                setJobPostingComplete(false);
                setAutoActiveHeaderTab('job_posting');
                setRightPanelMode('auto');
                if (inferredPostingChannelsP3.length > 0) {
                  setSelectedChannels(inferredPostingChannelsP3);
                }
                setLinkedinProcessing(shouldRestoreLinkedinProcessingP3);
                setNaukriProcessing(shouldRestoreNaukriProcessingP3);
              });
            } else {
              console.log('[restoreSessionState] Logs show in-progress but process_status is false, marking as complete');
              updateSessionState(sessionId, (state) => {
                state.jobPosting.phase = 'posted';
                state.jobPosting.complete = true;
                state.jobPosting.processing = false;
                return state;
              });
              applyIfCurrentSession(() => {
                setJobPostingPhase('posted');
                setJobPostingComplete(true);
                setJobPostingProcessing(false);
                setLinkedinProcessing(false);
                setNaukriProcessing(false);
              });
            }
          } else if (hasJobPostingStartedSignal && hasJobPostingCompletedSignal && !persistedJobPostingInProgress) {
            updateSessionState(sessionId, (state) => {
              state.jobPosting.phase = 'posted';
              state.jobPosting.complete = true;
              state.jobPosting.processing = false;
              return state;
            });
            applyIfCurrentSession(() => {
              setJobPostingPhase('posted');
              setJobPostingProcessing(false);
              setJobPostingComplete(true);
            });
          }

          // Restore sourcing state
          const linkedinStillProcessing = hasLinkedinSourcingStarted && !hasLinkedinSourcingCompleted;
          const shouldRestoreSourcing = hasSourcingStartedSignal &&
            (!hasSourcingCompletedSignal || linkedinStillProcessing);

          // Check session store for latest sourcing state — WebSocket handler may have already
          // set sourcing.complete=true while loadSessionData was running (race condition)
          // Only treat as truly complete if complete AND no channels still processing
          const latestSourcingState = sessionStateStoreRef.current.get(sessionId)?.sourcing;
          const sourcingAlreadyCompletedViaWs = latestSourcingState?.complete === true &&
            !latestSourcingState?.googleProcessing && !latestSourcingState?.linkedinProcessing && !latestSourcingState?.resumeBoxProcessing;

          if (shouldRestoreSourcing && !sourcingAlreadyCompletedViaWs) {
            // Restore selectedSourcingChannels from session store only (no localStorage fallback)
            let storedChannels = latestSourcingState?.selectedChannels || [];
            if (processStatus.searching === true) {
              console.log('[restoreSessionState] Logs show sourcing in-progress AND process_status confirms');
              if (linkedinStillProcessing) addActiveSourcingSession(sessionId);
              // Processing flags based on channel selection (DB is source of truth — no localStorage)
              const lsGoogleProcessing = shouldRunGoogleForChannels(storedChannels);
              const lsLinkedinProcessing = linkedinStillProcessing || shouldRunLinkedinForChannels(storedChannels);
              const lsResumeBoxProcessing = shouldRunResumeBoxForChannels(storedChannels);
              applyIfCurrentSession(() => {
                setTalentScoutingStarted(true);
                setSourcingComplete(false);
                setLinkedinSourcingProcessing(lsLinkedinProcessing);
                setGoogleSourcingProcessing(lsGoogleProcessing);
                setResumeBoxSourcingProcessing(lsResumeBoxProcessing);
                if (storedChannels.length > 0) setSelectedSourcingChannels(storedChannels);
                setActiveHeaderTab('sourcing');
                // If candidates already exist, show candidates page with logs; otherwise show sourcing page
                if (restoredCandidateCount > 0) {
                  setRightPanelMode('candidates');
                } else {
                  setRightPanelMode('sourcing');
                }
                setSystemMessages(['Candidate sourcing is in progress. Please wait...']);
                // NOTE: Sourcing logs come from DB chat messages or file API — no localStorage fallback
              });
            } else {
              console.log('[restoreSessionState] Logs show sourcing in-progress but process_status is false, marking as complete');
              removeActiveSourcingSession(sessionId);
              applyIfCurrentSession(() => {
                setTalentScoutingStarted(true);
                setSourcingComplete(true);
                setLinkedinSourcingProcessing(false);
                if (storedChannels.length > 0) setSelectedSourcingChannels(storedChannels);
                setRightPanelMode('candidates');
              });
            }
          } else if (hasSourcingStartedSignal && hasSourcingCompletedSignal && restoredCandidateCount > 0) {
            applyIfCurrentSession(() => {
              setTalentScoutingStarted(true);
              setSourcingComplete(true);
              setRightPanelMode('candidates');
            });
          }

          // Only restore outreach state if we have actual outreach log entries
          // (not just a greeting message that mentioned the word "outreach")
          if (hasOutreachStartedSignal && restoredOutreachLogs.length > 0) {
            if (hasOutreachCompletedSignal) {
              applyIfCurrentSession(() => {
                setOutreachPhase('done');
                setOutreachSending(false);
                setOutreachCompleted(true);
              });
            } else {
              if (processStatus.outreach === true) {
                console.log('[restoreSessionState] Logs show outreach in-progress AND process_status confirms');
                applyIfCurrentSession(() => {
                  setOutreachPhase('sending');
                  setOutreachSending(true);
                  setOutreachCompleted(false);
                  setAutoActiveHeaderTab('job_posting');
                  setRightPanelMode('auto');
                });
              } else {
                console.log('[restoreSessionState] Logs show outreach in-progress but process_status is false, marking as complete');
                applyIfCurrentSession(() => {
                  setOutreachPhase('done');
                  setOutreachSending(false);
                  setOutreachCompleted(true);
                });
              }
            }
          }

          // Check for channel selection phase in chat history to restore jobPostingPhase
          let hasChannelSelectionPending = false;
          let channelSelectionMetadata = null;
          
          for (let i = rawMessages.length - 1; i >= 0; i--) {
            const m = rawMessages[i];
            const metadata = parseMessageMetadata(m?.metadata);
            const phaseValue = String(m?.phase || metadata?.phase || '').toLowerCase();
            
            if (phaseValue === 'job_posting_channel_selection') {
              const hasUserResponseAfter = rawMessages.slice(i + 1).some(
                msg => msg.message_role === 'user' || msg.message_type === 'user'
              );
              if (!hasUserResponseAfter) {
                hasChannelSelectionPending = true;
                channelSelectionMetadata = metadata;
              }
              break;
            }
          }
          
          if (hasChannelSelectionPending && !hasJobPostingStartedSignal) {
            console.log('[restoreSessionState] Restoring channel selection phase from chat history');
            updateSessionState(sessionId, (state) => {
              state.jobPosting.phase = 'channel_selection';
              if (channelSelectionMetadata?.already_posted && Array.isArray(channelSelectionMetadata.already_posted)) {
                state.jobPosting.postedChannels = channelSelectionMetadata.already_posted;
              }
              if (channelSelectionMetadata?.available_channels && Array.isArray(channelSelectionMetadata.available_channels)) {
                state.jobPosting.availableChannels = channelSelectionMetadata.available_channels;
              }
              return state;
            });
            applyIfCurrentSession(() => {
              setJobPostingPhase('channel_selection');
              if (channelSelectionMetadata?.already_posted && Array.isArray(channelSelectionMetadata.already_posted)) {
                setPostedChannels(channelSelectionMetadata.already_posted);
              }
            });
          }

          const latestSearch = searches.length > 0 ? searches[searches.length - 1] : null;
          const initialQuery =
            sessionData.initial_query ||
            sessionData.query ||
            latestSearch?.query ||
            latestSearch?.query_text ||
            '';

          let dashboardBootstrap = null;
          try {
            const bootstrapKey = `dashboard_chat_bootstrap_${sessionId}`;
            const rawBootstrap = sessionStorage.getItem(bootstrapKey) || localStorage.getItem(bootstrapKey);
            dashboardBootstrap = rawBootstrap ? JSON.parse(rawBootstrap) : null;
          } catch (_bootstrapErr) {
            dashboardBootstrap = null;
          }

          const bootstrapMessages = sanitizeRestoredChatHistory(
            Array.isArray(dashboardBootstrap?.messages) ? dashboardBootstrap.messages : []
          );

          const buildFallbackChatHistory = () => {
            if (!initialQuery || typeof initialQuery !== 'string') return [];
            return [{
              id: 1,
              sender: 'user',
              message: initialQuery,
              timestamp: sessionData.created_at || sessionData.createdAt || null,
            }];
          };

          const mapped = rawMessages
            .filter((m) => {
              if (!m || !m.content) return false;
              if (isTransientJobPostingChatMessage(m)) return false;
              if (isLocalUiOnlyChatCommand(m.content) || isLocalUiNavigationHint(m.content)) return false;
              // Exclude log messages that are only for the sourcing/posting logs panel
              // These are shown in the right panel, not in chat bubbles
              if (m.message_type === 'log') {
                const meta = parseMessageMetadata(m?.metadata);
                // Keep logs marked show_in_chat, otherwise exclude
                if (meta?.show_in_chat === false || meta?.is_progress_update) return false;
                // Sourcing logs go to the right panel, not chat
                const phase = String(m?.phase || meta?.phase || '').toLowerCase();
                if (phase === 'source_candidates') return false;
                // Job posting and outreach logs also go to right panel
                if (phase.includes('job_post') || phase === 'linkedin' || phase === 'naukri') return false;
                if (phase.includes('outreach')) return false;
              }
              return true;
            })
            .map((m, idx) => {
              const parsedMeta = parseMessageMetadata(m?.metadata);
              return {
                id: m.id || `db_${idx}_${Date.now()}`,
                sender: m.message_type === 'user' || m.message_role === 'user' ? 'user' : 'agent',
                message: (m.message_type === 'user' || m.message_role === 'user')
                  ? m.content
                  : normalizeAssistantChatContent(m.content, parsedMeta, m.phase),
                timestamp: m.created_at || m.timestamp || null,
                metadata: parsedMeta,
                mode: parsedMeta?.mode || undefined, // Restore agent/ask mode from DB metadata
              };
            });
          const persistedChatHistory = sanitizeRestoredChatHistory(
            sessionStateStoreRef.current.get(sessionId)?.chat?.chatHistory || []
          );
          const mergedMapped = mergePersistedChatArtifacts(mapped, persistedChatHistory);
          const hydratedChatHistory = mergedMapped.length > 0
            ? mergedMapped
            : (bootstrapMessages.length > 0 ? bootstrapMessages : buildFallbackChatHistory());

          if (dashboardBootstrap && hydratedChatHistory.length > 0) {
            try {
              const bootstrapKey = `dashboard_chat_bootstrap_${sessionId}`;
              sessionStorage.removeItem(bootstrapKey);
              localStorage.removeItem(bootstrapKey);
            } catch (_removeBootstrapErr) {}
          }
          
          // CRITICAL: Always save chatHistory to session store so it's available when returning
          updateSessionState(sessionId, (state) => {
            state.chat.chatHistory = hydratedChatHistory;
            return state;
          });
          // Only update React state if user is still viewing this session
          applyIfCurrentSession(() => setChatHistory(hydratedChatHistory));
          await restoreRecoveredJdUiState(
            getLastJdMetadata(hydratedChatHistory) || getLastAssistantMetadata(hydratedChatHistory),
            restoredJdText
          );

          // Rehydrate filter chips/actions/breadcrumbs from the LAST
          // agent message so they survive a page refresh. Walk backwards
          // until we find an assistant message that carries any filter
          // UI state, and restore it. If the last assistant message has
          // none, clear the UI — the user is in a non-filter context.
          try {
            let lastFilterMeta = null;
            for (let i = hydratedChatHistory.length - 1; i >= 0; i--) {
              const msg = hydratedChatHistory[i];
              if (msg.sender !== 'agent') continue;
              const md = msg.metadata || {};
              if (md.filter_breadcrumbs || md.filter_actions || md.follow_up_suggestions) {
                lastFilterMeta = md;
                break;
              }
              // First agent message we hit that has no filter metadata
              // means the user moved past the filter context.
              break;
            }
            if (lastFilterMeta) {
              const fb = Array.isArray(lastFilterMeta.filter_breadcrumbs) ? lastFilterMeta.filter_breadcrumbs : [];
              const fa = Array.isArray(lastFilterMeta.filter_actions) ? lastFilterMeta.filter_actions : [];
              const fus = Array.isArray(lastFilterMeta.follow_up_suggestions) ? lastFilterMeta.follow_up_suggestions : [];
              applyIfCurrentSession(() => {
                setFilterBreadcrumbs(fb);
                setFilterActions(fa);
                setFollowUpSuggestions(fus);
                console.log('[loadSessionData] Rehydrated filter UI from last agent message:', { fb, fa: fa.length, fus: fus.length });
              });
              updateSessionState(sessionId, (s) => {
                s.filtering = s.filtering || {};
                s.filtering.breadcrumbs = fb;
                s.filtering.actions = fa;
                s.filtering.followUpSuggestions = fus;
                return s;
              });
            }
          } catch (filterHydrateErr) {
            console.log('[loadSessionData] Filter chip hydration failed (non-critical):', filterHydrateErr);
          }

          const latestMessageTs = rawMessages
            .map((m) => m?.created_at || m?.timestamp)
            .filter(Boolean)
            .map((value) => new Date(value))
            .filter((d) => !Number.isNaN(d.getTime()))
            .sort((a, b) => b.getTime() - a.getTime())[0];
          if (latestMessageTs) {
            updateJobPostingLastUpdated(sessionId, latestMessageTs);
          } else {
            const latestSessionTs = getLatestSessionTimestamp(sessionData);
            if (latestSessionTs) updateJobPostingLastUpdated(sessionId, latestSessionTs);
          }
        } else {
          // Fallback: use initial query if no stored messages
          const latestSearch = searches.length > 0 ? searches[searches.length - 1] : null;
          const initialQuery =
            sessionData.initial_query ||
            sessionData.query ||
            latestSearch?.query ||
            latestSearch?.query_text ||
            '';

          let dashboardBootstrap = null;
          try {
            const bootstrapKey = `dashboard_chat_bootstrap_${sessionId}`;
            const rawBootstrap = sessionStorage.getItem(bootstrapKey) || localStorage.getItem(bootstrapKey);
            dashboardBootstrap = rawBootstrap ? JSON.parse(rawBootstrap) : null;
          } catch (_bootstrapErr) {
            dashboardBootstrap = null;
          }

          const bootstrapMessages = sanitizeRestoredChatHistory(
            Array.isArray(dashboardBootstrap?.messages) ? dashboardBootstrap.messages : []
          );

          const fallbackHistory = bootstrapMessages.length > 0
            ? bootstrapMessages
            : (initialQuery && typeof initialQuery === 'string'
                ? [{
                    id: 1,
                    sender: 'user',
                    message: initialQuery,
                    timestamp: sessionData.created_at || sessionData.createdAt || null,
                  }]
                : []);

          if (dashboardBootstrap && fallbackHistory.length > 0) {
            try {
              const bootstrapKey = `dashboard_chat_bootstrap_${sessionId}`;
              sessionStorage.removeItem(bootstrapKey);
              localStorage.removeItem(bootstrapKey);
            } catch (_removeBootstrapErr) {}
          }
          // Save to session store always
          updateSessionState(sessionId, (state) => { state.chat.chatHistory = fallbackHistory; return state; });
          applyIfCurrentSession(() => setChatHistory(fallbackHistory));
          
          const latestSessionTs = getLatestSessionTimestamp(sessionData);
          if (latestSessionTs) updateJobPostingLastUpdated(sessionId, latestSessionTs);
        }
      } catch (historyError) {
        console.error('Error fetching chat history for session:', historyError);
        updateSessionState(sessionId, (state) => { state.chat.chatHistory = []; return state; });
        applyIfCurrentSession(() => setChatHistory([]));
      }

      // ── Poll for assistant response if last message is from user ──
      // This handles cases where the user refreshed after sending a message
      // but before the response was displayed (no lifecycle_inflight flag).
      try {
        const storedState = sessionStateStoreRef.current.get(sessionId);
        const restoredChat = sanitizeRestoredChatHistory(storedState?.chat?.chatHistory || []);
        const lastRestoredMsg = restoredChat.length > 0 ? restoredChat[restoredChat.length - 1] : null;
        const hasInflight = !!sessionStorage.getItem(`lifecycle_inflight_${sessionId}`);
        if (
          lastRestoredMsg &&
          lastRestoredMsg.sender === 'user' &&
          !hasInflight &&
          !lifecycleInflightRef.current &&
          !isLocalUiOnlyChatCommand(lastRestoredMsg.message)
        ) {
          console.log('[loadSessionData] Last message is from user — polling for assistant response');
          applyIfCurrentSession(() => setSessionLoader(sessionId, 'lifecycle', true));
          // Poll DB for the assistant's response
          (async () => {
            const maxAttempts = 10;
            const pollInterval = 2000; // 2 seconds
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              if (currentSessionRef.current !== sessionId) return;
              await new Promise((r) => setTimeout(r, pollInterval));
              try {
                const freshHistory = await aiAutomationApi.get(`/sessions/${sessionId}/chat-messages`);
                const freshMsgs = Array.isArray(freshHistory?.messages) ? freshHistory.messages : [];
                if (freshMsgs.length > 0) {
                  const sorted = [...freshMsgs].sort((a, b) => {
                    const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : Infinity;
                    const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : Infinity;
                    if (aSeq !== bSeq) return aSeq - bSeq;
                    const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return aT - bT;
                  });
                  const freshLast = sorted[sorted.length - 1];
                  const freshLastIsAgent = freshLast && (freshLast.message_type === 'assistant' || freshLast.message_role === 'assistant' || freshLast.message_type === 'system');
                  if (freshLastIsAgent) {
                    console.log('[loadSessionData] Poll found assistant response — updating chat');
                    if (currentSessionRef.current !== sessionId) return;
                    const freshMapped = sorted.filter((m) => {
                      if (!m || !m.content) return false;
                      if (isTransientJobPostingChatMessage(m)) return false;
                      if (isLocalUiOnlyChatCommand(m.content) || isLocalUiNavigationHint(m.content)) return false;
                      if (m.message_type === 'log') {
                        const meta = parseMessageMetadata(m?.metadata);
                        if (meta?.show_in_chat === false || meta?.is_progress_update) return false;
                        const phase = String(m?.phase || meta?.phase || '').toLowerCase();
                        if (phase === 'source_candidates') return false;
                        if (phase.includes('job_post') || phase === 'linkedin' || phase === 'naukri') return false;
                        if (phase.includes('outreach')) return false;
                      }
                      return true;
                    }).map((m, idx) => {
                      const parsedMeta = parseMessageMetadata(m?.metadata);
                      return {
                        id: m.id || `db_${idx}_${Date.now()}`,
                        sender: m.message_type === 'user' || m.message_role === 'user' ? 'user' : 'agent',
                        message: m.content,
                        timestamp: m.created_at || m.timestamp || null,
                        metadata: parsedMeta,
                        mode: parsedMeta?.mode || undefined,
                      };
                    });
                    const persistedFreshHistory = sanitizeRestoredChatHistory(
                      sessionStateStoreRef.current.get(sessionId)?.chat?.chatHistory || []
                    );
                    const mergedFreshMapped = mergePersistedChatArtifacts(freshMapped, persistedFreshHistory);
                    applyIfCurrentSession(() => setChatHistory(mergedFreshMapped));
                    updateSessionState(sessionId, (state) => {
                      state.chat.chatHistory = mergedFreshMapped;
                      return state;
                    });
                    await restoreRecoveredJdUiState(getLastAssistantMetadata(mergedFreshMapped));
                    applyIfCurrentSession(() => setSessionLoader(sessionId, 'lifecycle', false));
                    return;
                  }
                }
              } catch (pollErr) {
                console.log('[loadSessionData] Poll attempt failed:', pollErr);
              }
            }
            // Max attempts reached — clear loader
            console.log('[loadSessionData] Poll max attempts reached — clearing loader');
            applyIfCurrentSession(() => setSessionLoader(sessionId, 'lifecycle', false));
          })();
        } else if (
          lastRestoredMsg &&
          lastRestoredMsg.sender === 'user' &&
          isLocalUiOnlyChatCommand(lastRestoredMsg.message)
        ) {
          applyIfCurrentSession(() => setSessionLoader(sessionId, 'lifecycle', false));
          updateSessionState(sessionId, (state) => {
            state.chat.lifecycleLoading = false;
            state.chat.apiLoading = false;
            return state;
          });
        }
      } catch (_pollErr) {
        console.log('[loadSessionData] Poll setup failed (non-critical):', _pollErr);
      }

      // Restore lifecycle state (confirms/edit buttons) from backend
      try {
        const lsData = await aiAutomationApi.get(`/chat/lifecycle-state/${sessionId}`);
        applyIfCurrentSession(() => {
          setScorecardSetupStep(lsData?.setup_step || '');
          setScorecardSetupSessionId(lsData?.setup_step ? sessionId : '');
          setLifecycleScheduleActive(Boolean(lsData?.schedule_phase));
          if (!lsData?.awaiting_confirmation && !lsData?.schedule_phase) {
            setLifecycleAwaitingConfirmation(false);
            setDraftEditActions([]);
          }
        });
        if (!lsData?.setup_step) {
          try {
            const editor = await aiAutomationApi.get(`/chat/setup-scorecard/${encodeURIComponent(sessionId)}`);
            applyIfCurrentSession(() => {
              setScorecardSetupStep(editor?.setup_step || '');
              setScorecardSetupSessionId(editor?.setup_step ? sessionId : '');
            });
          } catch (_) {
            // The editor endpoint is available only at the topic and final review steps.
          }
        }
        if (lsData?.waiting_for_otp) {
          applyIfCurrentSession(() => {
            setOtpModal({
              isOpen: true,
              process: lsData.otp_process || 'verification',
              platform: lsData.otp_platform || '',
              message: lsData.otp_message || 'Verification code required to continue.',
              jobTitle: selectedJob?.title || 'Current Job',
            });
          });
        }
        if (lsData?.awaiting_confirmation || lsData?.schedule_phase) {
          applyIfCurrentSession(() => {
            setLifecycleAwaitingConfirmation(true);
            if (lsData?.schedule_phase) setLifecycleScheduleActive(true);
            const editActions = Array.isArray(lsData.edit_actions) && lsData.edit_actions.length > 0
              ? lsData.edit_actions
              : [{ label: 'Send', command: 'yes' }, { label: 'Cancel', command: 'cancel' }];
            // Only restore draft edit actions if the CURRENT LangGraph agent uses confirmations
            // Prevents stale Send/Cancel buttons from showing after refresh when agent has moved on
            const confirmationAgents = ['outreach', 'scheduling'];
            const lgState = agentStatePerSessionRef.current.get(sessionId);
            const currentLgAgent = lgState?.current_agent || '';
            if (lsData?.awaiting_confirmation && confirmationAgents.includes(currentLgAgent)) {
              setDraftEditActions(editActions);
            } else if (lsData?.awaiting_confirmation) {
              // Stale confirmation from previous agent flow — clear it
              console.log('[restoreSessionState] Clearing stale awaiting_confirmation — current agent:', currentLgAgent);
              setLifecycleAwaitingConfirmation(false);
            }
          });
        }
        // Restore LangGraph stage awareness
        if (lsData?.stage_label) {
          // Don't restore sourcing banner if sourcing already completed
          const sessionSourcingDone = sessionStateStoreRef.current.get(sessionId)?.sourcing?.complete;
          const isSourcingBanner = lsData.stage_label.toLowerCase().includes('sourcing');
          if (isSourcingBanner && sessionSourcingDone) {
            // Sourcing completed while user was away — skip banner restoration
            applyIfCurrentSession(() => {
              setAgentStageLabel('');
              setAgentStageCancellable(false);
            });
          } else {
            applyIfCurrentSession(() => {
              setAgentStageLabel(lsData.stage_label);
              setAgentStageCancellable(lsData.stage_cancellable || false);
              if (lsData.last_response_type) {
                setLastResponseType(lsData.last_response_type);
              }
            });
          }
        }
      } catch (_lsErr) {
        // non-critical — lifecycle state restore is best-effort
      }

      // Restore typing loader if a lifecycle API call was in-flight when user navigated away
      // The in-flight flag is stored in sessionStorage so it survives page navigation (component remount)
      try {
        const inflightRaw = sessionStorage.getItem(`lifecycle_inflight_${sessionId}`);
        if (inflightRaw) {
          const inflight = JSON.parse(inflightRaw);
          // Only restore if it's for the same session and not stale (< 2 minutes old)
          const retryCount = inflight.retries || 0;
          if (inflight.sessionId === sessionId && (Date.now() - inflight.ts) < 120000 && retryCount < 3) {
            console.log(`[restoreSessionState] Restoring typing loader — lifecycle request was in-flight (retry ${retryCount})`);
            setSessionLoader(sessionId, 'lifecycle', true);
            // Increment retry counter in sessionStorage so we don't retry forever
            try {
              sessionStorage.setItem(`lifecycle_inflight_${sessionId}`, JSON.stringify({ ...inflight, retries: retryCount + 1 }));
            } catch (_) {}
            // Check if the backend has already responded by looking at the last chat messages
            const lastMsgs = Array.isArray(rawMessages) ? rawMessages : [];
            const lastMsg = lastMsgs.length > 0 ? lastMsgs[lastMsgs.length - 1] : null;
            const lastMsgIsUser = lastMsg && (lastMsg.message_type === 'user' || lastMsg.message_role === 'user');
            if (lastMsgIsUser || lastMsgs.length === 0) {
              // Last message is from user with no response yet, OR no messages in DB yet
              // (the fire-and-forget user message save may not have completed before refresh).
              // The original API call may have been aborted during session switch or page refresh,
              // so re-send the request to the lifecycle agent.
              const userMessage = lastMsg?.content || inflight.message || '';

              // GUARD: Don't re-send if the action has already completed.
              // This prevents stale "sourcing already completed" responses after navigating back.
              const storedSt = sessionStateStoreRef.current.get(sessionId);
              const sourcingDone = storedSt?.sourcing?.complete === true;
              const userMsgLower = userMessage.toLowerCase();
              const isSourcingRequest = /source|sourcing|find.*candidate|search.*candidate/i.test(userMsgLower);
              if (sourcingDone && isSourcingRequest) {
                console.log('[restoreSessionState] Skipping inflight re-send — sourcing already completed');
                try { sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`); } catch (_) {}
                setSessionLoader(sessionId, 'lifecycle', false);
                // Skip the re-send — fall through to the else branch below
              } else {

              lifecycleInflightRef.current = true;
              console.log('[restoreSessionState] Re-sending lifecycle request for:', userMessage.substring(0, 80));

              // If no messages are in the chat yet, inject the user message so user sees their query
              if (lastMsgs.length === 0 && userMessage) {
                const inflightUserMsg = [{
                  id: 1,
                  sender: 'user',
                  message: userMessage,
                  timestamp: new Date(inflight.ts).toISOString(),
                }];
                applyIfCurrentSession(() => setChatHistory(inflightUserMsg));
                updateSessionState(sessionId, (state) => {
                  state.chat.chatHistory = inflightUserMsg;
                  return state;
                });
              }

              // Fire the re-request asynchronously
              (async () => {
                try {
                  // Wait a few seconds to give the original backend request time to complete
                  // and save its response to DB (avoids duplicate re-requests on quick refresh).
                  await new Promise((resolve) => setTimeout(resolve, 3000));
                  if (currentSessionRef.current !== sessionId) return;

                  // Double-check: refresh messages from DB in case the original request
                  // completed and saved after we loaded rawMessages (race condition).
                  try {
                    const freshHistory = await aiAutomationApi.get(`/sessions/${sessionId}/chat-messages`);
                    const freshMsgs = Array.isArray(freshHistory?.messages) ? freshHistory.messages : [];
                    if (freshMsgs.length > 0) {
                      const sorted = [...freshMsgs].sort((a, b) => {
                        const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : Infinity;
                        const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : Infinity;
                        if (aSeq !== bSeq) return aSeq - bSeq;
                        const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
                        const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
                        return aT - bT;
                      });
                      const freshLast = sorted[sorted.length - 1];
                      const freshLastIsAgent = freshLast && (freshLast.message_type === 'assistant' || freshLast.message_role === 'assistant');
                      if (freshLastIsAgent) {
                        // Response already arrived in DB — display it instead of re-requesting
                        console.log('[restoreSessionState] Response found in DB after re-check, displaying instead of re-requesting');
                        if (currentSessionRef.current !== sessionId) return;
                        const freshMapped = sorted.filter((m) => {
                          if (!m || !m.content) return false;
                          if (isLocalUiOnlyChatCommand(m.content) || isLocalUiNavigationHint(m.content)) return false;
                          if (m.message_type === 'log') {
                            const meta = parseMessageMetadata(m?.metadata);
                            if (meta?.show_in_chat === false || meta?.is_progress_update) return false;
                            const phase = String(m?.phase || meta?.phase || '').toLowerCase();
                            if (phase === 'source_candidates') return false;
                            if (phase.includes('job_post') || phase === 'linkedin' || phase === 'naukri') return false;
                            if (phase.includes('outreach')) return false;
                          }
                          return true;
                        }).map((m, idx) => {
                          const parsedMeta = parseMessageMetadata(m?.metadata);
                          return {
                            id: idx + 1,
                            sender: m.message_type === 'user' || m.message_role === 'user' ? 'user' : 'agent',
                            message: m.content,
                            timestamp: m.created_at || m.timestamp || null,
                            metadata: parsedMeta,
                            mode: parsedMeta?.mode || undefined,
                          };
                        });
                        setChatHistory(freshMapped);
                        await restoreRecoveredJdUiState(getLastAssistantMetadata(freshMapped));
                        setSessionLoader(sessionId, 'lifecycle', false);
                        lifecycleInflightRef.current = false;
                        try { sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`); } catch (_) {}
                        return;
                      }
                    }
                  } catch (freshCheckErr) {
                    console.log('[restoreSessionState] Fresh DB check failed:', freshCheckErr);
                  }

                  // Poll DB instead of re-sending to /chat (avoids duplicate processing)
                  console.log('[restoreSessionState] Response not in DB yet — polling for it');
                  const pollMaxAttempts = 15;
                  const pollDelay = 2000;
                  for (let pollAttempt = 0; pollAttempt < pollMaxAttempts; pollAttempt++) {
                    if (currentSessionRef.current !== sessionId) return;
                    await new Promise((r) => setTimeout(r, pollDelay));
                    try {
                      const pollHistory = await aiAutomationApi.get(`/sessions/${sessionId}/chat-messages`);
                      const pollMsgs = Array.isArray(pollHistory?.messages) ? pollHistory.messages : [];
                      if (pollMsgs.length > 0) {
                        const pollSorted = [...pollMsgs].sort((a, b) => {
                          const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : Infinity;
                          const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : Infinity;
                          if (aSeq !== bSeq) return aSeq - bSeq;
                          const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
                          const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
                          return aT - bT;
                        });
                        const pollLast = pollSorted[pollSorted.length - 1];
                        const pollIsAgent = pollLast && (pollLast.message_type === 'assistant' || pollLast.message_role === 'assistant');
                        if (pollIsAgent) {
                          console.log(`[restoreSessionState] Poll found response (attempt ${pollAttempt + 1})`);
                          if (currentSessionRef.current !== sessionId) return;
                          const pollMapped = pollSorted.filter((m) => {
                            if (!m || !m.content) return false;
                            if (isLocalUiOnlyChatCommand(m.content) || isLocalUiNavigationHint(m.content)) return false;
                            if (m.message_type === 'log') {
                              const meta = parseMessageMetadata(m?.metadata);
                              if (meta?.show_in_chat === false || meta?.is_progress_update) return false;
                              const phase = String(m?.phase || meta?.phase || '').toLowerCase();
                              if (phase === 'source_candidates') return false;
                              if (phase.includes('job_post') || phase === 'linkedin' || phase === 'naukri') return false;
                              if (phase.includes('outreach')) return false;
                            }
                            return true;
                          }).map((m, idx) => {
                            const parsedMeta = parseMessageMetadata(m?.metadata);
                            return {
                              id: idx + 1,
                              sender: m.message_type === 'user' || m.message_role === 'user' ? 'user' : 'agent',
                              message: m.content,
                              timestamp: m.created_at || m.timestamp || null,
                              metadata: parsedMeta,
                              mode: parsedMeta?.mode || undefined,
                            };
                          });
                          setChatHistory(pollMapped);
                          updateSessionState(sessionId, (state) => {
                            state.chat.chatHistory = pollMapped;
                            return state;
                          });
                          await restoreRecoveredJdUiState(getLastAssistantMetadata(pollMapped));
                          setSessionLoader(sessionId, 'lifecycle', false);
                          lifecycleInflightRef.current = false;
                          try { sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`); } catch (_) {}
                          return;
                        }
                      }
                    } catch (pollErr) {
                      console.log(`[restoreSessionState] Poll attempt ${pollAttempt + 1} failed:`, pollErr);
                    }
                  }
                  // Max poll attempts — give up
                  console.log('[restoreSessionState] Max poll attempts reached — showing retry message');
                  setAndPersistSystemMessage(['⚠️ The response is taking longer than expected. Please try sending your message again.'], { phase: 'lifecycle', forSessionId: sessionId });
                } catch (err) {
                  if (currentSessionRef.current !== sessionId) return;
                  console.error('[restoreSessionState] Lifecycle poll failed:', err);
                } finally {
                  setSessionLoader(sessionId, 'lifecycle', false);
                  lifecycleInflightRef.current = false;
                  try { sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`); } catch (_) {}
                }
              })();
              } // end of else (not sourcing-already-done)
            } else {
              // Last message is from agent — response already arrived, clear the flag
              setSessionLoader(sessionId, 'lifecycle', false);
              sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`);
            }
          } else {
            // Stale, different session, or max retries exceeded — clean up
            if (retryCount >= 3) {
              console.log('[restoreSessionState] Max retries exceeded for lifecycle re-request, giving up');
              setAndPersistSystemMessage(['⚠️ The previous request could not be completed. Please try sending your message again.'], { phase: 'lifecycle', forSessionId: sessionId });
            }
            sessionStorage.removeItem(`lifecycle_inflight_${sessionId}`);
          }
        }
      } catch (_inflightErr) {
        // non-critical — inflight restore is best-effort
      }
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      // Always clear sessionLoading to prevent stuck loader overlay.
      // chatHistoryLoading is session-scoped, so only clear if still viewing same session.
      setSessionLoading(false);
      if (currentViewedSessionRef.current === loadingForSession) {
        setChatHistoryLoading(false);

        // FINAL rightPanelMode determination: if sourcing completed (via WS or restore) and
        // candidates exist, ensure panel shows candidates — this prevents any intermediate
        // block (job posting, outreach) from accidentally overriding the panel mode.
        const finalSourcingState = sessionStateStoreRef.current.get(sessionId)?.sourcing;
        if (finalSourcingState?.complete === true && (finalSourcingState?.candidates?.length > 0)) {
          setRightPanelMode('candidates');
          setSourcingComplete(true);
          setTalentScoutingStarted(true);
        }
      }
    }
  };

  // -- Restore session from URL on page load --
  // When jobPostings are loaded and a session_id is in the URL, auto-select that job
  // and load its data so the user returns to the same state after refresh.
  useEffect(() => {
    if (!sessionId || selectedJobId) return;
    
    // Wait for jobPostings to be loaded first
    if (sidebarLoading) return;
    
    let matchingJob = jobPostings.find((job) => job.id === sessionId);
    if (!matchingJob && fromDashboard) {
      // Session was just created from dashboard — may not be in the fetched list yet.
      // Synthesize a placeholder entry so we can load it without redirecting.
      const syntheticJob = {
        id: sessionId,
        title: 'New Job Posting',
        lastUpdated: 'Just now',
        rawTimestamp: new Date().toISOString(),
        duration: 'Session',
        candidateCount: 0,
        hasPostedJob: false,
        cid: getCid() != null ? String(getCid()) : null,
      };
      setJobPostings((prev) => {
        if (prev.find((j) => j.id === sessionId)) return prev;
        return [syntheticJob, ...prev];
      });
      matchingJob = syntheticJob;
    }
    if (matchingJob) {
      // CRITICAL: Set currentViewedSessionRef BEFORE loadSessionData so that
      // applyIfCurrentSession() guards inside loadSessionData allow state updates.
      // Without this, all setChatHistory/setSystemMessages calls are silently skipped
      // because currentViewedSessionRef.current is still null on page refresh.
      currentViewedSessionRef.current = matchingJob.id;
      setSelectedJobId(matchingJob.id);
      setAutomationSessionId(matchingJob.id);
      // Restore saved tab from sessionStorage (don't force 'overview')
      const _savedTab = (() => { try { return sessionStorage.getItem('activeHeaderTab'); } catch { return null; } })();
      if (_savedTab) {
        setActiveHeaderTab(_savedTab);
      }
      let queuedDashboardInitialQuery = null;
      try {
        const queued = JSON.parse(sessionStorage.getItem('dashboard_pending_session_query') || 'null');
        queuedDashboardInitialQuery = String(queued?.sessionId || '') === String(matchingJob.id) && Boolean(queued?.query)
          ? queued
          : null;
      } catch {
        queuedDashboardInitialQuery = null;
      }
      if (queuedDashboardInitialQuery) {
        setChatHistoryLoading(false);
        setSessionLoading(false);
        setSessionRestored(true);
        setCandidateRestored(true);
        setChatApiLoading(queuedDashboardInitialQuery.localOnly !== true);
        return;
      }
      loadSessionData(matchingJob.id);
    } else if (sessionId) {
      // Session ID in URL but not in user's jobPostings — belongs to another user
      // or no longer exists. Redirect to dashboard.
      console.warn('[DEBUG] Session not in sidebar (may belong to another user), redirecting to dashboard:', sessionId);
      navigate('/dashboard', { replace: true });
      return;
    }
    // Mark session restore as attempted regardless of match
    setSessionRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPostings, sessionId, sidebarLoading]);

  // -- Restore candidate from URL on page load --
  // When candidates are loaded and a `candidate` email is in the URL, auto-select that candidate.
  useEffect(() => {
    const candidateIdFromUrl = searchParams.get('candidateId');
    const candidateEmail = searchParams.get('candidate');
    const urlSearchId = searchParams.get('searchId');
    if ((!candidateEmail && !candidateIdFromUrl) || selectedCandidate) return;

    // Try to find the candidate in the loaded candidates list
    if (candidates.length > 0) {
      const match = candidates.find((c) => {
        if (candidateIdFromUrl) {
          const currentId = c.id || c.candidate_id;
          if (currentId && String(currentId) === String(candidateIdFromUrl)) return true;
        }
        if (candidateEmail) {
          return (c.email || '').toLowerCase() === candidateEmail.toLowerCase();
        }
        return false;
      });
      if (match) {
        const withSearchId = { ...match, _searchId: urlSearchId || automationSearchId };
        setSelectedCandidate(withSearchId);
        setCandidateRestored(true);
        return;
      }
    }

    // If candidates aren't loaded yet but we have session data, try loading directly
    const currentSession = selectedJobId || automationSessionId || sessionId;
    if (currentSession && candidates.length === 0) {
      // Fetch session data to find the candidate
      (async () => {
        try {
          const sessionData = await aiAutomationApi.get(`/sessions/${currentSession}`);
          if (!sessionData?.searches?.length) {
            setCandidateRestored(true);
            return;
          }
          const allCandidates = (sessionData.searches || []).flatMap((search) => search?.candidates || []);
          const match = allCandidates.find((c) => {
            if (candidateIdFromUrl) {
              const currentId = c.id || c.candidate_id;
              if (currentId && String(currentId) === String(candidateIdFromUrl)) return true;
            }
            if (candidateEmail) {
              return (c.email || '').toLowerCase() === candidateEmail.toLowerCase();
            }
            return false;
          });
          if (match) {
            const matchedSearch = (sessionData.searches || []).find((search) =>
              (search?.candidates || []).some(
                (c) => {
                  if (candidateIdFromUrl) {
                    const currentId = c.id || c.candidate_id;
                    if (currentId && String(currentId) === String(candidateIdFromUrl)) return true;
                  }
                  if (candidateEmail) {
                    return (c.email || '').toLowerCase() === candidateEmail.toLowerCase();
                  }
                  return false;
                }
              )
            );
            const searchId = urlSearchId || matchedSearch?.search_id || automationSearchId;
            setSelectedCandidate({ ...match, _searchId: searchId });
          }
        } catch (err) {
          console.error('Error restoring candidate from URL:', err);
        } finally {
          setCandidateRestored(true);
        }
      })();
    } else if (!currentSession) {
      // No session to load candidate from
      setCandidateRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, searchParams, selectedJobId, automationSessionId]);

  // Function to fetch channel preferences from backend
  const fetchChannelPreferences = async (applyToSelectionState = true) => {
    try {
      const cid = getCid();
      if (!cid) {
        console.warn('No company ID available for channel preferences');
        return null;
      }
      const response = await aiAutomationApi.get(`/company/${cid}/channel-preferences`);
      if (response?.success && response?.preferences) {
        const sourcingChannels = response.preferences.sourcing_channels || [];
        const outreachChannels = response.preferences.outreach_channels || [];
        setConfiguredPostingChannels(getPostingChannelsFromSourcingPreferences(sourcingChannels));
        if (applyToSelectionState) {
          setSelectedSourcingChannels(sourcingChannels);
          setSelectedOutreachChannels(outreachChannels);
        }
        setChannelPreferencesLoaded(true);
        return response.preferences;
      }
      return null;
    } catch (error) {
      console.error('Error fetching channel preferences:', error);
      return null;
    }
  };

  useEffect(() => {
    if (channelPreferencesLoaded) return;

    // Use true so selectedOutreachChannels is populated on mount (needed for outreach guard)
    fetchChannelPreferences(true);
  }, [channelPreferencesLoaded]);

  // Function to save channel preferences to backend
  const saveChannelPreferencesToBackend = async (sourcingChannels, outreachChannels) => {
    try {
      const cid = getCid();
      if (!cid) return false;
      
      const payload = {};
      if (sourcingChannels !== undefined) payload.sourcing_channels = sourcingChannels;
      if (outreachChannels !== undefined) payload.outreach_channels = outreachChannels;
      
      const response = await aiAutomationApi.post(`/company/${cid}/channel-preferences`, payload);
      return response?.success || false;
    } catch (error) {
      console.error('Error saving channel preferences:', error);
      return false;
    }
  };

  // Wrapper function to check channel preferences before starting sourcing
  const initiateSourceCandidate = async () => {
    if (blockDisabledFeature('sourcing')) return;
    // Capture the current session ID BEFORE showing modal (in case user switches sessions)
    const currentSessionForSourcing = sessionId;
    setPendingSourcingSessionId(currentSessionForSourcing);
    pendingSourcingSessionRef.current = currentSessionForSourcing;
    console.log('[initiateSourceCandidate] Captured session for sourcing:', currentSessionForSourcing);
    
    // Always fetch fresh preferences from DB to check if they're saved
    const preferences = await fetchChannelPreferences();

    // Check if sourcing channels are set in DB
    const sourcingChannels = preferences?.sourcing_channels || [];
    
    if (sourcingChannels.length === 0) {
      // No preferences stored in DB - show channel selection modal
      // The pendingSourcingSessionId will be used when user confirms
      setChannelSelectionType('sourcing');
      setShowChannelSelectionModal(true);
      return;
    }

    // Preferences exist in DB - proceed with sourcing using selected channels
    setSelectedSourcingChannels(sourcingChannels);
    await startTalentScouting(sourcingChannels, currentSessionForSourcing);
  };

  // Function to handle channel selection from modal
  const handleChannelSelectionConfirm = async (channels) => {
    setShowChannelSelectionModal(false);
    
    if (channelSelectionType === 'sourcing') {
      setSelectedSourcingChannels(channels);
      
      // Save to DB if user opted to save
      if (saveChannelPreferences) {
        await saveChannelPreferencesToBackend(channels, undefined);
        setSaveChannelPreferences(false);
      }
      
      // Use the captured session ID from when sourcing was initiated, not the current session
      // This ensures sourcing happens for the correct session even if user switched
      const targetSessionId = pendingSourcingSessionRef.current || pendingSourcingSessionId || sessionId;
      console.log('[handleChannelSelectionConfirm] Using session for sourcing:', targetSessionId, 'current session:', sessionId);
      
      // Proceed with sourcing using the captured session
      await startTalentScouting(channels, targetSessionId);
      
      // Clear the pending sourcing session
      setPendingSourcingSessionId(null);
      pendingSourcingSessionRef.current = null;
    } else if (channelSelectionType === 'outreach') {
      if (blockDisabledFeature('outreach')) {
        setPendingOutreachData(null);
        setChannelSelectionType(null);
        return;
      }
      setSelectedOutreachChannels(channels);
      
      // Save to DB if user opted to save
      if (saveChannelPreferences) {
        await saveChannelPreferencesToBackend(undefined, channels);
        setSaveChannelPreferences(false);
      }
      
      // Proceed with outreach using selected channels if we have pending data
      if (pendingOutreachData) {
        await handleStartOutreach({ ...pendingOutreachData, outreachChannels: channels });
        setPendingOutreachData(null);
      }
    }
    
    setChannelSelectionType(null);
  };

  // Function to start talent scouting (call sourcing candidate API)
  // targetSessionId: optional - the session to run sourcing for (used when user switches sessions before confirming)
  const startTalentScouting = async (sourcingChannels = null, targetSessionId = null) => {
    if (blockDisabledFeature('sourcing')) return;
    // Use the target session if provided (from channel selection modal), otherwise use current session
    const effectiveSessionId = targetSessionId || automationSessionId;
    // Search ID: try React state first, then session→searchId ref map (handles async state updates)
    const effectiveSearchId = (automationSearchId ? automationSearchId : null)
      || (effectiveSessionId ? getSafeSearchId(effectiveSessionId) : null);

    console.log('[startTalentScouting] Using session:', effectiveSessionId, 'searchId:', effectiveSearchId, 'target was:', targetSessionId);

    if (!effectiveSearchId || !effectiveSessionId) {
      console.error('Missing search_id or session_id for talent scouting', { effectiveSearchId, effectiveSessionId, automationSearchId });
      setJdError('Missing search or session information. Please try again.');
      return;
    }
    
    // CRITICAL: Use session-scoped loader for typing indicator
    setSessionLoader(effectiveSessionId, 'chatApi', true);

    // If no channels provided, use the selected sourcing channels from state
    const channels = sourcingChannels || selectedSourcingChannels || [];

    try {
      let dbSourcingStatusKnown = false;

      // Check if sourcing is already in progress for this session using DATABASE table (most reliable)
      try {
        const processStatus = await aiAutomationApi.get(
          `/process-status/${effectiveSessionId}`
        );
        dbSourcingStatusKnown = processStatus?.success === true;
        
        // Case 1: searching=TRUE - sourcing is currently in progress
        if (processStatus?.success && processStatus?.searching === true) {
          console.log('[startTalentScouting] Return: sourcing already in progress according to database', processStatus);
          // Restore sourcing state instead of starting new
          setTalentScoutingStarted(true);
          addActiveSourcingSession(effectiveSessionId);

          // Set processing flags based on selected channels (not blindly both)
          const storedSrc = sessionStateStoreRef.current.get(effectiveSessionId)?.sourcing;
          const srcChannels = storedSrc?.selectedChannels || channels || [];
          const srcShowGoogle = shouldRunGoogleForChannels(srcChannels);
          const srcShowLinkedin = shouldRunLinkedinForChannels(srcChannels);
          const srcShowResumeBox = shouldRunResumeBoxForChannels(srcChannels);
          setGoogleSourcingProcessing(srcShowGoogle);
          setLinkedinSourcingProcessing(srcShowLinkedin);
          setResumeBoxSourcingProcessing(srcShowResumeBox);
          if (srcChannels.length > 0) setSelectedSourcingChannels(srcChannels);
          
          // Fetch existing logs from chat messages to show progress
          try {
            const historyResponse = await aiAutomationApi.get(
              `/sessions/${effectiveSessionId}/chat-messages`
            );
            const rawMessages = Array.isArray(historyResponse?.messages) ? historyResponse.messages : [];
            
            // Parse messages to extract sourcing logs
            const googleLogs = [];
            const linkedinLogs = [];
            const resumeBoxLogs = [];
            rawMessages.forEach((m) => {
              const content = String(m?.content || '').trim();
              if (!content) return;
              const lower = content.toLowerCase();
              const phaseValue = String(m?.phase || '').toLowerCase();
              
              if (phaseValue.includes('source_candidates') || phaseValue.includes('sourcing') || 
                  lower.includes('searching') || lower.includes('candidate')) {
                const entry = {
                  message: content,
                  timestamp: m?.created_at || m?.timestamp || new Date().toISOString(),
                  logType: lower.includes('error') || lower.includes('failed') ? 'error' : 'info',
                  type: lower.includes('error') || lower.includes('failed') ? 'error' : 'info',
                };
                if (lower.includes('google')) {
                  googleLogs.push(entry);
                } else if (lower.includes('linkedin')) {
                  linkedinLogs.push(entry);
                } else if (lower.includes('resumebox') || lower.includes('resume box') || lower.includes('resume database')) {
                  resumeBoxLogs.push(entry);
                }
              }
            });
            
            if (googleLogs.length > 0) setGoogleSourcingLogs(googleLogs);
            if (linkedinLogs.length > 0) setLinkedinSourcingLogs(linkedinLogs);
            if (resumeBoxLogs.length > 0) setResumeBoxSourcingLogs(resumeBoxLogs);
          } catch (logError) {
            console.warn('Could not fetch existing sourcing logs', logError);
          }
          
          // Show message that sourcing is already running
          setSystemMessages([`Candidate sourcing is already in progress. Reconnecting to live progress...`]);
          setRightPanelMode('sourcing');
          setSessionLoader(effectiveSessionId, 'chatApi', false);
          return;
        }
        
        // Case 2: searching=FALSE but sourcing_completed=TRUE - sourcing already completed
        if (processStatus?.success && processStatus?.sourcing_completed === true) {
          console.log('[startTalentScouting] Return: sourcing already completed according to database', processStatus);
          
          // Fetch existing candidates to show
          try {
            const searchResponse = await aiAutomationApi.get(`/automation_candidates/all?session_id=${effectiveSessionId}&limit=50`);
            const dbCandidates = searchResponse?.data || searchResponse?.candidates || [];
            if (dbCandidates.length > 0) {
              setCandidates(dbCandidates);
              const candidateCount = dbCandidates.length;
              // Show a friendly completion message (not a warning)
              setSystemMessages([
                `✅ ${buildSourcingCompletedMessage(effectiveSessionId, candidateCount, {
                  candidates: dbCandidates,
                  suffix: ' matching your requirements. View them in the Candidates panel.',
                })}`
              ]);
              setSourcingComplete(true);
              setRightPanelMode('candidates');
            } else {
              const sourceLabel = getCompletedSourcingSourcesLabel(effectiveSessionId);
              setSystemMessages([`ℹ️ ${sourceLabel} was previously completed but no candidates were found.`]);
              setRightPanelMode('candidates');
            }
          } catch (candidatesError) {
            console.warn('Could not fetch existing candidates', candidatesError);
            const sourceLabel = getCompletedSourcingSourcesLabel(effectiveSessionId);
            setSystemMessages([`✅ ${sourceLabel} completed! View candidates in the Candidates panel.`]);
            setRightPanelMode('candidates');
          }
          
          // Don't show warning - just silently prevent re-sourcing
          setSessionLoader(effectiveSessionId, 'chatApi', false);
          return;
        }

        if (processStatus?.success) {
          const localSourcing = sessionStateStoreRef.current.get(effectiveSessionId)?.sourcing;
          if (localSourcing?.complete === true) {
            console.log('[startTalentScouting] DB says sourcing is not complete; clearing stale local sourcing.complete before starting', {
              sessionId: effectiveSessionId,
              processStatus,
              localSourcing,
            });
            updateSessionState(effectiveSessionId, (state) => {
              state.sourcing.complete = false;
              state.sourcing.started = false;
              state.sourcing.googleProcessing = false;
              state.sourcing.linkedinProcessing = false;
              state.sourcing.resumeBoxProcessing = false;
              return state;
            });
            setSourcingComplete(false);
            setTalentScoutingStarted(false);
          }
        }
      } catch (statusError) {
        console.warn('Could not check process status from database, proceeding with new sourcing', statusError);
      }
      
      // Local sourcing state can be stale after navigation/session switching.
      // If the database did not confirm completion, clear the local completion flag
      // and continue into the source API call.
      const localSourcingState = sessionStateStoreRef.current.get(effectiveSessionId)?.sourcing;
      const sessionSourcingComplete = localSourcingState?.complete === true;
      if (sessionSourcingComplete) {
        console.log('[startTalentScouting] Clearing local sourcing.complete before starting; DB did not confirm completion', {
          sessionId: effectiveSessionId,
          dbSourcingStatusKnown,
          localSourcingState,
        });
        updateSessionState(effectiveSessionId, (state) => {
          state.sourcing.complete = false;
          state.sourcing.started = false;
          state.sourcing.googleProcessing = false;
          state.sourcing.linkedinProcessing = false;
          state.sourcing.resumeBoxProcessing = false;
          return state;
        });
        setSourcingComplete(false);
        setTalentScoutingStarted(false);
      }
      
      setTalentScoutingStarted(true);
      addActiveSourcingSession(effectiveSessionId); // Track active sourcing session for cross-session OTP
      // Reset sourcing OTP UI gating for this sourcing run
      sourcingOtpUiRef.current = { promptShown: false, submitted: false };
      // Reset sourcing logs for new sourcing session
      setGoogleSourcingLogs([]);
      setLinkedinSourcingLogs([]);
      setResumeBoxSourcingLogs([]);
      // Show loading based on selected channels
      const showGoogle = shouldRunGoogleForChannels(channels);
      const showLinkedin = shouldRunLinkedinForChannels(channels);
      const showResumeBox = shouldRunResumeBoxForChannels(channels);
      setGoogleSourcingProcessing(showGoogle);
      setLinkedinSourcingProcessing(showLinkedin);
      setResumeBoxSourcingProcessing(showResumeBox);
      setGoogleCandidateCount(0);
      setLinkedinCandidateCount(0);
      setResumeBoxCandidateCount(0);
      // Reset sourcing phase counts
      setGoogleSearchFoundCount(0);
      setLinkedinSearchFoundCount(0);
      setEnrichedCandidateCount(0);
      setMatchedCandidateCount(0);
      // CRITICAL: Save selectedSourcingChannels to session store immediately so they
      // survive page refresh and are available for UI restoration
      updateSessionState(effectiveSessionId, (state) => {
        state.sourcing.started = true;
        state.sourcing.complete = false;
        state.sourcing.selectedChannels = channels;
        state.sourcing.googleProcessing = showGoogle;
        state.sourcing.linkedinProcessing = showLinkedin;
        state.sourcing.resumeBoxProcessing = showResumeBox;
        return state;
      });
      // Clear old JD/system messages - don't show any initial sourcing message
      // User only wants to see the final success message after sourcing completes
      setSystemMessages([]);
      // Explicitly set right panel to sourcing mode
      setActiveHeaderTab('sourcing');
      setRightPanelMode('sourcing');
      setSourcingExpandToken((prev) => prev + 1);

      // Build query text from accumulated requirements
      const queryParts = [];
      const normalizedRequirementSkills = normalizeRequirementSkillsForQuery(finalRequirements.skills);
      if (finalRequirements.title || finalRequirements.role) {
        queryParts.push(finalRequirements.title || finalRequirements.role);
      }
      if (finalRequirements.location) {
        queryParts.push(`in ${finalRequirements.location}`);
      }
      if (finalRequirements.experience) {
        queryParts.push(`with ${finalRequirements.experience} experience`);
      }
      if (normalizedRequirementSkills.length > 0) {
        queryParts.push(`skilled in ${normalizedRequirementSkills.join(', ')}`);
      }
      const queryText = queryParts.length > 0 
        ? queryParts.join(' ')
        : 'Find candidates';

      // Call sourcing candidate API with encryption and token verification
      // aiAutomationApi automatically handles encryption and token verification
      // FIRE-AND-FORGET PATTERN: Don't await the response
      // All progress, logs, and results will come via WebSocket (sourcing_complete, search_phase_update)
      // This avoids timeout issues and keeps the UI responsive
      console.log('[startTalentScouting] Calling source API', {
        effectiveSessionId,
        effectiveSearchId,
        channels,
      });
      aiAutomationApi.post(
        `/search/${effectiveSearchId}/source`,
        {
          query: queryText,
          session_id: effectiveSessionId,
          cid: getCid() || undefined,
          parsed_requirements: finalRequirements,
          sourcing_channels: channels.length > 0 ? channels : null, // Pass selected channels to backend
        },
        { timeout: 0 }  // No timeout - fire and forget, results come via WebSocket
      ).then(sourceData => {
        // Handle API response for backward compatibility
        // WebSocket handlers will take care of real-time updates
        if (!sourceData) {
          console.warn('Sourcing API returned empty response — treating as completed');
          // Even with empty response, sourcing is done — send completion signal
          if (currentViewedSessionRef.current === effectiveSessionId) {
            const exitAgentEmpty = agentStageLabel || 'Candidate Sourcing';
            showToast(`Exited: ${exitAgentEmpty}`, 'info', 2000);
            setAgentStageLabel('');
            setAgentStageCancellable(false);
            setSourcingComplete(true);
            setTalentScoutingStarted(false);
            setGoogleSourcingProcessing(false);
            setLinkedinSourcingProcessing(false);
            // Update session store so state survives session switching
            updateSessionState(effectiveSessionId, (s) => {
              s.sourcing.complete = true;
              s.sourcing.started = false;
              s.sourcing.googleProcessing = false;
              s.sourcing.linkedinProcessing = false;
              s.agentStage = { label: '', cancellable: false };
              return s;
            });
            // Show exit transition pill
            setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__Exited ${exitAgentEmpty}`]);
            setSystemMessageToken((prev) => prev + 1);
            try {
              aiAutomationApi.post(`/sessions/${effectiveSessionId}/messages`, {
                message_type: 'assistant',
                content: `Exited ${exitAgentEmpty}`,
                message_role: 'assistant',
                metadata: { type: 'agent_transition', agent: exitAgentEmpty, action: 'exited' },
              }).catch(() => {});
            } catch (_) {}
            // Fetch all candidates from DB (includes resume_box + platform-sourced)
            // Retry once after 2s if first fetch returns 0 (ResumeBox may still be saving)
            (async () => {
              const fetchCandidatesFromDB = async () => {
                try {
                  const searchResp = await aiAutomationApi.get(`/automation_candidates/all?session_id=${effectiveSessionId}&limit=50`);
                  return searchResp?.candidates || searchResp?.data || [];
                } catch (_) {
                  return [];
                }
              };

              let dbCandidates = await fetchCandidatesFromDB();

              // If 0 candidates, wait 2s and retry — ResumeBox candidates may still be saving to DB
              if (dbCandidates.length === 0) {
                await new Promise(r => setTimeout(r, 2000));
                dbCandidates = await fetchCandidatesFromDB();
              }

              if (dbCandidates.length > 0) {
                if (currentViewedSessionRef.current === effectiveSessionId) {
                  setCandidates(dbCandidates);
                  setRightPanelMode('candidates');
                  setAndPersistSystemMessage([
                    buildSourcingCompletedMessage(effectiveSessionId, dbCandidates.length, {
                      candidates: dbCandidates,
                      suffix: ' Check the result panel.',
                    })
                  ], { phase: 'lifecycle' });
                }
                updateSessionState(effectiveSessionId, (state) => {
                  state.sourcing.candidates = dbCandidates;
                  state.sourcing.complete = true;
                  state.sourcing.started = false;
                  state.agentStage = { label: '', cancellable: false };
                  return state;
                });
                return;
              }

              // No candidates found from any source (Google, LinkedIn, ResumeBox all returned 0)
              if (currentViewedSessionRef.current === effectiveSessionId) {
                const sourceLabel = getCompletedSourcingSourcesLabel(effectiveSessionId);
                setAndPersistSystemMessage([
                  `${sourceLabel} completed. No matching candidates found.`
                ], { phase: 'lifecycle' });
                if (typeof switchRightPanelWithLoader === 'function') {
                  switchRightPanelWithLoader('jd');
                } else {
                  setRightPanelMode('jd');
                }
              }
            })();
          }
          // Send completion signal to backend
          try {
            aiAutomationApi.post('/chat', {
              message: '__sourcing_complete__',
              session_id: effectiveSessionId,
              cid: getCid(),
              additional_data: { search_id: effectiveSearchId, action: 'lifecycle', _internal: 'sourcing_complete_signal' },
            }).catch(() => {});
          } catch (_) {}
          updateSessionState(effectiveSessionId, (state) => {
            state.sourcing.complete = true;
            state.sourcing.googleProcessing = false;
            state.sourcing.linkedinProcessing = false;
            return state;
          });
          return;
        }

        // Handle sourcing_already_completed response - ask user if they want to re-source
        if (sourceData?.sourcing_already_completed && sourceData?.requires_confirmation) {
          console.log('Sourcing already completed, asking for confirmation', sourceData);
          // Always update session store
          updateSessionState(effectiveSessionId, (state) => {
            state.sourcing.started = false;
            state.sourcing.googleProcessing = false;
            state.sourcing.linkedinProcessing = false;
            return state;
          });
          removeActiveSourcingSession(effectiveSessionId);
          // Only update React state if still viewing this session
          if (currentViewedSessionRef.current === effectiveSessionId) {
            const statusMessage = sourceData?.message || 'Candidate sourcing has already been completed. Do you want to search for more candidates?';
            setSystemMessages([`ℹ️ ${statusMessage}`]);
            setShowResourcingConfirm(true);
            setPendingResourcingChannels(channels);
            setGoogleSourcingProcessing(false);
            setLinkedinSourcingProcessing(false);
            setTalentScoutingStarted(false);
            if (candidates.length > 0) {
              setRightPanelMode('candidates');
            } else {
              setRightPanelMode('jd');
            }
          }
          setSessionLoader(effectiveSessionId, 'chatApi', false);
          return;
        }

        console.log('Sourcing API completed', sourceData);

        // Sourcing finished — clear the banner and show completion message
        if (currentViewedSessionRef.current === effectiveSessionId) {
          // Clear the sourcing agent banner + show exit transition pill
          const exitAgent = agentStageLabel || 'Candidate Sourcing';
          showToast(`Exited: ${exitAgent}`, 'info', 2000);
          setAgentStageLabel('');
          setAgentStageCancellable(false);
          setLastResponseType('sourcing_complete');

          // Show "Exited" transition pill in chat
          setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__Exited ${exitAgent}`]);
          setSystemMessageToken((prev) => prev + 1);
          // Persist exit transition to DB
          try {
            aiAutomationApi.post(`/sessions/${effectiveSessionId}/messages`, {
              message_type: 'assistant',
              content: `Exited ${exitAgent}`,
              message_role: 'assistant',
              metadata: { type: 'agent_transition', agent: exitAgent, action: 'exited' },
            }).catch(() => {});
          } catch (_) {}

          // Mark sourcing as complete and clear all sourcing UI state
          setSourcingComplete(true);
          setTalentScoutingStarted(false);  // Clear so right panel sourcing priority doesn't match
          setGoogleSourcingProcessing(false);
          setLinkedinSourcingProcessing(false);

          // Update LangGraph state in DB so agent knows sourcing is done
          try {
            // Send a quick "sourcing completed" signal via /chat
            // This updates sourcing_data._phase = "done" in the persisted state
            aiAutomationApi.post('/chat', {
              message: '__sourcing_complete__',
              session_id: effectiveSessionId,
              cid: getCid(),
              additional_data: {
                search_id: effectiveSearchId,
                action: 'lifecycle',
                _internal: 'sourcing_complete_signal',
              },
            }).catch(() => {}); // fire and forget
          } catch (_) {}

          // Fetch ALL candidates from DB (includes resume_box + platform-sourced)
          // The API response may only have platform candidates; resume DB matches are stored separately
          const fetchAndShowCandidates = async () => {
            try {
              const searchResponse = await aiAutomationApi.get(`/automation_candidates/all?session_id=${effectiveSessionId}&limit=50`);
              const allCandidates = searchResponse?.candidates || searchResponse?.data || [];
              if (allCandidates.length > 0) {
                setCandidates(allCandidates);
                setMatchedCandidateCount(allCandidates.length);
                updateSessionState(effectiveSessionId, (state) => {
                  state.sourcing.candidates = allCandidates;
                  return state;
                });
                setAndPersistSystemMessage([
                  buildSourcingCompletedMessage(effectiveSessionId, allCandidates.length, {
                    candidates: allCandidates,
                    stats: sourceData?.stats,
                    fallbackMessage: sourceData?.message,
                    suffix: '\n\n',
                  }) +
                  'You can view them in the Candidates panel on the right.'
                ], { phase: 'lifecycle' });
                setRightPanelMode('candidates');
                setLifecycleActions([
                  { id: 'view_candidates', label: `View Candidates (${allCandidates.length})`, command: 'show candidates' },
                  { id: 'post_job', label: 'Post Job', command: getPostJobCommand() },
                ]);
                return;
              }
            } catch (fetchErr) {
              console.warn('[sourcing .then] Failed to fetch candidates from DB:', fetchErr);
            }

            // Fallback: use candidates from API response
            const sourcedCandidates = sourceData?.candidates || [];
            const totalCandidates = sourceData?.totalCandidates || sourcedCandidates.length || 0;
            if (totalCandidates > 0 && sourcedCandidates.length > 0) {
              setCandidates(sourcedCandidates);
              setAndPersistSystemMessage([
                buildSourcingCompletedMessage(effectiveSessionId, totalCandidates, {
                  candidates: sourcedCandidates,
                  stats: sourceData?.stats,
                  fallbackMessage: sourceData?.message,
                  suffix: '\n\n',
                }) +
                'You can view them in the Candidates panel on the right.'
              ], { phase: 'lifecycle' });
              setRightPanelMode('candidates');
              setLifecycleActions([
                { id: 'view_candidates', label: `View Candidates (${totalCandidates})`, command: 'show candidates' },
                { id: 'post_job', label: 'Post Job', command: getPostJobCommand() },
              ]);
            } else {
              const sourceLabel = getCompletedSourcingSourcesLabel(effectiveSessionId, {
                stats: sourceData?.stats,
                fallbackMessage: sourceData?.message,
              });
              setAndPersistSystemMessage([
                `${sourceLabel} completed, but no candidates with email were found in this search.\n\n` +
                'You can try:\n' +
                '- **Post the Job** on LinkedIn or Naukri to attract applicants directly\n' +
                '- **Start a new chat** with different search criteria\n' +
                '- **Add LinkedIn** as a sourcing platform in Account > Settings'
              ], { phase: 'lifecycle' });
              if (typeof switchRightPanelWithLoader === 'function') {
                switchRightPanelWithLoader('jd');
              } else {
                setRightPanelMode('jd');
              }
              setActiveHeaderTab('job_posting');
              setLifecycleActions([
                { id: 'post_job', label: 'Post Job', command: getPostJobCommand() },
                { id: 'view_jd', label: 'View JD', command: 'view jd' },
              ]);
            }
          };
          fetchAndShowCandidates();
        }

        // Update session store
        updateSessionState(effectiveSessionId, (state) => {
          state.sourcing.complete = true;
          state.sourcing.googleProcessing = false;
          state.sourcing.linkedinProcessing = false;
          return state;
        });
      }).catch(error => {
        // Log error but don't block - WebSocket handlers will show progress
        console.warn('Sourcing API error (fire-and-forget mode):', error?.message || error);
        // Error will be shown via WebSocket if backend sends one
      });

      // Continue immediately - UI is set to "sourcing in progress"
      // WebSocket handlers will update candidates when they arrive
      console.log('Talent scouting started (fire-and-forget mode) - awaiting WebSocket updates');
      setSessionLoader(effectiveSessionId, 'chatApi', false);

    } catch (error) {
      console.error('Error starting talent scouting:', error);
      const errorMessage = error?.message || 'Unknown error';
      // Always clean up session store
      updateSessionState(effectiveSessionId, (state) => {
        state.sourcing.started = false;
        state.sourcing.googleProcessing = false;
        state.sourcing.linkedinProcessing = false;
        return state;
      });
      // Only update React state if still viewing this session
      if (currentViewedSessionRef.current === effectiveSessionId) {
        setJdError(`Failed to start talent scouting: ${errorMessage}`);
        setTalentScoutingStarted(false);
        setSourcingComplete(false);
        setCandidates([]);
        setSourcingStats({});
        setGoogleSourcingProcessing(false);
        setLinkedinSourcingProcessing(false);
        setSystemMessages([]);
      }
      setSessionLoader(effectiveSessionId, 'chatApi', false);
    }
  };

  // Function to confirm re-sourcing when user clicks "Yes" in confirmation dialog
  const confirmResourcing = async () => {
    setShowResourcingConfirm(false);
    const channels = pendingResourcingChannels || selectedSourcingChannels || [];
    setPendingResourcingChannels([]); // Clear pending channels

    if (!automationSearchId || !automationSessionId) {
      console.error('Missing search_id or session_id for re-sourcing');
      setJdError('Missing search or session information. Please try again.');
      return;
    }

    // CRITICAL: Use session-scoped loader
    const resourcingSessionId = automationSessionId;
    setSessionLoader(resourcingSessionId, 'chatApi', true);

    try {
      // Reset and prepare for sourcing
      setTalentScoutingStarted(true);
      addActiveSourcingSession(resourcingSessionId);
      sourcingOtpUiRef.current = { promptShown: false, submitted: false };
      setGoogleSourcingLogs([]);
      setLinkedinSourcingLogs([]);
      setResumeBoxSourcingLogs([]);
      const showGoogle = shouldRunGoogleForChannels(channels);
      const showLinkedin = shouldRunLinkedinForChannels(channels);
      const showResumeBox = shouldRunResumeBoxForChannels(channels);
      setGoogleSourcingProcessing(showGoogle);
      setLinkedinSourcingProcessing(showLinkedin);
      setResumeBoxSourcingProcessing(showResumeBox);
      setGoogleCandidateCount(0);
      setLinkedinCandidateCount(0);
      setResumeBoxCandidateCount(0);
      setGoogleSearchFoundCount(0);
      setLinkedinSearchFoundCount(0);
      setEnrichedCandidateCount(0);
      setMatchedCandidateCount(0);
      // Clear system messages - don't show intermediate sourcing messages
      // User only wants to see the final success message after sourcing completes
      setSystemMessages([]);
      setActiveHeaderTab('sourcing');
      setRightPanelMode('sourcing');
      setSourcingExpandToken((prev) => prev + 1);

      // Build query text
      const queryParts = [];
      const normalizedRequirementSkills = normalizeRequirementSkillsForQuery(finalRequirements.skills);
      if (finalRequirements.title || finalRequirements.role) {
        queryParts.push(finalRequirements.title || finalRequirements.role);
      }
      if (finalRequirements.location) {
        queryParts.push(`in ${finalRequirements.location}`);
      }
      if (finalRequirements.experience) {
        queryParts.push(`with ${finalRequirements.experience} experience`);
      }
      if (normalizedRequirementSkills.length > 0) {
        queryParts.push(`skilled in ${normalizedRequirementSkills.join(', ')}`);
      }
      const queryText = queryParts.length > 0 ? queryParts.join(' ') : 'Find candidates';

      // Call sourcing API with force_resourcing=true
      // FIRE-AND-FORGET PATTERN: Don't await the response
      // All progress, logs, and results will come via WebSocket (sourcing_complete)
      aiAutomationApi.post(
        `/search/${automationSearchId}/source`,
        {
          query: queryText,
          session_id: automationSessionId,
          cid: getCid() || undefined,
          parsed_requirements: finalRequirements,
          sourcing_channels: channels.length > 0 ? channels : null,
          force_resourcing: true, // Force re-sourcing - append candidates to existing pool
        },
        { timeout: 0 }  // No timeout - fire and forget
      ).then(sourceData => {
        console.log('Re-sourcing API completed (fire-and-forget):', sourceData);
        // Most UI updates handled by WebSocket handlers
      }).catch(error => {
        console.warn('Re-sourcing API error (fire-and-forget):', error?.message || error);
        // Error will be shown via WebSocket if backend sends one
      });

      // Continue immediately - UI shows "sourcing in progress"  
      // WebSocket handlers will update candidates when they arrive
      console.log('Re-sourcing started (fire-and-forget mode) - awaiting WebSocket updates');
      setSessionLoader(resourcingSessionId, 'chatApi', false);

    } catch (error) {
      console.error('Error during re-sourcing:', error);
      const errorMessage = error?.message || 'Unknown error';
      updateSessionState(resourcingSessionId, (state) => {
        state.sourcing.started = false;
        state.sourcing.googleProcessing = false;
        state.sourcing.linkedinProcessing = false;
        state.sourcing.resumeBoxProcessing = false;
        return state;
      });
      if (currentViewedSessionRef.current === resourcingSessionId) {
        setJdError(`Failed to re-source candidates: ${errorMessage}`);
        setGoogleSourcingProcessing(false);
        setLinkedinSourcingProcessing(false);
        setResumeBoxSourcingProcessing(false);
      }
      setSessionLoader(resourcingSessionId, 'chatApi', false);
    }
  };

  // Function to cancel re-sourcing confirmation
  const cancelResourcing = () => {
    setShowResourcingConfirm(false);
    setPendingResourcingChannels([]);
    // Reset sourcing states properly
    setTalentScoutingStarted(false);
    setGoogleSourcingProcessing(false);
    setLinkedinSourcingProcessing(false);
    setResumeBoxSourcingProcessing(false);
    removeActiveSourcingSession(automationSessionId);
    // Show candidates panel if we have existing candidates
    if (candidates.length > 0) {
      setRightPanelMode('candidates');
      setSystemMessages(prev => [...prev, 'Re-sourcing cancelled. Showing existing candidates.']);
    } else {
      setRightPanelMode('jd');
      setSystemMessages(prev => [...prev, 'Re-sourcing cancelled. You can try sourcing again later.']);
    }
  };

  const handleViewLiveTalentPool = async () => {
    const liveChannels =
      postedChannels.length > 0
        ? postedChannels
        : (selectedChannels.length > 0 ? selectedChannels : []);
    setTalentPoolChannelFilter(liveChannels);
    setRightPanelMode('candidates');
    setActiveHeaderTab('status');

    if (!Array.isArray(candidates) || candidates.length === 0) {
      // Don't auto-trigger sourcing - just show message
      if (!talentScoutingStarted) {
        setSystemMessages(['No candidates available yet. Please start "Source Candidates" to find candidates.']);
      }
    }
  };

  // Wrapper function to check outreach channels before starting outreach
  const initiateOutreach = async ({ candidateIds, candidates: selectedCandidatesList }) => {
    if (blockDisabledFeature('outreach')) return;
    if (!candidateIds || candidateIds.length === 0) return;

    // Add candidates to working candidates so agent has context
    setWorkingCandidates(selectedCandidatesList);

    // Send command to chat agent to activate outreach workflow
    const candidateNames = selectedCandidatesList.map(c => c.name || 'candidate').join(', ');
    const primaryCandidate = Array.isArray(selectedCandidatesList) && selectedCandidatesList.length === 1
      ? selectedCandidatesList[0]
      : null;
    const primaryCandidateId = primaryCandidate ? (primaryCandidate.id || primaryCandidate.candidate_id || '') : '';
    const primaryCandidateName = primaryCandidate?.name || '';
    const primaryCandidateEmail = primaryCandidate?.email || '';
    const primaryCandidateLinkedIn = primaryCandidate?.linkedin_profile_url || primaryCandidate?.profile_url || '';
    // Use LinkedIn outreach command when candidate has no email but has LinkedIn URL
    const primaryCandidateHasEmail = !!(primaryCandidateEmail && primaryCandidateEmail.trim());
    const primaryCandidateHasLinkedIn = !!(primaryCandidateLinkedIn && primaryCandidateLinkedIn.toLowerCase().includes('linkedin'));
    const useLinkedInOnly = !primaryCandidateHasEmail && primaryCandidateHasLinkedIn;

    const command = primaryCandidate
      ? useLinkedInOnly
        ? `Send LinkedIn outreach to ${primaryCandidateName} via LinkedIn.`
        : `Send outreach email to ${primaryCandidateName}${primaryCandidateEmail ? ` (${primaryCandidateEmail})` : ''}.`
      : `Send outreach emails to these ${candidateIds.length} candidates: ${candidateNames}.`;

    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    setSourcingExpandToken((prev) => prev + 1);
    handleUserQueryForJD(command, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        candidate_ids: candidateIds,
        candidate_id: primaryCandidateId || '',
        candidate_name: primaryCandidateName,
        candidate_email: primaryCandidateEmail,
        candidate_linkedin: primaryCandidateLinkedIn || '',
        // _target_candidate lets the outreach agent pre-filter the draft to just this candidate
        ...(primaryCandidate ? {
          _target_candidate: {
            id: primaryCandidateId || '',
            name: primaryCandidateName,
            email: primaryCandidateEmail,
            linkedin_url: primaryCandidateLinkedIn || '',
          },
        } : {}),
      },
    });
  };

  // Handle starting outreach from CandidateResults
  const handleStartOutreach = async ({ candidateIds, candidates: selectedCandidatesList, outreachChannels = null }) => {
    if (blockDisabledFeature('outreach')) return;
    if (!candidateIds || candidateIds.length === 0) return;
    
    // Use provided channels or fall back to state
    const channelsToUse = outreachChannels || selectedOutreachChannels || [];
    console.log('Starting outreach for candidates:', candidateIds, 'with channels:', channelsToUse);
    
    // Set outreach state — switch away from candidates view so the outreach panel becomes visible
    setOutreachPhase('template');
    setOutreachWorkspaceName('');
    setRightPanelMode('auto');
    setOutreachCandidateIds(candidateIds);
    setOutreachCandidates(selectedCandidatesList);
    setIsLoadingOutreachTemplate(true);
    setOutreachTemplatesByChannel(null);
    setOutreachLogs([]);
    setOutreachSummary(null);
    setOutreachSending(false);
    setOutreachCompleted(false);
    
    // Generate template from API - pass selected channels
    try {
      const templateResponse = await aiAutomationApi.post('/outreach/generate-template', {
        session_id: automationSessionId,
        search_id: automationSearchId,
        candidate_ids: candidateIds,
        channels: channelsToUse, // Pass selected channels
        channel: 'email', // Keep for backward compatibility
        jd_text: jdText || '',
      });
      
      if (templateResponse) {
        setOutreachWorkspaceName(templateResponse.company_name || '');
        // Only set templates for channels the user selected
        const channelTemplates = {};
        
        // Map API response to our channel names
        // 'gmail' channel corresponds to email template
        // 'linkedin' channel corresponds to linkedin template
        // 'whatsapp' or 'call' correspond to call template
        if (channelsToUse.includes('gmail') || channelsToUse.length === 0) {
          channelTemplates.email = templateResponse.email_template || templateResponse.template || '';
        }
        if (channelsToUse.includes('linkedin') || channelsToUse.length === 0) {
          channelTemplates.linkedin = templateResponse.linkedin_template || '';
        }
        if (channelsToUse.includes('whatsapp') || channelsToUse.includes('call') || channelsToUse.length === 0) {
          channelTemplates.call = templateResponse.call_template || '';
        }

        setOutreachTemplatesByChannel(channelTemplates);

        // Keep main template as email body for sending (if email/gmail is selected)
        if (channelsToUse.includes('gmail') || channelsToUse.length === 0) {
          setOutreachTemplate(templateResponse.email_template || templateResponse.template || '');
        } else if (channelsToUse.includes('linkedin')) {
          setOutreachTemplate(templateResponse.linkedin_template || '');
        } else {
          setOutreachTemplate(templateResponse.call_template || '');
        }
      }
    } catch (error) {
      console.error('Error generating outreach template:', error);
      // Set a fallback template
      setOutreachTemplate(`Dear [CANDIDATE_NAME],

I hope this message finds you well. I came across your profile and was impressed by your background and experience.

We have an exciting opportunity at [COMPANY_NAME] that might interest you. Would you be open to a brief discussion about how your experience and career goals might align with our opportunities?

Thank you for considering this opportunity.

[COMPANY_NAME] Hiring Team`);
    } finally {
      setIsLoadingOutreachTemplate(false);
    }
  };

  const getCandidateInterviewLockFromServer = async (candidate) => {
    const email = String(candidate?.email || candidate?.candidate_email || '').trim().toLowerCase();
    if (!email) return null;
    const searchId = getCurrentSessionSearchId() || automationSearchId || selectedJobId || sessionId;
    try {
      const data = await aiAutomationApi.get('/interviews/calendar/all');
      const interviews = Array.isArray(data?.interviews) ? data.interviews : [];
      return interviews.find((interview) => {
        const interviewEmail = String(interview?.candidate_email || '').trim().toLowerCase();
        if (interviewEmail !== email) return false;
        if (!searchId) return true;
        return !interview?.search_id || String(interview.search_id) === String(searchId);
      }) || null;
    } catch (err) {
      console.warn('[interview lock] Could not verify candidate interview lock:', err?.message || err);
      return null;
    }
  };

  const blockIfCandidateInterviewLocked = async (candidate) => {
    const lock = await getCandidateInterviewLockFromServer(candidate);
    if (!lock) return false;
    const candidateName = candidate?.name || candidate?.candidate_name || lock?.candidate_name || 'this candidate';
    const date = lock?.scheduled_date ? ` on ${lock.scheduled_date}` : '';
    const time = lock?.scheduled_time ? ` at ${lock.scheduled_time}` : '';
    const message = `${candidateName} already has an interview record${date}${time}. Scheduling is locked for this candidate.`;
    showToast(message, 'warning', 5000);
    appendAgentChatActivity(message);
    return true;
  };

  async function handleCandidateCardAction(actionKey, candidate) {
    console.log('[handleCandidateCardAction] Called with:', { actionKey, candidate });
    if (!candidate) return;
    if (actionKey === 'send_outreach' && blockDisabledFeature('outreach')) return;
    if (actionKey === 'schedule_meeting') {
      if (blockDisabledSchedulingFlow()) return;
      if (await blockIfCandidateInterviewLocked(candidate)) return;
    }
    if (actionKey === 'request_resume' && blockDisabledFeature('outreach')) return;

    const candidateId = candidate.id || candidate.candidate_id;
    const candidateName = candidate.name || 'this candidate';
    const candidateEmail = candidate.email ? ` (${candidate.email})` : '';
    const candidateContext = `${candidateName}${candidateEmail}`;

    if (actionKey === 'send_outreach' && candidateId) {
      console.log('[handleCandidateCardAction] Initiating outreach for:', candidateId);
      const hasEmail = !!(candidate.email && String(candidate.email).trim());
      const candidateLinkedIn = candidate.linkedin_profile_url || candidate.profile_url || '';
      const hasLinkedIn = !!(candidateLinkedIn && String(candidateLinkedIn).trim().toLowerCase().includes('linkedin'));

      // Check outreach channels — use state if loaded, else fetch fresh to avoid false-block
      const _checkAndInitiateOutreach = async () => {
        let channels = Array.isArray(selectedOutreachChannels) ? selectedOutreachChannels : [];
        if (channels.length === 0) {
          // State not yet populated — fetch fresh from API
          try {
            const freshPrefs = await fetchChannelPreferences(true);
            channels = freshPrefs?.outreach_channels || [];
          } catch (_e) {
            channels = [];
          }
        }
        const linkedInOutreachEnabled = channels.some(ch => String(ch).toLowerCase() === 'linkedin');
        console.log('[handleCandidateCardAction] outreach channels:', channels, 'linkedin enabled:', linkedInOutreachEnabled, 'hasEmail:', hasEmail, 'hasLinkedIn:', hasLinkedIn);

        // Block only when candidate has no email AND LinkedIn fallback is not possible
        if (!hasEmail && !(hasLinkedIn && linkedInOutreachEnabled)) {
          const noEmailMsg = `⚠️ Cannot send outreach to **${candidateName}** — no email address on record for this candidate.`;
          showToast(`No email address found for ${candidateName}.`, 'warning', 5000);
          appendAgentChatActivity(noEmailMsg);
          return;
        }
        setActiveHeaderTab('sourcing');
        setRightPanelMode('sourcing');
        setSourcingExpandToken((prev) => prev + 1);
        initiateOutreach({
          candidateIds: [candidateId],
          candidates: [candidate],
        });
      };
      _checkAndInitiateOutreach();
      return;
    }

    if (actionKey === 'request_resume') {
      if (!candidate.email) {
        showToast(`No email address found for ${candidateName}.`, 'warning', 5000);
        appendAgentChatActivity(`Cannot request resume from **${candidateName}** because no email address is on record.`);
        return;
      }
      const command = `Request resume from ${candidateContext}. This is a resume request for this specific candidate only — do not send initial outreach or follow-up.`;
      setActiveHeaderTab('sourcing');
      setRightPanelMode('sourcing');
      appendUserChatActivity(command);
      handleUserQueryForJD(command, 'agent', {
        bypassLoadingGuard: true,
        additionalData: {
          action: 'request_resume',
          force_intent: 'request_resume',
          candidate_id: candidateId || '',
          candidate_name: candidateName,
          candidate_email: candidate.email || '',
          _target_candidate: {
            id: candidateId || '',
            name: candidateName,
            email: candidate.email || '',
          },
        },
      });
      return;
    }

    const actionCommand = actionKey === 'schedule_meeting'
      ? `Schedule interview with ${candidateContext}.`
      : `Send follow-up email to ${candidateName}${candidate.email ? ` (${candidate.email})` : ''}. This is a follow-up to previous outreach for this specific candidate only — do not contact other candidates.`;

    console.log('[handleCandidateCardAction] Sending command:', actionCommand);
    console.log('[handleCandidateCardAction] Setting sourcing panel mode');
    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    appendUserChatActivity(actionCommand);
    if (actionKey === 'schedule_meeting') {
      setLifecycleScheduleActive(true);
      appendAgentChatActivity(`Scheduling interview flow started for ${candidateContext}. I’ll use this exact candidate profile and guide you with the next manual step if anything else is needed.`);
    }
    console.log('[handleCandidateCardAction] Calling handleUserQueryForJD');
    handleUserQueryForJD(actionCommand, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        candidate_id: candidateId || '',
        candidate_name: candidateName,
        candidate_email: candidate.email || '',
        ...(actionKey === 'schedule_meeting' && getOnlyEnabledInterviewType() ? { preselected_setup_type: getOnlyEnabledInterviewType() } : {}),
        // Lock the agent to this specific candidate — prevents accidental bulk outreach trigger
        ...(actionKey === 'follow_up' ? {
          _target_candidate: {
            id: candidateId || '',
            name: candidateName,
            email: candidate.email || '',
          },
          action: 'send_follow_up',
        } : {}),
      },
    });
  }

  // Handler for marking candidate as priority
  const handleMarkPriority = async (candidate) => {
    console.log('[handleMarkPriority] Called with candidate:', candidate);
    if (!candidate) {
      console.warn('[handleMarkPriority] Invalid candidate:', candidate);
      return;
    }

    // Check if candidate has email - required for API call
    if (!candidate.email) {
      showToast(`${candidate.name} cannot be marked as priority - email is required.`, 'warning', 4000);
      return;
    }

    // Toggle priority locally for immediate UI feedback
    const newPriority = !candidate.priority;
    updateCandidateData(candidate.email, { priority: newPriority });
    // Also update candidate metadata in chat history messages
    setChatHistory(prev => prev.map(msg => {
      if (msg.metadata?.candidate?.email === candidate.email) {
        return {
          ...msg,
          metadata: {
            ...msg.metadata,
            candidate: { ...msg.metadata.candidate, priority: newPriority }
          }
        };
      }
      return msg;
    }));

    console.log('[handleMarkPriority] Candidate has email, proceeding to API call');
    // Call the direct API endpoint to persist the change
    try {
      const searchId = automationSearchId || selectedJobId || sessionId;
      const currentSessionId = automationSessionId || selectedJobId || sessionId;
      console.log('[handleMarkPriority] searchId:', searchId, 'currentSessionId:', currentSessionId);
      if (!searchId || !currentSessionId) {
        console.warn('[handleMarkPriority] No search_id or session_id available, skipping API call');
        return;
      }

      console.log('[handleMarkPriority] Calling API with:', { candidate_email: candidate.email, search_id: searchId, session_id: currentSessionId });
      const response = await aiAutomationApi.post('/candidates/mark-priority', {
        candidate_email: candidate.email,
        search_id: searchId,
        session_id: currentSessionId,
        priority: newPriority,
      });
      console.log('[handleMarkPriority] API response:', response);

      if (response?.status) {
        const persistedPriority = response?.priority ?? newPriority;
        updateCandidateData(candidate.email, { priority: persistedPriority });
        setChatHistory(prev => prev.map(msg => {
          if (msg.metadata?.candidate?.email === candidate.email) {
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                candidate: { ...msg.metadata.candidate, priority: persistedPriority }
              }
            };
          }
          return msg;
        }));
        console.log('[handleMarkPriority] Successfully marked candidate as priority:', candidate.email);
        // The API returns updated candidate data, update UI if needed
        if (!persistedPriority) {
          showToast(`${candidate.name} removed from priority.`, 'success', 3000);
        } else if (response.status === 'needs_resume') {
          // Backend indicates resume is needed, show appropriate message
          showToast('Candidate marked as priority. Resume may be required for further processing.', 'info', 4000);
        } else if (response.status === 'shortlisted') {
          showToast(`${candidate.name} has been marked as priority and shortlisted!`, 'success', 3000);
        } else {
          showToast(`${candidate.name} has been marked as priority!`, 'success', 3000);
        }
      }
    } catch (error) {
      console.error('[handleMarkPriority] Error marking candidate as priority:', error);
      // Revert the local change on error
      updateCandidateData(candidate.email, { priority: !newPriority });
      setChatHistory(prev => prev.map(msg => {
        if (msg.metadata?.candidate?.email === candidate.email) {
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              candidate: { ...msg.metadata.candidate, priority: !newPriority }
            }
          };
        }
        return msg;
      }));
      showToast('Failed to mark candidate as priority. Please try again.', 'error', 4000);
    }
  };

  // Handler for sending follow-up
  const handleSendFollowup = (candidate) => {
    console.log('[handleSendFollowup] Called with candidate:', candidate);
    if (blockDisabledFeature('outreach')) return;
    if (!candidate) return;
    const candidateContext = `${candidate.name || 'this candidate'}${candidate.email ? ` (${candidate.email})` : ''}`;
    const command = 'Send follow-up email to ' + (candidate.name || 'this candidate') + (candidate.email ? ' (' + candidate.email + ')' : '') + '. This is a follow-up to previous outreach for this specific candidate only — do not contact other candidates.';
    console.log('[handleSendFollowup] Setting tab to sourcing and sending command');
    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    appendUserChatActivity(command);
    handleUserQueryForJD(command, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        candidate_id: candidate.id || candidate.candidate_id || '',
        candidate_name: candidate.name || '',
        candidate_email: candidate.email || '',
        // Lock agent to this specific candidate — prevents accidental bulk outreach
        _target_candidate: {
          id: candidate.id || candidate.candidate_id || '',
          name: candidate.name || '',
          email: candidate.email || '',
        },
        action: 'send_follow_up',
      },
    });
  };

  // Handler for booking interview
  const handleBookInterview = async (candidate) => {
    console.log('[handleBookInterview] Called with candidate:', candidate);
    if (blockDisabledSchedulingFlow()) return;
    if (!candidate) return;
    if (await blockIfCandidateInterviewLocked(candidate)) return;
    if (interviewSetupInProgress) {
      setActiveHeaderTab('interview');
      appendAgentChatActivity('An interview setup is already being created in chat. Please finish it or type "cancel" before starting another.');
      return;
    }
    const candidateContext = `${candidate.name || 'this candidate'}${candidate.email ? ` (${candidate.email})` : ''}`;
    const command = `Schedule interview with ${candidateContext}.`;
    console.log('[handleBookInterview] Setting tab to sourcing and sending command');
    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    appendUserChatActivity(command);
    setLifecycleScheduleActive(true);
    handleUserQueryForJD(command, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        action: 'schedule_interview',
        force_intent: 'schedule',
        // Drive the conversational setup (interview type → AI config / reuse) in chat.
        conversational_setup: true,
        ...(getOnlyEnabledInterviewType() ? { preselected_setup_type: getOnlyEnabledInterviewType() } : {}),
        candidate_id: candidate.id || candidate.candidate_id || '',
        candidate_name: candidate.name || '',
        candidate_email: candidate.email || '',
      },
    });
  };

  // Start the conversational interview flow from the Interview tab (or any multi-candidate entry).
  // No interview_type / ai_configuration_id is passed — the chat agent walks the recruiter through
  // setup (reuse existing, or choose Manual/AI and build the blueprint) before scheduling.
  const handleStartConversationalInterview = (payload = {}) => {
    if (interviewSetupInProgress) {
      setActiveHeaderTab('interview');
      appendAgentChatActivity('An interview setup is already being created in chat. Please finish it or type "cancel" before starting another.');
      return;
    }
    const selectedCandidates = Array.isArray(payload.candidates) ? payload.candidates : [];
    const candidateNames = selectedCandidates
      .map((candidate) => candidate?.name || candidate?.candidate_name || candidate?.email || candidate?.candidate_email)
      .filter(Boolean);
    const candidateLabel = candidateNames.length
      ? candidateNames.slice(0, 4).join(', ') + (candidateNames.length > 4 ? ` and ${candidateNames.length - 4} more` : '')
      : 'the selected candidates';
    // The Interview panel can preselect the type (its "Interview with Wiza" / "Manual interview"
    // buttons); the chat then skips the Manual/AI question and goes straight to building the setup.
    // Keep the "Schedule interview" wording so intent routing still recognises it — the preselected
    // type travels in additional_data, not in the command text.
    const setupChoice = payload.setupChoice === 'ai' || payload.setupChoice === 'manual' ? payload.setupChoice : getOnlyEnabledInterviewType();
    if (setupChoice === 'ai' && blockDisabledFeature('ai_interview')) return;
    if (setupChoice === 'manual' && blockDisabledFeature('manual_interview')) return;
    if (!setupChoice && blockDisabledSchedulingFlow()) return;
    const command = `Schedule interview for ${candidateLabel}.`;
    // If a saved setup is being reused, carry its duration so the scheduler doesn't re-ask it.
    const presetDuration = Number(payload.interviewSetup?.configuration?.duration_minutes) || null;
    const selectedCandidateIds = selectedCandidates
      .map((candidate) => candidate?.id || candidate?.candidate_id || candidate?.source_candidate_id)
      .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')
      .map((id) => String(id));
    const selectedCandidateEmails = selectedCandidates
      .map((candidate) => candidate?.email || candidate?.candidate_email)
      .filter((email) => email !== undefined && email !== null && String(email).trim() !== '')
      .map((email) => String(email).trim().toLowerCase());

    // Conversational interview creation belongs in the Interview section, not Sourcing — the live
    // blueprint builds there as the recruiter answers in chat.
    setActiveHeaderTab('interview');
    appendUserChatActivity(command);
    setLifecycleScheduleActive(true);
    handleUserQueryForJD(command, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        action: 'schedule_interview',
        force_intent: 'schedule',
        conversational_setup: true,
        ...(setupChoice ? { preselected_setup_type: setupChoice } : {}),
        ...(presetDuration ? { preset_duration: presetDuration } : {}),
        search_id: payload.searchId || getCurrentSessionSearchId() || automationSearchId,
        source_session_id: payload.sourceSessionId || selectedJobId || automationSessionId || sessionId,
        selected_candidate_ids: selectedCandidateIds,
        selected_candidate_emails: selectedCandidateEmails,
        selected_candidates: selectedCandidates.map((candidate) => ({
          id: candidate?.id || candidate?.candidate_id || candidate?.source_candidate_id || '',
          name: candidate?.name || candidate?.candidate_name || '',
          email: candidate?.email || candidate?.candidate_email || '',
        })),
        candidate_id: selectedCandidates[0]?.id || selectedCandidates[0]?.candidate_id || selectedCandidates[0]?.source_candidate_id || '',
        candidate_name: selectedCandidates[0]?.name || selectedCandidates[0]?.candidate_name || '',
        candidate_email: selectedCandidates[0]?.email || selectedCandidates[0]?.candidate_email || '',
      },
    });
  };

  const handleInterviewCreationSchedulingAgent = (payload = {}) => {
    const interviewType = payload.interviewType === 'ai' ? 'ai' : 'manual';
    if (interviewType === 'ai' && blockDisabledFeature('ai_interview')) return;
    if (interviewType === 'manual' && blockDisabledFeature('manual_interview')) return;
    const selectedCandidates = Array.isArray(payload.candidates) ? payload.candidates : [];
    const candidateNames = selectedCandidates
      .map((candidate) => candidate?.name || candidate?.candidate_name || candidate?.email || candidate?.candidate_email)
      .filter(Boolean);
    const candidateLabel = candidateNames.length
      ? candidateNames.slice(0, 4).join(', ') + (candidateNames.length > 4 ? ` and ${candidateNames.length - 4} more` : '')
      : 'the selected candidate(s)';
    const command = interviewType === 'ai'
      ? `Schedule AI interview for ${candidateLabel}.`
      : `Schedule manual interview for ${candidateLabel}.`;
    const setup = payload.setup || {};
    const aiConfiguration = payload.aiConfiguration || setup.configuration || {};
    // Carry a known duration (from the saved setup / AI config) so the scheduler doesn't re-ask it.
    const presetDuration = Number(payload.duration || aiConfiguration.duration_minutes || setup.configuration?.duration_minutes) || null;
    const selectedCandidateIds = selectedCandidates
      .map((candidate) => candidate?.id || candidate?.candidate_id || candidate?.source_candidate_id)
      .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')
      .map((id) => String(id));
    const selectedCandidateEmails = selectedCandidates
      .map((candidate) => candidate?.email || candidate?.candidate_email)
      .filter((email) => email !== undefined && email !== null && String(email).trim() !== '')
      .map((email) => String(email).trim().toLowerCase());

    appendUserChatActivity(command);
    setLifecycleScheduleActive(true);
    appendAgentChatActivity(
      interviewType === 'ai'
        ? 'AI interview setup is saved. I’ll continue in chat with slot creation and skip interviewer selection for this flow.'
        : 'Manual interview setup is ready. I’ll continue in chat with the existing interviewer and availability scheduling flow.'
    );
    handleUserQueryForJD(command, 'agent', {
      bypassLoadingGuard: true,
      additionalData: {
        action: 'schedule_interview',
        force_intent: 'schedule',
        interview_type: interviewType,
        skip_interviewer_selection: interviewType === 'ai',
        ...(presetDuration ? { preset_duration: presetDuration } : {}),
        search_id: payload.searchId || getCurrentSessionSearchId() || automationSearchId,
        source_session_id: payload.sourceSessionId || selectedJobId || automationSessionId || sessionId,
        interview_setup_id: setup.id || setup.setup_id || null,
        round_id: setup.round_id || null,
        ai_configuration_id: setup.ai_configuration_id || aiConfiguration.id || null,
        ai_interviewer_id: setup.ai_interviewer_id || null,
        selected_candidate_ids: selectedCandidateIds,
        selected_candidate_emails: selectedCandidateEmails,
        selected_candidates: selectedCandidates.map((candidate) => ({
          id: candidate?.id || candidate?.candidate_id || candidate?.source_candidate_id || '',
          name: candidate?.name || candidate?.candidate_name || '',
          email: candidate?.email || candidate?.candidate_email || '',
        })),
        candidate_id: selectedCandidates[0]?.id || selectedCandidates[0]?.candidate_id || selectedCandidates[0]?.source_candidate_id || '',
        candidate_name: selectedCandidates[0]?.name || selectedCandidates[0]?.candidate_name || '',
        candidate_email: selectedCandidates[0]?.email || selectedCandidates[0]?.candidate_email || '',
      },
    });
  };

  const buildInterviewQuestionsPrompt = (candidate, jobTitle, jd) => {
    const candidateName = candidate?.name || candidate?.candidate_name || 'Candidate';
    const candidateRole = candidate?.title || candidate?.current_role || candidate?.role || '';
    const candidateEmail = candidate?.email || candidate?.candidate_email || '';
    const candidateLocation = candidate?.location || candidate?.city || '';
    const candidateExperience = candidate?.experience_years || candidate?.experience || '';
    const candidateSkills = Array.isArray(candidate?.skills)
      ? candidate.skills.filter(Boolean).join(', ')
      : typeof candidate?.skills === 'string'
        ? candidate.skills
        : '';
    const normalizedJobTitle = String(jobTitle || 'the role').trim() || 'the role';
    const normalizedJd = String(jd || '').trim();

    return `
You are generating interview questions for a recruiter reviewing a candidate.

Return valid JSON only with this shape:
{
  "jd_based_questions": [...],
  "candidate_based_questions": [...]
}

Requirements:
- Generate exactly 10 jd_based_questions and 5 candidate_based_questions.
- Questions must be practical, scenario-based, and tailored to the role.
- Mix technical, behavioral, and situational prompts.
- Avoid generic textbook questions.
- Include fields like id, source, question, type, difficulty, section, category, skill, answer_type, expected_answer, evaluation_hints, and follow_up_questions where appropriate.
- Keep the questions specific to the role and candidate profile.

Role: ${normalizedJobTitle}
Candidate name: ${candidateName}
Candidate role: ${candidateRole || 'N/A'}
Candidate email: ${candidateEmail || 'N/A'}
Candidate location: ${candidateLocation || 'N/A'}
Candidate experience: ${candidateExperience || 'N/A'}
Candidate skills: ${candidateSkills || 'N/A'}

Job description:
${normalizedJd || 'N/A'}
`.trim();
  };

  // Handler for generating AI interview - open modal
  const handleGenerateInterview = async (candidate) => {
    console.log('[handleGenerateInterview] Opening modal for:', candidate);
    if (blockDisabledFeature('ai_interview')) return;
    if (!candidate) return;

    setGenerateInterviewModal({
      isOpen: true,
      loading: true,
      candidate,
      questions: [],
      error: null,
    });

    try {
      const prompt = buildInterviewQuestionsPrompt(candidate, currentInterviewJobTitle, jdText);
      const response = await aiAutomationApi.post('/openai/interview-generate', {
        prompt,
      });

      const payload = response?.data || response || {};
      const generatedData = payload?.data || payload;
      const questions = [
        ...(Array.isArray(generatedData?.jd_based_questions) ? generatedData.jd_based_questions : []),
        ...(Array.isArray(generatedData?.candidate_based_questions) ? generatedData.candidate_based_questions : []),
      ];

      if ((payload?.status === 'success' || payload?.success || questions.length > 0) && questions.length > 0) {
        setGenerateInterviewModal(prev => ({
          ...prev,
          loading: false,
          questions,
          candidate: {
            ...prev.candidate,
            jobTitle: currentInterviewJobTitle,
          },
        }));
        showToast(`Generated ${questions.length} interview questions`, 'success');
      } else {
        throw new Error(payload?.message || generatedData?.message || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('[handleGenerateInterview] Error:', error);
      setGenerateInterviewModal(prev => ({
        ...prev,
        loading: false,
        error: error?.response?.data?.message || error?.message || 'Failed to generate questions',
      }));
      showToast('Failed to generate interview questions', 'error');
    }
  };

  // Handler for meeting analysis - open modal
  const handleMeetingAnalysis = async (candidate) => {
    console.log('[handleMeetingAnalysis] Opening modal for:', candidate);
    if (blockDisabledSchedulingFlow()) return;
    if (!candidate) return;
    const generation = ++meetingReportRequestRef.current;
    const updateModal = (updater) => {
      if (generation === meetingReportRequestRef.current) setMeetingAnalysisModal(updater);
    };

    setMeetingAnalysisModal({
      isOpen: true,
      loading: true,
      candidate,
      analysis: null,
      aiResult: null,
      scheduledInterviewId: null,
      error: null,
    });

    try {
      const candidateEmail = String(candidate.email || candidate.candidate_email || '').trim();
      if (!candidateEmail) {
        throw new Error('Candidate email not found');
      }

      const metadata = getMetadata?.() || {};
      const companyId = getCid() || '';
      const userId = metadata.uid || metadata.userId || metadata.id || '';
      const resolvedSearchId =
        candidate._searchId ||
        candidate.search_id ||
        candidate.searchId ||
        getCurrentSessionSearchId();
      const resolvedSessionId =
        candidate.session_id ||
        candidate.sessionId ||
        automationSessionId ||
        selectedJobId ||
        sessionId ||
        '';
      const resolvedCandidateId = candidate.id || candidate.candidate_id || '';
      const scopedInterviewId = candidate.calendar?.id || candidate.interview_id || candidate.interviewId || '';
      const scopedMeetingId = candidate.ai_note_taker_meeting_id || candidate.meeting_id || candidate.meetingId || '';

      if (scopedInterviewId && String(candidate.calendar?.interview_type || candidate.interview_type || '').toLowerCase() === 'ai') {
        updateModal((previous) => ({ ...previous, loading: false, scheduledInterviewId: scopedInterviewId }));
        return;
      }

      // AI interviews are the authoritative "meeting analysis". Resolve the candidate's AI interview
      // from the calendar (the same source the interview workspace uses) and show its scored result
      // directly. The notes endpoint frequently returns a human/note-taker record whose id makes
      // /ai-result 400 — which is what dropped us into the "note-taker meeting_id not found" path
      // even though the candidate completed an AI interview.
      try {
        const _calParams = new URLSearchParams();
        if (companyId) _calParams.set('cid', String(companyId));
        if (userId) _calParams.set('user_id', String(userId));
        if (resolvedSearchId) _calParams.set('search_id', String(resolvedSearchId));
        _calParams.set('candidate_email', candidateEmail);
        const _calData = await aiAutomationApi.get(`/interviews/calendar/all?${_calParams.toString()}`);
        const _aiInterview = (Array.isArray(_calData?.interviews) ? _calData.interviews : [])
          .filter((iv) => String(iv?.candidate_email || '').toLowerCase() === candidateEmail.toLowerCase())
          .filter((iv) => !resolvedSearchId || String(iv.search_id || '') === String(resolvedSearchId))
          .filter((iv) => scopedInterviewId ? String(iv.id) === String(scopedInterviewId)
            : (iv.meeting_completed || ['completed', 'interviewed'].includes(String(iv.status || '').toLowerCase())))
          .filter((iv) => String(iv?.interview_type || '').toLowerCase() === 'ai' || iv?.ai_launch_token || iv?.ai_interview_id)
          .sort((a, b) => {
            const at = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
            const bt = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
            return bt - at;
          })[0];
        if (_aiInterview?.id) {
          // This candidate's meeting analysis IS the AI interview result — never fall back to a
          // (possibly stale/mismatched) human note-taker analysis if it can't be loaded.
          updateModal((prev) => ({ ...prev, loading: false, scheduledInterviewId: _aiInterview.id, analysis: null, error: null }));
          return;
        }
      } catch { /* calendar lookup failed — fall through to the notes-based path */ }

      const requestCandidateNotes = async ({
        searchId = resolvedSearchId,
        interviewId = scopedInterviewId,
        meetingId = scopedMeetingId,
        includeSession = true,
        includeCandidate = true,
      } = {}) => {
        const params = new URLSearchParams();
        if (searchId) params.set('search_id', String(searchId));
        if (includeSession && resolvedSessionId) params.set('session_id', String(resolvedSessionId));
        if (includeCandidate && resolvedCandidateId) params.set('candidate_id', String(resolvedCandidateId));
        if (companyId) params.set('cid', String(companyId));
        if (userId) params.set('user_id', String(userId));
        if (interviewId) params.set('interview_id', String(interviewId));
        if (meetingId) params.set('meeting_id', String(meetingId));
        const query = params.toString();
        const data = await aiAutomationApi.get(
          `/interviews/candidate/${encodeURIComponent(candidateEmail)}/notes${query ? `?${query}` : ''}`
        );
        return Array.isArray(data?.interviews) ? data.interviews : [];
      };

      const canOpenInterviewNotes = (interview) => {
        if (!interview) return false;
        const noteTakerStatus = String(interview.ai_note_taker_status || '').trim().toLowerCase();
        if (['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)) {
          return Boolean(interview.ai_note_taker_meeting_id || interview.notes_cached_at || interview.meeting_completed);
        }

        if (interview.meeting_completed && (interview.notes_cached_at || interview.ai_note_taker_meeting_id)) {
          return true;
        }

        const interviewStatus = String(interview.status || '').trim().toLowerCase();
        return interviewStatus === 'completed' && (interview.notes_cached_at || interview.ai_note_taker_meeting_id);
      };

      let interviewNotes = await requestCandidateNotes();

      if (interviewNotes.length === 0 && resolvedSearchId && !scopedInterviewId) {
        interviewNotes = await requestCandidateNotes({
          searchId: resolvedSearchId,
          includeSession: false,
          includeCandidate: false,
          interviewId: '',
          meetingId: '',
        });
      }

      if (interviewNotes.length === 0 && scopedInterviewId) {
        const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/notes`);
        if (data?.interview_id) {
          interviewNotes = [data];
        }
      }

      if (interviewNotes.length === 0) {
        // Scope by candidate_email ONLY. Passing cid + user_id makes /interviews/calendar/all run its
        // `EXISTS(automation_email_sync_history WHERE company_id=$cid AND user_id=$uid)` gate, which
        // drops a completed AI interview whose search has no matching sync_history row for this user —
        // the exact reason the report shows "No meeting analysis available" on the server while the
        // overview drawer (which calls calendar/all with NO params) renders it fine. candidate_email is
        // a plain filter that doesn't trigger that gate; we pick the completed interview client-side below.
        const query = new URLSearchParams();
        query.set('candidate_email', candidateEmail);

        const calendarData = await aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`);
        const calendarInterviews = Array.isArray(calendarData?.interviews) ? calendarData.interviews : [];
        const fallbackInterview = calendarInterviews
          .filter((interview) => String(interview?.candidate_email || '').toLowerCase() === candidateEmail.toLowerCase())
          .filter((interview) => !scopedInterviewId || String(interview.id) === String(scopedInterviewId))
          .filter((interview) => !resolvedSearchId || String(interview.search_id || '') === String(resolvedSearchId))
          .filter((interview) => {
            if (canOpenInterviewNotes(interview)) return true;
            const interviewStatus = String(interview?.status || '').trim().toLowerCase();
            const noteTakerStatus = String(interview?.ai_note_taker_status || '').trim().toLowerCase();
            const hasInterviewContext = Boolean(interview?.id || interview?.ai_note_taker_meeting_id || interview?.google_meet_link);
            return (
              hasInterviewContext &&
              (
                interviewStatus === 'completed' ||
                interviewStatus === 'interviewed' ||
                ['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)
              )
            );
          })
          .sort((a, b) => {
            const aStamp = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
            const bStamp = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
            return bStamp - aStamp;
          })[0];

        if (fallbackInterview?.id) {
          // A completed AI interview has NO note-taker notes, so /notes is empty — but it DOES have a
          // scored /ai-result. Try that first, otherwise the AI interview shows "No meeting analysis
          // available" here even though the overview drawer renders it fine.
          try {
            const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(fallbackInterview.id))}/ai-result`);
            if (!aiResult) throw new Error('Could not retrieve this interview result.');
            if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
              updateModal((prev) => ({ ...prev, loading: false, scheduledInterviewId: fallbackInterview.id, aiResult, analysis: null, error: null }));
              return;
            }
          } catch (error) { if (error?.status !== 400) throw error; }
          const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(fallbackInterview.id))}/notes`);
          if (data?.interview_id) {
            interviewNotes = [data];
          }
        }
      }

      // Last resort: we still have no notes but a scoped interview id exists — a pure AI interview with
      // no note-taker notes. Surface its scored /ai-result rather than throwing "No meeting analysis".
      if (interviewNotes.length === 0 && scopedInterviewId) {
        try {
          const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/ai-result`);
          if (!aiResult) throw new Error('Could not retrieve this interview result.');
          if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
            updateModal((prev) => ({ ...prev, loading: false, scheduledInterviewId: scopedInterviewId, aiResult, analysis: null, error: null }));
            return;
          }
        } catch (error) { if (error?.status !== 400) throw error; }
      }

      if (interviewNotes.length === 0) {
        throw new Error('No meeting analysis available');
      }

      // Prefer an AI-interview record when the candidate has one — its scored result is what we want
      // to show (a note flagged 'ai' / with a launch token / an ai_interview_id).
      if (scopedInterviewId) interviewNotes = interviewNotes.filter((note) => String(note.interview_id) === String(scopedInterviewId));
      const prioritizedNote =
        interviewNotes.find((note) => String(note?.interview_type || '').toLowerCase() === 'ai' || note?.ai_launch_token || note?.ai_interview_id) ||
        interviewNotes.find((note) => note?.meeting_analysis_json?.success) ||
        interviewNotes.find((note) => note?.meeting_completed || note?.notes_cached_at) ||
        interviewNotes[0];

      // The scored AI interview result is authoritative — ALWAYS try it first for the chosen
      // interview (the note's interview_type isn't always tagged 'ai'). Only if this is genuinely a
      // human interview does /ai-result return 400 and we fall through to the note-taker analysis.
      if (prioritizedNote?.interview_id) {
        try {
          const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(prioritizedNote.interview_id))}/ai-result`);
          if (!aiResult) throw new Error('Could not retrieve this interview result.');
          if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
            updateModal((prev) => ({ ...prev, loading: false, scheduledInterviewId: prioritizedNote.interview_id, aiResult, analysis: null, error: null }));
            return;
          }
        } catch (error) { if (error?.status !== 400) throw error; }
      }

      let analysis = prioritizedNote?.meeting_analysis_json || null;
      if ((!analysis || analysis.success === false) && prioritizedNote?.interview_id) {
        analysis = await aiAutomationApi.post(`/interviews/${encodeURIComponent(String(prioritizedNote.interview_id))}/notes/analyze`);
      }

      if (!analysis) {
        throw new Error('No meeting analysis available');
      }

      updateModal(prev => ({
        ...prev,
        loading: false,
        analysis,
      }));
    } catch (error) {
      console.error('[handleMeetingAnalysis] Error:', error);
      updateModal(prev => ({
        ...prev,
        loading: false,
        error: error?.response?.data?.message || error?.message || 'No meeting analysis available',
      }));
      showToast('No meeting analysis available for this candidate', 'warning');
    }
  };

  // Handle actual outreach sending
  const handleSendOutreach = async ({ template, linkedinTemplate, candidates: outreachCandidatesList, candidateIds: outreachIds, companyName: selectedCompanyName }) => {
    if (blockDisabledFeature('outreach')) return;
    console.log('🚀 handleSendOutreach called with:', { 
      templateLength: template?.length, 
      candidatesCount: outreachCandidatesList?.length, 
      candidateIdsCount: outreachIds?.length,
      candidateIds: outreachIds,
      storedCandidatesCount: outreachCandidates?.length,
      storedCandidateIdsCount: outreachCandidateIds?.length
    });
    
    // Always prefer stored state (set when user first clicked "St                    art Outreach For Selected")
    // Fallback to passed parameters if state is empty
    let candidatesToUse = outreachCandidates && outreachCandidates.length > 0 
      ? outreachCandidates 
      : (outreachCandidatesList && outreachCandidatesList.length > 0 ? outreachCandidatesList : null);
    
    let candidateIdsToUse = outreachCandidateIds && outreachCandidateIds.length > 0
      ? outreachCandidateIds
      : (outreachIds && outreachIds.length > 0 ? outreachIds : null);
    
    // If still no candidates, try to get from main candidates list using IDs
    if (!candidatesToUse || candidatesToUse.length === 0) {
      console.log('⚠️ No candidates in state or passed. Fetching from main candidates list...');
      console.log('📋 Looking for IDs:', candidateIdsToUse);
      console.log('📋 Available candidates count:', candidates?.length);
      if (candidates?.length > 0) {
        console.log('📋 Sample candidate ID formats:', 
          candidates.slice(0, 3).map(c => ({ id: c.id, candidate_id: c.candidate_id, typeof_id: typeof c.id, typeof_candidate_id: typeof c.candidate_id }))
        );
      }
      if (candidateIdsToUse && candidateIdsToUse.length > 0) {
        console.log('📋 Looking for ID types:', candidateIdsToUse.map(id => ({ id, typeof: typeof id })));
        candidatesToUse = candidates.filter(c => {
          const cId = String(c.id || c.candidate_id || '');
          const matched = candidateIdsToUse.some(id => String(id) === cId);
          if (!matched && candidates.length <= 5) {
            console.log('📋 No match for candidate:', { cId, candidateIdsToUse: candidateIdsToUse.map(id => String(id)) });
          }
          return matched;
        });
        console.log(`✅ Found ${candidatesToUse.length} candidates from main list`);
      }
    }
    
    if (!candidateIdsToUse || candidateIdsToUse.length === 0) {
      console.error('❌ No candidate IDs available');
      setSystemMessages(['No candidates selected for outreach.']);
      return;
    }
    
    if (!template || !template.trim()) {
      console.error('❌ No template provided');
      setSystemMessages(['No template available. Please generate a template first.']);
      return;
    }
    
    if (!candidatesToUse || candidatesToUse.length === 0) {
      console.error('❌ No candidate details found after all fallbacks');
      // Don't show error in chat - just log it
      console.error('Available candidates in main list:', candidates.length);
      console.error('Looking for IDs:', candidateIdsToUse);
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      return;
    }
    
    // Process with found candidates
    return processOutreachSending(template, candidatesToUse, candidateIdsToUse, selectedCompanyName, linkedinTemplate);
  };
  
  // Helper function to process outreach sending
  const processOutreachSending = async (template, candidatesToUse, candidateIdsToUse, selectedCompanyName, linkedinTemplate) => {
    // Capture session ID at request time for stale-response detection
    const outreachRequestSessionId = automationSessionId;
    
    console.log('✅ All validations passed. Starting outreach process...');
    console.log('Sending outreach to selected candidates:', candidateIdsToUse.length, 'candidates');
    console.log('Selected outreach channels:', selectedOutreachChannels);
    setOutreachPhase('sending');
    setIsLoadingOutreachTemplate(true);
    setOutreachSending(true);
    setOutreachCompleted(false);
    setOutreachLogs([
      {
        message: `Starting outreach for ${candidateIdsToUse.length} selected candidate(s) via ${selectedOutreachChannels.length > 0 ? selectedOutreachChannels.join(', ') : 'all channels'}`,
        success: true,
        timestamp: new Date().toISOString(),
      },
    ]);
    setOutreachSummary({
      total: candidateIdsToUse.length,
      sent: 0,
      failed: 0,
      email_sent: 0,
      email_failed: 0,
      whatsapp_sent: 0,
      whatsapp_failed: 0,
      linkedin_sent: 0,
      linkedin_failed: 0,
    });
    
    try {
      // Build messages array from template and selected candidates
      // Each candidate can have multiple messages (email, linkedin, whatsapp) based on available data
      const messages = [];
      
      // Get company name from finalRequirements or use placeholder
      const companyName =
        (selectedCompanyName && String(selectedCompanyName).trim()) ||
        finalRequirements.companyName ||
        '[COMPANY_NAME]';
      
      // Helper function to personalize template
      const personalizeMessage = (templateText, candidateName, companyName) => {
        return templateText
          .replace(/\[CANDIDATE_NAME\]/g, candidateName)
          .replace(/\[candidate_name\]/g, candidateName)
          .replace(/\{CANDIDATE_NAME\}/g, candidateName)
          .replace(/\{candidate_name\}/g, candidateName)
          .replace(/\[COMPANY_NAME\]/g, companyName)
          .replace(/\[company_name\]/g, companyName)
          .replace(/\{COMPANY_NAME\}/g, companyName)
          .replace(/\{company_name\}/g, companyName)
          .replace(/\[JOB_TITLE\]/g, finalRequirements.title || finalRequirements.role || '[JOB_TITLE]')
          .replace(/\[job_title\]/g, finalRequirements.title || finalRequirements.role || '[job_title]');
      };
      
      // Process each selected candidate
      for (const candidate of candidatesToUse) {
        const candidateId = String(candidate.id || candidate.candidate_id || '');
        const candidateName = candidate.name || candidate.full_name || 'Candidate';
        const candidateEmail = candidate.email || candidate.email_id || null;
        const candidateLinkedIn = candidate.profile_url || candidate.linkedin_url || null;
        const candidateMobile = candidate.mobile_number || candidate.phone || null;
        
        // Personalize the template for this candidate
        const personalizedMessage = personalizeMessage(template, candidateName, companyName);
        
        // Generate email subject
        const jobTitle = finalRequirements.title || finalRequirements.role || 'Opportunity';
        const subject = `${jobTitle} at ${companyName}`;
        
        // Determine which channels to use (empty array means all channels)
        const useAllChannels = selectedOutreachChannels.length === 0;
        
        // Add email message if email is available AND 'gmail' channel is selected (or all channels)
        if (candidateEmail && candidateEmail.trim() && (useAllChannels || selectedOutreachChannels.includes('gmail'))) {
          messages.push({
            candidateId: candidateId,
            candidateName: candidateName,
            email: candidateEmail,
            message: personalizedMessage,
            subject: subject,
            channel: 'email'
          });
        }
        
        // Add LinkedIn message if LinkedIn URL is available AND 'linkedin' channel is selected (or all channels)
        if (candidateLinkedIn && candidateLinkedIn.trim() && (useAllChannels || selectedOutreachChannels.includes('linkedin'))) {
          // Use the dedicated LinkedIn template if available, fall back to email template
          const liTemplateSource = (linkedinTemplate && linkedinTemplate.trim()) ? linkedinTemplate : template;
          const personalizedLinkedIn = personalizeMessage(liTemplateSource, candidateName, companyName);
          messages.push({
            candidateId: candidateId,
            candidateName: candidateName,
            email: null,
            message: personalizedLinkedIn,
            subject: null,
            channel: 'linkedin'
          });
        }
        
        // Add WhatsApp/SMS message if mobile number is available AND 'whatsapp' channel is selected (or all channels)
        if (candidateMobile && candidateMobile.trim() && (useAllChannels || selectedOutreachChannels.includes('whatsapp'))) {
          messages.push({
            candidateId: candidateId,
            candidateName: candidateName,
            email: null,
            message: personalizedMessage,
            subject: null,
            channel: 'sms' // WhatsApp uses 'sms' channel
          });
        }
      }
      
      if (messages.length === 0) {
        throw new Error('No messages could be created. Please ensure candidates have email, LinkedIn, or phone number.');
      }
      
      console.log(`✅ Created ${messages.length} messages for ${candidateIdsToUse.length} selected candidates`);
      console.log('📤 Calling /outreach/send with:', {
        messagesCount: messages.length,
        session_id: automationSessionId,
        search_id: automationSearchId
      });
      
      // Call the outreach send API directly
      const sendResponse = await aiAutomationApi.post('/outreach/send', {
        messages: messages,
        session_id: automationSessionId,
        search_id: automationSearchId,
        companyName: companyName,
        cid: getCid(),
      });
      
      console.log('✅ API Response received:', sendResponse);
      
      // -- Stale-response guard: discard if user switched sessions --
      if (currentSessionRef.current !== outreachRequestSessionId) {
        console.log('[Outreach] Discarding stale response — user switched sessions');
        return;
      }
      
      if (sendResponse) {
        const totalSent = sendResponse.totalSent || 0;
        const totalFailed = sendResponse.totalFailed || 0;
        const emailSent = sendResponse.emailSent || 0;
        const emailFailed = sendResponse.emailFailed || 0;
        const linkedinSent = sendResponse.linkedinSent || 0;
        const linkedinFailed = sendResponse.linkedinFailed || 0;
        const linkedinPending = sendResponse.linkedinPending || 0;
        const whatsappSent = sendResponse.whatsappSent || 0;
        const whatsappFailed = sendResponse.whatsappFailed || 0;
        const totalMessages =
          Number.isFinite(sendResponse.total) && sendResponse.total > 0
            ? sendResponse.total
            : (Array.isArray(messages) ? messages.length : (totalSent + totalFailed + linkedinPending));

        // Check if all failures are due to the active mail provider not connected
        if (isMailProviderAuthError(sendResponse)) {
          setIsLoadingOutreachTemplate(false);
          setOutreachSending(false);
          setAndPersistSystemMessage(['Mail provider is not connected. Opening authorization...'], { phase: 'outreach' });
          const connected = await handleMailProviderReconnect({
            cid: getCid(),
            error: sendResponse,
            onPopupBlocked: (err, providerLabel, provider) => setMailAuthPrompt({
              authUrl: err.authUrl,
              provider,
              providerLabel,
              action: 'outreach',
              sessionId: automationSessionId || selectedJobId || sessionId,
              searchId: getCurrentSessionSearchId(),
              retryMessage: 'yes',
            }),
            onConnecting: (providerLabel) => setAndPersistSystemMessage([`Connecting ${providerLabel}... Please complete authorization in the popup window.`], { phase: 'outreach' }),
            onConnected: (email, providerLabel) => setAndPersistSystemMessage([`${providerLabel} connected successfully${email ? ` (${email})` : ''}! Please try sending the outreach again.`], { phase: 'outreach' }),
            onSwitchPending: (prevEmail, newEmail, warning) => Promise.resolve(window.confirm(
              `Switch Gmail from ${prevEmail} to ${newEmail}?\n\n` +
              'Past email conversations from the previous account will no longer sync.\n\n' +
              'Click OK to confirm the switch, or Cancel to keep the current account.'
            )),
            onError: (msg) => setAndPersistSystemMessage([`Mail connection failed: ${msg}. Please go to Account → Credentials to connect your mail provider.`], { phase: 'outreach' }),
          });
          if (connected) {
            setOutreachPhase('template');
          } else {
            setOutreachPhase('template');
          }
          return;
        }

        setOutreachSummary({
          total: totalMessages,
          sent: totalSent,
          failed: totalFailed,
          email_sent: emailSent,
          email_failed: emailFailed,
          whatsapp_sent: whatsappSent,
          whatsapp_failed: whatsappFailed,
          linkedin_sent: linkedinSent,
          linkedin_failed: linkedinFailed,
        });

        if (Array.isArray(sendResponse.results) && sendResponse.results.length > 0) {
          const resultLogs = sendResponse.results.map((item) => {
            const candidateName = item?.candidateName || item?.candidate_name || 'Candidate';
            const message = item?.success
              ? `Message sent to ${candidateName}`
              : `Failed to send to ${candidateName}`;
            return {
              message,
              detail: item?.error || null,
              success: item?.success !== false,
              error: item?.error || null,
              timestamp: new Date().toISOString(),
            };
          });

          setOutreachLogs((prev) => [...prev, ...resultLogs]);
        }
        
        // If LinkedIn messages are pending (processing in background), keep showing progress
        // Otherwise, show completion
        if (linkedinPending > 0) {
          // LinkedIn processing in background - keep in 'sending' state
          setOutreachPhase('sending');
          setOutreachSending(true);
          setOutreachCompleted(false);
          setIsLoadingOutreachTemplate(false);
          
          // Add a log entry for LinkedIn pending
          setOutreachLogs((prev) => [
            ...prev,
            {
              message: `LinkedIn outreach in progress for ${linkedinPending} candidate(s)...`,
              success: true,
              timestamp: new Date().toISOString(),
            },
          ]);
          
          // Show partial success message
          let inProgressMessage = '';
          if (emailSent > 0) inProgressMessage += `${emailSent} email(s) sent. `;
          if (whatsappSent > 0) inProgressMessage += `${whatsappSent} WhatsApp message(s) sent. `;
          inProgressMessage += `LinkedIn outreach in progress for ${linkedinPending} candidate(s)...`;
          
          setAndPersistSystemMessage([inProgressMessage], { phase: 'outreach' });
        } else {
          // No LinkedIn pending - show completion
          setOutreachPhase('done');
          setOutreachSending(false);
          setOutreachCompleted(true);
          setIsLoadingOutreachTemplate(false);
          
          // Show detailed success message
          let successMessage = `Outreach completed! ${totalSent} message(s) sent successfully.`;
          if (emailSent > 0) successMessage += ` ${emailSent} email(s)`;
          if (linkedinSent > 0) successMessage += ` ${linkedinSent} LinkedIn message(s)`;
          if (whatsappSent > 0) successMessage += ` ${whatsappSent} WhatsApp message(s)`;
          if (totalFailed > 0) successMessage += ` (${totalFailed} failed)`;
          
          setAndPersistSystemMessage([successMessage], { phase: 'outreach' });
        }
      } else {
        throw new Error('No response from outreach send API');
      }
    } catch (error) {
      // -- Stale-response guard --
      if (currentSessionRef.current !== outreachRequestSessionId) {
        console.log('[Outreach error] Discarding — user switched sessions');
        return;
      }
      console.error('Error sending outreach:', error);
      setIsLoadingOutreachTemplate(false);
      setOutreachSending(false);
      
      // Check if error is due to the active mail provider not connected — auto-trigger OAuth
      if (isMailProviderAuthError(error)) {
        setAndPersistSystemMessage(['Mail provider is not connected. Opening authorization...'], { phase: 'outreach' });
        const connected = await handleMailProviderReconnect({
          cid: getCid(),
          error,
          onPopupBlocked: (err, providerLabel, provider) => setMailAuthPrompt({
            authUrl: err.authUrl,
            provider,
            providerLabel,
            action: 'outreach',
            sessionId: automationSessionId || selectedJobId || sessionId,
            searchId: getCurrentSessionSearchId(),
            retryMessage: 'yes',
          }),
          onConnecting: (providerLabel) => setAndPersistSystemMessage([`Connecting ${providerLabel}... Please complete authorization in the popup window.`], { phase: 'outreach' }),
          onConnected: (email, providerLabel) => setAndPersistSystemMessage([`${providerLabel} connected successfully${email ? ` (${email})` : ''}! Please try sending the outreach again.`], { phase: 'outreach' }),
          onSwitchPending: (prevEmail, newEmail, warning) => Promise.resolve(window.confirm(
            `Switch Gmail from ${prevEmail} to ${newEmail}?\n\n` +
            'Past email conversations from the previous account will no longer sync.\n\n' +
            'Click OK to confirm the switch, or Cancel to keep the current account.'
          )),
          onError: (msg) => setAndPersistSystemMessage([`Mail connection failed: ${msg}. Please go to Account → Credentials to connect your mail provider.`], { phase: 'outreach' }),
        });
        setOutreachPhase('template');
        return;
      }
      
      setOutreachPhase('template'); // Go back to template view
      const googlePermMsg = getGooglePermissionError(error);
      if (googlePermMsg) {
        showToast(googlePermMsg, 'warning', 8000);
        setAndPersistSystemMessage([googlePermMsg], { phase: 'outreach' });
      } else {
        const errorMessage = error?.response?.data?.detail?.message || error?.response?.data?.detail || error?.message || 'Failed to send outreach. Please try again.';
        setAndPersistSystemMessage([`Error: ${errorMessage}`], { phase: 'outreach' });
      }
    }
  };

  // Handle going back from outreach to candidates view
  const handleBackFromOutreach = () => {
    setOutreachPhase('idle');
    setOutreachTemplate('');
    setOutreachWorkspaceName('');
    setOutreachCandidates([]);
    setOutreachCandidateIds([]);
    // Return to candidates view if candidates exist, otherwise auto
    if (candidates && candidates.length > 0) {
      setRightPanelMode('candidates');
    }
  };

  // Connect to WebSocket for job posting, sourcing, and outreach logs
  useEffect(() => () => {
    const socket = jobPostingSocketRef.current;
    if (socket?.__sessionId === automationSessionId) {
      socket.onclose = null;
      socket.onmessage = null;
      try { socket.close(1000, 'Session changed'); } catch { /* ignore */ }
      if (jobPostingSocketRef.current === socket) jobPostingSocketRef.current = null;
    }
  }, [automationSessionId]);

  useEffect(() => {
    if (!automationSessionId) return;

    // NOTE: WebSocket keep-alive now relies on React state and activeSourcingSessions Set (not localStorage)
    let lsHasSourcingProcessing = false; // no longer read from localStorage
    const shouldConnect =
      jobPostingPhase === 'posting' ||
      jobPostingPhase === 'collecting_details' ||
      jobPostingPhase === 'posted' ||
      (talentScoutingStarted && !sourcingComplete) ||
      // Keep WebSocket alive if ANY sourcing channel is still processing (even if overall complete)
      googleSourcingProcessing || linkedinSourcingProcessing || resumeBoxSourcingProcessing ||
      lsHasSourcingProcessing ||
      outreachPhase === 'sending' ||
      activeJobPostingSessions.has(automationSessionId) || // Keep WebSocket alive for background posting
      activeSourcingSessions.has(automationSessionId); // Keep WebSocket alive for background sourcing

    const existingSocket = jobPostingSocketRef.current;
    if (existingSocket && existingSocket.__sessionId !== automationSessionId) {
      existingSocket.onclose = null;
      existingSocket.onmessage = null;
      try { existingSocket.close(1000, 'Replacing stale session socket'); } catch { /* ignore */ }
      jobPostingSocketRef.current = null;
    }

    if (shouldConnect && !jobPostingSocketRef.current) {
//        const baseUrl =
  //        process.env.REACT_APP_AI_AUTOMATION_URL || window.location.origin;
  //      const wsUrl = baseUrl.replace(/^http/, 'ws') + `/ws/otp-status/${automationSessionId}`;

    //    const socket = new WebSocket(wsUrl);
      
//const baseUrl =
 // process.env.REACT_APP_SERVERSOCKET_URL ||
 // window.location.origin.replace(/^http/, "ws") + "/aibot/api/v1/ws";
const wsBase = process.env.REACT_APP_SERVERSOCKET_URL;
console.log('Connecting to WebSocket at:', wsBase);

const wsUrl = `${wsBase}/otp-status/${automationSessionId}`;



const socket = new WebSocket(wsUrl);
	      socket.__sessionId = automationSessionId;
	      jobPostingSocketRef.current = socket;

	        socket.onmessage = (event) => {
	            let data;
	            try {
	              data = JSON.parse(event.data || '{}');
	            } catch (e) {
	              console.error('Failed to parse job posting WebSocket message', e);
	              return;
	            }
	            const { type } = data;

            // Store connection_id when connection is confirmed
            if (type === 'connection_confirmed' && data.connection_id) {
              setWsConnectionId(data.connection_id);
              console.log('[WebSocket] Connection ID received:', data.connection_id);
              return;
            }

            // Get session info from message or use appropriate fallback based on message type
            // CRITICAL: For sourcing messages without session_id, use active sourcing session
            // instead of current viewed session to prevent cross-session data leakage
            const sourcingMessageTypes = [
              'sourcing_complete', 'source_complete', 'sourcing_partial_complete',
              'sourcing_already_completed', 'sourcing_already_in_progress',
              'search_phase_update', 'google_search',
              'linkedin_search', 'linkedin_candidates_complete', 'candidate_found', 'sourcing_started'
            ];
            const phaseValue = String(data?.phase || data?.data?.phase || '').toLowerCase();
            const hasSourcingPhase =
              phaseValue.includes('source_candidates') ||
              phaseValue.includes('sourcing') ||
              phaseValue.includes('google_search') ||
              phaseValue.includes('linkedin_search');
            const isSourcingMessage = sourcingMessageTypes.includes(type) || 
              hasSourcingPhase ||
              data?.source === 'google' || data?.source === 'linkedin' ||
              data?.data?.source === 'google' || data?.data?.source === 'linkedin';
            
            // For sourcing messages, prefer active sourcing session over current session
            const activeSourcingRef = pendingSourcingSessionRef.current;
            const activeSourcingFromSet = activeSourcingSessions.size > 0 ? 
              Array.from(activeSourcingSessions)[activeSourcingSessions.size - 1] : null;
            
            // Determine the correct session ID for this message
            let messageSessionId;
            const explicitSessionId = data?.session_id || data?.data?.session_id;
            if (explicitSessionId) {
              // Message has explicit session_id - use it
              messageSessionId = explicitSessionId;
            } else if (isSourcingMessage) {
              // Sourcing/log messages without session_id must be routed to active sourcing session.
              // If we can't resolve one, ignore the message to prevent cross-session leakage.
              messageSessionId = activeSourcingRef || activeSourcingFromSet || null;
              if (!messageSessionId) {
                console.warn('[WebSocket] Ignoring sourcing/log message without session_id (no active sourcing session found):', {
                  type,
                  phase: phaseValue,
                  source: data?.source || data?.data?.source,
                });
                return;
              }
              console.log('[WebSocket] Sourcing message routed to active session:', messageSessionId);
            } else {
              // Fallback to current automation session
              messageSessionId = automationSessionId;
            }

            const eventId = String(data?.event_id || data?.data?.event_id || '').trim();
            const eventFingerprint = eventId || [
              messageSessionId,
              type,
              data?.process_type || data?.data?.process_type || '',
              data?.data?.sync_id || data?.sync_id || '',
              data?.data?.completed_at || data?.completed_at || '',
              data?.data?.field_name || data?.field_name || '',
              data?.data?.attempt || data?.attempt || '',
              data?.data?.message || data?.message || '',
            ].join('|');
            const now = Date.now();
            const processedEvents = processedWebSocketEventsRef.current;
            for (const [key, handledAt] of processedEvents) {
              if (now - handledAt > 30 * 60 * 1000) processedEvents.delete(key);
            }
            if (eventFingerprint && processedEvents.has(eventFingerprint)) {
              console.debug('[WebSocket] Ignoring duplicate event:', eventFingerprint);
              return;
            }
            if (eventFingerprint) processedEvents.set(eventFingerprint, now);

            const platform = normalizePlatformName(data?.platform || data?.data?.platform) || 'Naukri';
            
            // ===== SESSION ROUTING: Route all WebSocket events through central store =====
            const shouldRenderToUI = routeEventToSession(messageSessionId, data, `ws_${type}`);
            
            // Check if user is viewing a different session - use currentViewedSessionRef for accuracy
            const activeViewedSession = currentViewedSessionRef.current || selectedJobId;
            const isViewingDifferentSession = !shouldRenderToUI || (activeViewedSession && messageSessionId && activeViewedSession !== messageSessionId);

            if (type === 'naukri_dynamic_step_fields') {
              const fieldName = data?.data?.field_name || data?.field_name;
              const prompt =
                data?.data?.message || data?.data?.prompt || data?.prompt || data?.message;
              const fields = data?.data?.fields || data?.fields || [];
              const stepTitle = data?.data?.step_title || data?.step_title || 'Naukri form step';
              const stepIndex = data?.data?.step_index ?? data?.step_index ?? 0;
              const modalRequestKey = `${messageSessionId || automationSessionId || ''}|${fieldName || ''}`;
              const dismissedRequests = dismissedDynamicNaukriRequestsRef.current;
              for (const [key, dismissedAt] of dismissedRequests) {
                if (now - dismissedAt > 5 * 60 * 1000) dismissedRequests.delete(key);
              }
              if (modalRequestKey && dismissedRequests.has(modalRequestKey)) {
                console.debug('[WebSocket] Ignoring dismissed dynamic Naukri request:', modalRequestKey);
                return;
              }

              setWaitingForBackendQuestion(false);

              if (fieldName) {
                updateSessionState(messageSessionId, (state) => {
                  state.jobPosting.phase = 'collecting_details';
                  state.jobPosting.waitingForQuestion = false;
                  state.jobPosting.dynamicFieldRequest = {
                    sessionId: messageSessionId,
                    fieldName,
                    prompt,
                    fields,
                    stepTitle,
                    stepIndex,
                    platform,
                  };
                  return state;
                });
              }

              if (isViewingDifferentSession) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle: job?.title || 'Job Posting',
                  type: 'field_request',
                  message: prompt || 'Naukri needs more details to continue.',
                  platform,
                  fieldName,
                });
                return;
              }

              setDynamicNaukriFieldRequest({
                sessionId: messageSessionId || automationSessionId,
                fieldName,
                prompt,
                fields,
                stepTitle,
                stepIndex,
                platform,
              });
              setJobPostingPhase('collecting_details');
              return;
            }

            if (type === 'naukri_missing_field' || type === 'linkedin_missing_field') {
              const fieldName = data?.data?.field_name || data?.field_name;
              const prompt =
                data?.data?.message || data?.data?.prompt || data?.prompt || data?.message;
              const options = data?.data?.options || data?.options || [];

              // Stop waiting animation when we receive a question
              setWaitingForBackendQuestion(false);
              
              // CRITICAL: Skip if job posting has already failed for this session
              // This prevents stale WebSocket messages from restarting field collection
              const sessionState = sessionStateStoreRef.current.get(messageSessionId);
              if (sessionState?.jobPosting?.failed) {
                console.log('[WebSocket] Ignoring field request - job posting already failed for session:', messageSessionId);
                return;
              }
              
              // CRITICAL: ALWAYS persist to session store FIRST — before any early return.
              // This ensures when the user returns to this session, the question is already
              // stored and hydrateUIFromSession restores it without the WS re-asking.
              if (fieldName) {
                const existingField = sessionStateStoreRef.current.get(messageSessionId)?.jobPosting?.pendingField;
                if (existingField !== fieldName) {
                  updateSessionState(messageSessionId, (state) => {
                    state.jobPosting.pendingField = fieldName;
                    state.jobPosting.pendingFieldOptions = options || [];
                    state.jobPosting.phase = 'collecting_details';
                    state.jobPosting.waitingForQuestion = false;
                    if (prompt) {
                      const confirms = (state.chat.systemMessages || []).filter(
                        (msg) => typeof msg === 'string' && msg.trim().startsWith('✅')
                      );
                      state.chat.systemMessages = [...confirms, prompt];
                    }
                    return state;
                  });
                }
                // Also update the ref for immediate availability
                fieldCollectionStateRef.current = {
                  ...fieldCollectionStateRef.current,
                  currentField: fieldName,
                };
              }
              
              // CRITICAL: Prevent duplicate questions in UI - check if this field is already being asked
              // This can happen when session is restoring from DB while WebSocket sends same question
              // Only check for currently viewed session (when isViewingDifferentSession is false)
              if (!isViewingDifferentSession) {
                const currentPendingField = pendingNaukriField || fieldCollectionStateRef.current?.currentField;
                if (currentPendingField === fieldName) {
                  console.log('[WebSocket] Ignoring duplicate field request for UI:', fieldName);
                  return;
                }
              }
              
              // If user is viewing a different session, show modal instead
              if (isViewingDifferentSession && activeJobPostingSessions.size > 0) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                const jobTitle = job?.title || 'Job Posting';
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle,
                  type: 'field_request',
                  message: prompt,
                  options: options,
                  platform,
                  fieldName,
                });
                return;
              }
              
              // Skip rendering if viewing different session (even without active job posting)
              if (isViewingDifferentSession) {
                return;
              }
              
              if (prompt) {
                setSystemMessages((prev) => {
                  // After job-posting starts, we only want a clean Q&A log:
                  // [all ✅ confirmations] + [latest ❓ question]
                  const confirms = prev.filter((msg) =>
                    typeof msg === 'string' && msg.trim().startsWith('✅')
                  );
                  return [...confirms, prompt];
                });
                // Force ChatMain to process this message (token increment resets processedCount)
                setSystemMessageToken((prev) => prev + 1);
              }
              if (fieldName) {
                setPendingNaukriField(fieldName);
              }
              // Store options for dropdown selection
              if (options && options.length > 0) {
                setPendingNaukriFieldOptions(options);
              } else {
                setPendingNaukriFieldOptions([]);
              }
            } else if (type === 'naukri_field_response_received') {
              const fieldName = data?.data?.field_name || data?.field_name;
              const confirmMsg =
                data?.data?.message ||
                data?.message ||
                (fieldName
                  ? `Got it - I've updated ${fieldName} for your job posting.`
                  : null);
              // We intentionally do NOT push confirm messages into chat,
              // to avoid long repeated "✅ Received response..." logs.
            } else if (type === 'linkedin_app_approval_required') {
              const approvalStatus = data?.status || data?.data?.status || 'waiting';
              const approvalMessage =
                data?.message ||
                data?.data?.message ||
                'Open your LinkedIn mobile app and tap Yes to approve this sign-in.';
              const attempt = data?.attempt || data?.data?.attempt || 1;
              const maxAttempts = data?.max_attempts || data?.data?.max_attempts || 3;
              const timeoutSeconds = data?.timeout_seconds || data?.data?.timeout_seconds || 60;
              const screenshotUrl = resolveAiAutomationAssetUrl(
                data?.api_screenshot_url ||
                data?.data?.api_screenshot_url ||
                data?.screenshot_url ||
                data?.data?.screenshot_url ||
                (data?.screenshot_filename ? `/screenshots/${data.screenshot_filename}` : '') ||
                (data?.data?.screenshot_filename ? `/screenshots/${data.data.screenshot_filename}` : '')
              );
              const screenshotFallbackUrl = resolveAiAutomationAssetUrl(
                data?.screenshot_url ||
                data?.data?.screenshot_url ||
                (data?.screenshot_filename ? `/screenshots/${data.screenshot_filename}` : '') ||
                (data?.data?.screenshot_filename ? `/screenshots/${data.data.screenshot_filename}` : '')
              );

              if (isViewingDifferentSession) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle: job?.title || 'LinkedIn verification',
                  type: 'linkedin_app_approval',
                  message: approvalMessage,
                  options: [],
                  platform: 'LinkedIn',
                  fieldName: null,
                });
                return;
              }

              if (approvalStatus === 'completed' || approvalStatus === 'failed') {
                setLinkedinAppApprovalModal(prev => ({ ...prev, isOpen: false, status: approvalStatus }));
              } else {
                setLinkedinAppApprovalModal({
                  isOpen: true,
                  sessionId: messageSessionId,
                  platform: 'LinkedIn',
	                  message: approvalMessage,
	                  screenshotUrl,
	                  screenshotFallbackUrl,
	                  attempt,
                  maxAttempts,
                  timeoutSeconds,
                  status: approvalStatus,
                });
              }
            } else if (type === 'otp_required') {
              // Handle OTP requests (Naukri or other platforms)
              // CRITICAL: Only show modal if this message is for THIS connection (same system)
              // If the message includes a connection_id and it doesn't match ours, skip it
              if (data.connection_id && data.connection_id !== wsConnectionId) {
                console.log('[WebSocket] OTP message for different connection, skipping:', data.connection_id, 'ours:', wsConnectionId);
                return;
              }

              const otpPlatform =
                normalizePlatformName(data?.platform || data?.data?.platform) || 'Naukri';
              
              // Get flow context tag from backend (e.g., "Job Posting", "Sourcing Candidates", "Outreach")
              const flowContext = data?.flow_context || data?.data?.flow_context || '';
              
              // Build OTP message with flow context tag
              let otpMessage =
                data?.message ||
                data?.data?.message ||
                data?.instructions ||
                data?.data?.instructions || '';
              
              // If no message with tag, build one with the flow context
              if (!otpMessage || !otpMessage.includes('[')) {
                const contextTag = flowContext ? `[${flowContext}] ` : '';
                otpMessage = `${contextTag}${otpPlatform} requires OTP verification. Please enter the 6-digit code sent to your phone.`;
              }

              // Determine process type for the modal
              const processType = flowContext.toLowerCase().includes('sourcing') ? 'sourcing' 
                : flowContext.toLowerCase().includes('job') ? 'job_posting'
                : otpPlatform.toLowerCase();

              // Get current job title for the modal
              const currentJobTitle = selectedJob?.title || jdText?.split('\n')[0]?.replace(/^(job\s*title:?\s*|position:?\s*)/i, '').trim() || 'Current Job';

              // If user is viewing a different session, show cross-session notification
              if (isViewingDifferentSession && (activeJobPostingSessions.size > 0 || activeSourcingSessions.size > 0)) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                const jobTitle = job?.title || 'Job Posting';
                
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle,
                  type: 'otp_request',
                  message: otpMessage,
                  options: [],
                  platform: otpPlatform,
                  fieldName: null,
                });
                return;
              }
              
              // Skip rendering if viewing different session (even without active job posting)
              if (isViewingDifferentSession) {
                return;
              }

              // Show OTP in modal instead of chat
              setOtpModal({
                isOpen: true,
                sessionId: messageSessionId,
                process: processType,
                platform: otpPlatform,
                message: otpMessage,
                jobTitle: currentJobTitle,
              });
            } else if (type === 'otp_status_update') {
              // Handle OTP status updates from LinkedIn or Naukri
              // CRITICAL: Only show modal if this message is for THIS connection (same system)
              // If the message includes a connection_id and it doesn't match ours, skip it
              if (data.connection_id && data.connection_id !== wsConnectionId) {
                console.log('[WebSocket] OTP status update for different connection, skipping:', data.connection_id, 'ours:', wsConnectionId);
                return;
              }

              const otpRequired = data?.otp_required ?? data?.data?.otp_required;
              const otpPlatformName =
                normalizePlatformName(data?.platform || data?.data?.platform) || 'LinkedIn';
              
              // Get flow context tag from backend (e.g., "Job Posting", "Sourcing Candidates", "Outreach")
              const flowContext = data?.flow_context || data?.data?.flow_context || '';
              
              // Build OTP message with flow context tag
              let otpMessage =
                data?.message ||
                data?.data?.message || '';
              
              // If no message or message doesn't have tag, build one with the flow context
              if (otpRequired) {
                if (!otpMessage || !otpMessage.includes('[')) {
                  const contextTag = flowContext ? `[${flowContext}] ` : '';
                  otpMessage = `${contextTag}${otpPlatformName} requires OTP verification. Please enter the 6-digit code sent to your phone.`;
                }
              } else {
                otpMessage = otpMessage || null;
              }

              if (otpMessage && otpRequired) {
                // Determine process type for the modal
                const processType = flowContext.toLowerCase().includes('sourcing') ? 'sourcing' 
                  : flowContext.toLowerCase().includes('job') ? 'job_posting'
                  : otpPlatformName.toLowerCase();

                // Get current job title for the modal
                const currentJobTitle = selectedJob?.title || jdText?.split('\n')[0]?.replace(/^(job\s*title:?\s*|position:?\s*)/i, '').trim() || 'Current Job';

                // Check if user is viewing a different session - show cross-session notification
                if (isViewingDifferentSession) {
                  const job = jobPostings.find(j => j.id === messageSessionId);
                  const jobTitle = job?.title || 'Session';
                  
                  setCrossSessionNotification({
                    sessionId: messageSessionId,
                    jobTitle,
                    type: 'otp_request',
                    message: otpMessage,
                    options: [],
                    platform: otpPlatformName,
                    fieldName: null,
                  });
                  return;
                }
                
                // Show OTP in modal instead of chat
                setOtpModal({
                  isOpen: true,
                  sessionId: messageSessionId,
                  process: processType,
                  platform: otpPlatformName,
                  message: otpMessage,
                  jobTitle: currentJobTitle,
                });
              } else if (otpMessage && !otpRequired) {
                // OTP completed - close the modal only if same session
                if (!isViewingDifferentSession) {
                  setOtpModal(prev => ({ ...prev, isOpen: false }));
                  
                  // If OTP is completed successfully, show brief success message
                  if (otpMessage.toLowerCase().includes('completed') || otpMessage.toLowerCase().includes('success')) {
                    // Clear any OTP-related messages from chat
                    setSystemMessages(prev => prev.filter(msg => {
                      if (typeof msg !== 'string') return true;
                      const msgLower = msg.toLowerCase();
                      return !msgLower.includes('otp') && !msgLower.includes('verification');
                    }));
                  }
                }
              }
            } else if (type === 'credentials_required') {
              // Handle credentials request from backend (LinkedIn/Naukri login required)
              const credPlatform = normalizePlatformName(data?.platform || data?.data?.platform) || 'Platform';
              const attempt = data?.attempt || data?.data?.attempt || 1;
              const maxAttempts = data?.max_attempts || data?.data?.max_attempts || 3;
              const attemptMessage = data?.attempt_message || data?.data?.attempt_message || `Attempt ${attempt}/${maxAttempts}`;
              const isRetry = data?.is_retry || data?.data?.is_retry || false;
              const errorMessage = data?.error_message || data?.data?.error_message || null;
              const prompt = data?.prompt || data?.data?.prompt || `Please enter your ${credPlatform} credentials`;
              
              // CRITICAL: Update session store FIRST
              updateSessionState(messageSessionId, (state) => {
                state.credentialsModal = {
                  platform: credPlatform,
                  attempt,
                  maxAttempts,
                  attemptMessage,
                  isRetry,
                  errorMessage,
                  prompt,
                };
                return state;
              });
              
              // If user is viewing a different session, show cross-session notification
              if (isViewingDifferentSession) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                const jobTitle = job?.title || 'Job Posting';
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle,
                  type: 'credentials_request',
                  message: prompt,
                  options: [],
                  platform: credPlatform,
                  fieldName: null,
                });
                return;
              }
              
              // Only show credentials modal if viewing same session
              setCredentialsModal({
                sessionId: messageSessionId || automationSessionId,
                platform: credPlatform,
                attempt,
                maxAttempts,
                attemptMessage,
                isRetry,
                errorMessage,
                prompt,
              });
            } else if (type === 'credentials_failed') {
              // Handle credential failure - stop loading but don't mark as "failed" (user can retry with correct credentials)
              const credFailPlatform = normalizePlatformName(data?.platform || data?.data?.platform) || 'Platform';
              const credFailMessage = data?.data?.message || data?.message || 'Credential verification failed';
              
              console.log('[credentials_failed] Stopping loading for:', credFailPlatform, credFailMessage);
              
              // Add error message to logs
              const errorLog = {
                message: `⚠️ ${credFailMessage}`,
                timestamp: new Date().toISOString(),
                type: 'warning',
                logType: 'warning',
              };
              
              // CRITICAL: Update session store FIRST
              const credFailState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              if (credFailPlatform === 'Naukri') {
                credFailState.jobPosting.naukriLogs = [...(credFailState.jobPosting.naukriLogs || []), errorLog];
                credFailState.jobPosting.naukriProcessing = false;
              } else if (credFailPlatform === 'LinkedIn') {
                credFailState.jobPosting.linkedinLogs = [...(credFailState.jobPosting.linkedinLogs || []), errorLog];
                credFailState.jobPosting.linkedinProcessing = false;
              }
              const stillPosting =
                Boolean(credFailState.jobPosting.linkedinProcessing) ||
                Boolean(credFailState.jobPosting.naukriProcessing);
              credFailState.jobPosting.processing = stillPosting;
              credFailState.jobPosting.complete = !stillPosting;
              credFailState.jobPosting.phase = stillPosting ? 'posting' : 'posted';
              sessionStateStoreRef.current.set(messageSessionId, credFailState);
              
              // Only update React state if current session
              if (!isViewingDifferentSession) {
                if (credFailPlatform === 'Naukri') {
                  setNaukriLogs((prev) => [...prev, errorLog]);
                  setNaukriProcessing(false);
                } else if (credFailPlatform === 'LinkedIn') {
                  setLinkedinLogs((prev) => [...prev, errorLog]);
                  setLinkedinProcessing(false);
                }
                
                if (!credFailState.jobPosting.linkedinProcessing && !credFailState.jobPosting.naukriProcessing) {
                  setJobPostingProcessing(false);
                  setJobPostingComplete(true);
                  setJobPostingPhase('posted');
                } else {
                  setJobPostingProcessing(true);
                  setJobPostingComplete(false);
                  setJobPostingPhase('posting');
                }
              }
              // Close credentials modal if open
              setCredentialsModal(null);
              // Important: NOT setting jobPostingFailed=true so RetryChannelSelector shows
            } else if (type === 'work_email_required') {
              // Handle work email verification request from backend (LinkedIn company email verification)
              const companyName = data?.company_name || data?.data?.company_name || '';
              const attempt = data?.attempt || data?.data?.attempt || 1;
              const maxAttempts = data?.max_attempts || data?.data?.max_attempts || 3;
              const attemptMessage = data?.attempt_message || data?.data?.attempt_message || `Attempt ${attempt}/${maxAttempts}`;
              const isRetry = data?.is_retry || data?.data?.is_retry || false;
              const errorMessage = data?.error_message || data?.data?.error_message || null;
              const prompt = data?.prompt || data?.data?.prompt || `Please enter your ${companyName ? companyName + ' ' : ''}work email address`;
              
              console.log('[work_email_required] Opening work email modal:', companyName, attemptMessage);
              
              // If user is viewing a different session, show cross-session notification
              if (isViewingDifferentSession) {
                const job = jobPostings.find(j => j.id === messageSessionId);
                const jobTitle = job?.title || 'Job Posting';
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle,
                  type: 'work_email_request',
                  message: prompt,
                  options: [],
                  platform: 'LinkedIn',
                  fieldName: 'work_email',
                  companyName,
                });
                return;
              }
              
              // Only show work email modal if viewing same session
              setWorkEmailModal({
                sessionId: messageSessionId || automationSessionId,
                companyName,
                attempt,
                maxAttempts,
                attemptMessage,
                isRetry,
                errorMessage,
                prompt,
              });
            } else if (type === 'email_sync_started') {
              const payload = data?.data || {};
              const syncSearchId = payload.search_id;
              const syncId = payload.sync_id;
              if (syncSearchId && syncId) {
                connectToExistingSync(syncSearchId, syncId, {
                  jobTitle: selectedJob?.title || '',
                });
                if (!isViewingDifferentSession) {
                  showToast(
                    payload.auto_sync
                      ? 'Automatic email sync started.'
                      : 'Email sync started.',
                    'info',
                    3000,
                  );
                }
              }
            } else if (type === 'sync_complete') {
              // Background sync completed — show toast alert (non-intrusive)
              const payload = data?.data || {};
              const newCount = payload.new_count || 0;
              const isError = payload.error === true;
              const syncSearchId = payload.search_id || getCurrentSessionSearchId() || automationSearchId;

              console.log('[sync_complete]', { newCount, isError, session: messageSessionId });
              window.dispatchEvent(new CustomEvent('notificationsChanged'));
              if (syncSearchId) {
                refreshSyncState(syncSearchId).catch((refreshError) => {
                  console.warn('[sync_complete] Failed to refresh email sync state', refreshError);
                });
              }

              // Show toast notification (doesn't interrupt chat)
              if (isError) {
                const currentSyncState = syncSearchId ? getSyncState(syncSearchId) : null;
                if (currentSyncState?.phase === 'complete' || currentSyncState?.syncing) {
                  return;
                }
                if (payload?.mail_auth_needed || payload?.gmail_auth_needed || isMailProviderAuthError(payload)) {
                  showToast('Mail authorization required. Reconnect to continue sync.', 'warning', 4000);
                } else {
                  showToast('Sync failed. Please try again.', 'error', 4000);
                }
              } else if (newCount > 0) {
                showToast(`Sync complete — ${newCount} new repl${newCount === 1 ? 'y' : 'ies'} found. Check the pipeline for results.`, 'success', 5000);
              } else {
                showToast('Sync complete — no new replies.', 'info', 3000);
              }

            } else if (type === 'job_posting_complete') {
              // Handle job posting complete - show success message in chat
              const message = data?.data?.message || data?.message || '✅ Job posting completed successfully!';
              const results = data?.data?.results || data?.results || {};
              const explicitPlatform = normalizePlatformName(data?.data?.platform || data?.platform);
              const terminalPlatforms = explicitPlatform
                ? [explicitPlatform]
                : Object.keys(data?.data?.results || data?.results || {}).map(normalizePlatformName).filter(Boolean);
              const terminalKey = `${messageSessionId}:job_posting_complete:${terminalPlatforms.sort().join(',') || 'all'}`;
              if (handledPostingTerminalEventsRef.current.has(terminalKey)) return;
              handledPostingTerminalEventsRef.current.add(terminalKey);
              
              console.log('🎉 [job_posting_complete] Job posting completed:', message);
              
              // Build success message with channel details
              const successChannels = [];
              if (results?.linkedin?.success === true) successChannels.push('LinkedIn');
              if (results?.naukri?.success === true) successChannels.push('Naukri');
              // Build next steps based on session progress
              const hasSourced = sessionStateStoreRef.current.get(messageSessionId)?.sourcing?.complete;
              const candidateCount = candidates?.length || 0;
              let nextStepsText = '';
              if (candidateCount > 0) {
                nextStepsText = '\n\n**Next steps:**\n- **Send Outreach** — Reach out to sourced candidates\n- **View Pipeline** — Check candidate status';
              } else if (hasSourced) {
                nextStepsText = '\n\n**Next steps:**\n- **Source Candidates** — Find talent via Google Search & ResumeBox\n- **View Pipeline** — Monitor job posting applicants';
              } else {
                nextStepsText = '\n\n**Next steps:**\n- **Source Candidates** — Find matching talent for this role\n- **Send Outreach** — Reach out to candidates via email';
              }

              const successMessage = successChannels.length > 0
                ? `Job posted successfully on **${successChannels.join(' and ')}**!${nextStepsText}`
                : `${message}${nextStepsText}`;
              
              // Store success message in database
              try {
                aiAutomationApi.post(`/sessions/${messageSessionId}/messages`, {
                  message_type: 'assistant',
                  content: successMessage,
                  message_role: 'assistant',
                  metadata: { 
                    phase: 'job_posting_completed', 
                    channels: successChannels,
                    results,
                    type: 'job_posting_success'
                  },
                }).catch(e => console.warn('Failed to store job posting success in DB:', e));
              } catch (e) {
                console.warn('Failed to store job posting success in DB:', e);
              }
              
              // CRITICAL: Update session store FIRST
              const completeState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              const targetedPlatforms = explicitPlatform
                ? [explicitPlatform]
                : [
                    ...(results?.linkedin ? ['LinkedIn'] : []),
                    ...(results?.naukri ? ['Naukri'] : []),
                  ];

              if (targetedPlatforms.includes('LinkedIn') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) {
                completeState.jobPosting.linkedinProcessing = false;
              }
              if (targetedPlatforms.includes('Naukri') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) {
                completeState.jobPosting.naukriProcessing = false;
              }

              const stillPostingNow =
                Boolean(completeState.jobPosting.linkedinProcessing) ||
                Boolean(completeState.jobPosting.naukriProcessing);

              completeState.jobPosting.processing = stillPostingNow;
              completeState.jobPosting.complete = !stillPostingNow;
              completeState.jobPosting.phase = stillPostingNow ? 'posting' : 'posted';
              completeState.jobPosting.waitingForQuestion = false;
              completeState.jobPosting.fieldCollectionActive = false;
              completeState.jobPosting.pendingField = null;
              completeState.jobPosting.pendingFieldsQueue = [];
              if (results?.linkedin?.success === true) {
                completeState.jobPosting.postedChannels = [...new Set([...(completeState.jobPosting.postedChannels || []), 'LinkedIn'])];
              }
              if (results?.naukri?.success === true) {
                completeState.jobPosting.postedChannels = [...new Set([...(completeState.jobPosting.postedChannels || []), 'Naukri'])];
              }
              sessionStateStoreRef.current.set(messageSessionId, completeState);
              
              persistJobPostingStateSnapshot(messageSessionId, completeState.jobPosting);
              
              // CRITICAL: Also add to chat history in session store so it shows after session switch
              const successHistoryEntry = {
                id: Date.now(),
                sender: 'agent',
                message: successMessage,
                timestamp: new Date().toISOString(),
                metadata: { phase: 'job_posting_completed', type: 'job_posting_success' }
              };
              updateSessionState(messageSessionId, (state) => {
                state.chat.chatHistory = [...(state.chat.chatHistory || []), successHistoryEntry];
                return state;
              });
              
              // Only update React state if current session
              if (!isViewingDifferentSession) {
                // Show success message in chat with channel info
                setSystemMessages((prev) => {
                  const filtered = prev.filter(msg => typeof msg === 'string' && !msg.includes('Starting job posting'));
                  return [...filtered, successMessage];
                });
                setSystemMessageToken((prev) => prev + 1);

                // Also add to chatHistory for React state
                setChatHistory((prev) => [...prev, successHistoryEntry]);

                // Update state
                if (targetedPlatforms.includes('LinkedIn') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) {
                  setLinkedinProcessing(false);
                }
                if (targetedPlatforms.includes('Naukri') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) {
                  setNaukriProcessing(false);
                }
                const stillPostingReact =
                  ((targetedPlatforms.includes('LinkedIn') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) ? false : linkedinProcessing) ||
                  ((targetedPlatforms.includes('Naukri') || (!explicitPlatform && !results?.naukri && !results?.linkedin)) ? false : naukriProcessing);
                setJobPostingProcessing(stillPostingReact);
                setJobPostingComplete(!stillPostingReact);
                setJobPostingPhase(stillPostingReact ? 'posting' : 'posted');
                setWaitingForBackendQuestion(false);

                // Safety net: clear agent banner if still active
                if (agentStageLabel) {
                  const exitAgent = agentStageLabel;
                  showToast(`Exited: ${exitAgent}`, 'info', 2000);
                  setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__Exited ${exitAgent}`]);
                  setSystemMessageToken((prev) => prev + 1);
                  setAgentStageLabel('');
                  setAgentStageCancellable(false);
                }

                // Update posted channels based on results
                if (results?.linkedin?.success === true) {
                  setPostedChannels((prev) => prev.includes('LinkedIn') ? prev : [...prev, 'LinkedIn']);
                }
                if (results?.naukri?.success === true) {
                  setPostedChannels((prev) => prev.includes('Naukri') ? prev : [...prev, 'Naukri']);
                }
              }
              if (!stillPostingNow) {
                removeActiveJobPostingSession(messageSessionId);
              }
              
            } else if (type === 'job_posting_error') {
              // Handle job posting error - show error message in chat
              const rawMessage = data?.data?.message || data?.message || 'Job posting failed.';
              const errorDetail = data?.data?.error || data?.error || '';
              const platform = normalizePlatformName(data?.data?.platform || data?.platform) || 'Naukri';
              const terminalKey = `${messageSessionId}:job_posting_error:${platform}`;
              if (handledPostingTerminalEventsRef.current.has(terminalKey)) return;
              handledPostingTerminalEventsRef.current.add(terminalKey);
              const message = formatJobPostingErrorMessage(rawMessage, { platform, detail: errorDetail });
              
              console.error('❌ [job_posting_error] Job posting failed:', message, errorDetail);
              
              // Store failure message in database
              try {
                aiAutomationApi.post(`/sessions/${messageSessionId}/messages`, {
                  message_type: 'assistant',
                  content: `❌ ${message}`,
                  message_role: 'assistant',
                  metadata: { 
                    phase: 'job_posting_failed', 
                    platform,
                    error: errorDetail,
                    type: 'job_posting_error'
                  },
                }).catch(e => console.warn('Failed to store job posting error in DB:', e));
              } catch (e) {
                console.warn('Failed to store job posting error in DB:', e);
              }
              
              // CRITICAL: Update session store FIRST - including clearing field collection
              const errorState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              if (platform === 'LinkedIn') {
                errorState.jobPosting.linkedinProcessing = false;
              }
              if (platform === 'Naukri') {
                errorState.jobPosting.naukriProcessing = false;
              }
              errorState.jobPosting.failed = true;
              const stillPostingNow =
                Boolean(errorState.jobPosting.linkedinProcessing) ||
                Boolean(errorState.jobPosting.naukriProcessing);
              errorState.jobPosting.processing = stillPostingNow;
              errorState.jobPosting.complete = !stillPostingNow;
              errorState.jobPosting.phase = stillPostingNow ? 'posting' : 'posted';
              errorState.jobPosting.waitingForQuestion = false;
              // Clear field collection state on failure
              errorState.jobPosting.pendingField = null;
              errorState.jobPosting.pendingFieldsQueue = [];
              errorState.jobPosting.fieldCollectionActive = false;
              sessionStateStoreRef.current.set(messageSessionId, errorState);
              
              persistJobPostingStateSnapshot(messageSessionId, errorState.jobPosting);
              
              // Only update React state if current session
              if (!isViewingDifferentSession) {
                // Show error message in chat
                setSystemMessages((prev) => {
                  const filtered = prev.filter(msg => typeof msg === 'string' && !msg.includes('Starting job posting'));
                  return [...filtered, `❌ ${message}`];
                });
                setSystemMessageToken((prev) => prev + 1);

                // Update state
                if (platform === 'LinkedIn') {
                  setLinkedinProcessing(false);
                }
                if (platform === 'Naukri') {
                  setNaukriProcessing(false);
                }
                const stillPostingReact =
                  (platform === 'LinkedIn' ? false : linkedinProcessing) ||
                  (platform === 'Naukri' ? false : naukriProcessing);
                setJobPostingProcessing(stillPostingReact);
                setJobPostingFailed(true);
                setJobPostingComplete(!stillPostingReact);
                setJobPostingPhase(stillPostingReact ? 'posting' : 'posted');
                setWaitingForBackendQuestion(false);
                // Clear field collection state on failure
                setPendingNaukriField(null);
                setPendingJobPostingFieldsQueue([]);
                fieldCollectionStateRef.current = { isActive: false, currentField: null, queue: [] };

                // Safety net: clear agent banner if still active
                if (agentStageLabel) {
                  const exitAgent = agentStageLabel;
                  showToast(`Exited: ${exitAgent}`, 'info', 2000);
                  setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__Exited ${exitAgent}`]);
                  setSystemMessageToken((prev) => prev + 1);
                  setAgentStageLabel('');
                  setAgentStageCancellable(false);
                }
              }
              if (!stillPostingNow) {
                removeActiveJobPostingSession(messageSessionId);
              }
              
            } else if (type === 'job_posting_update') {
              // Handle general job posting updates that might contain user prompts
              const message = data?.data?.message || data?.message;
              const phase = data?.phase || data?.data?.phase;
              const status = data?.status || data?.data?.status;
              const platform = normalizePlatformName(data?.data?.platform || data?.platform);
              
              // CRITICAL: Ignore job posting updates if viewing a different session
              // This prevents WebSocket messages from background sessions from overwriting current state
              if (isViewingDifferentSession) {
                console.log('[job_posting_update] Ignoring update from different session:', messageSessionId);
                return;
              }
              
              // Only show messages that are questions or prompts to the user
              if (message && (message.includes('❓') || message.includes('Please') || message.includes('provide'))) {
                setSystemMessages((prev) => {
                  const filtered = prev.filter(
                    (msg) =>
                      typeof msg === 'string' &&
                      !msg.toLowerCase().includes('otp') &&
                      !msg.toLowerCase().includes('verification')
                  );
                  return [...filtered, message];
                });
                // Force ChatMain to render this message
                setSystemMessageToken((prev) => prev + 1);
              }
              
              // When backend starts posting (linkedin or naukri phase), switch to posting phase and show logs
              if (phase === 'linkedin' || phase === 'naukri') {
                const platformKey = phase === 'linkedin' ? 'linkedin' : 'naukri';
                const sessionState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                sessionState.jobPosting = sessionState.jobPosting || {};
                sessionState.jobPosting.phase = 'posting';
                sessionState.jobPosting.processing = true;
                sessionState.jobPosting.complete = false;
                sessionState.jobPosting.failed = false;
                sessionState.jobPosting.linkedinProcessing =
                  platformKey === 'linkedin' ? true : Boolean(sessionState.jobPosting.linkedinProcessing);
                sessionState.jobPosting.naukriProcessing =
                  platformKey === 'naukri' ? true : Boolean(sessionState.jobPosting.naukriProcessing);

                // Reset only the platform that is starting now so a new Naukri run does not
                // wipe LinkedIn logs, and vice versa.
                if (!logsInitializedRef.current[platformKey]) {
                  logsInitializedRef.current[platformKey] = true;
                  if (platformKey === 'linkedin') {
                    sessionState.jobPosting.linkedinLogs = [];
                  } else {
                    sessionState.jobPosting.naukriLogs = [];
                  }
                  if (!isViewingDifferentSession) {
                    if (platformKey === 'linkedin') {
                      setLinkedinLogs([]);
                    } else {
                      setNaukriLogs([]);
                    }
                  }
                }

                sessionState.loaders = sessionState.loaders || {};
                sessionState.loaders.linkedin = Boolean(sessionState.jobPosting.linkedinProcessing);
                sessionState.loaders.naukri = Boolean(sessionState.jobPosting.naukriProcessing);
                sessionStateStoreRef.current.set(messageSessionId, sessionState);
                persistJobPostingStateSnapshot(messageSessionId, sessionState.jobPosting);
                
                // Only update UI state if current session
                if (!isViewingDifferentSession) {
                  // Switch to posting phase to show logs view
                  setJobPostingPhase('posting');
                  setJobPostingProcessing(true);
                  setWaitingForBackendQuestion(false);
                  // Clear old system messages to start fresh for job posting logs
                  setSystemMessages([]);
                  // Ensure right panel shows job posting logs, not sourcing
                  setRightPanelMode('auto');
                  // Reset outreach phase so job posting logs can show in right panel
                  setOutreachPhase('idle');
                  
                  // Mark only the platform that started; don't shut down the other platform's loader.
                  if (phase === 'linkedin') {
                    setLinkedinProcessing(true);
                  } else if (phase === 'naukri') {
                    setNaukriProcessing(true);
                  }
                }
                
                // Determine platform from phase or explicit platform field
                const logPlatform = platform || (phase === 'linkedin' ? 'LinkedIn' : phase === 'naukri' ? 'Naukri' : null);
                
                // Capture job posting status messages as logs - use session-aware helper
                if (message && logPlatform) {
                  const logEntry = {
                    message: message,
                    timestamp: new Date().toISOString(),
                    status: status || 'info'
                  };
                  
                  if (logPlatform === 'LinkedIn' || phase === 'linkedin') {
                    appendSessionLog(messageSessionId, 'linkedin', logEntry);
                  } else if (logPlatform === 'Naukri' || phase === 'naukri') {
                    appendSessionLog(messageSessionId, 'naukri', logEntry);
                  }
                }

                const screenshotFilename = data?.data?.screenshot_filename || data?.data?.screenshot_path;
                if (screenshotFilename && logPlatform) {
                  const artifactLogEntry = {
                    message: `Error screenshot saved: ${screenshotFilename}`,
                    timestamp: new Date().toISOString(),
                    status: 'error',
                  };
                  if (logPlatform === 'LinkedIn' || phase === 'linkedin') {
                    appendSessionLog(messageSessionId, 'linkedin', artifactLogEntry);
                  } else if (logPlatform === 'Naukri' || phase === 'naukri') {
                    appendSessionLog(messageSessionId, 'naukri', artifactLogEntry);
                  }
                }
              }
              
              // Handle final completion message (phase === 'completed' means ALL platforms are done)
              if (phase === 'completed') {
                console.log('🎉 All job posting platforms completed');
                
                // Extract results if available
                const completionResults = data?.data?.results || data?.results || {};
                
                // Always update session store first
                const sessionState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                sessionState.jobPostingComplete = true;
                sessionState.loaders = sessionState.loaders || {};
                const completionPlatforms = [
                  ...(completionResults?.linkedin ? ['LinkedIn'] : []),
                  ...(completionResults?.naukri ? ['Naukri'] : []),
                ];
                if (completionPlatforms.includes('LinkedIn') || completionPlatforms.length === 0) {
                  sessionState.loaders.linkedin = false;
                  sessionState.jobPosting.linkedinProcessing = false;
                }
                if (completionPlatforms.includes('Naukri') || completionPlatforms.length === 0) {
                  sessionState.loaders.naukri = false;
                  sessionState.jobPosting.naukriProcessing = false;
                }
                const stillPostingNow =
                  Boolean(sessionState.jobPosting?.linkedinProcessing) ||
                  Boolean(sessionState.jobPosting?.naukriProcessing);
                sessionState.loaders.jobPostingProcessing = stillPostingNow;
                sessionState.jobPosting.processing = stillPostingNow;
                sessionState.jobPosting.complete = !stillPostingNow;
                sessionState.jobPostingPhase = stillPostingNow ? 'posting' : 'posted';
                sessionState.jobPosting.phase = stillPostingNow ? 'posting' : 'posted';
                
                // CRITICAL: Update postedChannels based on results or selectedChannels
                // Use results if available, otherwise fall back to selectedChannels from session state
                if (completionResults?.linkedin?.success === true) {
                  sessionState.jobPosting = sessionState.jobPosting || {};
                  sessionState.jobPosting.postedChannels = [...new Set([...(sessionState.jobPosting.postedChannels || []), 'LinkedIn'])];
                }
                if (completionResults?.naukri?.success === true) {
                  sessionState.jobPosting = sessionState.jobPosting || {};
                  sessionState.jobPosting.postedChannels = [...new Set([...(sessionState.jobPosting.postedChannels || []), 'Naukri'])];
                }
                // If no results but we know which channels were selected, use those
                if (!completionResults?.linkedin && !completionResults?.naukri && sessionState.jobPosting?.selectedChannels?.length > 0) {
                  sessionState.jobPosting.postedChannels = [...new Set([...(sessionState.jobPosting.postedChannels || []), ...sessionState.jobPosting.selectedChannels])];
                }
                
                sessionStateStoreRef.current.set(messageSessionId, sessionState);
                persistJobPostingStateSnapshot(messageSessionId, sessionState.jobPosting);
                
                // Only update React state if this is the current session
                if (!isViewingDifferentSession) {
                  if (completionPlatforms.includes('LinkedIn') || completionPlatforms.length === 0) {
                    setLinkedinProcessing(false);
                  }
                  if (completionPlatforms.includes('Naukri') || completionPlatforms.length === 0) {
                    setNaukriProcessing(false);
                  }
                  const stillPostingReact =
                    ((completionPlatforms.includes('LinkedIn') || completionPlatforms.length === 0) ? false : linkedinProcessing) ||
                    ((completionPlatforms.includes('Naukri') || completionPlatforms.length === 0) ? false : naukriProcessing);
                  setJobPostingProcessing(stillPostingReact);
                  setJobPostingComplete(!stillPostingReact);
                  setJobPostingPhase(stillPostingReact ? 'posting' : 'posted');
                  
                  // Update postedChannels in React state
                  if (completionResults?.linkedin?.success === true) {
                    setPostedChannels((prev) => prev.includes('LinkedIn') ? prev : [...prev, 'LinkedIn']);
                  }
                  if (completionResults?.naukri?.success === true) {
                    setPostedChannels((prev) => prev.includes('Naukri') ? prev : [...prev, 'Naukri']);
                  }
                  // If no results but we have selected channels, use those
                  if (!completionResults?.linkedin && !completionResults?.naukri && selectedChannels.length > 0) {
                    setPostedChannels((prev) => {
                      const newChannels = selectedChannels.filter(c => !prev.includes(c));
                      return newChannels.length > 0 ? [...prev, ...newChannels] : prev;
                    });
                  }
                } else {
                  console.log('[job_posting_update] Storing completion state for session:', messageSessionId);
                }
                
                // Call session API to get final job details (with auth token)
                if (sessionId) {
                  const baseUrl = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/aibot/v1';
                  const sessionApiUrl = `${baseUrl}/sessions/${sessionId}`;
                  console.log('📞 Calling session API:', sessionApiUrl);
                  
                  const authToken = localStorage.getItem('auth_token');
                  fetch(sessionApiUrl, {
                    headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
                  })
                    .then(response => response.ok ? response.json() : null)
                    .then(sessionData => {
                      if (sessionData) {
                        console.log('✅ Session data retrieved:', sessionData);
                        // You can store session data in state if needed
                        // setSessionDetails(sessionData);
                      } else {
                        console.warn('⚠️ Session API returned non-ok response');
                      }
                    })
                    .catch(error => {
                      console.error('❌ Error calling session API:', error);
                    });
                }
                
                // Only clear activeJobPostingSession if this was the active one
                if (!stillPostingNow) {
                  removeActiveJobPostingSession(messageSessionId);
                }
                
                // Check if there were any failures and update session store
                const results = data?.data?.results || {};
                const linkedinFailed = results?.linkedin?.success === false;
                const naukriFailed = results?.naukri?.success === false;
                
                // Update session store with posted channels
                const state = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                state.postedChannels = state.postedChannels || [];
                if (results?.linkedin?.success === true && !state.postedChannels.includes('LinkedIn')) {
                  state.postedChannels.push('LinkedIn');
                }
                if (results?.naukri?.success === true && !state.postedChannels.includes('Naukri')) {
                  state.postedChannels.push('Naukri');
                }
                state.jobPostingFailed = linkedinFailed || naukriFailed;
                sessionStateStoreRef.current.set(messageSessionId, state);
                
                // Only update React state if current session
                if (!isViewingDifferentSession) {
                  if (results?.linkedin?.success === true) {
                    setPostedChannels((prev) =>
                      prev.includes('LinkedIn') ? prev : [...prev, 'LinkedIn']
                    );
                  }
                  if (results?.naukri?.success === true) {
                    setPostedChannels((prev) =>
                      prev.includes('Naukri') ? prev : [...prev, 'Naukri']
                    );
                  }
                  if (linkedinFailed || naukriFailed) {
                    setJobPostingFailed(true);
                  }
                }
                return;
              }
              
              // Handle completion status for individual platforms
              const logMessage = cleanLogMessage(data?.data?.logMessage || data?.data?.message || '');
              if ((status === 'completed' && !logMessage.toLowerCase().includes('waiting for page to load') && !logMessage.toLowerCase().includes('attempt')) || status === 'failed') {
                // Add completion log to the appropriate platform
                const completionPlatform = platform || (phase === 'linkedin' ? 'LinkedIn' : phase === 'naukri' ? 'Naukri' : null);
                const successStatus = data?.data?.success === true;
                
                // Always update session store first
                const sessionState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                if (completionPlatform === 'LinkedIn' || phase === 'linkedin') {
                  sessionState.loaders = sessionState.loaders || {};
                  sessionState.loaders.linkedin = false;
                  sessionState.jobPosting = sessionState.jobPosting || {};
                  if ((status === 'completed' && !logMessage.toLowerCase().includes('waiting for page to load') && !logMessage.toLowerCase().includes('attempt')) || status === 'failed') {
                    sessionState.jobPosting.linkedinProcessing = false;
                  }
                  if (successStatus) {
                    sessionState.jobPosting.postedChannels = sessionState.jobPosting.postedChannels || [];
                    if (!sessionState.jobPosting.postedChannels.includes('LinkedIn')) {
                      sessionState.jobPosting.postedChannels.push('LinkedIn');
                    }
                  }
                } else if (completionPlatform === 'Naukri' || phase === 'naukri') {
                  sessionState.loaders = sessionState.loaders || {};
                  sessionState.loaders.naukri = false;
                  sessionState.jobPosting = sessionState.jobPosting || {};
                  if ((status === 'completed' && !logMessage.toLowerCase().includes('waiting for page to load') && !logMessage.toLowerCase().includes('attempt')) || status === 'failed') {
                    sessionState.jobPosting.naukriProcessing = false;
                  }
                  if (successStatus) {
                    sessionState.jobPosting.postedChannels = sessionState.jobPosting.postedChannels || [];
                    if (!sessionState.jobPosting.postedChannels.includes('Naukri')) {
                      sessionState.jobPosting.postedChannels.push('Naukri');
                    }
                  }
                }
                const stillPosting =
                  Boolean(sessionState.jobPosting?.linkedinProcessing) ||
                  Boolean(sessionState.jobPosting?.naukriProcessing);
                sessionState.jobPosting.processing = stillPosting;
                sessionState.jobPosting.complete = !stillPosting;
                sessionState.jobPosting.phase = stillPosting ? 'posting' : 'posted';
                if (status === 'failed') {
                  sessionState.jobPosting.failed = true;
                }
                sessionStateStoreRef.current.set(messageSessionId, sessionState);
                persistJobPostingStateSnapshot(messageSessionId, sessionState.jobPosting);
                
                // Call session API when individual platform completes (with auth token)
                if (sessionId && status === 'completed') {
                  try {
                    const baseUrl = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/aibot/v1';
                    const sessionApiUrl = `${baseUrl}/sessions/${sessionId}`;
                    console.log(`📞 [${completionPlatform}] Calling session API:`, sessionApiUrl);
                    
                    const authToken = localStorage.getItem('auth_token');
                    fetch(sessionApiUrl, {
                      headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
                    })
                      .then(response => response.ok ? response.json() : null)
                      .then(sessionData => {
                        if (sessionData) {
                          console.log(`✅ [${completionPlatform}] Session data:`, sessionData);
                        }
                      })
                      .catch(error => console.error(`❌ [${completionPlatform}] Session API error:`, error));
                  } catch (error) {
                    console.error(`❌ [${completionPlatform}] Error calling session API:`, error);
                  }
                }
                
                // Stop processing animation for the completed platform - ONLY if current session
                if (!isViewingDifferentSession) {
                  const otherPlatformStillProcessing =
                    completionPlatform === 'LinkedIn' || phase === 'linkedin'
                      ? Boolean(sessionState.jobPosting?.naukriProcessing)
                      : Boolean(sessionState.jobPosting?.linkedinProcessing);
                  if (completionPlatform === 'LinkedIn' || phase === 'linkedin') {
                    setLinkedinProcessing(false);
                    if (successStatus) {
                      setPostedChannels((prev) =>
                        prev.includes('LinkedIn') ? prev : [...prev, 'LinkedIn']
                      );
                    }
                    if (!otherPlatformStillProcessing) {
                      setJobPostingProcessing(false);
                      setJobPostingComplete(true);
                      setJobPostingPhase('posted');
                    } else {
                      setJobPostingProcessing(true);
                      setJobPostingComplete(false);
                      setJobPostingPhase('posting');
                    }
                  } else if (completionPlatform === 'Naukri' || phase === 'naukri') {
                    setNaukriProcessing(false);
                    if (successStatus) {
                      setPostedChannels((prev) =>
                        prev.includes('Naukri') ? prev : [...prev, 'Naukri']
                      );
                    }
                    if (!otherPlatformStillProcessing) {
                      setJobPostingProcessing(false);
                      setJobPostingComplete(true);
                      setJobPostingPhase('posted');
                    } else {
                      setJobPostingProcessing(true);
                      setJobPostingComplete(false);
                      setJobPostingPhase('posting');
                    }
                  }
                } else {
                  console.log('[job_posting_update] Platform completion stored for session:', messageSessionId, completionPlatform);
                }
                
                if (message && completionPlatform) {
                  const logEntry = {
                    message: message,
                    timestamp: new Date().toISOString(),
                    status: successStatus ? 'success' : (status === 'failed' ? 'error' : 'completed')
                  };
                  
                  // Use session-aware helper for logs
                  if (completionPlatform === 'LinkedIn') {
                    appendSessionLog(messageSessionId, 'linkedin', logEntry);
                  } else if (completionPlatform === 'Naukri') {
                    appendSessionLog(messageSessionId, 'naukri', logEntry);
                  }
                  
                  // Show error messages in chat for failed status (only for current session)
                  if (!isViewingDifferentSession && (status === 'failed' || data?.data?.show_in_chat)) {
                    const rawErrorMessage = data?.data?.error || message || `${completionPlatform} job posting failed`;
                    const errorMessage = formatJobPostingErrorMessage(rawErrorMessage, {
                      platform: completionPlatform,
                      detail: data?.data?.error_details || data?.data?.error_detail || '',
                    });
                    setSystemMessages((prev) => {
                      const filtered = prev.filter(m => 
                        typeof m === 'string' && 
                        !m.includes('Starting job posting') &&
                        !m.includes(completionPlatform)
                      );
                      return [...filtered, `❌ ${errorMessage}`];
                    });
                    setJobPostingFailed(true);
                    
                    // CRITICAL: Store failure message in database for persistence when user switches back
                    try {
                      aiAutomationApi.post(`/sessions/${messageSessionId}/messages`, {
                        message_type: 'assistant',
                        content: `❌ ${errorMessage}`,
                        message_role: 'assistant',
                        metadata: { 
                          phase: 'job_posting_failed', 
                          platform: completionPlatform,
                          type: 'job_posting_update_failure'
                        },
                      }).catch(e => console.warn(`Failed to store ${completionPlatform} failure in DB:`, e));
                    } catch (e) {
                      console.warn(`Failed to store ${completionPlatform} failure in DB:`, e);
                    }
                  }
                }
                
                // Keep jobPostingPhase as 'posting' or 'posted' - don't reset to idle
                // This ensures the logs view stays visible after completion
                console.log('Job posting workflow completed for:', completionPlatform, '- keeping logs visible');
              }
            } else if (type === 'job_posting_confirmation_request') {
              // Handle confirmation request for job posting details
              const confirmationMessage = data?.data?.message || data?.message;
              const collectedDetails = data?.data?.collected_details;
              
              // CRITICAL: Update session store FIRST
              if (collectedDetails) {
                updateSessionState(messageSessionId, (state) => {
                  state.jobPosting.collectedDetails = collectedDetails;
                  state.jobPosting.showConfirmationModal = true;
                  if (confirmationMessage) {
                    state.chat.systemMessages = [confirmationMessage];
                  }
                  return state;
                });
              }
              
              // Ignore if viewing a different session
              if (isViewingDifferentSession) {
                console.log('[job_posting_confirmation_request] Ignoring from different session:', messageSessionId);
                return;
              }
              
              // Show the confirmation modal with collected details (only if same session)
              if (collectedDetails) {
                setCollectedJobPostingDetails(collectedDetails);
                setShowJobPostingConfirmationModal(true);
              }
              
              // Also show the text message in chat for reference
              if (confirmationMessage) {
                setSystemMessages([confirmationMessage]);
              }
            } else if (type === 'outreach_status_update' || type === 'outreach_progress' || type === 'outreach_log') {
              const phase = data?.phase || data?.data?.phase;
              const status = data?.status || data?.data?.status;
              const payload = data?.data || {};

              const total =
                payload.total ??
                payload.totalCandidates ??
                payload.total_candidates ??
                payload.total_messages ??
                0;
              const sent = payload.sent ?? payload.totalSent ?? payload.total_sent ?? 0;
              const failed = payload.failed ?? payload.totalFailed ?? payload.total_failed ?? 0;

              // Always update session store first
              const sessionState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              sessionState.outreachSummary = {
                ...(sessionState.outreachSummary || {}),
                total,
                sent,
                failed,
                email_sent: payload.email_sent ?? payload.emailSent ?? sessionState.outreachSummary?.email_sent ?? 0,
                email_failed: payload.email_failed ?? payload.emailFailed ?? sessionState.outreachSummary?.email_failed ?? 0,
                whatsapp_sent: payload.whatsapp_sent ?? payload.whatsappSent ?? sessionState.outreachSummary?.whatsapp_sent ?? 0,
                whatsapp_failed: payload.whatsapp_failed ?? payload.whatsappFailed ?? sessionState.outreachSummary?.whatsapp_failed ?? 0,
                linkedin_sent: payload.linkedin_sent ?? payload.linkedinSent ?? sessionState.outreachSummary?.linkedin_sent ?? 0,
                linkedin_failed: payload.linkedin_failed ?? payload.linkedinFailed ?? sessionState.outreachSummary?.linkedin_failed ?? 0,
              };
              sessionStateStoreRef.current.set(messageSessionId, sessionState);

              // Only update React state if current session
              if (!isViewingDifferentSession) {
                setOutreachSummary((prev) => ({
                  ...(prev || {}),
                  total,
                  sent,
                  failed,
                  email_sent: payload.email_sent ?? payload.emailSent ?? prev?.email_sent ?? 0,
                  email_failed: payload.email_failed ?? payload.emailFailed ?? prev?.email_failed ?? 0,
                  whatsapp_sent: payload.whatsapp_sent ?? payload.whatsappSent ?? prev?.whatsapp_sent ?? 0,
                  whatsapp_failed: payload.whatsapp_failed ?? payload.whatsappFailed ?? prev?.whatsapp_failed ?? 0,
                  linkedin_sent: payload.linkedin_sent ?? payload.linkedinSent ?? prev?.linkedin_sent ?? 0,
                  linkedin_failed: payload.linkedin_failed ?? payload.linkedinFailed ?? prev?.linkedin_failed ?? 0,
                }));
              }

              const activity = payload.activity || data?.activity || {};
              const message =
                activity?.message ||
                payload?.message ||
                data?.message ||
                (activity?.candidate_name
                  ? (activity.success === false
                      ? `Failed to send to ${activity.candidate_name}`
                      : `Message sent to ${activity.candidate_name}`)
                  : null);

              if (message && String(message).trim()) {
                // Always update session store first
                const state = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                state.outreachLogs = state.outreachLogs || [];
                const alreadyExists = state.outreachLogs.some((row) => row?.message === message);
                if (!alreadyExists) {
                  state.outreachLogs.push({
                    message: String(message),
                    detail: activity?.error || payload?.error || null,
                    success: activity?.success !== false && status !== 'failed',
                    error: activity?.error || payload?.error || null,
                    timestamp: new Date().toISOString(),
                  });
                  sessionStateStoreRef.current.set(messageSessionId, state);
                }
                
                // Only update React state if current session
                if (!isViewingDifferentSession) {
                  setOutreachLogs((prev) => {
                    const alreadyExistsReact = prev.some((row) => row?.message === message);
                    if (alreadyExistsReact) return prev;
                    return [
                      ...prev,
                      {
                        message: String(message),
                        detail: activity?.error || payload?.error || null,
                        success: activity?.success !== false && status !== 'failed',
                        error: activity?.error || payload?.error || null,
                        timestamp: new Date().toISOString(),
                      },
                    ];
                  });
                }
              }

              // Update session store with completion state
              const finishState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              const isCompleted = status === 'completed' || phase === 'completed' || phase === 'done' || payload?.completed === true;
              if (isCompleted) {
                finishState.outreachSending = false;
                finishState.outreachCompleted = true;
                finishState.outreachPhase = 'done';
              } else {
                finishState.outreachSending = true;
                finishState.outreachPhase = 'sending';
              }
              sessionStateStoreRef.current.set(messageSessionId, finishState);
              
              // Only update React state if current session
              if (!isViewingDifferentSession) {
                if (isCompleted) {
                  setOutreachSending(false);
                  setOutreachCompleted(true);
                  setOutreachPhase('done');
                  
                  // Show completion message in chat
                  const linkedinSentFinal = payload.linkedin_sent ?? payload.linkedinSent ?? 0;
                  const linkedinFailedFinal = payload.linkedin_failed ?? payload.linkedinFailed ?? 0;
                  if (linkedinSentFinal > 0 || linkedinFailedFinal > 0) {
                    const completionMsg = `LinkedIn outreach completed: ${linkedinSentFinal} sent${linkedinFailedFinal > 0 ? `, ${linkedinFailedFinal} failed` : ''}`;
                    setAndPersistSystemMessage([completionMsg], { phase: 'outreach' });
                  }
                } else {
                  setOutreachSending(true);
                  setOutreachPhase('sending');
                }
              }
            } else if (type === 'candidates_update') {
              // Handle incremental candidate updates (when Resume Box, Google, or LinkedIn finishes)
              // This allows UI to show candidates immediately without waiting for all sources
              const payload = data?.data || {};
              const incomingCandidates = Array.isArray(payload?.candidates)
                ? payload.candidates
                : [];
              const source = payload?.source || 'unknown';
              const message = payload?.message || '';
              
              console.log(`[candidates_update] Received from ${source}:`, incomingCandidates.length, 'candidates');
              console.log('[candidates_update] Message:', message);
              
              if (incomingCandidates.length === 0) {
                console.log('[candidates_update] No candidates in update, ignoring');
                return;
              }

              // Helper to merge candidates by unique key
              const mergeCandidates = (prev, incoming) => {
                const keyFor = (c) =>
                  String(
                    c?.id ||
                      c?.candidate_id ||
                      c?.email ||
                      c?.linkedin_profile_url ||
                      c?.linkedin_url ||
                      c?.profile_url ||
                      `${c?.name || ''}|${c?.company || ''}|${c?.location || ''}`
                  ).trim();
                const map = new Map((prev || []).map((c) => [keyFor(c), c]));
                incoming.forEach((c) => {
                  const key = keyFor(c);
                  if (!key) return;
                  const existing = map.get(key) || {};
                  map.set(key, { ...existing, ...c });
                });
                return Array.from(map.values());
              };

              // Count by source type. LinkedIn rows are commonly saved as
              // linkedin_paid, so do not treat only source_type="linkedin" as LinkedIn.
              const normalizeCandidateSourceType = (candidate) =>
                String(candidate?.source_type || candidate?.source || candidate?.platform || '').toLowerCase();
              const isLinkedInCandidateSource = (candidate) => {
                const sourceType = normalizeCandidateSourceType(candidate);
                return sourceType === 'linkedin' || sourceType === 'linkedin_paid' || sourceType === 'linkedin_recruiter';
              };
              const resumeBoxCount = incomingCandidates.filter(c => normalizeCandidateSourceType(c) === 'resume_box').length;
              const googleCount = incomingCandidates.filter(c => normalizeCandidateSourceType(c) === 'google' || normalizeCandidateSourceType(c) === 'google_search').length;
              const linkedinCount = incomingCandidates.filter(isLinkedInCandidateSource).length;
              
              console.log(`[candidates_update] Breakdown - Resume Box: ${resumeBoxCount}, Google: ${googleCount}, LinkedIn: ${linkedinCount}`);

              // Update session store first
              updateSessionState(messageSessionId, (state) => {
                state.sourcing.candidates = mergeCandidates(state.sourcing.candidates || [], incomingCandidates);
                if (resumeBoxCount > 0) {
                  state.resumeBoxCandidateCount = resumeBoxCount;
                  state.sourcing.resumeBoxCandidateCount = resumeBoxCount;
                }
                if (googleCount > 0) {
                  state.googleCandidateCount = googleCount;
                  state.sourcing.googleCandidateCount = googleCount;
                }
                if (linkedinCount > 0) {
                  state.linkedinCandidateCount = linkedinCount;
                  state.sourcing.linkedinCandidateCount = linkedinCount;
                }
                return state;
              });

              // If viewing a different session, just store and return
              if (isViewingDifferentSession) {
                console.log('[candidates_update] Stored for session:', messageSessionId, 'candidates:', incomingCandidates.length);
                return;
              }

              // Update React state for current session - transition to candidates view
              const updatedState = getSessionState(messageSessionId);
              const sessionCandidates = updatedState?.sourcing?.candidates || [];
              
              if (sessionCandidates.length > 0) {
                setCandidates(sessionCandidates);
                setRightPanelMode('candidates');
                console.log('[candidates_update] Transitioned to candidates view with', sessionCandidates.length, 'candidates');
                
                // Update Resume Box candidate count if needed
                if (resumeBoxCount > 0) {
                  setResumeBoxCandidateCount(resumeBoxCount);
                }
                if (googleCount > 0) {
                  setGoogleCandidateCount(googleCount);
                }
                if (linkedinCount > 0) {
                  setLinkedinCandidateCount(linkedinCount);
                  setLinkedinSearchFoundCount(linkedinCount);
                }
                setSourcingRefreshToken((prev) => prev + 1);
              }
            } else if (type === 'linkedin_candidates_complete') {
              const payload = data?.data || {};
              const totalLinkedinCandidates = Number(
                payload.totalCandidates ??
                payload.total_candidates ??
                payload.count ??
                0
              );
              const completionMessage =
                payload.message ||
                data?.message ||
                `LinkedIn extraction complete${totalLinkedinCandidates ? ` - Found ${totalLinkedinCandidates} candidates` : ''}`;

              updateSessionState(messageSessionId, (state) => {
                state.sourcing = state.sourcing || {};
                state.sourcing.started = true;
                state.sourcing.linkedinProcessing = false;
                state.loaders = state.loaders || {};
                state.loaders.linkedinSourcing = false;
                if (Number.isFinite(totalLinkedinCandidates)) {
                  state.linkedinCandidateCount = totalLinkedinCandidates;
                  state.sourcing.linkedinCandidateCount = totalLinkedinCandidates;
                }
                state.sourcing.linkedinLogs = state.sourcing.linkedinLogs || [];
                const exists = state.sourcing.linkedinLogs.some((log) => log.message === completionMessage);
                if (!exists) {
                  state.sourcing.linkedinLogs.push({
                    message: completionMessage,
                    timestamp: new Date().toISOString(),
                    logType: 'info',
                    type: 'info',
                  });
                }
                const stillProcessing =
                  state.sourcing.googleProcessing ||
                  state.sourcing.resumeBoxProcessing;
                if (!stillProcessing) {
                  state.sourcing.complete = true;
                  state.sourcing.started = false;
                }
                return state;
              });

              if (!isViewingDifferentSession) {
                setLinkedinSourcingProcessing(false);
                if (Number.isFinite(totalLinkedinCandidates)) {
                  setLinkedinCandidateCount(totalLinkedinCandidates);
                  setLinkedinSearchFoundCount(totalLinkedinCandidates);
                }
                setLinkedinSourcingLogs((prev) => {
                  const exists = prev.some((log) => log.message === completionMessage);
                  if (exists) return prev;
                  return [
                    ...prev,
                    {
                      message: completionMessage,
                      timestamp: new Date().toISOString(),
                      logType: 'info',
                      type: 'info',
                    },
                  ];
                });

                const curState = sessionStateStoreRef.current.get(messageSessionId);
                const googleStillActive = curState?.sourcing?.googleProcessing || curState?.loaders?.googleSourcing;
                const resumeBoxStillActive = curState?.sourcing?.resumeBoxProcessing || curState?.loaders?.resumeBoxSourcing;
                if (!googleStillActive && !resumeBoxStillActive) {
                  setSourcingComplete(true);
                  setTalentScoutingStarted(false);
                  setRightPanelMode('candidates');
                  removeActiveSourcingSession(messageSessionId || automationSessionId);
                }

                aiAutomationApi.get(`/automation_candidates/all?session_id=${messageSessionId}&limit=50`)
                  .then((fetchResp) => {
                    const dbCandidates = fetchResp?.candidates || fetchResp?.data || [];
                    if (dbCandidates.length > 0) {
                      setCandidates(dbCandidates);
                      setMatchedCandidateCount(dbCandidates.length);
                    }
                  })
                  .catch(() => {});
              }
            } else if (type === 'sourcing_already_in_progress') {
              // Handle sourcing already in progress notification
              console.log('[MainWS] Sourcing already in progress:', data);
              
              const statusMessage = data?.message || 'Candidate sourcing is already in progress for this session.';
              
              // CRITICAL: Update session store FIRST
              updateSessionState(messageSessionId, (state) => {
                state.sourcing.started = true;
                if (data?.google_sourcing?.status === 'in_progress') {
                  state.sourcing.googleProcessing = true;
                }
                if (data?.linkedin_sourcing?.status === 'in_progress') {
                  state.sourcing.linkedinProcessing = true;
                }
                state.chat.systemMessages = [...(state.chat.systemMessages || []), `ℹ️ ${statusMessage}`];
                return state;
              });
              
              // CRITICAL: Only update UI if this is the current session
              if (isViewingDifferentSession) {
                console.log('[sourcing_already_in_progress] Ignoring from different session:', messageSessionId);
                return;
              }
              
              setSystemMessages(prev => [...prev, `ℹ️ ${statusMessage}`]);
              
              // Update sourcing state based on what's in progress
              if (data?.google_sourcing?.status === 'in_progress') {
                setGoogleSourcingProcessing(true);
              }
              if (data?.linkedin_sourcing?.status === 'in_progress') {
                setLinkedinSourcingProcessing(true);
              }
              
              // Ensure we're in sourcing view
              setTalentScoutingStarted(true);
              setRightPanelMode('auto');
            } else if (type === 'sourcing_partial_complete') {
              // Initial selected sources are done. LinkedIn may still be running.
              const payload = data?.data || {};
              const partialCandidates = Array.isArray(payload.candidates) ? payload.candidates : [];
              const linkedinEnabled = payload.linkedin_enabled === true;
              const partialCount = payload.candidate_count || partialCandidates.length;
              const notificationChannels = getSourcingNotificationChannels(
                messageSessionId,
                payload.sourcing_channels || payload.selected_channels || []
              );
              const completedSourcesLabel =
                buildSourcingSourcesLabel(notificationChannels, { fallbackMessage: payload.message }) ||
                'selected sources';

              console.log('[sourcing_partial_complete]', { partialCount, linkedinEnabled, candidatesReceived: partialCandidates.length, session: messageSessionId });

              // Update session store
              updateSessionState(messageSessionId, (state) => {
                state.sourcing.partialComplete = true;
                state.sourcing.linkedinEnabled = linkedinEnabled;
                if (partialCandidates.length > 0) {
                  state.sourcing.candidates = partialCandidates;
                  state.candidates = partialCandidates;
                }
                return state;
              });

              // Release sourcing agent flow lock — user can now use other agents
              // LinkedIn will continue in background without blocking the chat
              // Get search_id from session store if available
              const sessionState = sessionStateStoreRef.current.get(messageSessionId);
              const currentSearchId = sessionState?.searchId || automationSearchId;
              aiAutomationApi.post('/chat', {
                message: '__sourcing_complete__',
                session_id: messageSessionId,
                additional_data: {
                  search_id: currentSearchId,
                  action: 'lifecycle',
                  _internal: 'sourcing_complete_signal',
                },
              }).catch(() => {});

              if (!isViewingDifferentSession) {
                if (partialCount > 0) {
                  // FOUND CANDIDATES: show them immediately, even if another source is still running
                  setSourcingComplete(!linkedinEnabled);
                  setTalentScoutingStarted(linkedinEnabled);
                  setGoogleSourcingProcessing(false);
                  setResumeBoxSourcingProcessing(false);
                  setLinkedinSourcingProcessing(linkedinEnabled);
                  
                  // IMPORTANT: Use candidates directly from the WebSocket message first
                  // This ensures we show candidates immediately without waiting for API
                  if (partialCandidates.length > 0) {
                    setCandidates(partialCandidates);
                    setMatchedCandidateCount(partialCandidates.length);
                  }
                  
                  // Switch to candidates panel immediately and keep sourcing tab active
                  setRightPanelMode('candidates');
                  setActiveHeaderTab('sourcing');
                  
                  // Also fetch from API as backup (may have more complete data)
                  aiAutomationApi.get(`/automation_candidates/all?session_id=${messageSessionId}&limit=50`)
                    .then((fetchResp) => {
                      const dbCandidates = fetchResp?.candidates || fetchResp?.data || [];
                      if (dbCandidates.length > 0) {
                        setCandidates(dbCandidates);
                        setMatchedCandidateCount(dbCandidates.length);
                        setRightPanelMode('candidates');
                      }
                    })
                    .catch((err) => {
                      console.warn('[sourcing_partial_complete] API fetch failed, using WS candidates', err);
                    });
                  
                  if (linkedinEnabled) {
                    setAndPersistSystemMessage(
                      [`Found ${partialCount} candidate${partialCount !== 1 ? 's' : ''} from ${completedSourcesLabel}. LinkedIn search continuing in background...`],
                      { phase: 'lifecycle', skipPersist: true }
                    );
                  } else {
                    setAndPersistSystemMessage(
                      [`${completedSourcesLabel} completed! Found ${partialCount} candidate${partialCount !== 1 ? 's' : ''}.`],
                      { phase: 'lifecycle', skipPersist: true }
                    );
                  }
                } else if (linkedinEnabled) {
                  // ZERO CANDIDATES BUT LINKEDIN RUNNING: Keep sourcing panel active to show logs
                  // CRITICAL: Do NOT mark sourcing complete, do NOT move away from sourcing panel
                  // Keep the user viewing the sourcing logs while LinkedIn continues in background
                  setTalentScoutingStarted(true);  // Keep sourcing active
                  setRightPanelMode('sourcing');   // Stay on sourcing panel to show LinkedIn logs
                  setGoogleSourcingProcessing(false);  // Google done
                  setLinkedinSourcingProcessing(true); // LinkedIn still running
                  setResumeBoxSourcingProcessing(false); // ResumeBox done
                  
                  setAndPersistSystemMessage(
                    [`No candidates found from ${completedSourcesLabel}. LinkedIn search still in progress...`],
                    { phase: 'lifecycle', skipPersist: true }
                  );
                } else {
                  // ZERO CANDIDATES AND NO LINKEDIN: Wait for sourcing_complete to show JD
                  setSourcingComplete(true);
                  setTalentScoutingStarted(false);
                }
              }

            } else if (type === 'sourcing_complete' || type === 'source_complete') {
              const payload = data?.data || {};
              const incomingCandidates = Array.isArray(payload?.candidates)
                ? payload.candidates
                : [];
              const totalGoogleCandidates = Number(
                payload?.totalGoogleCandidates ??
                payload?.total_google_candidates ??
                payload?.stats?.google_count ??
                0
              );
              const totalLinkedinCandidates = Number(
                payload?.totalLinkedinCandidates ??
                payload?.totalLinkedInCandidates ??
                payload?.total_linkedin_candidates ??
                payload?.stats?.linkedin_count ??
                0
              );
              const totalResumeBoxCandidates = Number(
                payload?.totalResumeBoxCandidates ??
                payload?.total_resume_box_candidates ??
                payload?.stats?.resume_box_count ??
                0
              );
              const completionPlatform = normalizePlatformName(
                data?.platform || payload?.platform || data?.data?.platform || ''
              );
              const priorSourcingState = sessionStateStoreRef.current.get(messageSessionId)?.sourcing || {};
              const otherChannelsStillProcessing = !!(
                priorSourcingState.googleProcessing || priorSourcingState.linkedinProcessing
              );
              const isPrematureCompletion =
                (completionPlatform === 'ResumeBox' || !completionPlatform) &&
                incomingCandidates.length === 0 &&
                otherChannelsStillProcessing;

              // Helper to merge candidates by unique key
              const mergeCandidatesForComplete = (prev, incoming) => {
                const keyFor = (c) =>
                  String(
                    c?.id ||
                      c?.candidate_id ||
                      c?.email ||
                      c?.linkedin_profile_url ||
                      c?.linkedin_url ||
                      c?.profile_url ||
                      `${c?.name || ''}|${c?.company || ''}|${c?.location || ''}`
                  ).trim();
                const map = new Map((prev || []).map((c) => [keyFor(c), c]));
                incoming.forEach((c) => {
                  const key = keyFor(c);
                  if (!key) return;
                  const existing = map.get(key) || {};
                  map.set(key, { ...existing, ...c });
                });
                return Array.from(map.values());
              };

              if (isPrematureCompletion) {
                updateSessionState(messageSessionId, (state) => {
                  state.sourcing.complete = false;
                  state.sourcing.started = true;
                  state.sourcing.resumeBoxProcessing = false;
                  return state;
                });

                // NOTE: Sourcing state comes from DB/file API — no localStorage persistence needed
              } else {
                // CRITICAL: Always update session store first
                updateSessionState(messageSessionId, (state) => {
                  state.sourcing.complete = true;
                  state.sourcing.googleProcessing = false;
                  state.sourcing.linkedinProcessing = false;
                  state.sourcing.resumeBoxProcessing = false;
                  state.sourcing.started = false;
                  if (incomingCandidates.length > 0) {
                    state.sourcing.candidates = mergeCandidatesForComplete(state.sourcing.candidates || [], incomingCandidates);
                  }
                  if (Number.isFinite(totalGoogleCandidates)) {
                    state.googleCandidateCount = totalGoogleCandidates;
                    state.sourcing.googleCandidateCount = totalGoogleCandidates;
                  }
                  if (Number.isFinite(totalLinkedinCandidates)) {
                    state.linkedinCandidateCount = totalLinkedinCandidates;
                    state.sourcing.linkedinCandidateCount = totalLinkedinCandidates;
                  }
                  if (Number.isFinite(totalResumeBoxCandidates)) {
                    state.resumeBoxCandidateCount = totalResumeBoxCandidates;
                    state.sourcing.resumeBoxCandidateCount = totalResumeBoxCandidates;
                  }
                  return state;
                });
                
                // Persist final sourcing state with logs to localStorage (don't remove it)
                // This allows logs to be restored on page refresh even after sourcing completes
                // NOTE: Sourcing complete state comes from DB/file API — no localStorage persistence needed

                // Clear active sourcing session regardless
                removeActiveSourcingSession(messageSessionId);
              }

              // CRITICAL: Only update React state if viewing this session
              if (isViewingDifferentSession) {
                console.log('[sourcing_complete] Stored for session:', messageSessionId, 'candidates:', incomingCandidates.length);
              } else if (isPrematureCompletion) {
                setSourcingComplete(false);
                setTalentScoutingStarted(true);
                setResumeBoxSourcingProcessing(false);
                setRightPanelMode('sourcing');
                setActiveHeaderTab('sourcing');
              } else {
                setSourcingComplete(true);
                removeActiveSourcingSession(messageSessionId || automationSessionId);
                setGoogleSourcingProcessing(false);
                setLinkedinSourcingProcessing(false);
                setResumeBoxSourcingProcessing(false);
                setTalentScoutingStarted(false);
                if (Number.isFinite(totalGoogleCandidates)) {
                  setGoogleCandidateCount(totalGoogleCandidates);
                }
                if (Number.isFinite(totalLinkedinCandidates)) {
                  setLinkedinCandidateCount(totalLinkedinCandidates);
                  setLinkedinSearchFoundCount(totalLinkedinCandidates);
                }
                if (Number.isFinite(totalResumeBoxCandidates)) {
                  setResumeBoxCandidateCount(totalResumeBoxCandidates);
                }
                setSourcingRefreshToken((prev) => prev + 1);

                // Clear the LangGraph agent banner and show exit transition pill + toast
                const exitingAgent = agentStageLabel || 'Candidate Sourcing';
                showToast(`Exited: ${exitingAgent}`, 'info', 2000);
                setAgentStageLabel('');
                setAgentStageCancellable(false);
                setLastResponseType('sourcing_complete');

                // Show "Exited" transition pill in chat
                setSystemMessages((prev) => [
                  ...prev,
                  `__AGENT_TRANSITION__Exited ${exitingAgent}`,
                ]);
                setSystemMessageToken((prev) => prev + 1);

                // Persist exit transition to DB
                try {
                  aiAutomationApi.post(`/sessions/${messageSessionId}/messages`, {
                    message_type: 'assistant',
                    content: `Exited ${exitingAgent}`,
                    message_role: 'assistant',
                    metadata: { type: 'agent_transition', agent: exitingAgent, action: 'exited' },
                  }).catch(() => {});
                } catch (_) {}

                // Fetch ALL candidates from DB (includes resume_box + platform-sourced)
                // Retry once after 2s if first fetch returns 0 (ResumeBox may still be saving)
                const fetchAllCandidates = async () => {
                  const fetchFromDB = async () => {
                    try {
                      if (!messageSessionId) return [];
                      const searchResponse = await aiAutomationApi.get(`/automation_candidates/all?session_id=${messageSessionId}&limit=50`);
                      return searchResponse?.candidates || searchResponse?.data || [];
                    } catch (fetchErr) {
                      console.warn('[sourcing_complete] Failed to fetch candidates from DB:', fetchErr);
                      return [];
                    }
                  };

                  let dbCandidates = await fetchFromDB();

                  // If 0 from DB, check session store (WS payload)
                  if (dbCandidates.length === 0) {
                    const updatedState = getSessionState(messageSessionId);
                    const sessionCandidates = updatedState?.sourcing?.candidates || [];
                    if (sessionCandidates.length > 0) {
                      dbCandidates = sessionCandidates;
                    }
                  }

                  // Still 0 — wait 2s and retry (ResumeBox candidates may still be saving to DB)
                  if (dbCandidates.length === 0) {
                    await new Promise(r => setTimeout(r, 2000));
                    dbCandidates = await fetchFromDB();
                  }

                  if (dbCandidates.length > 0) {
                    updateSessionState(messageSessionId, (state) => {
                      state.sourcing.candidates = dbCandidates;
                      return state;
                    });
                    if (currentViewedSessionRef.current === messageSessionId) {
                      setCandidates(dbCandidates);
                      setMatchedCandidateCount(dbCandidates.length);
                      setRightPanelMode('candidates');
                      setSourcingRefreshToken((prev) => prev + 1);
                      setAndPersistSystemMessage([
                        buildSourcingCompletedMessage(messageSessionId, dbCandidates.length, {
                          candidates: dbCandidates,
                          stats: payload?.stats,
                          fallbackMessage: payload?.message,
                          suffix: ' Check the result panel.',
                        })
                      ], { phase: 'lifecycle' });
                    }
                  } else {
                    // No candidates from any source (Google, LinkedIn, ResumeBox all returned 0)
                    if (currentViewedSessionRef.current === messageSessionId) {
                      const sourceLabel = getCompletedSourcingSourcesLabel(messageSessionId, {
                        stats: payload?.stats,
                        fallbackMessage: payload?.message,
                      });
                      setRightPanelMode('jd');
                      setAndPersistSystemMessage([
                        `${sourceLabel} completed. No matching candidates found.`
                      ], { phase: 'lifecycle' });
                    }
                  }
                };
                fetchAllCandidates();

                // Send __sourcing_complete__ signal to backend to update LangGraph state
                const wsSearchId = getSafeSearchId(messageSessionId) || (automationSearchId?.startsWith('search_') ? automationSearchId : '');
                try {
                  aiAutomationApi.post('/chat', {
                    message: '__sourcing_complete__',
                    session_id: messageSessionId,
                    cid: getCid(),
                    additional_data: {
                      search_id: wsSearchId,
                      action: 'lifecycle',
                      _internal: 'sourcing_complete_signal',
                    },
                  }).catch(() => {});
                } catch (_) {}
              }
            } else if (type === 'sourcing_already_completed') {
              // Handle sourcing already completed - ask user if they want to re-source
              console.log('[MainWS] Sourcing already completed, asking for confirmation:', data);
              
              const statusMessage = data?.message || 'Candidate sourcing has already been completed for this session. Do you want to search for more candidates?';
              
              // CRITICAL: Update session store FIRST
              updateSessionState(messageSessionId, (state) => {
                state.sourcing.complete = true;
                state.sourcing.started = false;
                state.sourcing.googleProcessing = false;
                state.sourcing.linkedinProcessing = false;
                state.sourcing.resumeBoxProcessing = false;
                state.sourcing.showResourcingConfirm = true;
                state.chat.systemMessages = [...(state.chat.systemMessages || []), `ℹ️ ${statusMessage}`];
                return state;
              });
              
              // NOTE: Sourcing state comes from DB/file API — no localStorage persistence needed
              
              
              // CRITICAL: Only update UI if this is the current session
              if (isViewingDifferentSession) {
                console.log('[sourcing_already_completed] Ignoring from different session:', messageSessionId);
                return;
              }
              
              // Reset sourcing states since we're just asking for confirmation
              setTalentScoutingStarted(false);
              setGoogleSourcingProcessing(false);
              setLinkedinSourcingProcessing(false);
              setResumeBoxSourcingProcessing(false);
              removeActiveSourcingSession(messageSessionId);
              
              // Show confirmation dialog
              setShowResourcingConfirm(true);
              
              // Add system message asking for confirmation
              setSystemMessages(prev => [...prev, `ℹ️ ${statusMessage}`]);
            } else if (type === 'search_phase_update') {
              // Handle detailed automation logs from LinkedIn and Naukri
              const source = data?.source || data?.data?.source;
              const logMessage = data?.data?.log_message || data?.log_message || data?.data?.message || data?.message;
              const logType = data?.data?.log_type || data?.log_type || 'info';
              const phase = data?.phase || data?.data?.phase;
              const platform = data?.data?.platform || data?.platform;
              const payloadData = data?.data || {};

              // Handle sourcing candidate payload updates (Google/LinkedIn) in real-time.
              if (phase === 'source_candidates') {
                const normalizedSource = normalizePlatformName(source || platform || '');
                const incomingCandidates = Array.isArray(payloadData?.candidates)
                  ? payloadData.candidates
                  : [];
                const progressStatus = payloadData?.search_progress?.status || '';
                const statusValue = (data?.status || '').toLowerCase();
                const isFailureStatus =
                  statusValue === 'failed' ||
                  statusValue === 'error' ||
                  (typeof logMessage === 'string' &&
                    (logMessage.toLowerCase().includes('failed') || logMessage.toLowerCase().includes('error')));
                const logMessageLower = String(logMessage || '').toLowerCase();
                const isLinkedinFinalCompletion =
                  normalizedSource === 'LinkedIn' &&
                  statusValue === 'completed' &&
                  (
                    incomingCandidates.length > 0 ||
                    logMessageLower.includes('extraction complete') ||
                    logMessageLower.includes('valid candidate') ||
                    logMessageLower.includes('linkedin search completed') ||
                    logMessageLower.includes('linkedin profile search completed') ||
                    logMessageLower.includes('profile search automation completed') ||
                    logMessageLower.includes('linkedin sourcing completed')
                  );

                const mergeCandidates = (prev, incoming) => {
                  if (!Array.isArray(incoming) || incoming.length === 0) return prev;
                  const keyFor = (c) =>
                    String(
                      c?.id ||
                        c?.candidate_id ||
                        c?.email ||
                        c?.linkedin_profile_url ||
                        c?.linkedin_url ||
                        c?.profile_url ||
                        `${c?.name || ''}|${c?.company || ''}|${c?.location || ''}`
                    ).trim();
                  const map = new Map((prev || []).map((c) => [keyFor(c), c]));
                  incoming.forEach((c) => {
                    const key = keyFor(c);
                    if (!key) return;
                    const existing = map.get(key) || {};
                    map.set(key, { ...existing, ...c });
                  });
                  return Array.from(map.values());
                };

                // Always update session store first
                const sessionState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                if (incomingCandidates.length > 0) {
                  sessionState.candidates = mergeCandidates(sessionState.candidates || [], incomingCandidates);
                  sessionState.talentScoutingStarted = true;
                  sessionState.matchedCandidateCount = sessionState.candidates.length;
                  sessionStateStoreRef.current.set(messageSessionId, sessionState);
                }

                // Only update React state if current session
                if (!isViewingDifferentSession) {
                  if (incomingCandidates.length > 0) {
                    setTalentScoutingStarted(true);
                    // Switch to candidates view when candidates arrive
                    // CandidateResults shows logs via showSourcingLogs prop while sourcing is in progress
                    setRightPanelMode('candidates');
                    // CRITICAL: Get candidates from session store to avoid cross-session contamination
                    // Don't merge with React state `prev` which may contain candidates from another session
                    const sessionCandidates = sessionState.candidates || [];
                    setCandidates(sessionCandidates);
                    setMatchedCandidateCount(sessionCandidates.length);
                  }
                } else {
                  console.log('[source_candidates] Candidates stored for session:', messageSessionId, 'count:', incomingCandidates.length);
                }

                if (normalizedSource === 'Google') {
                  // Update session store first
                  const googleState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                  googleState.loaders = googleState.loaders || {};
                  googleState.sourcing = googleState.sourcing || {};
                  if (statusValue === 'in_progress') {
                    googleState.loaders.googleSourcing = true;
                    googleState.sourcing.googleProcessing = true;
                  }
                  if (incomingCandidates.length > 0 || statusValue === 'completed' || isFailureStatus) {
                    googleState.loaders.googleSourcing = false;
                    googleState.sourcing.googleProcessing = false;
                  }
                  const googleCount =
                    payloadData?.totalGoogleCandidates ??
                    payloadData?.totalCandidates ??
                    payloadData?.total_candidates ??
                    incomingCandidates.length;
                  if (Number.isFinite(Number(googleCount))) {
                    googleState.googleCandidateCount = Number(googleCount);
                  }
                  sessionStateStoreRef.current.set(messageSessionId, googleState);
                  
                  // Only update React state if current session
                  if (!isViewingDifferentSession) {
                    const logMessage = cleanLogMessage(payloadData?.logMessage || payloadData?.message || '');
                    if (statusValue === 'in_progress') {
                      setGoogleSourcingProcessing(true);
                    }
                    if (incomingCandidates.length > 0 || (statusValue === 'completed' && !logMessage.toLowerCase().includes('discovery')) || isFailureStatus) {
                      setGoogleSourcingProcessing(false);
                      // When Google sourcing completes, switch to candidates if no other channel is processing
                      // Use session store (ref-based, always current) instead of closure variable
                      // which may be stale (linkedinSourcingProcessing is not in effect dependencies)
                      if (statusValue === 'completed' && !logMessage.toLowerCase().includes('discovery')) {
                        const curState = sessionStateStoreRef.current.get(messageSessionId);
                        const linkedinStillActive = curState?.sourcing?.linkedinProcessing || curState?.loaders?.linkedinSourcing;
                        if (!linkedinStillActive) {
                          setSourcingComplete(true);
                          setRightPanelMode('candidates');
                          // CRITICAL: Also update session store so loadSessionData race condition guard works
                          // Without this, loadSessionData may override rightPanelMode back to 'auto'
                          updateSessionState(messageSessionId, (state) => {
                            state.sourcing.complete = true;
                            state.sourcing.googleProcessing = false;
                            state.sourcing.linkedinProcessing = false;
                            return state;
                          });
                        }
                      }
                    }
                    if (Number.isFinite(Number(googleCount))) {
                      setGoogleCandidateCount(Number(googleCount));
                    }
                    // Only add logs that should be shown in chat (not progress-only updates)
                    const showInChat = payloadData?.show_in_chat !== false && !payloadData?.is_progress_update;
                    if ((progressStatus || logMessage) && showInChat) {
                      const msg = progressStatus || logMessage;
                      const logEntry = {
                        message: msg,
                        timestamp: new Date().toISOString(),
                        logType: isFailureStatus ? 'error' : (logType || 'info'),
                        type: isFailureStatus ? 'error' : (logType || 'info'),
                      };
                      
                      // CRITICAL: Update session store first for persistence
                      updateSessionState(messageSessionId, (state) => {
                        const existingLogs = state.sourcing.googleLogs || [];
                        const exists = existingLogs.some((log) => log.message === msg);
                        if (!exists) {
                          state.sourcing.googleLogs = [...existingLogs, logEntry];
                        }
                        return state;
                      });
                      
                      setGoogleSourcingLogs((prev) => {
                        const exists = prev.some((log) => log.message === msg);
                        if (exists) return prev;
                        return [...prev, logEntry];
                      });
                      
                      // Also add to system messages so it appears in chat (real-time)
                      setSystemMessages((prev) => {
                        const exists = prev.some((m) => m === msg || m?.message === msg);
                        if (exists) return prev;
                        return [...prev, msg];
                      });
                      setSystemMessageToken((prev) => prev + 1);
                    }
                  }
                } else if (normalizedSource === 'LinkedIn') {
                  // Update session store first
                  const linkedinState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                  linkedinState.loaders = linkedinState.loaders || {};
                  linkedinState.sourcing = linkedinState.sourcing || {};
                  const isLinkedinProgressMilestone =
                    statusValue === 'in_progress' ||
                    (statusValue === 'completed' && !isLinkedinFinalCompletion && !isFailureStatus);
                  if (isLinkedinProgressMilestone) {
                    linkedinState.loaders.linkedinSourcing = true;
                    linkedinState.sourcing.linkedinProcessing = true;
                  }
                  // Stop LinkedIn loader when we got results OR LinkedIn completed/failed.
                  if (isLinkedinFinalCompletion || isFailureStatus) {
                    linkedinState.loaders.linkedinSourcing = false;
                    linkedinState.sourcing.linkedinProcessing = false;
                  }
                  const linkedinCount =
                    payloadData?.totalLinkedInCandidates ??
                    payloadData?.totalCandidates ??
                    payloadData?.total_candidates ??
                    incomingCandidates.length;
                  if (Number.isFinite(Number(linkedinCount))) {
                    linkedinState.linkedinCandidateCount = Number(linkedinCount);
                  }
                  sessionStateStoreRef.current.set(messageSessionId, linkedinState);
                  
                  // Only update React state if current session
                  if (!isViewingDifferentSession) {
                    const logMessage = cleanLogMessage(payloadData?.logMessage || payloadData?.message || '');
                    if (isLinkedinProgressMilestone) {
                      setLinkedinSourcingProcessing(true);
                    }
                    if (isLinkedinFinalCompletion || isFailureStatus) {
                      setLinkedinSourcingProcessing(false);
                      // When LinkedIn sourcing completes, switch to candidates if no other channel is processing
                      // Use session store (ref-based, always current) instead of closure variable
                      if (isLinkedinFinalCompletion) {
                        const curState = sessionStateStoreRef.current.get(messageSessionId);
                        const googleStillActive = curState?.sourcing?.googleProcessing || curState?.loaders?.googleSourcing;
                        if (!googleStillActive) {
                          setSourcingComplete(true);
                          setRightPanelMode('candidates');
                          // CRITICAL: Also update session store so loadSessionData race condition guard works
                          updateSessionState(messageSessionId, (state) => {
                            state.sourcing.complete = true;
                            state.sourcing.googleProcessing = false;
                            state.sourcing.linkedinProcessing = false;
                            return state;
                          });
                        }
                      }
                    }
                    if (Number.isFinite(Number(linkedinCount))) {
                      setLinkedinCandidateCount(Number(linkedinCount));
                    }
                    // Only add logs that should be shown in chat (not progress-only updates)
                    const showInChat = payloadData?.show_in_chat !== false && !payloadData?.is_progress_update;
                    if ((progressStatus || logMessage) && showInChat) {
                      const msg = progressStatus || logMessage;
                      const logEntry = {
                        message: msg,
                        timestamp: new Date().toISOString(),
                        logType: isFailureStatus ? 'error' : (logType || 'info'),
                        type: isFailureStatus ? 'error' : (logType || 'info'),
                      };
                      
                      // CRITICAL: Update session store first for persistence
                      updateSessionState(messageSessionId, (state) => {
                        const existingLogs = state.sourcing.linkedinLogs || [];
                        const exists = existingLogs.some((log) => log.message === msg);
                        if (!exists) {
                          state.sourcing.linkedinLogs = [...existingLogs, logEntry];
                        }
                        return state;
                      });
                      
                      setLinkedinSourcingLogs((prev) => {
                        const exists = prev.some((log) => log.message === msg);
                        if (exists) return prev;
                        return [...prev, logEntry];
                      });
                      
                      // Also add to system messages so it appears in chat (real-time)
                      setSystemMessages((prev) => {
                        const exists = prev.some((m) => m === msg || m?.message === msg);
                        if (exists) return prev;
                        return [...prev, msg];
                      });
                      setSystemMessageToken((prev) => prev + 1);
                    }
                  }
                }
              }

              // Handle outreach logs sent as search_phase_update from backend
              if (
                (typeof phase === 'string' && phase.toLowerCase().includes('outreach')) ||
                (typeof source === 'string' && source.toLowerCase().includes('outreach')) ||
                (typeof platform === 'string' && platform.toLowerCase().includes('outreach')) ||
                (typeof logMessage === 'string' && logMessage.toLowerCase().includes('outreach'))
              ) {
                if (logMessage && logMessage.trim()) {
                  // Always update session store first
                  const outreachState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                  outreachState.outreachPhase = 'sending';
                  outreachState.outreachSending = true;
                  outreachState.outreachLogs = outreachState.outreachLogs || [];
                  const alreadyExists = outreachState.outreachLogs.some((row) => row?.message === logMessage);
                  if (!alreadyExists) {
                    outreachState.outreachLogs.push({
                      message: logMessage,
                      success: logType !== 'error',
                      error: logType === 'error' ? logMessage : null,
                      timestamp: new Date().toISOString(),
                    });
                  }
                  sessionStateStoreRef.current.set(messageSessionId, outreachState);
                  
                  // Only update React state if current session
                  if (!isViewingDifferentSession) {
                    setOutreachPhase('sending');
                    setOutreachSending(true);
                    setOutreachLogs((prev) => {
                      const alreadyExistsReact = prev.some((row) => row?.message === logMessage);
                      if (alreadyExistsReact) return prev;
                      return [
                        ...prev,
                        {
                          message: logMessage,
                          success: logType !== 'error',
                          error: logType === 'error' ? logMessage : null,
                          timestamp: new Date().toISOString(),
                        },
                      ];
                    });
                  }
                }
                return;
              }
              
              // Only process log messages (not status updates)
              if (source === 'log' && logMessage) {
                // Skip empty log messages
                if (!logMessage.trim()) {
                  return;
                }
                
                // Skip JD creation / chat messages that mention platform names
                // These should NOT be stored as job posting logs
                const lowerMsg = logMessage.toLowerCase();
                if (lowerMsg.includes('has been created') && lowerMsg.includes('successfully') && lowerMsg.includes('jd for')) {
                  return;
                }
                if (lowerMsg.includes('next step:') && (lowerMsg.includes('source candidates') || lowerMsg.includes('post job'))) {
                  return;
                }
                // Skip agent activation / promotional messages
                if (lowerMsg.includes('starting **job posting**') || lowerMsg.includes('starting job posting')) {
                  if (lowerMsg.includes('available platforms') || lowerMsg.includes('guide you through')) {
                    return;
                  }
                }
                if (lowerMsg.includes('ready to post the job for') && lowerMsg.includes('available platforms')) {
                  return;
                }
                
                // Determine platform - prioritize explicit platform field from backend
                let detectedPlatform = null;
                const messageLower = logMessage.toLowerCase();
                
                // FIRST: Check explicit platform field (most reliable when provided by backend)
                if (platform) {
                  detectedPlatform = platform;
                }
                // SECOND: Check message content for platform keywords
                else if (messageLower.includes('linkedin') || messageLower.includes('linkedin.com')) {
                  detectedPlatform = 'LinkedIn';
                } else if (messageLower.includes('naukri') || messageLower.includes('naukri.com') || messageLower.includes('hiring.naukri')) {
                  detectedPlatform = 'Naukri';
                }
                // THIRD: Check phase
                else if (phase === 'linkedin') {
                  detectedPlatform = 'LinkedIn';
                } else if (phase === 'naukri') {
                  detectedPlatform = 'Naukri';
                } else if (phase === 'job_posting') {
                  // job_posting phase - check message content, otherwise skip to avoid wrong assignment
                  if (messageLower.includes('linkedin')) {
                    detectedPlatform = 'LinkedIn';
                  } else if (messageLower.includes('naukri')) {
                    detectedPlatform = 'Naukri';
                  }
                  // Don't assign if unclear - skip this log
                } else if (phase === 'source_candidates') {
                  // source_candidates phase - prioritize explicit platform field, then check message content
                  if (platform) {
                    detectedPlatform = normalizePlatformName(platform);
                  } else if (messageLower.includes('google') || messageLower.includes('searching google')) {
                    detectedPlatform = 'Google';
                  } else if (messageLower.includes('linkedin') || messageLower.includes('linkedin.com') || messageLower.includes('linkedin login')) {
                    detectedPlatform = 'LinkedIn';
                  } else if (messageLower.includes('resume box') || messageLower.includes('resumebox') || messageLower.includes('local resume') || messageLower.includes('faiss') || messageLower.includes('matching resumes')) {
                    detectedPlatform = 'ResumeBox';
                  } else {
                    // Default to LinkedIn for source_candidates if unclear
                    detectedPlatform = 'LinkedIn';
                  }
                }
                
                // Handle sourcing logs (source_candidates phase)
                if (phase === 'source_candidates' && (detectedPlatform === 'Google' || detectedPlatform === 'LinkedIn' || detectedPlatform === 'ResumeBox')) {
                  const logEntry = {
                    message: logMessage,
                    timestamp: new Date().toISOString(),
                    logType: logType || 'info',
                    type: logType || 'info'
                  };
                  
                  // Helper to persist sourcing state to localStorage for refresh restoration
                  // NOTE: Sourcing state comes from DB/file API — localStorage persistence is disabled
                  const persistSourcingToLocalStorage = () => {}; // no-op: logs come from file API
                  
                  // Always update session store first
                  const srcState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                  
                  if (detectedPlatform === 'Google') {
                    srcState.googleSourcingLogs = srcState.googleSourcingLogs || [];
                    const exists = srcState.googleSourcingLogs.some(log => log.message === logMessage);
                    if (!exists) {
                      srcState.googleSourcingLogs.push(logEntry);
                    }
                    srcState.loaders = srcState.loaders || {};
                    srcState.loaders.googleSourcing = true;
                    srcState.sourcing = srcState.sourcing || {};
                    srcState.sourcing.googleLogs = srcState.googleSourcingLogs;
                    srcState.sourcing.googleProcessing = true;
                    srcState.sourcing.started = true;
                    
                    // Extract candidate count from message if available. Final combined
                    // summaries include "Total N" first, so prefer the channel-specific
                    // breakdown when present.
                    const googleSpecificCountMatch =
                      logMessage.match(/(?:Online\s*Sourcing|Google)\s*:\s*(\d+)/i) ||
                      logMessage.match(/(\d+)\s*(?:profiles?|candidates?)\s*(?:from|found from)\s*(?:google|online sourcing)/i);
                    const countMatch = googleSpecificCountMatch || logMessage.match(/(\d+)\s*(?:profiles?|candidates?|found)/i);
                    if (countMatch) {
                      srcState.googleCandidateCount = parseInt(countMatch[1], 10);
                    }
                    sessionStateStoreRef.current.set(messageSessionId, srcState);
                    
                    // Persist to localStorage for refresh restoration
                    persistSourcingToLocalStorage(messageSessionId, srcState);
                    
                    // Only update React state if current session
                    if (!isViewingDifferentSession) {
                      setGoogleSourcingLogs((prev) => {
                        const existsReact = prev.some(log => log.message === logMessage);
                        return existsReact ? prev : [...prev, logEntry];
                      });
                      setGoogleSourcingProcessing(true);
                      
                      if (countMatch) {
                        setGoogleCandidateCount(parseInt(countMatch[1], 10));
                      }
                      
                      // Track "search found" count when scanning completes
                      const scanCompleteMatch = logMessage.match(/scanning completed.*?(\d+)\s*profiles?\s*found/i);
                      if (scanCompleteMatch) {
                        setGoogleSearchFoundCount(parseInt(scanCompleteMatch[1]));
                      }
                      
                      // Track enrichment completion count
                      const enrichmentMatch = logMessage.match(/enrichment completed.*?(\d+)\s*profiles?\s*enriched/i);
                      if (enrichmentMatch) {
                        setEnrichedCandidateCount(prev => prev + parseInt(enrichmentMatch[1]));
                      }

                      // Show sourcing log in chat (real-time)
                      const showInChatGoogle = payloadData?.show_in_chat !== false && !payloadData?.is_progress_update;
                      if (showInChatGoogle) {
                        setSystemMessages((prev) => {
                          const exists = prev.some((m) => m === logMessage || m?.message === logMessage);
                          return exists ? prev : [...prev, logMessage];
                        });
                        setSystemMessageToken((prev) => prev + 1);
                      }
                    }
                  } else if (detectedPlatform === 'LinkedIn') {
                    srcState.linkedinSourcingLogs = srcState.linkedinSourcingLogs || [];
                    const exists = srcState.linkedinSourcingLogs.some(log => log.message === logMessage);
                    if (!exists) {
                      srcState.linkedinSourcingLogs.push(logEntry);
                    }
                    srcState.loaders = srcState.loaders || {};
                    srcState.loaders.linkedinSourcing = true;
                    srcState.sourcing = srcState.sourcing || {};
                    srcState.sourcing.linkedinLogs = srcState.linkedinSourcingLogs;
                    srcState.sourcing.linkedinProcessing = true;
                    srcState.sourcing.started = true;
                    
                    // Extract candidate count from message if available. Final combined
                    // summaries include "Total N" first, so prefer "LinkedIn: N".
                    const linkedinSpecificCountMatch =
                      logMessage.match(/LinkedIn\s*:\s*(\d+)/i) ||
                      logMessage.match(/(\d+)\s*(?:profiles?|candidates?)\s*(?:from|found from)\s*linkedin/i);
                    const countMatch = linkedinSpecificCountMatch || logMessage.match(/(\d+)\s*(?:profiles?|candidates?|found)/i);
                    if (countMatch) {
                      srcState.linkedinCandidateCount = parseInt(countMatch[1], 10);
                    }
                    sessionStateStoreRef.current.set(messageSessionId, srcState);
                    
                    // Persist to localStorage for refresh restoration
                    persistSourcingToLocalStorage(messageSessionId, srcState);
                    
                    // Only update React state if current session
                    if (!isViewingDifferentSession) {
                      setLinkedinSourcingLogs((prev) => {
                        const existsReact = prev.some(log => log.message === logMessage);
                        return existsReact ? prev : [...prev, logEntry];
                      });
                      setLinkedinSourcingProcessing(true);
                      
                      if (countMatch) {
                        setLinkedinCandidateCount(parseInt(countMatch[1], 10));
                      }
                      
                      // Track "search found" count
                      const foundMatch = logMessage.match(/found\s*(\d+)\s*candidates?\s*from\s*linkedin/i);
                      if (foundMatch) {
                        setLinkedinSearchFoundCount(parseInt(foundMatch[1]));
                      }
                      
                      // Track enrichment completion count
                      const enrichmentMatch = logMessage.match(/enrichment completed.*?(\d+)\s*profiles?\s*enriched/i);
                      if (enrichmentMatch) {
                        setEnrichedCandidateCount(prev => prev + parseInt(enrichmentMatch[1]));
                      }

                      // Show sourcing log in chat (real-time)
                      const showInChatLinkedin = payloadData?.show_in_chat !== false && !payloadData?.is_progress_update;
                      if (showInChatLinkedin) {
                        setSystemMessages((prev) => {
                          const exists = prev.some((m) => m === logMessage || m?.message === logMessage);
                          return exists ? prev : [...prev, logMessage];
                        });
                        setSystemMessageToken((prev) => prev + 1);
                      }
                    }

                    // Fallback for flows where backend sends OTP request via sourcing log text.
                    const isOtpRequestedByLog =
                      messageLower.includes('otp') ||
                      messageLower.includes('verification code') ||
                      messageLower.includes('one-time') ||
                      messageLower.includes('2fa') ||
                      messageLower.includes('two-factor');
                    if (
                      isOtpRequestedByLog &&
                      !sourcingOtpUiRef.current.submitted &&
                      !sourcingOtpUiRef.current.promptShown
                    ) {
                      sourcingOtpUiRef.current.promptShown = true;
                      setSystemMessages([
                        '[Sourcing Candidates] LinkedIn requires OTP verification. Please enter the 6-digit code sent to your phone.',
                      ]);
                    }
                  } else if (detectedPlatform === 'ResumeBox') {
                    srcState.resumeBoxSourcingLogs = srcState.resumeBoxSourcingLogs || [];
                    const exists = srcState.resumeBoxSourcingLogs.some(log => log.message === logMessage);
                    if (!exists) {
                      srcState.resumeBoxSourcingLogs.push(logEntry);
                    }
                    srcState.loaders = srcState.loaders || {};
                    srcState.loaders.resumeBoxSourcing = true;
                    srcState.sourcing = srcState.sourcing || {};
                    srcState.sourcing.resumeBoxLogs = srcState.resumeBoxSourcingLogs;
                    srcState.sourcing.resumeBoxProcessing = true;
                    srcState.sourcing.started = true;
                    
                    // Extract candidate count from message if available. Final combined
                    // summaries include "Total N" first, so prefer "Resume Box: N".
                    const resumeBoxSpecificCountMatch =
                      logMessage.match(/Resume\s*Box\s*:\s*(\d+)/i) ||
                      logMessage.match(/(\d+)\s*(?:profiles?|candidates?|resumes?)\s*(?:from|found from)\s*(?:resume box|resumebox|resume library)/i);
                    const countMatch = resumeBoxSpecificCountMatch || logMessage.match(/(\d+)\s*(?:profiles?|candidates?|found|matched|resumes?)/i);
                    if (countMatch) {
                      srcState.resumeBoxCandidateCount = parseInt(countMatch[1], 10);
                    }
                    sessionStateStoreRef.current.set(messageSessionId, srcState);
                    
                    // Persist to localStorage for refresh restoration
                    persistSourcingToLocalStorage(messageSessionId, srcState);
                    
                    // Only update React state if current session
                    if (!isViewingDifferentSession) {
                      setResumeBoxSourcingLogs((prev) => {
                        const existsReact = prev.some(log => log.message === logMessage);
                        return existsReact ? prev : [...prev, logEntry];
                      });
                      setResumeBoxSourcingProcessing(true);
                      
                      if (countMatch) {
                        setResumeBoxCandidateCount(parseInt(countMatch[1], 10));
                      }
                    }
                  }
                  
                   // Check if sourcing is complete. LinkedIn has successful milestones
                  // such as login and filter fill, so only final extraction/search messages
                  // should end the whole LinkedIn sourcing channel.
                  const isGoogleSourcingComplete =
                    detectedPlatform === 'Google' &&
                    (
                      messageLower.includes('complete') ||
                      messageLower.includes('done') ||
                      messageLower.includes('finished') ||
                      (messageLower.startsWith('total ') && messageLower.includes('candidates from google search'))
                    );
                  const isResumeBoxSourcingComplete =
                    detectedPlatform === 'ResumeBox' &&
                    (
                      messageLower.includes('matching complete') ||
                      messageLower.includes('resume box complete') ||
                      messageLower.includes('finished')
                    );
                  const isLinkedinSourcingComplete =
                    detectedPlatform === 'LinkedIn' &&
                    (
                      messageLower.includes('extraction complete') ||
                      messageLower.includes('valid candidate') ||
                      messageLower.includes('linkedin search completed') ||
                      messageLower.includes('linkedin profile search completed') ||
                      messageLower.includes('profile search automation completed') ||
                      messageLower.includes('linkedin sourcing completed')
                    );
                  if (
                    isGoogleSourcingComplete ||
                    isResumeBoxSourcingComplete ||
                    isLinkedinSourcingComplete
                  ) {
                    // Always update session store first
                    const completeState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                    completeState.loaders = completeState.loaders || {};
                    completeState.sourcing = completeState.sourcing || {};
                    if (detectedPlatform === 'Google') {
                      completeState.loaders.googleSourcing = false;
                      completeState.sourcing.googleProcessing = false;
                    } else if (detectedPlatform === 'LinkedIn') {
                      completeState.loaders.linkedinSourcing = false;
                      completeState.sourcing.linkedinProcessing = false;
                    } else if (detectedPlatform === 'ResumeBox') {
                      completeState.loaders.resumeBoxSourcing = false;
                      completeState.sourcing.resumeBoxProcessing = false;
                    }
                    sessionStateStoreRef.current.set(messageSessionId, completeState);
                    // Persist updated processing flags to localStorage immediately
                    persistSourcingToLocalStorage(messageSessionId, completeState);
                    
                    // Only update React state if current session
                    if (!isViewingDifferentSession) {
                      if (detectedPlatform === 'Google') {
                        setGoogleSourcingProcessing(false);
                      } else if (detectedPlatform === 'LinkedIn') {
                        setLinkedinSourcingProcessing(false);
                      } else if (detectedPlatform === 'ResumeBox') {
                        setResumeBoxSourcingProcessing(false);
                      }
                      // CandidateResults owns its own DB-backed list. When an
                      // individual source completes, force the same refresh path
                      // as the Refresh button so newly saved candidates appear
                      // without waiting for a manual click. The delayed bump
                      // covers the save/commit race immediately after logs.
                      setSourcingRefreshToken((prev) => prev + 1);
                      window.setTimeout(() => {
                        if (currentViewedSessionRef.current === messageSessionId) {
                          setSourcingRefreshToken((prev) => prev + 1);
                        }
                      }, 1500);
                    }
                  }
                  
                  // Check for errors
                  if (logType === 'error' || messageLower.includes('failed') || messageLower.includes('error')) {
                    // Always update session store first
                    const errState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                    errState.loaders = errState.loaders || {};
                    errState.sourcing = errState.sourcing || {};
                    if (detectedPlatform === 'Google') {
                      errState.loaders.googleSourcing = false;
                      errState.sourcing.googleProcessing = false;
                    } else if (detectedPlatform === 'LinkedIn') {
                      errState.loaders.linkedinSourcing = false;
                      errState.sourcing.linkedinProcessing = false;
                    } else if (detectedPlatform === 'ResumeBox') {
                      errState.loaders.resumeBoxSourcing = false;
                      errState.sourcing.resumeBoxProcessing = false;
                    }
                    sessionStateStoreRef.current.set(messageSessionId, errState);
                    // Persist updated processing flags to localStorage immediately
                    persistSourcingToLocalStorage(messageSessionId, errState);
                    
                    // Only update React state if current session
                    if (!isViewingDifferentSession) {
                      if (detectedPlatform === 'Google') {
                        setGoogleSourcingProcessing(false);
                      } else if (detectedPlatform === 'LinkedIn') {
                        setLinkedinSourcingProcessing(false);
                      } else if (detectedPlatform === 'ResumeBox') {
                        setResumeBoxSourcingProcessing(false);
                      }
                    }
                  }
                }
                // Only add log if we're confident about the platform AND it belongs to job posting
                else if (
                  (detectedPlatform === 'LinkedIn' || detectedPlatform === 'Naukri') &&
                  (phase === 'job_posting' || phase === 'posting' || phase === 'job_post' || phase === 'post_job')
                ) {
                  // Check if this is a failure message
                  const isFailure = logType === 'error' || 
                    messageLower.includes('failed') || 
                    messageLower.includes('error') ||
                    messageLower.includes('timeout') ||
                    messageLower.includes('limit reached') ||
                    messageLower.includes("don't have access") ||
                    messageLower.includes('payment_required') ||
                    messageLower.includes('free_job_limit_reached') ||
                    messageLower.includes('do not have access');
                  
                  // Check if this is a FINAL success message (job posted successfully)
                  // Be specific - only "posted successfully" should stop processing
                  // NOT intermediate success messages like "OTP verification success" or "login success"
                  const isJobPostSuccess = 
                    messageLower.includes('posted successfully') ||
                    (logType === 'success' && messageLower.includes('job'));
                  
                  const logEntry = {
                    message: logMessage,
                    timestamp: new Date().toISOString(),
                    status: isFailure ? 'error' : (isJobPostSuccess ? 'success' : (logType || 'info'))
                  };
                  
                  // Always update session store first (using NESTED structure)
                  const jpState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                  jpState.jobPosting = jpState.jobPosting || { linkedinLogs: [], naukriLogs: [] };
                  if (detectedPlatform === 'LinkedIn') {
                    jpState.jobPosting.linkedinLogs = jpState.jobPosting.linkedinLogs || [];
                    jpState.jobPosting.linkedinLogs.push(logEntry);
                    if (isFailure || isJobPostSuccess) {
                      jpState.loaders = jpState.loaders || {};
                      jpState.loaders.linkedin = false;
                      jpState.jobPosting.linkedinProcessing = false;
                      if (isFailure) {
                        jpState.jobPosting.failed = true;
                      }
                      if (isJobPostSuccess) {
                        jpState.jobPosting.postedChannels = jpState.jobPosting.postedChannels || [];
                        if (!jpState.jobPosting.postedChannels.includes('LinkedIn')) {
                          jpState.jobPosting.postedChannels.push('LinkedIn');
                        }
                      }
                    }
                  } else if (detectedPlatform === 'Naukri') {
                    jpState.jobPosting.naukriLogs = jpState.jobPosting.naukriLogs || [];
                    jpState.jobPosting.naukriLogs.push(logEntry);
                    if (isFailure || isJobPostSuccess) {
                      jpState.loaders = jpState.loaders || {};
                      jpState.loaders.naukri = false;
                      jpState.jobPosting.naukriProcessing = false;
                      if (isFailure) {
                        jpState.jobPosting.failed = true;
                      }
                      if (isJobPostSuccess) {
                        jpState.jobPosting.postedChannels = jpState.jobPosting.postedChannels || [];
                        if (!jpState.jobPosting.postedChannels.includes('Naukri')) {
                          jpState.jobPosting.postedChannels.push('Naukri');
                        }
                      }
                    }
                  }
                  const stillPosting =
                    Boolean(jpState.jobPosting.linkedinProcessing) ||
                    Boolean(jpState.jobPosting.naukriProcessing);
                  jpState.jobPosting.processing = stillPosting;
                  jpState.jobPosting.complete = !stillPosting;
                  jpState.jobPosting.phase = stillPosting ? 'posting' : 'posted';
                  sessionStateStoreRef.current.set(messageSessionId, jpState);
                  
                  // NOTE: Logs and processing state come from DB/file API — no localStorage persistence needed
                  
                  // Only update React state if current session
                  if (!isViewingDifferentSession) {
                    if (detectedPlatform === 'LinkedIn') {
                      setLinkedinLogs((prev) => [...prev, logEntry]);
                      // If LinkedIn job posting completed (success or failure), stop LinkedIn processing
                      // Only stop on FINAL job post success, not intermediate successes like OTP
                      if (isFailure || isJobPostSuccess) {
                        setLinkedinProcessing(false);
                        if (isFailure) {
                          setJobPostingFailed(true);
                        }
                        if (isJobPostSuccess) {
                          setPostedChannels((prev) =>
                            prev.includes('LinkedIn') ? prev : [...prev, 'LinkedIn']
                          );
                        }
                        if (!jpState.jobPosting.naukriProcessing) {
                          setJobPostingProcessing(false);
                          setJobPostingComplete(true);
                          setJobPostingPhase('posted');
                        } else {
                          setJobPostingProcessing(true);
                          setJobPostingComplete(false);
                          setJobPostingPhase('posting');
                        }
                      }
                    } else if (detectedPlatform === 'Naukri') {
                      setNaukriLogs((prev) => [...prev, logEntry]);
                      // If Naukri job posting completed (success or failure), stop Naukri processing
                      // Only stop on FINAL job post success, not intermediate successes
                      if (isFailure || isJobPostSuccess) {
                        setNaukriProcessing(false);
                        if (isFailure) {
                          setJobPostingFailed(true);
                        }
                        if (isJobPostSuccess) {
                          setPostedChannels((prev) =>
                            prev.includes('Naukri') ? prev : [...prev, 'Naukri']
                          );
                        }
                        if (!jpState.jobPosting.linkedinProcessing) {
                          setJobPostingProcessing(false);
                          setJobPostingComplete(true);
                          setJobPostingPhase('posted');
                        } else {
                          setJobPostingProcessing(true);
                          setJobPostingComplete(false);
                          setJobPostingPhase('posting');
                        }
                      }
                    }
                  } else {
                    console.log('[search_phase_update] Job posting log stored for session:', messageSessionId, detectedPlatform);
                  }
                } else {
                  // Log to console for debugging if we can't determine platform
                  console.log('Could not determine platform for log:', { phase, platform, message: logMessage.substring(0, 50) });
                }
              }
            } else if (type === 'session_log_update') {
              // Handle real-time log updates from session log files
              const logEntry = data?.log_entry || {};
              const sessionType = data?.session_type || 'job_post';
              const message = logEntry?.message;
              const level = logEntry?.level?.toLowerCase() || 'info';
              const logPlatform = logEntry?.platform;

              if (message) {
                const formattedLog = {
                  message: message,
                  timestamp: logEntry?.timestamp || new Date().toISOString(),
                  type: level === 'error' ? 'error' : 'info',
                  logType: level === 'error' ? 'error' : 'info',
                };

                // Always update session store first (using NESTED structure)
                const logState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
                if (sessionType === 'job_post') {
                  const normalizedPlatform = String(logPlatform || '').toLowerCase();
                  logState.jobPosting = logState.jobPosting || {};
                  if (normalizedPlatform === 'linkedin') {
                    logState.jobPosting.linkedinLogs = logState.jobPosting.linkedinLogs || [];
                    logState.jobPosting.linkedinLogs.push(formattedLog);
                  } else {
                    logState.jobPosting.naukriLogs = logState.jobPosting.naukriLogs || [];
                    logState.jobPosting.naukriLogs.push(formattedLog);
                  }
                } else if (sessionType === 'source_candidates' && logPlatform === 'Naukri') {
                  logState.candidateSync = logState.candidateSync || { applicantLogs: [] };
                  logState.candidateSync.applicantLogs = logState.candidateSync.applicantLogs || [];
                  logState.candidateSync.applicantLogs.push(formattedLog);
                } else if (sessionType === 'linkedin_search' || logPlatform === 'LinkedIn') {
                  logState.sourcing = logState.sourcing || { linkedinLogs: [] };
                  logState.sourcing.linkedinLogs = logState.sourcing.linkedinLogs || [];
                  logState.sourcing.linkedinLogs.push(formattedLog);
                } else if (sessionType === 'google_search' || logPlatform === 'Google') {
                  logState.sourcing = logState.sourcing || { googleLogs: [] };
                  logState.sourcing.googleLogs = logState.sourcing.googleLogs || [];
                  logState.sourcing.googleLogs.push(formattedLog);
                } else if (sessionType === 'outreach') {
                  logState.outreach = logState.outreach || { logs: [] };
                  logState.outreach.logs = logState.outreach.logs || [];
                  logState.outreach.logs.push(formattedLog);
                }
                sessionStateStoreRef.current.set(messageSessionId, logState);

                // Only update React state if current session
                if (!isViewingDifferentSession) {
                  if (sessionType === 'job_post') {
                    if (String(logPlatform || '').toLowerCase() === 'linkedin') {
                      setLinkedinLogs((prev) => [...prev, formattedLog]);
                    } else {
                      setNaukriLogs((prev) => [...prev, formattedLog]);
                    }
                  } else if (sessionType === 'linkedin_search' || logPlatform === 'LinkedIn') {
                    setLinkedinSourcingLogs((prev) => [...prev, formattedLog]);
                  } else if (sessionType === 'google_search' || logPlatform === 'Google') {
                    setGoogleSourcingLogs((prev) => [...prev, formattedLog]);
                  } else if (sessionType === 'outreach') {
                    setOutreachLogs((prev) => [...prev, formattedLog]);
                  }
                }
              }
            } else if (type === 'naukri_error') {
              // Handle network/page load errors from Naukri automation
              const errorMessage = data?.message || 'An error occurred during Naukri automation';
              const errorType = data?.error_type || 'unknown';
              const retryAfter = data?.retry_after;

              const errorLog = {
                message: errorMessage,
                timestamp: new Date().toISOString(),
                type: 'error',
                logType: 'error',
              };

              // Always update session store first
              const errState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              errState.jobPosting = errState.jobPosting || { linkedinLogs: [], naukriLogs: [] };
              errState.jobPosting.naukriLogs = errState.jobPosting.naukriLogs || [];
              errState.jobPosting.naukriLogs.push(errorLog);
              if (errorType === 'network_issue' || errorType === 'free_job_limit_reached') {
                errState.loaders = errState.loaders || {};
                errState.loaders.naukri = false;
                errState.jobPosting.naukriProcessing = false;
                errState.jobPosting.failed = true;
                errState.jobPosting.processing = Boolean(errState.jobPosting.linkedinProcessing);
                errState.jobPosting.complete = !errState.jobPosting.processing;
                errState.jobPosting.phase = errState.jobPosting.processing ? 'posting' : 'posted';
              }
              sessionStateStoreRef.current.set(messageSessionId, errState);

              // CRITICAL: Store error message in database for persistence when user switches back
              try {
                const fullErrorMessage = `❌ ${errorMessage}${retryAfter ? ` Please try again in ${retryAfter} seconds.` : ''}`;
                aiAutomationApi.post(`/sessions/${messageSessionId}/messages`, {
                  message_type: 'assistant',
                  content: fullErrorMessage,
                  message_role: 'assistant',
                  metadata: { 
                    phase: 'job_posting_error', 
                    platform: 'Naukri',
                    error_type: errorType,
                    type: 'naukri_error'
                  },
                }).catch(e => console.warn('Failed to store Naukri error in DB:', e));
              } catch (e) {
                console.warn('Failed to store Naukri error in DB:', e);
              }

              // Only update React state if current session
              if (!isViewingDifferentSession) {
                setNaukriLogs((prev) => [...prev, errorLog]);

                // Show error in system messages for all error types that should be visible
                if (errorType === 'network_issue' || errorType === 'free_job_limit_reached') {
                  setSystemMessages((prev) => [
                    ...prev.filter((m) => !m?.includes?.('network') && !m?.includes?.('Network') && !m?.includes?.('free job limit')),
                    `❌ ${errorMessage}${retryAfter ? ` Please try again in ${retryAfter} seconds.` : ''}`,
                  ]);
                  setNaukriProcessing(false);
                  setJobPostingFailed(true);
                }
              }
            } else if (type === 'naukri_site_error_recovery') {
              // Handle Naukri site "Something Went Wrong" errors - show modal with retry/cancel options
              const errorMessage = data?.message || 'Naukri is experiencing issues. Please choose an action.';
              const prompt = data?.prompt || 'Would you like to retry or cancel?';
              const errorData = data?.data || {};
              const clearLogsOnRetry = errorData?.clear_logs_on_retry === true;
              
              console.log('[naukri_site_error_recovery] Received:', { errorMessage, prompt, errorData });

              // Update session store first
              const errState = sessionStateStoreRef.current.get(messageSessionId) || createEmptySessionState();
              errState.siteErrorRecovery = {
                active: true,
                message: errorMessage,
                prompt: prompt,
                clearLogsOnRetry: clearLogsOnRetry,
              };
              sessionStateStoreRef.current.set(messageSessionId, errState);

              // Only show modal if viewing current session
              if (!isViewingDifferentSession) {
                // Show the site error recovery modal
                setSiteErrorRecoveryModal({
                  open: true,
                  sessionId: messageSessionId,
                  message: errorMessage,
                  prompt: prompt,
                  clearLogsOnRetry: clearLogsOnRetry,
                  onRetry: async () => {
                    console.log('[naukri_site_error_recovery] User chose RETRY');
                    // Clear logs if requested
                    if (clearLogsOnRetry) {
                      setNaukriLogs([]);
                      const state = sessionStateStoreRef.current.get(messageSessionId);
                      if (state) {
                        state.naukriLogs = [];
                        sessionStateStoreRef.current.set(messageSessionId, state);
                      }
                    }
                    // Send retry response to backend
                    try {
                      await aiAutomationApi.post(`/post-job/submit-field-response`, {
                        session_id: messageSessionId,
                        field_name: 'site_error_recovery',
                        field_value: 'retry',
                      });
                    } catch (err) {
                      console.error('[naukri_site_error_recovery] Error sending retry response:', err);
                    }
                    setSiteErrorRecoveryModal(prev => ({ ...prev, open: false }));
                  },
                  onCancel: async () => {
                    console.log('[naukri_site_error_recovery] User chose CANCEL');
                    // Send cancel response to backend
                    try {
                      await aiAutomationApi.post(`/post-job/submit-field-response`, {
                        session_id: messageSessionId,
                        field_name: 'site_error_recovery',
                        field_value: 'cancel',
                      });
                    } catch (err) {
                      console.error('[naukri_site_error_recovery] Error sending cancel response:', err);
                    }
                    // Update UI state to show job posting was cancelled
                    setNaukriProcessing(false);
                    setJobPostingFailed(true);
                    setSystemMessages(['Job posting cancelled due to Naukri site error.']);
                    setSiteErrorRecoveryModal(prev => ({ ...prev, open: false }));
                  },
                });
              }
            } else if (type === 'get_candidates_update') {
              // Handle sync posted job candidates updates
              // IMPORTANT: Don't show ANY messages in chat - only update candidates silently
              const payload = data?.data || {};
              const candidatesFromWS = Array.isArray(payload.candidates) ? payload.candidates : [];
              const status = data?.status || data?.data?.status;
              
              console.log('[get_candidates_update]', { 
                status, 
                candidateCount: candidatesFromWS.length,
                session: messageSessionId 
              });
              
              // Only update candidates when status is completed and we have candidates
              if ((status === 'completed' || status === 'partial') && candidatesFromWS.length > 0 && !isViewingDifferentSession) {
                // Update session store
                updateSessionState(messageSessionId, (state) => {
                  // Merge with existing candidates - new candidates take precedence
                  const existingMap = new Map((state.candidates || []).map(c => [
                    c.id || c.candidate_id || c.email || c.profile_url,
                    c
                  ]));
                  candidatesFromWS.forEach(c => {
                    const key = c.id || c.candidate_id || c.email || c.profile_url;
                    existingMap.set(key, c);
                  });
                  state.candidates = Array.from(existingMap.values());
                  return state;
                });
                
                // Update React state
                setCandidates((prev) => {
                  const existingMap = new Map((prev || []).map(c => [
                    c.id || c.candidate_id || c.email || c.profile_url,
                    c
                  ]));
                  candidatesFromWS.forEach(c => {
                    const key = c.id || c.candidate_id || c.email || c.profile_url;
                    existingMap.set(key, c);
                  });
                  return Array.from(existingMap.values());
                });
                setMatchedCandidateCount(prev => Math.max(prev, candidatesFromWS.length));
                
                // Switch to candidates view if we have candidates
                setRightPanelMode('candidates');
              }
              // NO messages shown in chat - progress updates are silently processed
            } else if (type === 'chat_log') {
              // Handle chat_log messages from backend
              const message = data?.data?.message || data?.message;
              const status = data?.status || 'info';
              const phase = data?.phase || data?.data?.phase || 'general';
              const platform = data?.data?.platform || data?.platform || '';
              
              // IMPORTANT: Do NOT show sync_complete or sync_candidates messages in chat
              // User wants silent updates for get_postedjob_candidates process
              const isSyncMessage = phase === 'sync_complete' || phase === 'sync_candidates' || platform === 'Sync';
              
              // Skip sync messages entirely - they should not appear in chat
              if (isSyncMessage) {
                console.log('[WebSocket] chat_log sync message ignored (silent mode):', { message, phase, platform });
                return;
              }
              
              const shouldShowMessage = message && !isViewingDifferentSession;
              
              console.log('[WebSocket] chat_log received:', { message, phase, status, platform, shouldShowMessage });
              
              if (shouldShowMessage) {
                // Add to system messages and force re-render
                setSystemMessages((prev) => {
                  // Remove duplicate chat_log messages with same content
                  const filtered = prev.filter(m => m !== message);
                  return [...filtered, message];
                });
                
                // Force ChatMain to re-render and add the message
                setSystemMessageToken((prev) => prev + 1);
                
                console.log('[WebSocket] chat_log added to systemMessages:', message);
              }
            } else if (type === 'candidates_saved') {
              // Handle final candidates saved message (after deduplication)
              // NOTE: Do NOT show any message in chat - user wants silent updates
              const savedCount = data?.data?.saved_count || 0;
              const failedCount = data?.data?.failed_count || 0;
              console.log('[candidates_saved]', { savedCount, failedCount, session: messageSessionId });
              // Silent processing - no UI updates needed here
              // Candidates are updated via get_candidates_update or shortlisted_candidates_update
            } else if (type === 'shortlisted_candidates_update') {
              // Handle shortlisted candidates update from get_postedjob_candidates endpoint
              // This updates the candidates list without requiring a page refresh
              const payload = data?.data || {};
              const candidatesFromWS = Array.isArray(payload.candidates) ? payload.candidates : [];
              const candidateCount = payload.candidate_count || candidatesFromWS.length;
              const newCount = payload.new_count || 0;
              const message = payload.message || `Added ${newCount} new candidates to shortlist`;
              
              console.log('[shortlisted_candidates_update]', { 
                candidateCount, 
                newCount, 
                session: messageSessionId 
              });
              
              // Update session store
              updateSessionState(messageSessionId, (state) => {
                if (candidatesFromWS.length > 0) {
                  // Merge with existing candidates - new candidates take precedence
                  const existingMap = new Map((state.candidates || []).map(c => [
                    c.id || c.candidate_id || c.email || c.profile_url,
                    c
                  ]));
                  candidatesFromWS.forEach(c => {
                    const key = c.id || c.candidate_id || c.email || c.profile_url;
                    existingMap.set(key, c);
                  });
                  state.candidates = Array.from(existingMap.values());
                }
                return state;
              });
              
              if (!isViewingDifferentSession) {
                // Update the candidates list in the current view
                if (candidatesFromWS.length > 0) {
                  setCandidates((prev) => {
                    // Merge with existing candidates - new candidates take precedence
                    const existingMap = new Map((prev || []).map(c => [
                      c.id || c.candidate_id || c.email || c.profile_url,
                      c
                    ]));
                    candidatesFromWS.forEach(c => {
                      const key = c.id || c.candidate_id || c.email || c.profile_url;
                      existingMap.set(key, c);
                    });
                    return Array.from(existingMap.values());
                  });
                  setMatchedCandidateCount(candidateCount);
                }
                
                // Switch to candidates view if we have candidates
                if (candidateCount > 0) {
                  setRightPanelMode('candidates');
                }
	              }
	            }
	            // Other types like otp_status_update or job_posting_update can be handled here later
	        };

        socket.onerror = (error) => {
          console.error('Job posting WebSocket error:', error);
        };

        socket.onclose = (event) => {
          if (jobPostingSocketRef.current === socket) {
            jobPostingSocketRef.current = null;
          }
          // If socket closed unexpectedly while still processing, reconnect instead of treating
          // the posting run itself as failed. The automation can continue server-side.
          if (event.code !== 1000) {
            const sessionState = sessionStateStoreRef.current.get(automationSessionId);
            const stillPosting =
              Boolean(sessionState?.jobPosting?.linkedinProcessing) ||
              Boolean(sessionState?.jobPosting?.naukriProcessing) ||
              Boolean(jobPostingProcessing && !jobPostingComplete);

            if (stillPosting) {
              console.warn('WebSocket closed unexpectedly while job posting was in progress. Reconnecting...', event);
              window.setTimeout(() => {
                setJobPostingSocketReconnectKey((prev) => prev + 1);
              }, 1000);
            }
          }
        };
    }

    // Clean up when phase goes back to idle / done - BUT keep alive for background job posting
    if (!shouldConnect && jobPostingSocketRef.current && activeJobPostingSessions.size === 0) {
      try {
        jobPostingSocketRef.current.close();
      } catch {
        // ignore
      }
      jobPostingSocketRef.current = null;
    }
  }, [automationSessionId, jobPostingPhase, talentScoutingStarted, sourcingComplete, outreachPhase, activeJobPostingSessions, activeSourcingSessions, googleSourcingProcessing, linkedinSourcingProcessing, resumeBoxSourcingProcessing, jobPostingProcessing, jobPostingComplete, jobPostingSocketReconnectKey]);

  // Background sourcing WebSockets: maintain one connection per active sourcing session when user switches away
  useEffect(() => {
    // Keep a websocket for each active sourcing session except the currently viewed one.
    const backgroundSourcingSessions = [...activeSourcingSessions].filter(
      (sid) => sid && sid !== automationSessionId
    );
    const desiredSessions = new Set(backgroundSourcingSessions);
    const socketsBySession = backgroundSourcingSocketRef.current || new Map();
    backgroundSourcingSocketRef.current = socketsBySession;
    
    console.log(
      '[BackgroundSourcingWS] Check - activeSourcingSessions:',
      [...activeSourcingSessions],
      'automationSessionId:',
      automationSessionId,
      'backgroundSessions:',
      backgroundSourcingSessions
    );

    // Open missing sockets for newly active background sourcing sessions.
    for (const backgroundSessionId of backgroundSourcingSessions) {
      if (socketsBySession.has(backgroundSessionId)) continue;
      try {
        const wsBase = process.env.REACT_APP_SERVERSOCKET_URL;
        const wsUrl = `${wsBase}/otp-status/${backgroundSessionId}`;
        const socket = new WebSocket(wsUrl);
        socketsBySession.set(backgroundSessionId, socket);

        console.log('[BackgroundSourcingWS] Connected to:', wsUrl);

        socket.onopen = () => {
          console.log('[BackgroundSourcingWS] Socket opened for session:', backgroundSessionId);
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data || '{}');
            const { type } = data;
            const messageSessionId = data?.session_id || data?.data?.session_id || backgroundSessionId;
            const platformName = normalizePlatformName(data?.platform || data?.data?.platform) || 'LinkedIn';
            
            console.log('[BackgroundSourcingWS] Message received:', type, data);

            if (type === 'linkedin_app_approval_required') {
              const approvalStatus = data?.status || data?.data?.status || 'waiting';
              const approvalMessage =
                data?.message ||
                data?.data?.message ||
                'Open your LinkedIn mobile app and tap Yes to approve this sign-in.';
              const screenshotUrl = resolveAiAutomationAssetUrl(
                data?.api_screenshot_url ||
                data?.data?.api_screenshot_url ||
                data?.screenshot_url ||
                data?.data?.screenshot_url ||
                (data?.screenshot_filename ? `/screenshots/${data.screenshot_filename}` : '') ||
                (data?.data?.screenshot_filename ? `/screenshots/${data.data.screenshot_filename}` : '')
              );
              const screenshotFallbackUrl = resolveAiAutomationAssetUrl(
                data?.screenshot_url ||
                data?.data?.screenshot_url ||
                (data?.screenshot_filename ? `/screenshots/${data.screenshot_filename}` : '') ||
                (data?.data?.screenshot_filename ? `/screenshots/${data.data.screenshot_filename}` : '')
              );

              if (approvalStatus === 'completed' || approvalStatus === 'failed') {
                setLinkedinAppApprovalModal(prev => (
                  prev.sessionId === messageSessionId ? { ...prev, isOpen: false, status: approvalStatus } : prev
                ));
              } else {
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle: 'LinkedIn verification',
                  type: 'linkedin_app_approval',
                  message: approvalMessage,
                  options: [],
	                  platform: 'LinkedIn',
	                  screenshotUrl,
	                  screenshotFallbackUrl,
	                  fieldName: null,
                });
              }
              return;
            }

            // Handle OTP status updates from background sourcing session
            if (type === 'otp_status_update' || type === 'otp_required') {
              console.log('[BackgroundSourcingWS] OTP message detected!');
              const otpRequired = data?.otp_required ?? data?.data?.otp_required ?? true;
              
              // Get flow context tag from backend (e.g., "Job Posting", "Sourcing Candidates", "Outreach")
              const flowContext = data?.flow_context || data?.data?.flow_context || '';
              
              // Build OTP message with flow context tag
              let otpMessage =
                data?.message ||
                data?.data?.message ||
                data?.instructions ||
                data?.data?.instructions || '';
              
              // If no message or message doesn't have tag, build one with the flow context
              if (!otpMessage || !otpMessage.includes('[')) {
                const contextTag = flowContext ? `[${flowContext}] ` : '';
                otpMessage = `${contextTag}${platformName} requires OTP verification. Please enter the 6-digit code sent to your phone.`;
              }

              if (otpMessage && otpRequired) {
                console.log('[BackgroundSourcingWS] Setting crossSessionNotification for OTP');
                // User is in different session - always show modal for background sourcing OTP
                const job = jobPostings.find(j => j.id === messageSessionId);
                const jobTitle = job?.title || 'Sourcing Session';
                
                setCrossSessionNotification({
                  sessionId: messageSessionId,
                  jobTitle,
                  type: 'otp_request',
                  message: otpMessage,
                  options: [],
                  platform: platformName,
                  fieldName: null,
                });
              }
            }

            // Handle sourcing already in progress notification
            if (type === 'sourcing_already_in_progress') {
              console.log('[BackgroundSourcingWS] Sourcing already in progress:', data);
              const statusMessage = data?.message || 'Candidate sourcing is already in progress for this session.';
              const isViewingThisSession = currentViewedSessionRef.current === messageSessionId;

              // Always update session store
              updateSessionState(messageSessionId, (state) => {
                state.sourcing.started = true;
                if (data?.google_sourcing?.status === 'in_progress') state.sourcing.googleProcessing = true;
                if (data?.linkedin_sourcing?.status === 'in_progress') state.sourcing.linkedinProcessing = true;
                state.chat.systemMessages = [...(state.chat.systemMessages || []), `ℹ️ ${statusMessage}`];
                return state;
              });

              // Only update React state if viewing this session
              if (isViewingThisSession) {
                setSystemMessages(prev => [...prev, `ℹ️ ${statusMessage}`]);
                if (data?.google_sourcing?.status === 'in_progress') setGoogleSourcingProcessing(true);
                if (data?.linkedin_sourcing?.status === 'in_progress') setLinkedinSourcingProcessing(true);
                setTalentScoutingStarted(true);
                setRightPanelMode('auto');
              }
            }

            // Handle sourcing completion from background session
            if (type === 'sourcing_complete' || type === 'source_complete') {
              const payload = data?.data || {};
              const completionPlatform = normalizePlatformName(
                data?.platform || payload?.platform || data?.data?.platform || ''
              );
              const priorSourcingState = sessionStateStoreRef.current.get(messageSessionId)?.sourcing || {};
              const otherChannelsStillProcessing = !!(
                priorSourcingState.googleProcessing || priorSourcingState.linkedinProcessing
              );
              const isPrematureCompletion =
                (completionPlatform === 'ResumeBox' || !completionPlatform) &&
                otherChannelsStillProcessing;

              if (isPrematureCompletion) {
                updateSessionState(messageSessionId, (state) => {
                  state.sourcing.complete = false;
                  state.sourcing.started = true;
                  state.sourcing.resumeBoxProcessing = false;
                  return state;
                });
              } else {
                removeActiveSourcingSession(messageSessionId);
                // Update session store
                updateSessionState(messageSessionId, (state) => {
                  state.sourcing.complete = true;
                  state.sourcing.started = false;
                  state.sourcing.googleProcessing = false;
                  state.sourcing.linkedinProcessing = false;
                  state.sourcing.resumeBoxProcessing = false;
                  return state;
                });
              }

              // NOTE: Sourcing state comes from DB/file API — no localStorage persistence needed

              if (isPrematureCompletion) {
                return;
              }
              // Send __sourcing_complete__ signal to backend
              const bgSearchId = getSafeSearchId(messageSessionId) || (automationSearchId?.startsWith('search_') ? automationSearchId : '');
              try {
                aiAutomationApi.post('/chat', {
                  message: '__sourcing_complete__',
                  session_id: messageSessionId,
                  cid: getCid(),
                  additional_data: {
                    search_id: bgSearchId,
                    action: 'lifecycle',
                    _internal: 'sourcing_complete_signal',
                  },
                }).catch(() => {});
              } catch (_) {}
              // Fetch all candidates from DB (including resume_box) and store for when user switches back
              // Retry after 2s if first fetch returns 0 (ResumeBox may still be saving)
              if (messageSessionId) {
                const fetchBgCandidates = async () => {
                  try {
                    const searchResp = await aiAutomationApi.get(`/automation_candidates/all?session_id=${messageSessionId}&limit=50`);
                    let dbCandidates = searchResp?.candidates || searchResp?.data || [];
                    if (dbCandidates.length === 0) {
                      await new Promise(r => setTimeout(r, 2000));
                      const retryResp = await aiAutomationApi.get(`/automation_candidates/all?session_id=${messageSessionId}&limit=50`);
                      dbCandidates = retryResp?.candidates || retryResp?.data || [];
                    }
                    if (dbCandidates.length > 0) {
                      updateSessionState(messageSessionId, (state) => {
                        state.sourcing.candidates = dbCandidates;
                        return state;
                      });
                      console.log('[BackgroundSourcingWS] Stored', dbCandidates.length, 'candidates (incl. resume_box) for session:', messageSessionId);
                    }
                  } catch (_) {}
                };
                fetchBgCandidates();
              }
            }
          } catch (err) {
            console.warn('Error parsing background sourcing WebSocket message:', err);
          }
        };

        socket.onerror = (error) => {
          console.warn('Background sourcing WebSocket error:', error);
        };

        socket.onclose = () => {
          const mapRef = backgroundSourcingSocketRef.current;
          if (mapRef && mapRef.get(backgroundSessionId) === socket) {
            mapRef.delete(backgroundSessionId);
          }
        };
      } catch (err) {
        console.error('Failed to connect background sourcing WebSocket:', err);
      }
    }

    // Close sockets for sessions that are no longer active background sourcing sessions.
    for (const [sid, sock] of socketsBySession.entries()) {
      if (!desiredSessions.has(sid)) {
        try {
          sock.close();
        } catch {
          // ignore
        }
        socketsBySession.delete(sid);
      }
    }
  }, [activeSourcingSessions, automationSessionId, jobPostings]);

  // Cleanup background sourcing sockets on unmount.
  useEffect(() => {
    return () => {
      const socketsBySession = backgroundSourcingSocketRef.current;
      if (!socketsBySession || typeof socketsBySession.entries !== 'function') return;
      for (const [, sock] of socketsBySession.entries()) {
        try {
          sock.close();
        } catch {
          // ignore
        }
      }
      backgroundSourcingSocketRef.current = new Map();
    };
  }, []);

  // Separate function to create JD (called after confirmation)
  const createJobDescription = async (searchId, sessionId, parsedRequirements, queryText) => {
    try {
      // CRITICAL: Use session store for loader state
      setSessionLoader(sessionId, 'jd', true);
      jdLoadingSessionRef.current = sessionId; // Track which session started the loading
      
      // Update session store with state changes
      const sessionState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
      sessionState.jdError = '';
      sessionState.analysisUxPhase = 'building';
      sessionState.systemMessages = ['Sourcing Strategy & JD creation in progress...'];
      sessionStateStoreRef.current.set(sessionId, sessionState);
      
      // Only update React state if this is the current session
      if (currentViewedSessionRef.current === sessionId) {
        setJdError('');
        setAnalysisUxPhase('building');
        setShowResultsPanel(true); // show sourcing strategy while JD is building
        setSystemMessages(['Sourcing Strategy & JD creation in progress...']);
      }

      // Phase 2: create job description
      const jdData = await aiAutomationApi.post(
        `/search/${searchId}/create-jd`,
        {
          query: queryText,
          session_id: sessionId,
          parsed_requirements: parsedRequirements,
        }
      );
      
      // Store agent state from backend response for session-isolated state management
      if (jdData.agent_state && sessionId) {
        agentStatePerSessionRef.current.set(sessionId, jdData.agent_state);
        console.log('[Agent State] Updated agent state after JD creation:', sessionId, jdData.agent_state);
      }
      
      const jdTextFromApi =
        jdData?.job_description ||
        jdData?.job_description_text ||
        jdData?.description ||
        '';
      const finalSummaryMessage = jdData?.final_summary_message || '';

      if (!jdTextFromApi) {
        // CRITICAL: Update session store FIRST
        const errorState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
        errorState.jdError = 'Job description could not be generated. Please refine your input and try again.';
        errorState.loaders = errorState.loaders || {};
        errorState.loaders.jd = false;
        sessionStateStoreRef.current.set(sessionId, errorState);
        
        // Only update React state if current session
        if (currentViewedSessionRef.current === sessionId) {
          setJdError('Job description could not be generated. Please refine your input and try again.');
          setIsJDLoading(false);
        }
        jdLoadingSessionRef.current = null;
        return;
      }

      // CRITICAL: Store response in session store FIRST
      updateSessionState(sessionId, (state) => {
        state.jd.text = jdTextFromApi;
        state.jd.error = '';
        state.jd.loading = false;
        state.jd.waitingForConfirmation = false;
        state.jd.analysisPhase = 'done';
        state.jd.requirements = parsedRequirements || {};
        if (finalSummaryMessage) {
          state.chat.systemMessages = ['Sourcing Strategy & JD creation in progress...', finalSummaryMessage];
        } else {
          state.chat.systemMessages = [];
        }
        return state;
      });
      
      jdLoadingSessionRef.current = null; // Clear session tracking on success
      
      // Only update React state if this is the current session
      if (currentViewedSessionRef.current === sessionId) {
        setJdText(jdTextFromApi);
        setIsJDLoading(false);
        setWaitingForConfirmation(false);
        setSystemMessage('');
        if (finalSummaryMessage) {
          setSystemMessages(['Sourcing Strategy & JD creation in progress...', finalSummaryMessage]);
        } else {
          setSystemMessages([]);
        }
        setAnalysisUxPhase('done');
        setFinalRequirements(parsedRequirements || {});
      }

      // After successful JD creation, update job posting entry
      // Extract title from parsed requirements or query
      const jobTitle =
        parsedRequirements.title ||
        parsedRequirements.role ||
        queryText.split(' ').slice(0, 5).join(' ') ||
        'New Job Posting';

      // Truncate title if too long
      const MAX_TITLE_LENGTH = 80;
      const shortTitle =
        jobTitle.length > MAX_TITLE_LENGTH
          ? `${jobTitle.slice(0, MAX_TITLE_LENGTH - 1)}…`
          : jobTitle;

      openJdCriteriaReview(sessionId, shortTitle);

      // Persist the JD title as custom_title in DB
      aiAutomationApi.patch(`/sessions/${sessionId}/title`, { title: shortTitle }).catch((e) => {
        console.warn('Failed to persist JD title as custom_title', e);
      });

      // Update existing job posting entry (created when first message was sent)
      setJobPostings((prev) => {
        const updated = prev.map((job) =>
          job.id === sessionId
            ? {
                ...job,
                title: shortTitle,
                lastUpdated: formatLastUpdated(),
                duration: 'Active',
              }
            : job
        );
        return updated;
      });
    } catch (error) {
      console.error('Error during JD creation:', error);
      const errorMessage = error?.message || 'Something went wrong while creating the job description.';
      
      // CRITICAL: Update session store FIRST
      setSessionLoader(sessionId, 'jd', false);
      const errorState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
      errorState.jd.error = errorMessage;
      errorState.jd.waitingForConfirmation = false;
      sessionStateStoreRef.current.set(sessionId, errorState);
      
      // Only update React state if current session
      if (currentViewedSessionRef.current === sessionId) {
        setJdError(errorMessage);
        setWaitingForConfirmation(false);
      }
      jdLoadingSessionRef.current = null; // Clear session tracking on error
    }
  };
  // Handle dropdown option selection for Naukri fields
  const handleNaukriFieldOptionSelect = async (option) => {
    if (!pendingNaukriField || !option) return;
    
    try {
      await aiAutomationApi.post('/post-job/submit-field-response', {
        session_id: automationSessionId,
        field_name: pendingNaukriField,
        field_value: option,
      });
      setPendingNaukriField(null);
      setPendingNaukriFieldOptions([]);
      // Add confirmation message
      setSystemMessages((prev) => [
        ...prev.filter((msg) => typeof msg === 'string' && msg.trim().startsWith('✅')),
        `✅ Selected: ${option}`,
      ]);
    } catch (error) {
      console.error('Failed to submit Naukri field response', error);
      setSystemMessages((prev) => [
        ...prev,
        'There was an issue submitting that selection. Please try again.',
      ]);
    }
  };

  const handleDynamicNaukriFieldsSubmit = async (values) => {
    const request = dynamicNaukriFieldRequest;
    if (!request?.fieldName) return;

    try {
      await aiAutomationApi.post('/post-job/submit-field-response', {
        session_id: request.sessionId || automationSessionId,
        field_name: request.fieldName,
        field_value: JSON.stringify(values || {}),
      });

      updateSessionState(request.sessionId || automationSessionId, (state) => {
        if (state.jobPosting) {
          state.jobPosting.dynamicFieldRequest = null;
          state.jobPosting.phase = 'posting';
        }
        return state;
      });
      setDynamicNaukriFieldRequest(null);
    } catch (error) {
      console.error('Failed to submit dynamic Naukri field response', error);
      setSystemMessages((prev) => [
        ...prev,
        'There was an issue submitting those Naukri details. Please try again.',
      ]);
      setSystemMessageToken((prev) => prev + 1);
    }
  };

  const handleDynamicNaukriFieldsClose = async () => {
    const request = dynamicNaukriFieldRequest;
    if (!request?.fieldName) {
      setDynamicNaukriFieldRequest(null);
      return;
    }

    dismissedDynamicNaukriRequestsRef.current.set(
      `${request.sessionId || automationSessionId || ''}|${request.fieldName}`,
      Date.now()
    );
    setDynamicNaukriFieldRequest(null);
    updateSessionState(request.sessionId || automationSessionId, (state) => {
      if (state.jobPosting) {
        state.jobPosting.dynamicFieldRequest = null;
      }
      return state;
    });

    try {
      await aiAutomationApi.post('/post-job/submit-field-response', {
        session_id: request.sessionId || automationSessionId,
        field_name: request.fieldName,
        field_value: '__cancel__',
      });
    } catch (error) {
      console.error('Failed to cancel dynamic Naukri field request', error);
    }
  };


  // ── Session title editing ──
  const handleSaveSessionTitle = async (newTitle) => {
    const trimmedTitle = (newTitle || '').trim();
    if (!trimmedTitle) return;
    const sid = automationSessionId || selectedJobId || sessionId;
    if (!sid) return;

    try {
      await aiAutomationApi.patch(`/sessions/${sid}/title`, { title: trimmedTitle });
      // Update local jobPostings state so sidebar reflects the change
      setJobPostings((prev) =>
        prev.map((p) => (p.id === sid ? { ...p, title: trimmedTitle } : p))
      );
      setEditingTitle(false);
    } catch (e) {
      console.error('Failed to save session title', e);
    }
  };

  // ===================== ARCHIVE / DELETE / RESTORE =====================
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  // Unified undo toast for both archive and delete: { type: 'archive'|'delete', sessionId, title, job, timeoutId }
  const [undoToast, setUndoToast] = useState(null);

  const clearCurrentSession = (isCurrentChat, undoInfo) => {
    setSelectedJobId(null);
    setAutomationSessionId(null);
    currentViewedSessionRef.current = null;
    if (isCurrentChat) {
      // Pass undo info to dashboard via navigation state so toast shows there
      navigate('/dashboard', { replace: true, state: undoInfo ? { undoAction: undoInfo } : undefined });
    }
  };

  const showUndoToast = (type, sessionId, title, job, wasArchived = false) => {
    if (undoToast?.timeoutId) clearTimeout(undoToast.timeoutId);
    const timeoutId = setTimeout(async () => {
      // Toast expired — finalize the action
      if (type === 'delete') {
        try { await aiAutomationApi.delete(`/sessions/${sessionId}`); } catch (_) {}
      }
      setUndoToast(null);
    }, 5000);
    setUndoToast({ type, sessionId, title, job, timeoutId, wasArchived });
  };

  const handleArchiveJob = async (sessionId) => {
    const job = jobPostings.find((p) => p.id === sessionId);
    if (!job) return;
    const isCurrentChat = selectedJobId === sessionId;

    try {
      await aiAutomationApi.patch(`/sessions/${sessionId}/archive`);
      setJobPostings((prev) => prev.filter((p) => p.id !== sessionId));
      setArchivedCount((c) => c + 1);
      if (showArchived) {
        setArchivedJobs((prev) => [{ ...job }, ...prev]);
      }
      if (isCurrentChat) {
        clearCurrentSession(true, { type: 'archive', sessionId, title: job.title });
      } else {
        showUndoToast('archive', sessionId, job.title, job);
      }
    } catch (e) {
      console.error('Failed to archive session', e);
    }
  };

  const handleRestoreJob = async (sessionId, jobData) => {
    // Add to UI immediately using cached job data
    const restored = jobData || archivedJobs.find((p) => p.id === sessionId);
    if (restored) {
      setJobPostings((prev) => [{ ...restored }, ...prev]);
    }
    setArchivedJobs((prev) => prev.filter((p) => p.id !== sessionId));
    setArchivedCount((c) => Math.max(0, c - 1));
    // API call in background
    try {
      await aiAutomationApi.patch(`/sessions/${sessionId}/restore`);
    } catch (e) {
      console.error('Failed to restore session', e);
      // Revert UI if API fails
      if (restored) {
        setJobPostings((prev) => prev.filter((p) => p.id !== sessionId));
        setArchivedCount((c) => c + 1);
      }
    }
  };

  const handleDeleteJob = async (sessionId) => {
    try {
      const check = await aiAutomationApi.get(`/sessions/${sessionId}/can-delete`);
      if (!check.can_delete) {
        showNotification(check.reason || 'Cannot delete — use Archive instead.', 'warning');
        return;
      }
      const isArchived = archivedJobs.some((p) => p.id === sessionId);
      const job = jobPostings.find((p) => p.id === sessionId) || archivedJobs.find((p) => p.id === sessionId);
      const isCurrentChat = selectedJobId === sessionId;

      if (isArchived) {
        // Already archived — hard delete directly, undo will re-create as archived
        setArchivedJobs((prev) => prev.filter((p) => p.id !== sessionId));
        setArchivedCount((c) => Math.max(0, c - 1));
        showUndoToast('delete', sessionId, job?.title || 'Untitled', job, true);
      } else {
        // Active session — archive first (so undo can restore), then hard delete after 5s
        await aiAutomationApi.patch(`/sessions/${sessionId}/archive`);
        setJobPostings((prev) => prev.filter((p) => p.id !== sessionId));
        if (isCurrentChat) {
          clearCurrentSession(true, { type: 'delete', sessionId, title: job?.title || 'Untitled' });
        } else {
          showUndoToast('delete', sessionId, job?.title || 'Untitled', job);
        }
      }
    } catch (e) {
      console.error('Failed to delete session', e);
      if (e?.status === 409) {
        showNotification('Cannot delete — session has candidates or a posted job. Use Archive instead.', 'warning');
      }
    }
  };

  const handleUndoAction = async () => {
    if (!undoToast) return;
    if (undoToast.timeoutId) clearTimeout(undoToast.timeoutId);

    if (undoToast.wasArchived) {
      // Was deleted from archived list — put back in archived (don't restore to active)
      if (undoToast.job) {
        setArchivedJobs((prev) => [{ ...undoToast.job }, ...prev]);
        setArchivedCount((c) => c + 1);
      }
      // No API call needed — session is still archived in DB (hard delete hasn't fired yet)
    } else {
      // Was deleted/archived from active list — restore to active
      await handleRestoreJob(undoToast.sessionId, undoToast.job);
    }
    setUndoToast(null);
  };

  const handleRenameJob = async (sessionId, newTitle) => {
    try {
      await aiAutomationApi.patch(`/sessions/${sessionId}/title`, { title: newTitle });
      setJobPostings((prev) => prev.map((p) => (p.id === sessionId ? { ...p, title: newTitle } : p)));
    } catch (e) {
      console.error('Failed to rename session', e);
    }
  };

  const handleToggleArchived = async () => {
    const newShow = !showArchived;
    setShowArchived(newShow);
    if (newShow && archivedJobs.length === 0) {
      // Lazy load archived sessions
      try {
        const archived = await aiAutomationApi.get('/sessions/archived');
        const mapped = (archived || []).map((s) => ({
          id: s.id,
          title: s.title || s.custom_title || s.initial_query || 'Untitled',
          rawTimestamp: s.updated_at || s.created_at,
          candidateCount: s.candidate_count || 0,
          hasPostedJob: s.has_posted_job || false,
          cid: s.cid != null ? String(s.cid) : (getCid() ? String(getCid()) : null),
        }));
        setArchivedJobs(mapped);
        setArchivedCount(mapped.length);
      } catch (e) {
        console.error('Failed to load archived sessions', e);
      }
    }
  };

  // Handle Ask-mode filter action button click → handoff to Agent mode
  const handleFilterAction = async (actionType) => {
    if (blockDisabledFeature('filter')) return;
    const count = workingCandidates.length;
    console.log(`[FilterHandoff] Action: ${actionType}, candidates: ${count}`);

    // Switch mode to agent.
    setChatMode('agent');
    showToast('Exited Pipeline Agent', 'info', 2000);

    // Persistent exit pill in chat history (same shape as the toggle
    // and modal exit paths). The previous transient pill via
    // filterPillText was deliberately removed because it disappeared
    // after the next render cycle and didn't survive refresh.
    const handoffExitEntry = {
      id: `transition_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      sender: 'agent',
      message: 'Exited Pipeline Agent',
      timestamp: new Date().toISOString(),
      metadata: {
        type: 'agent_transition',
        agent: 'Pipeline Agent',
        action: 'exited',
        exit_reason: 'handoff',
        handoff_action: actionType,
      },
    };
    setChatHistory((prev) => [...prev, handoffExitEntry]);

    // Clear filter UI (backend will also clear working_candidates after injection)
    setFilterBreadcrumbs([]);
    setFilterActions([]);
    setWorkingCandidates([]);
    setFollowUpSuggestions([]);

    // Persist cleared state + the exit pill so it survives refresh.
    const chatSessionId = selectedJobId || automationSessionId || sessionId;
    if (chatSessionId) {
      updateSessionState(chatSessionId, (s) => {
        s.chat = s.chat || {};
        s.chat.chatHistory = [
          ...(s.chat.chatHistory || []),
          handoffExitEntry,
        ];
        s.filtering = { chatMode: 'agent', breadcrumbs: [], actions: [], workingCandidates: [], followUpSuggestions: [] };
        return s;
      });
      aiAutomationApi.post(`/sessions/${chatSessionId}/messages`, {
        message_type: 'assistant',
        content: 'Exited Pipeline Agent',
        message_role: 'assistant',
        metadata: {
          type: 'agent_transition',
          agent: 'Pipeline Agent',
          action: 'exited',
          exit_reason: 'handoff',
          handoff_action: actionType,
        },
      }).catch((e) => {
        console.warn('Failed to persist handoff exit pill', e);
      });
    }

    // Send handoff signal to backend.
    // The message format is `__handoff_<kind>__ <natural-language hint>`
    // — the `__handoff_<kind>__` prefix lets the supervisor route by
    // intent, and the trailing natural-language hint gives the
    // receiving agent enough context to start its flow without asking
    // the user "what scheduling action did you mean?". The signed
    // candidate manifest + filter_handoff state key (set by the filter
    // agent on the previous turn) carry the structured payload the
    // receiving agent should actually act on.
    // See: docs/filter_handoff_contract.md
    const chatSearchId = getSafeSearchId(chatSessionId) || (automationSearchId?.startsWith('search_') ? automationSearchId : '');

    // Build a friendly hint per action so the receiving agent's
    // classifier has something natural to read after the prefix.
    const actionHints = {
      outreach: `start outreach for the ${count} filtered candidate${count === 1 ? '' : 's'}`,
      schedule: `start scheduling interviews for the ${count} filtered candidate${count === 1 ? '' : 's'}`,
      followup: `send follow-up messages to the ${count} filtered candidate${count === 1 ? '' : 's'}`,
      draft:    `draft replies for the ${count} filtered candidate${count === 1 ? '' : 's'}`,
      nudge:    `send nudges to the ${count} filtered candidate${count === 1 ? '' : 's'}`,
    };
    const hint = actionHints[actionType] || `act on the ${count} filtered candidate${count === 1 ? '' : 's'}`;
    const handoffMessage = `__handoff_${actionType}__ ${hint}`;

    try {
      setChatApiLoading(true);
      const handoffResp = await aiAutomationApi.post('/chat', {
        message: handoffMessage,
        session_id: chatSessionId,
        cid: getCid(),
        additional_data: {
          search_id: chatSearchId || '',
          action: 'lifecycle',
          mode: 'agent',
        },
      });
      if (handoffResp?.success && handoffResp?.response) {
        const resp = handoffResp.response;
        const msg = resp.message || resp.summary || '';
        if (msg) {
          setAndPersistSystemMessage([msg], { phase: 'lifecycle', skipPersist: true });
        }
        // Update lifecycle actions from handoff response
        if (resp.suggestions && Array.isArray(resp.suggestions) && resp.suggestions.length > 0) {
          setLifecycleActions(resp.suggestions.map((s, idx) => ({
            id: `lifecycle_${s.action || idx}`,
            label: s.label,
            command: s.command || s.label,
          })));
        }
        if (resp.pipeline_snapshot) setPipelineSnapshot(resp.pipeline_snapshot);
        // Handle agent stage from handoff
        if (resp.stage_label) {
          setAgentStageLabel(resp.stage_label);
          setAgentStageCancellable(resp.stage_cancellable || false);
        }
        // Show target agent activation pill
        if (resp.agent_pill?.type === 'activated' && resp.agent_pill?.label) {
          setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__${resp.agent_pill.label} Agent activated`]);
          setSystemMessageToken((prev) => prev + 1);
        }
      }
    } catch (err) {
      console.error('[FilterHandoff] Failed:', err);
      showToast('Failed to switch to Agent mode. Please try again.', 'error', 3000);
    } finally {
      setChatApiLoading(false);
      setWorkingCandidates([]);
    }
  };

  // Handle user query - check if it's a confirmation or new details
  const handleUserQueryForJD = async (queryText, chatMode, options = {}) => {
    // ── Trace for diagnosing "chip click eaten" bugs ─────────────
    // Generates a unique id per turn. If you see [QFTRACE:start] but not
    // [QFTRACE:reached_chat_call] with the same id, the function
    // returned early — search the console for that id to see if any
    // guard explicitly logged. If no guard logged, find the
    // [QFTRACE:return_*] tag closest in time to the start.
    const _qfTraceId = `qf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    console.warn('[QFTRACE:start]', _qfTraceId, {
      queryText: String(queryText || '').slice(0, 80),
      chatMode,
      sessionLoading,
      chatHistoryLoading,
      pendingNaukriField,
      jobPostingPhase,
      agentStageLabel,
    });
    console.log('[handleUserQueryForJD] Called with:', { queryText, chatMode, sessionLoading, chatHistoryLoading });
    const trimmed = (queryText || '').trim();
    const normalizedTrimmed = trimmed.toLowerCase();
    const isLocalUiOnlyCommand = isLocalUiOnlyChatCommand(trimmed);
    const hasJdAvailable = typeof jdText === 'string' && jdText.trim().length > 0;
    const isWorkspaceSelectionTurn = workspaceSelectionRequired === true;

    const disabledFeatureForCommand = isWorkspaceSelectionTurn
      ? null
      : getDisabledFeatureForCommand(trimmed, chatMode);
    if (disabledFeatureForCommand) {
      console.warn('[QFTRACE:return_feature_disabled]', _qfTraceId, disabledFeatureForCommand);
      if (disabledFeatureForCommand === 'scheduling') {
        blockDisabledSchedulingFlow();
      } else {
        blockDisabledFeature(disabledFeatureForCommand);
      }
      return;
    }

    // Auto-collapse sidebar on any user interaction
    if (!sidebarCollapsed) setSidebarCollapsed(true);

    // Block input while session is still loading to prevent race conditions
    if (!options.bypassLoadingGuard && (sessionLoading || chatHistoryLoading)) {
      console.log('[DEBUG] Input blocked - session still loading');
      setSystemMessages(['Please wait while the session is loading...']);
      return;
    }
    console.log('[handleUserQueryForJD] Passed session loading check');

    const forceNewSessionFromDashboard = forceNewSessionFromDashboardRef.current;
    forceNewSessionFromDashboardRef.current = false; // Clear immediately — session already created by initiateNewChat
    let persistedUserMessage = false;

    // Persist ALL non-empty user messages to hr_automation_chat_message
    // so confirmations like "yes", "proceed" etc. are also stored.
    // SKIP if field collection is active - handleJobPostingFieldResponse stores with metadata
    // Check BOTH state AND ref to handle React state timing issues
    const isFieldCollectionActive = pendingNaukriField || fieldCollectionStateRef.current?.isActive;
    console.log('[handleUserQueryForJD] isFieldCollectionActive:', isFieldCollectionActive);
    // Clear new chat pending flag — user sent a real message, session is no longer abandonable
    if (trimmed && newChatPendingRef.current) {
      newChatPendingRef.current = null;
    }

    // Fire-and-forget: persist user message without blocking the UI/typing indicator
    if (trimmed && !isFieldCollectionActive && !isLocalUiOnlyCommand) {
      const sessionIdToUse = options.sessionId
        || (forceNewSessionFromDashboard
          ? null
          : (automationSessionId || selectedJobId || sessionId || automationSearchId));
      if (sessionIdToUse) {
        // NOTE: Initial welcome greeting is client-side only (createInitialMessages).
        // Do NOT save it to DB — it would duplicate with the /chat response on reload.
        if (!initialGreetingSavedRef.current) {
          initialGreetingSavedRef.current = true;
        }

        persistedUserMessage = true; // Mark immediately to prevent duplicate saves downstream
        const userMsgPayload = {
          message_type: 'user',
          content: trimmed,
          message_role: 'user',
        };
        // Persist chat mode in metadata for restoration on page refresh
        if (chatMode) {
          userMsgPayload.metadata = { mode: chatMode };
        }
        aiAutomationApi.post(`/sessions/${sessionIdToUse}/messages`, userMsgPayload).then(() => {
          updateJobPostingLastUpdated(sessionIdToUse, new Date());
        }).catch((e) => {
          console.warn('Failed to persist user chat message', e);
        });
      }
    }
    
    // Handle OTP submission with highest priority so it isn't blocked by channel-selection guards
    const isInJobPostingFlow = jobPostingPhase === 'posting' || jobPostingPhase === 'collecting_details';
    const isInSourcingFlow = talentScoutingStarted && !sourcingComplete;
    const isInOutreachFlow = outreachPhase === 'sending' || outreachSending;
    const isOtpInput = !!trimmed && /^\d{4,8}$/.test(trimmed);
    if (!isWorkspaceSelectionTurn && !pendingNaukriField && isOtpInput && (isInJobPostingFlow || isInSourcingFlow || isInOutreachFlow)) {
      try {
        // Determine platform for OTP routing:
        // - If otpModal has platform, use that
        // - If sourcing/outreach flow, typically LinkedIn
        // - If job posting flow, check selectedChannels
        let otpPlatform = otpModal?.platform?.toLowerCase() || '';
        if (!otpPlatform) {
          if (isInSourcingFlow || isInOutreachFlow) {
            otpPlatform = 'linkedin';
          } else if (isInJobPostingFlow) {
            // Check which channel is active - prefer naukri if selected, else linkedin
            otpPlatform = selectedChannels.includes('naukri') ? 'naukri' : 'linkedin';
          }
        }
        
        await aiAutomationApi.post('/submit-otp', {
          session_id: automationSessionId,
          otp_code: trimmed,
          platform: otpPlatform,  // Send platform for correct routing
        });
        if (isInSourcingFlow) {
          // Sourcing flow requirement: ONLY 2 OTP messages total (prompt + submitted)
          sourcingOtpUiRef.current.submitted = true;
          setSystemMessages([
            "Thanks! I've submitted your OTP. The platform will continue the login flow.",
          ]);
        } else if (isInOutreachFlow) {
          addSystemMessage("Thanks! I've submitted your OTP. Outreach will continue once verification completes.", {
            removeKeywords: ['otp', 'verification'],
            checkExact: true
          });
        } else {
          addSystemMessage("Thanks! I've submitted your OTP. The platform will continue the login and job posting flow.", {
            removeKeywords: ['otp', 'verification'],
            checkExact: true
          });
        }
      } catch (error) {
        console.error('Failed to submit OTP code', error);
        setSystemMessages((prev) => [
          ...prev,
          'There was an issue submitting the OTP code. Please double-check the code and try again.',
        ]);
      }
      return;
    }

    // ── Catch-all cancel for Job Posting (any phase: channel_selection, collecting_details) ──
    // Fires when banner shows "Job Posting" OR jobPostingPhase is active.
    if (!isWorkspaceSelectionTurn && trimmed && chatMode !== 'ask') {
      const cancelWord = trimmed.toLowerCase();
      const isJobPostingActive = agentStageLabel === 'Job Posting' ||
        jobPostingPhase === 'channel_selection' ||
        jobPostingPhase === 'collecting_details';
      if (isJobPostingActive && (cancelWord === 'cancel' || cancelWord === 'exit' || cancelWord === 'stop')) {
        console.log('[Job Posting] Cancel caught (phase:', jobPostingPhase, ', banner:', agentStageLabel, ')');
        setJobPostingPhase('idle');
        setSelectedChannels([]);
        setPendingJobPostingFieldsQueue([]);
        setPendingNaukriField(null);
        setCollectedJobPostingDetails(null);
        fieldCollectionStateRef.current = { isActive: false, currentField: null, queue: [] };

        const exitLabel = agentStageLabel || 'Job Posting';
        showToast(`Exited: ${exitLabel}`, 'info', 2000);
        setAgentStageLabel('');
        setAgentStageCancellable(false);

        setSystemMessages([
          `__AGENT_TRANSITION__Exited ${exitLabel}`,
          '❌ Job posting cancelled.',
        ]);
        setSystemMessageToken((prev) => prev + 1);

        if (automationSessionId) {
          updateSessionState(automationSessionId, (state) => {
            state.jobPosting.phase = 'idle';
            state.jobPosting.pendingField = null;
            state.jobPosting.pendingFieldsQueue = [];
            state.jobPosting.fieldCollectionActive = false;
            state.jobPosting.selectedChannels = [];
            state.jobPosting.collectedDetails = null;
            state.chat.systemMessages = ['❌ Job posting cancelled.'];
            return state;
          });
          try { localStorage.removeItem(`job_posting_state_${automationSessionId}`); } catch (_) {}
          try {
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'user', content: trimmed, message_role: 'user',
              metadata: { phase: 'job_posting', action: 'cancelled' },
            });
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant', content: `Exited ${exitLabel}`, message_role: 'assistant',
              metadata: { type: 'agent_transition', agent: exitLabel, action: 'exited' },
            });
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant', content: 'Job posting cancelled.', message_role: 'assistant',
              metadata: { phase: 'job_posting_cancelled' },
            });
          } catch (_) {}
        }
        return;
      }
    }

    const simpleJobPostIntentMatch = trimmed.match(/^(?:(?:re\s*)?post(?:\s+job(?:\s+posting)?)?|retry(?:\s+job(?:\s+posting)?)?|job\s+post|post\s+jd)(?:\s+(?:on|to|for))?(?:\s+(linkedin|naukri)|.*?\b(linkedin|naukri)\b)?$/i);
    if (!isWorkspaceSelectionTurn && trimmed && !hasJdAvailable && simpleJobPostIntentMatch) {
      setSystemMessages(['Please create the JD first. Job posting is available only after the JD is created.']);
      return;
    }
    if (
      !isWorkspaceSelectionTurn &&
      trimmed &&
      hasJdAvailable &&
      simpleJobPostIntentMatch &&
      jobPostingPhase !== 'channel_selection' &&
      !pendingNaukriField &&
      !(fieldCollectionStateRef.current?.isActive)
    ) {
      const requestedChannelRaw = simpleJobPostIntentMatch[1] || simpleJobPostIntentMatch[2];
      const requestedChannel = requestedChannelRaw
        ? (requestedChannelRaw.toLowerCase() === 'linkedin' ? 'LinkedIn' : 'Naukri')
        : null;

      if (requestedChannel && !actionablePostingChannels.includes(requestedChannel)) {
        if (effectivePostedChannels.includes(requestedChannel)) {
          setSystemMessages([`${requestedChannel} job posting is already completed for this JD. You can sync applicants or use the remaining channel.`]);
        } else {
          setSystemMessages([`${requestedChannel} is not available to post right now for this session.`]);
        }
        return;
      }

      const channelsToPost = requestedChannel
        ? [requestedChannel]
        : (actionablePostingChannels.length === 1 ? [actionablePostingChannels[0]] : []);

      if (channelsToPost.length === 1) {
        setSelectedChannels(channelsToPost);
        updateSessionState(automationSessionId, (state) => {
          state.jobPosting.selectedChannels = channelsToPost;
          return state;
        });
        await openJobPostingConfirmationWithAnalysis(channelsToPost);
        return;
      }

      const channelSelectionMessage = `Select the channel you want to post first.\n\nAvailable channels: ${actionablePostingChannels.join(', ')}`;
      setJobPostingPhase('channel_selection');
      setPendingJobPostingSessionId(automationSessionId);
      pendingJobPostingSessionRef.current = automationSessionId;
      setSystemMessages([channelSelectionMessage]);
      setSystemMessageToken((prev) => prev + 1);
      updateSessionState(automationSessionId, (state) => {
        state.jobPosting.phase = 'channel_selection';
        state.jobPosting.availableChannels = actionablePostingChannels;
        return state;
      });
      return;
    }

    // PRIORITY: Handle pending Naukri field responses during job posting
    // This must come before other guards to allow user input during posting phase
    // CRITICAL: Check SESSION STORE for current session, not global state
    // This ensures field collection only applies to the session where it was started
    const currentSessionFieldState = sessionStateStoreRef.current.get(automationSessionId);
    const sessionHasFieldCollection = 
      currentSessionFieldState?.jobPosting?.fieldCollectionActive ||
      currentSessionFieldState?.jobPosting?.pendingField;
    const currentPendingField = currentSessionFieldState?.jobPosting?.pendingField || null;
    
    if (!isWorkspaceSelectionTurn && sessionHasFieldCollection && trimmed && chatMode !== 'ask' && jobPostingPhase !== 'channel_selection') {
      // Check if user wants to cancel the job posting process
      const lowerTrimmed = trimmed.toLowerCase();
      if (lowerTrimmed === 'cancel' || lowerTrimmed === 'exit' || lowerTrimmed === 'stop') {
        console.log('[Job Posting] User cancelled job posting field collection');
        // Reset all job posting state including ref
        setJobPostingPhase('idle');
        setPendingJobPostingFieldsQueue([]);
        setPendingNaukriField(null);
        setCollectedJobPostingDetails(null);
        fieldCollectionStateRef.current = { isActive: false, currentField: null, queue: [] };

        // Exit agent banner + show transition pill
        const exitingJobAgent = agentStageLabel || 'Job Posting';
        if (agentStageLabel) {
          showToast(`Exited: ${exitingJobAgent}`, 'info', 2000);
        }
        setAgentStageLabel('');
        setAgentStageCancellable(false);

        setSystemMessages([
          `__AGENT_TRANSITION__Exited ${exitingJobAgent}`,
          '❌ Job posting process cancelled. You can start a new job posting anytime by pasting a job description.',
        ]);
        setSystemMessageToken((prev) => prev + 1);

        // Update session store with cancelled state
        if (automationSessionId) {
          updateSessionState(automationSessionId, (state) => {
            state.jobPosting.phase = 'idle';
            state.jobPosting.pendingField = null;
            state.jobPosting.pendingFieldsQueue = [];
            state.jobPosting.fieldCollectionActive = false;
            state.jobPosting.collectedDetails = null;
            state.chat.systemMessages = ['❌ Job posting process cancelled. You can start a new job posting anytime by pasting a job description.'];
            return state;
          });

          // Clean up localStorage since job posting is cancelled
          try {
            localStorage.removeItem(`job_posting_state_${automationSessionId}`);
          } catch (e) {
            // Ignore
          }
        }

        // Store cancellation in database + exit transition
        try {
          if (automationSessionId) {
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'user',
              content: trimmed,
              message_role: 'user',
              metadata: { phase: 'job_posting_field_collection', action: 'cancelled' },
            });
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant',
              content: `Exited ${exitingJobAgent}`,
              message_role: 'assistant',
              metadata: { type: 'agent_transition', agent: exitingJobAgent, action: 'exited' },
            });
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant',
              content: 'Job posting process cancelled by user.',
              message_role: 'assistant',
              metadata: { phase: 'job_posting_cancelled' },
            });
          }
        } catch (e) {
          console.warn('Failed to persist cancellation to chat', e);
        }

        // Notify backend LangGraph to clear agent state
        try {
          aiAutomationApi.post('/chat', {
            message: 'cancel',
            session_id: automationSessionId,
            cid: getCid(),
            additional_data: {
              search_id: (automationSearchId?.startsWith('search_') ? automationSearchId : '') || getSafeSearchId(automationSessionId),
              action: 'lifecycle',
              mode: 'agent',
            },
          }).catch(() => {});
        } catch (_) {}

        return;
      }
      
      // Check if user is trying to use a command during field collection
      // Show a helpful message instead of processing as field value
      // NOTE: "view jd" and "show jd" are ALLOWED through - they just switch the view without cancelling
      const blockedCommandPatterns = [
        'post job', 'search candidates',
        'start sourcing', 'sourcing', 'outreach', 'start outreach',
        'show candidates', 'view candidates'
      ];
      // Allow "view jd" and "show jd" to pass through even during field collection
      const wantsViewJdCmd = /view\s*jd|show\s*jd|open\s*jd|job\s*description/.test(lowerTrimmed);
      if (wantsViewJdCmd && jdText) {
        appendJdCardToChat();
        return;
      }
      if (blockedCommandPatterns.some(cmd => lowerTrimmed.includes(cmd))) {
        const currentQuestion = getFieldQuestion(currentPendingField);
        const helpMessage = `⚠️ **Job posting in progress** - You're currently answering questions for job posting.\n\nTo use other features, please type **"cancel"** to stop the job posting process first.\n\n${currentQuestion}`;
        
        // Set message and increment token to force re-render in ChatMain
        setSystemMessages([helpMessage]);
        setSystemMessageToken((prev) => prev + 1);
        
        // Store BOTH user input and help message in database
        try {
          if (automationSessionId) {
            // First store the user's blocked command
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'user',
              content: trimmed,
              message_role: 'user',
              metadata: { phase: 'job_posting_field_collection', type: 'blocked_command' },
            });
            // Then store the help response
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant',
              content: helpMessage,
              message_role: 'assistant',
              metadata: { phase: 'job_posting_field_collection', type: 'command_blocked', attempted_command: trimmed },
            });
          }
        } catch (e) {
          console.warn('Failed to persist help message to chat', e);
        }
        return;
      }
      
      // Check if we're in the step-by-step field collection flow (not WebSocket-driven)
      // Check both state AND ref for queue
      // Check if we're in the step-by-step field collection flow (not WebSocket-driven)
      // Use session store instead of global state for session isolation
      const queueFromSession = currentSessionFieldState?.jobPosting?.pendingFieldsQueue || [];
      const hasQueuedFields = queueFromSession.length > 0;
      const sessionPhase = currentSessionFieldState?.jobPosting?.phase;
      if (hasQueuedFields || sessionPhase === 'collecting_details' || sessionHasFieldCollection) {
        // Use the field from session store
        const fieldToHandle = currentPendingField || queueFromSession[0];
        if (fieldToHandle) {
          await handleJobPostingFieldResponse(fieldToHandle, trimmed);
          return;
        }
      }
      
      // Original WebSocket-driven flow (for during actual job posting automation)
      // Frontend validation for salary and experience fields
      let validationError = null;
      const valueClean = trimmed.replace(/,/g, '').replace(/\s/g, '').trim();
      
      if (pendingNaukriField === 'companyName') {
        // Validate company name is string not just numbers
        if (/^\d+$/.test(valueClean)) {
          validationError = `"${trimmed}" is not a valid company name. Company name cannot be only numbers. Please enter a proper company name.`;
        }
      } else if (pendingNaukriField === 'salaryMin' || pendingNaukriField === 'salaryMax') {
        if (!/^\d+$/.test(valueClean)) {
          validationError = `"${trimmed}" is not a valid number. Please enter a numeric salary value (e.g., 50000).`;
        } else if (parseInt(valueClean, 10) <= 0) {
          validationError = 'Please enter a valid salary amount greater than 0.';
        } else if (pendingNaukriField === 'salaryMax') {
          // Check if max salary is less than min salary using local state
          const salaryMin = naukriFieldResponses.salaryMin;
          if (salaryMin && parseInt(valueClean, 10) < parseInt(salaryMin, 10)) {
            validationError = `Maximum salary (${valueClean}) must be greater than or equal to minimum salary (${salaryMin}). Please enter a higher amount.`;
          }
        }
      } else if (pendingNaukriField === 'experienceMin' || pendingNaukriField === 'experienceMax') {
        if (!/^\d+$/.test(valueClean)) {
          validationError = `"${trimmed}" is not a valid number. Please enter experience in years (e.g., 2).`;
        }
      }
      
      if (validationError) {
        // Show validation error in chat - do NOT submit to backend
        setSystemMessages((prev) => [
          ...prev,
          `❌ ${validationError}`,
        ]);
        return;
      }
      
      // Store response in local state for validation
      setNaukriFieldResponses(prev => ({
        ...prev,
        [pendingNaukriField]: trimmed
      }));
      
      try {
        await aiAutomationApi.post('/post-job/submit-field-response', {
          session_id: automationSessionId,
          field_name: pendingNaukriField,
          field_value: trimmed,
        });
        setPendingNaukriField(null); 
        setPendingNaukriFieldOptions([]);
      } catch (error) {
        console.error('Failed to submit Naukri field response', error);
        setSystemMessages((prev) => [
          ...prev,
          'There was an issue submitting that detail to the job posting flow. Please try again.',
        ]);
      }
      return;
    }

    // GUARD: If in field collection phase, block "post job" commands and redirect to field handler
    // This prevents users from accidentally starting a new job posting while answering questions
    // CRITICAL: Use session store for session isolation
    const sessionFieldPhase = currentSessionFieldState?.jobPosting?.phase;
    const sessionFieldsQueue = currentSessionFieldState?.jobPosting?.pendingFieldsQueue || [];
    const sessionPendingField = currentSessionFieldState?.jobPosting?.pendingField;
    if (!isWorkspaceSelectionTurn && (sessionFieldPhase === 'collecting_details' || sessionFieldsQueue.length > 0)) {
      const lowerTrimmed = trimmed.toLowerCase();
      // If user types something that looks like a command, warn them and re-ask the current question
      if (lowerTrimmed.includes('post job') || lowerTrimmed.includes('post jd')) {
        const currentField = sessionPendingField || sessionFieldsQueue[0];
        if (currentField) {
          const question = getFieldQuestion(currentField);
          if (question) {
            setSystemMessages([`⚠️ You're currently in the job posting process. Please answer the question or type "cancel" to stop.\n\n${question}`]);
            return;
          }
        }
      }
      // Treat any other input as a field answer
      if (sessionPendingField) {
        await handleJobPostingFieldResponse(sessionPendingField, trimmed);
        return;
      }
    }

    // PRIORITY: If in channel selection phase, handle it first
    // But first: if job posting is actively processing (not just 'posted' with no activity), show logs panel
    // ── Global navigation commands ──
    // These switch right-panel views or kick off sourcing. They are
    // Agent-mode actions only — gating with `chatMode !== 'ask'` is
    // critical because sourcing shortcuts would otherwise eat Filter-mode
    // messages about a candidate source column before /chat can handle them.
    // Same logic for "view candidates" / "show jd" — Filter mode has
    // its own handling for these intents inside filter_hardened.
    //
    // IMPORTANT: Skip these shortcuts for long messages (> 200 chars).
    // A pasted/uploaded JD may contain words like "talent", "sourcing",
    // "profiles", "job description" — which would falsely match these
    // regexes and route the JD paste to talent scouting instead of JD
    // creation. Short commands are always < 200 chars.
    const normalizedChannelText = trimmed.toLowerCase();
    const isLongDataPaste = trimmed.length > 200;
    if (!isWorkspaceSelectionTurn && chatMode !== 'ask' && !isLongDataPaste) {
      const wantsViewJd = /view\s*jd|show\s*jd|open\s*jd|job\s*description/.test(normalizedChannelText);
      const wantsViewCandidates =
        /show\s*candidates?|view\s*candidates?|candidate\s*list|profiles?/.test(normalizedChannelText);
      const wantsSourcing = isSourcingRelatedIntent(normalizedChannelText);

      if (wantsViewJd && jdText) {
        console.warn('[QFTRACE:return_wantsViewJd]');
        appendJdCardToChat();
        return;
      }

      if (wantsViewCandidates) {
        console.warn('[QFTRACE:return_wantsViewCandidates]');
        setRightPanelMode('candidates');
        setActiveHeaderTab('sourcing');
        if (!candidates || candidates.length === 0) {
          if (talentScoutingStarted && !sourcingComplete) {
            setSystemMessages(['Candidate sourcing is in progress. Please wait...']);
          } else if (!talentScoutingStarted) {
            setSystemMessages(['No candidates available yet. Please start "Source Candidates" to find candidates.']);
          } else {
            setSystemMessages(['No candidates available for this session.']);
          }
        } else {
          appendAgentChatActivity('📋 Check the candidate list in the right sourcing panel.', automationSessionId);
        }
        return;
      }

      if (wantsSourcing) {
        console.warn('[QFTRACE:return_wantsSourcing]');
        setRightPanelMode('sourcing');
        setActiveHeaderTab('sourcing');
        if (!talentScoutingStarted) {
          initiateSourceCandidate();
        }
        return;
      }
    }

    const hasPersistedJobPostingLogs =
      (getSessionState(automationSessionId)?.jobPosting?.linkedinLogs?.length > 0 ||
        getSessionState(automationSessionId)?.jobPosting?.naukriLogs?.length > 0) ||
      linkedinLogs.length > 0 ||
      naukriLogs.length > 0;
    const isJobPostingActivelyProcessing =
      effectiveLinkedinPostingProcessing || effectiveNaukriPostingProcessing || jobPostingPhase === 'posting';
    if ((isJobPostingActivelyProcessing || hasPersistedJobPostingLogs) && chatMode !== 'ask') {
      const lowerTrimmed = trimmed.toLowerCase();
      const isRetryIntent = /\b(retry|repost)\b/.test(lowerTrimmed);
      const wantsPostJob = /post\s*job|post\s*jd|job\s*post|show\s*logs?|view\s*logs?|posting\s*logs?/.test(lowerTrimmed);
      const explicitPlatformMatch = lowerTrimmed.match(/\b(linkedin|naukri)\b/);
      const explicitPlatform = explicitPlatformMatch
        ? (explicitPlatformMatch[1] === 'linkedin' ? 'LinkedIn' : 'Naukri')
        : null;
      const explicitPlatformCanStart = explicitPlatform &&
        availablePostingChannels.includes(explicitPlatform) &&
        !effectivePostedChannels.includes(explicitPlatform) &&
        !(
          (explicitPlatform === 'LinkedIn' && effectiveLinkedinPostingProcessing) ||
          (explicitPlatform === 'Naukri' && effectiveNaukriPostingProcessing)
        );
      const isExplicitChannelPostIntent =
        wantsPostJob &&
        Boolean(explicitPlatform) &&
        !isRetryIntent &&
        /\b(to|on)\b/.test(lowerTrimmed);
      if (isExplicitChannelPostIntent && explicitPlatformCanStart) {
        // Let the explicit channel post request continue to backend instead of
        // hijacking it into the generic "show logs" response.
      } else
      if ((wantsPostJob && !isRetryIntent) || !trimmed) {
        // Switch right panel to show job posting logs
        setActiveHeaderTab('job_posting');
        setRightPanelMode('auto');
        const sessionState = getSessionState(automationSessionId);
        const hasLogs = (sessionState?.jobPosting?.linkedinLogs?.length > 0 || sessionState?.jobPosting?.naukriLogs?.length > 0) ||
                        (linkedinLogs.length > 0 || naukriLogs.length > 0);
        if (hasLogs) {
          // Restore logs from session state if React state is empty
          if (linkedinLogs.length === 0 && sessionState?.jobPosting?.linkedinLogs?.length > 0) {
            setLinkedinLogs(sessionState.jobPosting.linkedinLogs);
          }
          if (naukriLogs.length === 0 && sessionState?.jobPosting?.naukriLogs?.length > 0) {
            setNaukriLogs(sessionState.jobPosting.naukriLogs);
          }
          setSystemMessages(['📋 Job posting logs are displayed in the right panel.']);
          setSystemMessageToken((prev) => prev + 1);
        } else {
          setSystemMessages(['📋 Job posting is in progress. Logs will appear in the right panel as they come in.']);
          setSystemMessageToken((prev) => prev + 1);
        }
        return;
      }
    }

    if (jobPostingPhase === 'channel_selection' && chatMode !== 'ask') {
      const channelNames = availablePostingChannels;
      
      // Helper to format a list of channels for display text
      const formatChannelList = (list) => {
        if (list.length === 1) return list[0];
        if (list.length === 2) return `${list[0]} and ${list[1]}`;
        const allButLast = list.slice(0, -1).join(', ');
        const last = list[list.length - 1];
        return `${allButLast} and ${last}`;
      };
      
      // Smart channel detection — uses backend OpenAI with local fallback
      const detectChannelsFromInput = (text) => {
        const lower = text.toLowerCase().trim();
        if (/\b(both|all|dono|sab|two)\b/i.test(lower)) return [...channelNames];
        const quickMatch = channelNames.filter(ch => lower.includes(ch.toLowerCase()));
        if (quickMatch.length > 0) return quickMatch;
        
        // Fuzzy matching for misspellings
        const detected = [];
        if (/\b(linked\s*in|linkdin|linkden|linkdein|linkin|linkedin|linkd?in|lnkdn|lnkdin)\b/i.test(lower) && channelNames.includes('LinkedIn')) detected.push('LinkedIn');
        if (/\b(naukri|nauri|anurki|nakri|nukri|naukr|nauki|naukr?i|nkri|nawkri|naukrii|naukkri)\b/i.test(lower) && channelNames.includes('Naukri')) detected.push('Naukri');
        if (/\b1\b/.test(lower) && channelNames.length >= 1 && !detected.includes(channelNames[0])) detected.push(channelNames[0]);
        if (/\b2\b/.test(lower) && channelNames.length >= 2 && !detected.includes(channelNames[1])) detected.push(channelNames[1]);
        
        // If no match found, try the backend detect-channel API
        if (detected.length === 0) {
          return null; // Signal to caller to try backend
        }
        return detected;
      };
      
      // Check if message is "Post JD to [channels]" format (from button selection or manual typing)
      const postJDToMatch = trimmed.match(/post\s+jd\s+to\s+(.+)/i);
      if (postJDToMatch) {
        const channelText = postJDToMatch[1].trim();
        let postJDChannels = detectChannelsFromInput(channelText);
        if (!postJDChannels) {
          try {
            setChatApiLoading(true);
            const response = await aiAutomationApi.post('/post-job/detect-channel', {
              user_input: channelText,
              available_channels: channelNames,
            });
            setChatApiLoading(false);
            postJDChannels = (response.data?.success && response.data?.channels?.length > 0) ? response.data.channels : [];
          } catch (err) {
            setChatApiLoading(false);
            postJDChannels = [];
          }
        }
        
        if (postJDChannels.length > 0) {
          setSelectedChannels(postJDChannels);
          // Persist to session store
          updateSessionState(automationSessionId, (state) => {
            state.jobPosting.selectedChannels = postJDChannels;
            return state;
          });
          // Skip text-based question flow - directly open confirmation modal
          await openJobPostingConfirmationWithAnalysis(postJDChannels);
          return;
        }
      }
      
      // Check if user typed channel names directly (without "Post JD to")
      let matchedChannels = detectChannelsFromInput(trimmed);
      
      // If local matching failed, try backend API for fuzzy detection
      if (!matchedChannels) {
        try {
          setChatApiLoading(true);
          const response = await aiAutomationApi.post('/post-job/detect-channel', {
            user_input: trimmed,
            available_channels: channelNames,
          });
          setChatApiLoading(false);
          if (response.data?.success && response.data?.channels?.length > 0) {
            matchedChannels = response.data.channels;
          } else {
            matchedChannels = [];
          }
        } catch (err) {
          setChatApiLoading(false);
          console.warn('[detectChannelsFromInput] Backend failed:', err);
          matchedChannels = [];
        }
      }
      
      if (matchedChannels.length > 0) {
        setSelectedChannels(matchedChannels);
        // Persist to session store
        updateSessionState(automationSessionId, (state) => {
          state.jobPosting.selectedChannels = matchedChannels;
          return state;
        });
        // Skip text-based question flow - directly open confirmation modal
        await openJobPostingConfirmationWithAnalysis(matchedChannels);
        return;
      }
      
      // If user has selected a channel via button and pressed Enter (empty message)
      // This is a fallback: user clicks button, then presses Enter without text
      if (selectedChannels.length > 0 && trimmed === '') {
        console.log('Channel selection: Proceeding with selected channel:', selectedChannels[0]);
        // Skip text-based question flow - directly open confirmation modal
        await openJobPostingConfirmationWithAnalysis(selectedChannels);
        return;
      }

      // If user mentions channels that are already posted, guide to remaining channels
      const attemptedPostedChannels = ALL_POST_CHANNELS.filter(
        (channel) =>
          effectivePostedChannels.includes(channel) &&
          trimmed.toLowerCase().includes(channel.toLowerCase())
      );
      if (attemptedPostedChannels.length > 0) {
        const remainingText =
          availablePostingChannels.length > 0
            ? availablePostingChannels.join(', ')
            : 'none';
        setSystemMessages([
          `You already posted this job in ${attemptedPostedChannels.join(', ')}. Please choose remaining channels: ${remainingText}.`,
        ]);
        return;
      }
      
      // Allow key navigation commands even while channel selection is open
      const normalizedChannelText = trimmed.toLowerCase();
      const wantsViewJd = /view\s*jd|show\s*jd|open\s*jd|job\s*description/.test(normalizedChannelText);
      const wantsViewCandidates =
        /show\s*candidates?|view\s*candidates?|candidate\s*list|profiles?/.test(normalizedChannelText);
      const wantsSourcing = isSourcingRelatedIntent(normalizedChannelText);

      if (wantsViewJd) {
        setRightPanelMode('jd');
        setActiveHeaderTab('job_posting');
        return;
      }

      if (wantsViewCandidates) {
        setRightPanelMode('candidates');
        setActiveHeaderTab('sourcing');
        if (!candidates || candidates.length === 0) {
          // Don't auto-trigger sourcing when user just wants to view candidates
          // If sourcing was started, show waiting message
          // If sourcing hasn't started, tell user to start sourcing first
          if (talentScoutingStarted && !sourcingComplete) {
            setSystemMessages(['Candidate sourcing is in progress. Please wait...']);
          } else if (!talentScoutingStarted) {
            setSystemMessages(['No candidates available yet. Please start "Source Candidates" to find candidates.']);
          } else {
            setSystemMessages(['No candidates available for this session.']);
          }
        } else {
          appendAgentChatActivity('📋 Check the candidate list in the right sourcing panel.', automationSessionId);
        }
        return;
      }

      if (wantsSourcing) {
        // Always set to 'sourcing' mode when user wants to source candidates
        setRightPanelMode('sourcing');
        setActiveHeaderTab('sourcing');
        if (!talentScoutingStarted) {
          initiateSourceCandidate();
        }
        return;
      }

      // Check if user typed "post job" or similar - re-show channel selection message
      const jobPostingKeywordsInChannelSelection = ['post', 'posting', 'post jd', 'post job', 'job post', 'jobposting'];
      const wantsToPostJob = jobPostingKeywordsInChannelSelection.some(keyword => 
        normalizedChannelText.includes(keyword.toLowerCase())
      );
      if (wantsToPostJob) {
        if (channelNames.length === 1) {
          setSelectedChannels(channelNames);
          updateSessionState(automationSessionId, (state) => {
            state.jobPosting.selectedChannels = channelNames;
            return state;
          });
          await openJobPostingConfirmationWithAnalysis(channelNames);
          return;
        }

        // Capture current session before showing channel selection
        const currentSessionForJobPosting = automationSessionId;
        setPendingJobPostingSessionId(currentSessionForJobPosting);
        pendingJobPostingSessionRef.current = currentSessionForJobPosting;
        
        // Re-show the channel selection message
        const alreadyPostedNotice =
          postedChannels.length > 0
            ? `Already posted in: ${postedChannels.join(', ')}.\nOnly remaining channels are shown.\n\n`
            : '';
        const channelSelectionMessage = `${alreadyPostedNotice}Select the Channel you want to post first?\n\nP.S. - We can upload the JD to multiple Channels but we have to go one by one`;
        
        // Store channel selection message to database
        try {
          await aiAutomationApi.post(`/sessions/${currentSessionForJobPosting}/messages`, {
            role: 'assistant',
            message: channelSelectionMessage,
            phase: 'job_posting_channel_selection',
            metadata: {
              available_channels: channelNames,
              already_posted: postedChannels
            }
          });
        } catch (dbErr) {
          console.error('Failed to store channel selection message:', dbErr);
        }
        
        // Only update UI if still viewing the same session
        if (currentViewedSessionRef.current === currentSessionForJobPosting) {
          setSystemMessages([channelSelectionMessage]);
          setSystemMessageToken((prev) => prev + 1); // Force re-add message
        }
        return;
      }

      // If we're in channel_selection and this isn't a known command, ask user to clarify
      const channelListDisplay = channelNames.length > 0 ? channelNames.join(', ') : 'LinkedIn, Naukri';
      setSystemMessages([
        `I couldn't identify the channel from your input. Please type one of the following:\n\n- **LinkedIn** — Post to LinkedIn\n- **Naukri** — Post to Naukri\n- **Both** — Post to both channels\n\nAvailable channels: ${channelListDisplay}`,
      ]);
      setSystemMessageToken((prev) => prev + 1);
      return;
    }

    // Allow empty messages only when in channel_selection phase (already handled above)
    if (!trimmed) return;

    // ═══════════════════════════════════════════════════════════════
    // UNIFIED CHAT FLOW — ALL messages route through /chat endpoint
    // The backend LangGraph supervisor handles ALL intent classification
    // and routing. Frontend only handles UI-specific guards above.
    // ═══════════════════════════════════════════════════════════════
    // Use URL session_id first (set by navigation), then React state, then generate new
    let requestSessionId = options.sessionId || automationSessionId || selectedJobId || sessionId;

    // Generate session_id if still missing
    if (!requestSessionId) {
      requestSessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      console.log('[CHAT] Generated missing session_id:', requestSessionId);
    }
    if (options.sessionId && String(options.sessionId) === String(sessionId || options.sessionId)) {
      currentViewedSessionRef.current = String(options.sessionId);
    }
    // Always sync state with the session we're using
    if (requestSessionId !== automationSessionId) {
      setAutomationSessionId(requestSessionId);
      setSelectedJobId(requestSessionId);
      currentSessionRef.current = requestSessionId;
    }
    if (!searchParams.get('session_id')) {
      setSearchParams(buildSearchParams({ session_id: requestSessionId }), { replace: true });
    }

    // Show typing indicator immediately
    setSessionLoader(requestSessionId, 'chatApi', true);

    // If user message wasn't persisted earlier (e.g. dashboard search where session didn't exist yet),
    // save it now that we have a valid session ID
    if (trimmed && !persistedUserMessage && requestSessionId && !isLocalUiOnlyCommand) {
      persistedUserMessage = true;
      aiAutomationApi.post(`/sessions/${requestSessionId}/messages`, {
        message_type: 'user',
        content: trimmed,
        message_role: 'user',
        ...(chatMode ? { metadata: { mode: chatMode } } : {}),
      }).catch((e) => {
        console.warn('Failed to persist user chat message (deferred)', e);
      });
    }

    try {

    // ── Build additional_data for the backend ──
    const chatSessionId = requestSessionId;
    // Use the best session-scoped search_id we can resolve for this chat turn.
    const chatSearchId = getCurrentSessionSearchId(chatSessionId) || null;

    const chatAdditionalData = {
      search_id: chatSearchId || '',
      action: 'lifecycle',  // Let backend supervisor decide actual routing
    };
    if (chatMode) chatAdditionalData.mode = chatMode;
    if (workingCandidates && workingCandidates.length > 0) {
      chatAdditionalData.working_candidates = workingCandidates;
    }
    const currentRequirementsForChat = {
      ...(accumulatedRequirements || {}),
      ...(finalRequirements || {}),
    };
    const hasCurrentRequirementsForChat = Object.values(currentRequirementsForChat).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
    if (hasCurrentRequirementsForChat) {
      chatAdditionalData.current_requirements = currentRequirementsForChat;
    }
    if (jdText && String(jdText).trim()) {
      chatAdditionalData.current_jd_text = jdText;
    }
    if (options.additionalData && typeof options.additionalData === 'object') {
      Object.assign(chatAdditionalData, options.additionalData);
    }
    if (
      isInterviewSchedulingIntent(normalizedTrimmed)
      && chatAdditionalData.conversational_setup !== false
      && !chatAdditionalData.preselected_setup_type
    ) {
      const onlyEnabledInterviewType = getOnlyEnabledInterviewType();
      if (onlyEnabledInterviewType) {
        chatAdditionalData.preselected_setup_type = onlyEnabledInterviewType;
      }
    }

    console.log('[CHAT] Sending to /chat — single unified flow', {
      session_id: chatSessionId,
      search_id: chatSearchId,
      message: trimmed.substring(0, 60),
    });

    // ── Abort controller for session switching ──
    const abortCtrl = new AbortController();
    lifecycleAbortRef.current = abortCtrl;

    // Track inflight
    lifecycleInflightRef.current = true;
    try { sessionStorage.setItem(`lifecycle_inflight_${chatSessionId}`, JSON.stringify({ sessionId: chatSessionId, ts: Date.now(), message: trimmed })); } catch (_) {}

    // ═══════════════════════════════════════════════════════════════
    // SINGLE /chat CALL — backend LangGraph supervisor handles ALL
    // intent classification, routing, and response generation.
    // ═══════════════════════════════════════════════════════════════
    console.warn('[QFTRACE:reached_chat_call]', _qfTraceId, {
      sessionId: chatSessionId,
      message: trimmed.slice(0, 60),
    });
    console.log('[handleUserQueryForJD] Making /chat API call with:', { message: trimmed, session_id: chatSessionId, additional_data: chatAdditionalData });
    const chatResponse = await aiAutomationApi.post('/chat', {
      message: trimmed,
      session_id: chatSessionId,
      cid: getCid(),
      additional_data: chatAdditionalData,
    });
    console.log('[handleUserQueryForJD] /chat API response:', chatResponse);

    // Stale-response guard — check what user is actually viewing
    if (currentViewedSessionRef.current !== requestSessionId) {
      console.log('[CHAT] Discarding stale response — user switched sessions', { request: requestSessionId, viewing: currentViewedSessionRef.current });
      // Still persist to session store (below), but skip all React state updates
      if (chatResponse?.success && chatResponse?.response && requestSessionId) {
        const staleResp = chatResponse.response;
        updateSessionState(requestSessionId, (s) => {
          const hasBreadcrumbs = Array.isArray(staleResp.filter_breadcrumbs);
          const hasActions = Array.isArray(staleResp.filter_actions);
          const hasWorkingCandidates = Array.isArray(staleResp.working_candidates);
          const hasFollowUpSuggestions = Array.isArray(staleResp.follow_up_suggestions);
          s.filtering = {
            chatMode: staleResp.mode || 'agent',
            breadcrumbs: hasBreadcrumbs ? staleResp.filter_breadcrumbs : (s.filtering?.breadcrumbs || []),
            actions: hasActions ? staleResp.filter_actions : (s.filtering?.actions || []),
            workingCandidates: hasWorkingCandidates ? staleResp.working_candidates : (s.filtering?.workingCandidates || []),
            followUpSuggestions: hasFollowUpSuggestions ? staleResp.follow_up_suggestions : (s.filtering?.followUpSuggestions || []),
          };
          return s;
        });
      }
      return;
    }

    if (!chatResponse?.success || !chatResponse?.response) {
      const errorMsg = chatResponse?.response?.error || chatResponse?.error || 'Something went wrong. Please try again.';
      setAndPersistSystemMessage([errorMsg], { phase: 'chat' });
      return;
    }

    const resp = chatResponse.response;
    const isCurrentSession = currentViewedSessionRef.current === requestSessionId;
    const frontendAction = isCurrentSession ? resp.frontend_action : null;
    if (isCurrentSession && Array.isArray(resp.workspace_options)) {
      setWorkspaceOptions(resp.workspace_options);
    }
    if (isCurrentSession && typeof resp.workspace_selection_required === 'boolean') {
      setWorkspaceSelectionRequired(resp.workspace_selection_required);
    }
    let responseMessage = resp.message || 'Request processed.';
    if (
      frontendAction === 'start_sync' &&
      /sync started/i.test(responseMessage)
    ) {
      responseMessage = 'Sync started. Track live progress in the Sourcing panel on the right.';
    }
    if (frontendAction === 'start_sourcing' && blockDisabledFeature('sourcing')) {
      console.warn('[QFTRACE:return_feature_disabled_backend_sourcing]', _qfTraceId);
      return;
    }

    if (resp.search_id && String(resp.search_id).startsWith('search_')) {
      setAutomationSearchId(resp.search_id);
      searchIdBySessionRef.current[requestSessionId] = resp.search_id;
      updateSessionState(requestSessionId, (state) => {
        state.searchId = resp.search_id;
        return state;
      });
    }

    // Update candidate data if backend returns updated candidates
    if (resp.updated_candidates && Array.isArray(resp.updated_candidates)) {
      resp.updated_candidates.forEach(updatedCandidate => {
        if (updatedCandidate.email) {
          updateCandidateData(updatedCandidate.email, updatedCandidate);
        }
      });
    }

    // Piggyback: update AI health status from chat response (zero extra API calls)
    if (resp.ai_health) {
      window.dispatchEvent(new CustomEvent('aiHealthUpdate', { detail: resp.ai_health }));
    }

    console.log('[CHAT] Response received:', {
      type: resp.type,
      intent: resp.execution?.intent,
      agent: resp.current_agent,
      stage: resp.stage_label,
      frontend_action: resp.frontend_action,
    });

    // ── Update stage awareness banner + agent transition pill ──
    const newStageLabel = resp.stage_label || '';

    // Backend provides agent_pill for workflow agent transitions (not for utility agents like pipeline).
    // Stage-label changes are used for banner display (JD Creation, Candidate Sourcing).
    const agentPill = resp.agent_pill || null;
    const stageChanged = newStageLabel && newStageLabel !== agentStageLabel;
    const stageExited = !newStageLabel && agentStageLabel;

    let activatedLabel = null;
    let exitedLabel = null;

    if (agentPill) {
      // Backend explicitly says show a pill
      if (agentPill.type === 'activated') activatedLabel = agentPill.label;
      else if (agentPill.type === 'exited') exitedLabel = agentPill.label;
    } else if (stageChanged) {
      activatedLabel = newStageLabel;
    } else if (stageExited) {
      exitedLabel = agentStageLabel;
    }

    // Show step pills for multi-step flows (scheduling) — no toast, just inline pill
    // Only show step pill when there's NO agent activation (internal step transition)
    const stepPill = resp.step_pill;

    if (activatedLabel) {
      // Agent activation: show pill + toast ONLY if it's a real new agent (not a step transition)
      if (!stepPill) {
        showToast(`${activatedLabel} Agent activated`, 'info', 3000);
      }
      // If there's also a step pill, show it AFTER the activation (handled below)
    } else if (exitedLabel) {
      showToast(`Exited: ${exitedLabel}`, 'info', 2000);
    }

    // Step pills — show after agent pill processing, only for internal step transitions
    if (stepPill && !activatedLabel) {
      setSystemMessages((prev) => [...prev, `__STEP_PILL__${stepPill}`]);
      setSystemMessageToken((prev) => prev + 1);
    }
    setAgentStageLabel(newStageLabel);
    setAgentStageCancellable(resp.stage_cancellable || false);
    // Persist stage label to session store for cross-session isolation
    if (requestSessionId) {
      updateSessionState(requestSessionId, (s) => {
        s.agentStage = { label: newStageLabel, cancellable: resp.stage_cancellable || false };
        return s;
      });
    }
    setLastResponseType(resp.type || '');
    setScorecardSetupStep(resp.setup_step || '');
    setScorecardSetupSessionId(resp.setup_step ? requestSessionId : '');
    if (!resp.setup_step && (isCurrentSession || currentSessionRef.current === requestSessionId) &&
        ['lifecycle_setup_collect', 'lifecycle_setup_review'].includes(resp.type)) {
      aiAutomationApi.get(`/chat/setup-scorecard/${encodeURIComponent(requestSessionId)}`)
        .then((editor) => {
          if (currentViewedSessionRef.current === requestSessionId || currentSessionRef.current === requestSessionId) {
            setScorecardSetupStep(editor?.setup_step || '');
            setScorecardSetupSessionId(editor?.setup_step ? requestSessionId : '');
          }
        })
        .catch(() => {});
    }

    // ── Track response mode (agent/ask) ──
    setLastResponseMode(resp.mode || 'agent');

    // ── Track lifecycle interaction time ──
    lastLifecycleInteractionRef.current = Date.now();

    // ── Persist agent transition messages to DB ──
    if (activatedLabel) {
      const transitionMsg = `${activatedLabel} Agent activated`;
      try {
        await aiAutomationApi.post(`/sessions/${requestSessionId}/messages`, {
          message_type: 'assistant',
          content: transitionMsg,
          message_role: 'assistant',
          metadata: { type: 'agent_transition', agent: activatedLabel, action: 'activated' },
        });
      } catch (_) {}
    } else if (exitedLabel) {
      const transitionMsg = `Exited ${exitedLabel}`;
      try {
        await aiAutomationApi.post(`/sessions/${requestSessionId}/messages`, {
          message_type: 'assistant',
          content: transitionMsg,
          message_role: 'assistant',
          metadata: { type: 'agent_transition', agent: exitedLabel, action: 'exited' },
        });
      } catch (_) {}
    }

    // ── Inject agent transition pill + response as chat messages ──
    const messagesToShow = [];
    if (activatedLabel) {
      messagesToShow.push(`__AGENT_TRANSITION__${activatedLabel} Agent activated`);
    } else if (exitedLabel) {
      messagesToShow.push(`__AGENT_TRANSITION__Exited ${exitedLabel}`);
    }
    if (responseMessage) {
      messagesToShow.push(responseMessage);
    }

    // ── Display the response message (skip if empty — e.g. silent ack) ──
    if (messagesToShow.length > 0) {
      setAndPersistSystemMessage(messagesToShow, {
        phase: resp.type?.startsWith('lifecycle_') || resp.type === 'ask_response' ? 'lifecycle' : 'chat',
        skipPersist: true,
        forSessionId: requestSessionId,
      });
    }

    // ── Handle frontend_action hints from backend ──
    // Only apply UI-changing actions if user is still viewing this session
    if (
      frontendAction === 'open_bulk_reply_drafts'
      && Array.isArray(resp.bulk_drafts)
      && resp.bulk_drafts.length > 0
    ) {
      setBulkReplyDraftModal({ isOpen: true, drafts: resp.bulk_drafts });
      setLifecycleAwaitingConfirmation(false);
      setDraftEditActions([]);
    }

    if (frontendAction === 'start_sourcing') {
      // Backend told us user wants sourcing — trigger the frontend sourcing flow
      // Ensure search_id is set before triggering (React state is async)
      const sourcingSearchId = resp.search_id || automationSearchId || getSafeSearchId(requestSessionId);
      if (sourcingSearchId) {
        setAutomationSearchId(sourcingSearchId);
        if (requestSessionId) {
          searchIdBySessionRef.current[requestSessionId] = sourcingSearchId;
        }
        if (chatSessionId && chatSessionId !== requestSessionId) {
          searchIdBySessionRef.current[chatSessionId] = sourcingSearchId;
        }
      }
      // Fetch channel preferences and start sourcing directly (bypasses async state issues)
      const prefs = await fetchChannelPreferences();
      const srcChannels = prefs?.sourcing_channels || [];
      if (srcChannels.length === 0) {
        setChannelSelectionType('sourcing');
        setPendingSourcingSessionId(requestSessionId);
        pendingSourcingSessionRef.current = requestSessionId;
        setShowChannelSelectionModal(true);
      } else {
        setSelectedSourcingChannels(srcChannels);
        await startTalentScouting(srcChannels, requestSessionId);
      }
      // NOTE: Do NOT override rightPanelMode here — initiateSourceCandidate() → startTalentScouting()
      // sets it to 'sourcing' which is the correct mode for showing live sourcing logs.
      // Setting 'auto' here would override that and hide the logs panel.
      setActiveHeaderTab('sourcing');
      setRightPanelMode('sourcing');
      setSourcingExpandToken((prev) => prev + 1);
    } else if (frontendAction === 'start_job_posting') {
      // Backend told us user wants to post job — enter channel selection
      // Backend already verified JD exists, so no need to re-check jdText
      setJobPostingPhase('channel_selection');
      setActiveHeaderTab('job_posting');
      setRightPanelMode('auto');
      setJobPostingExpandToken((prev) => prev + 1);
      // Show banner during the frontend-managed job posting flow
      setAgentStageLabel('Job Posting');
      setAgentStageCancellable(true);
    } else if (frontendAction === 'show_job_posting_logs') {
      // Job posting is in progress or already done — switch right panel to show logs
      setActiveHeaderTab('job_posting');
      setRightPanelMode('auto');
      setJobPostingExpandToken((prev) => prev + 1);
    } else if (frontendAction === 'show_candidates_panel') {
      // Backend told us user wants to see candidates
      setRightPanelMode('candidates');
      if (!shouldPreserveJobPostingView) {
        setActiveHeaderTab('sourcing');
        setSourcingExpandToken((prev) => prev + 1);
      }
      setSourcingComplete(true);
      // Fetch candidates from DB if not already loaded
      const currentSessionForFetch = automationSessionId || sessionId;
      if (currentSessionForFetch && (!candidates || candidates.length === 0)) {
        try {
          const fetchResp = await aiAutomationApi.get(`/automation_candidates/all?session_id=${currentSessionForFetch}&limit=50`);
          const dbCandidates = fetchResp?.data || fetchResp?.candidates || (Array.isArray(fetchResp) ? fetchResp : []);
          if (dbCandidates.length > 0) {
            setCandidates(dbCandidates);
            console.log(`[show_candidates_panel] Loaded ${dbCandidates.length} candidates from DB`);
          }
        } catch (fetchErr) {
          console.warn('[show_candidates_panel] Failed to fetch candidates:', fetchErr);
        }
      }
    } else if (frontendAction === 'show_jd_panel') {
      // Backend told us user wants to see the JD
      if (typeof switchRightPanelWithLoader === 'function') {
        switchRightPanelWithLoader('jd');
      } else {
        setRightPanelMode('jd');
      }
      setActiveHeaderTab('job_posting');
    } else if (frontendAction === 'jd_created') {
      // JD was just created — store it and show in result panel
      const createdJdText = resp.jd_text || '';
      const createdJdTitle = resp.jd_title || '';
      const createdSearchId = resp.search_id || '';
      if (createdJdText) {
        setJdText(createdJdText);

        // Set search_id so sourcing/post-job can use it
        if (createdSearchId) {
          setAutomationSearchId(createdSearchId);
          // Also store in the session→searchId mapping
          if (chatSessionId) {
            searchIdBySessionRef.current[chatSessionId] = createdSearchId;
          }
          console.log('[CHAT] automationSearchId set from JD creation:', createdSearchId);
        }

        // Show JD in result panel
        if (typeof switchRightPanelWithLoader === 'function') {
          switchRightPanelWithLoader('jd');
        } else {
          setRightPanelMode('jd');
        }
        setActiveHeaderTab('job_posting');
        setAnalysisUxPhase('done');
        openJdCriteriaReview(chatSessionId || automationSessionId || selectedJobId || sessionId, createdJdTitle || currentInterviewJobTitle);

        // Update session title to the JD job title
        if (createdJdTitle && chatSessionId) {
          try {
            await aiAutomationApi.patch(`/sessions/${chatSessionId}/title`, { title: createdJdTitle });
            // Also update the sidebar list so the title shows immediately
            setJobPostings((prev) =>
              prev.map((job) =>
                job.id === chatSessionId ? { ...job, title: createdJdTitle } : job
              )
            );
            console.log('[CHAT] Session title updated to JD title:', createdJdTitle);
          } catch (e) {
            console.warn('Failed to update session title to JD title', e);
          }
          // Set job title as subject_key so email sync can search by subject
          try {
            await aiAutomationApi.put('/email/session-subject-key', { session_id: chatSessionId, subject_key: createdJdTitle });
            console.log('[CHAT] subject_key set to JD title:', createdJdTitle);
          } catch (e) {
            console.warn('Failed to set subject_key from JD title', e);
          }
        }
      }
    } else if (frontendAction === 'start_sync') {
      // Backend already started the sync — just connect SSE to track progress
      const syncSearchId =
        resp.search_id ||
        getCurrentSessionSearchId(requestSessionId) ||
        getSafeSearchId(chatSessionId) ||
        '';
      const syncId = resp.sync_id;
      if (syncSearchId && syncId) {
        setActiveHeaderTab('sourcing');
        setRightPanelMode('sourcing');
        setSourcingExpandToken((prev) => prev + 1);
        setAutomationSearchId(syncSearchId);
        searchIdBySessionRef.current[requestSessionId] = syncSearchId;
        updateSessionState(requestSessionId, (state) => {
          state.searchId = syncSearchId;
          return state;
        });
        setCurrentSearchId(syncSearchId);
        updateSyncState(syncSearchId, {
          syncing: true,
          phase: 'syncing',
          logs: [responseMessage || 'Sync started'],
          progress: responseMessage || 'Sync started',
        });
        const handleSyncMailAuthRequired = async (syncError) => {
          const needsMailAuth = Boolean(syncError?.gmail_auth_needed || syncError?.mail_auth_needed)
            || isMailProviderAuthError(syncError);
          if (!needsMailAuth) return false;
          showToast('Mail authorization required. Opening authorization to continue sync...', 'warning');
          await handleMailProviderReconnect({
            cid: getCid(),
            error: syncError,
            onPopupBlocked: (err, providerLabel, provider) => setMailAuthPrompt({
              authUrl: err.authUrl,
              provider,
              providerLabel,
              action: 'sync',
              sessionId: requestSessionId,
              searchId: syncSearchId,
              retryMessage: 'yes',
            }),
            onConnecting: (providerLabel) => setAndPersistSystemMessage(
              [`Connecting ${providerLabel}... Please complete authorization in the popup window.`],
              { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId }
            ),
            onConnected: async (email, providerLabel, _provider, connectionResult) => {
              showToast(`${providerLabel} connected${email ? ` (${email})` : ''}!`, 'success');
              setAndPersistSystemMessage(
                [`${providerLabel} connected successfully${email ? ` (${email})` : ''}! Retrying sync...`],
                { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId }
              );
              const resumed = await attachResumedMailAuthSync({
                resumed: getMailOAuthResumed(connectionResult),
                fallbackSearchId: syncSearchId,
                sessionId: requestSessionId,
                providerLabel,
              });
              if (resumed) return;
              const resumeKey = getMailOAuthResumeKey({
                pendingMailAuthActionId: syncError?.pending_mail_auth_action_id || syncError?.pendingMailAuthActionId,
                sessionId: requestSessionId,
                searchId: syncSearchId,
                action: 'sync',
              });
              if (mailOAuthResumeHandledRef.current.has(resumeKey) || mailOAuthResumeInFlightRef.current.has(resumeKey)) return;
              await new Promise(resolve => setTimeout(resolve, 2000));
              const retrySync = await startSync(syncSearchId, { sessionId: requestSessionId, cid: getCid(), force: true });
              if (retrySync?.started || retrySync?.alreadySyncing) {
                setAndPersistSystemMessage([
                  'Sync started. Track live progress in the Sourcing panel on the right.'
                ], { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId });
              } else {
                await markMailAuthSyncResumeComplete({
                  searchId: syncSearchId,
                  sessionId: requestSessionId,
                  providerLabel,
                  resumeKey,
                });
              }
            },
          });
          return true;
        };
        connectToExistingSync(syncSearchId, syncId, {
          onFail: async (syncError) => {
            await handleSyncMailAuthRequired(syncError);
          },
        });
        window.setTimeout(async () => {
          try {
            const status = await aiAutomationApi.get(`/email/sync-status/${encodeURIComponent(syncId)}`);
            if (status?.status === 'failed') {
              await handleSyncMailAuthRequired(status);
            }
          } catch (statusError) {
            console.warn('[sync] Failed to check immediate sync status', statusError);
          }
        }, 1200);
      }
    } else if (!frontendAction && isCurrentSession) {
      // Auto-detect: if job posting is processing and response mentions it, show logs panel
      if ((linkedinProcessing || naukriProcessing || jobPostingPhase === 'posting') && 
          (linkedinLogs.length > 0 || naukriLogs.length > 0)) {
        const responseText = (resp.message || resp.response || '').toLowerCase();
        if (responseText.includes('in progress') || responseText.includes('posting') || responseText.includes('logs')) {
          setActiveHeaderTab('job_posting');
          setRightPanelMode('auto');
        }
      }
    }

    // ── Clear stale right panel when in JD creation flow ──
    // During JD creation, the right panel should show the analysis/JD section,
    // not outreach logs or sourcing from a previous flow
    const respType = resp.type || '';
    if (respType.startsWith('jd_') && respType !== 'jd_saved') {
      // Clear outreach state so "Live Outreach In Progress" doesn't show
      if (outreachPhase && outreachPhase !== 'idle') {
        setOutreachPhase(null);
      }
      // Set right panel to show the default analysis view
      setRightPanelMode('auto');
      setActiveHeaderTab('job_posting');
      // Set analysis phase to 'analysing' to show the "Analyzing Your Request" section
      if (analysisUxPhase === 'idle') {
        setAnalysisUxPhase('analysing');
      }
    }

    // ── Handle lifecycle candidates — populate right panel ──
    if (resp.lifecycle_candidates && Array.isArray(resp.lifecycle_candidates) && resp.lifecycle_candidates.length > 0) {
      const mappedCandidates = resp.lifecycle_candidates.map((c, idx) => ({
        id: c.id || `lifecycle_${idx}`,
        name: c.name || 'Unknown',
        email: c.email || '',
        phone: c.phone || '',
        title: c.current_title || c.headline || '',
        current_title: c.current_title || '',
        headline: c.headline || '',
        company: c.current_company || '',
        current_company: c.current_company || '',
        location: c.location || '',
        experience: c.experience || '',
        skills: c.skills || '',
        match_percentage: c.match_percentage || 0,
        score_details: c.score_details || c.scoreDetails || {},
        status: c.status || c.funnel_stage || '',
        funnel_stage: c.funnel_stage || '',
        doc_id: c.doc_id || '',
        source: c.source || 'lifecycle',
        _source_type: c.source || 'lifecycle',
        platforms: Array.isArray(c.platforms) && c.platforms.length > 0 ? c.platforms : (c.source ? [c.source] : ['lifecycle']),
        linkedin_url: c.linkedin_url || '',
        linkedin_profile_url: c.linkedin_url || '',
        profile_url: c.profile_url || c.linkedin_url || c.email || `lifecycle_${c.id || idx}`,
        priority: c.priority || '',
        search_id: automationSearchId || '',
        highlights: c.highlights || [],
        key_skills: c.highlights || [],
        summary: c.summary || c.summary_text || '',
        summary_text: c.summary_text || c.summary || '',
        match_breakdown: c.match_breakdown || {},
        experience_years: c.experience_years || '',
        cv_filename: c.cv_filename || '',
      }));
      setCandidates(mappedCandidates);
      setRightPanelMode('candidates');
      // Don't yank the panel to Sourcing while the conversational interview setup is running — it
      // must stay on the Interview section so the live blueprint is visible.
      const isLifecycleSetupResp = typeof resp.type === 'string' && resp.type.startsWith('lifecycle_setup');
      if (!shouldPreserveJobPostingView && !isLifecycleSetupResp) {
        setActiveHeaderTab('sourcing');
      }
      setTalentPoolChannelFilter([]);
    }

    // ── Conversational interview setup: keep the Interview section active and show the live
    //    blueprint as it's built (updates after every answer). ──
    if (typeof resp.type === 'string' && resp.type.startsWith('lifecycle_setup')) {
      setActiveHeaderTab('interview');
    }
    if (resp.setup_draft) {
      setInterviewSetupDraft(resp.setup_draft);
    } else {
      // Any response without a live draft ends the preview — the setup was saved, cancelled, or the
      // flow was exited (e.g. "Exited Interview Scheduling" → type 'agent_cancelled'). Functional
      // update keeps unrelated responses cheap (no-op when already cleared).
      setInterviewSetupDraft((prev) => (prev ? null : prev));
    }

    // ── Handle suggestions / action buttons ──
    if (resp.suggestions && Array.isArray(resp.suggestions) && resp.suggestions.length > 0) {
      setLifecycleActions(resp.suggestions.map((s, idx) => ({
        id: `lifecycle_${s.action || idx}`,
        label: s.label,
        command: s.command || s.label,
      })));
    } else {
      // Clear old buttons so they don't persist from previous responses
      setLifecycleActions([]);
    }

    // ── Store pipeline snapshot for button logic ──
    if (resp.pipeline_snapshot) setPipelineSnapshot(resp.pipeline_snapshot);

    // ── Handle filter state from Ask-mode filtering agent ──
    console.log('[CHAT] Filter state in response:', {
      type: resp.type,
      has_breadcrumbs: !!resp.filter_breadcrumbs,
      breadcrumbs: resp.filter_breadcrumbs,
      has_actions: !!resp.filter_actions,
      actions_count: resp.filter_actions?.length,
      has_working: !!resp.working_candidates,
      working_count: resp.working_candidates?.length,
    });
    // Only update React filter state if user is still viewing this session
    if (isCurrentSession && Array.isArray(resp.filter_breadcrumbs)) {
      setFilterBreadcrumbs(resp.filter_breadcrumbs);
    }
    if (isCurrentSession && Array.isArray(resp.filter_actions)) {
      setFilterActions(resp.filter_actions);
    }
    if (isCurrentSession && Array.isArray(resp.working_candidates)) {
      setWorkingCandidates(resp.working_candidates);
    }
    // Follow-up suggestion chips — replace on every turn so the user
    // always sees the suggestions for the LATEST response only. Empty
    // array if the response doesn't carry any.
    if (isCurrentSession) {
      const fus = Array.isArray(resp.follow_up_suggestions) ? resp.follow_up_suggestions : [];
      setFollowUpSuggestions(fus);
    }
    // New-candidates hint count. Backend sets this when a refresh / read
    // path detected more matches than the cached working set. We always
    // overwrite (not max) so that running a refresh — which the BE
    // returns with new_candidates_count=0 — properly clears the chip.
    if (isCurrentSession) {
      const nc = Number.isInteger(resp.new_candidates_count) ? resp.new_candidates_count : 0;
      setNewCandidatesCount(nc);
    }
    // Clear filter state when response explicitly clears it (filter_cleared type)
    if (isCurrentSession && resp.type === 'filter_cleared') {
      setFilterBreadcrumbs([]);
      setFilterActions([]);
      setWorkingCandidates([]);
      setFollowUpSuggestions([]);
      setNewCandidatesCount(0);
    }
    // Persist filter state to session store after every response
    if (requestSessionId) {
      const fb = Array.isArray(resp.filter_breadcrumbs) ? resp.filter_breadcrumbs : filterBreadcrumbs;
      const fa = Array.isArray(resp.filter_actions) ? resp.filter_actions : filterActions;
      const wc = Array.isArray(resp.working_candidates) ? resp.working_candidates : workingCandidates;
      const fus = Array.isArray(resp.follow_up_suggestions) ? resp.follow_up_suggestions : followUpSuggestions;
      updateSessionState(requestSessionId, (s) => {
        s.filtering = {
          chatMode: resp.mode || chatMode,
          breadcrumbs: resp.type === 'filter_cleared' ? [] : (Array.isArray(fb) ? fb : []),
          actions: resp.type === 'filter_cleared' ? [] : (Array.isArray(fa) ? fa : []),
          workingCandidates: resp.type === 'filter_cleared' ? [] : (Array.isArray(wc) ? wc : []),
          followUpSuggestions: resp.type === 'filter_cleared' ? [] : (Array.isArray(fus) ? fus : []),
        };
        return s;
      });
    }

    // ── Handle edit actions (draft preview buttons) ──
    if (resp.edit_actions && Array.isArray(resp.edit_actions) && resp.edit_actions.length > 0) {
      setDraftEditActions(resp.edit_actions);
      setLifecycleAwaitingConfirmation(true);
    } else if (resp.type && !resp.type.includes('preview') && !resp.type.includes('confirm')) {
      // Clear confirmation state if response isn't a preview/confirm type
      if (lifecycleAwaitingConfirmation && !resp.type.includes('schedule') && !resp.type.includes('reschedule')) {
        setLifecycleAwaitingConfirmation(false);
        setDraftEditActions([]);
      }
    }

    // ── Handle scheduling flow state ──
    // Cancel checked first (some cancel types contain "schedule"). Conversational setup phases
    // (lifecycle_setup_*) keep the flow active so subsequent answers route to the scheduling agent.
    if (resp.type === 'lifecycle_schedule_cancelled' || resp.type === 'agent_cancelled') {
      setLifecycleScheduleActive(false);
    } else if (resp.type?.includes('schedule') || resp.type?.includes('setup')) {
      setLifecycleScheduleActive(true);
    }
    // Keep the Interview tab panel in sync with setup/scheduling changes made in chat
    // (so a newly-saved blueprint and updated buckets appear without a manual reload).
    if (resp.type?.includes('schedule') || resp.type?.includes('setup') || resp.setup_saved) {
      setInterviewRefreshKey((k) => k + 1);
    }
    // Track whether a setup is mid-creation so we can block a second concurrent creation.
    if (resp.type?.startsWith('lifecycle_setup')) {
      setInterviewSetupInProgress(true);
    } else if (resp.type === 'lifecycle_schedule_cancelled' || resp.type === 'agent_cancelled'
               || resp.type?.includes('schedule')) {
      // Moved past setup (into scheduling) or cancelled → creation no longer in progress.
      setInterviewSetupInProgress(false);
    }

    // ── Handle Google Calendar auth ──
    if (resp.auth_url) {
      console.log('[CHAT] Google Calendar auth required, opening popup');
      const w = window.open(resp.auth_url, 'gcal_auth', 'width=600,height=700');
      // window.open here runs after the /chat await, so the browser usually blocks it. If blocked,
      // keep the URL and show a Connect button the user can click (a fresh gesture opens it reliably).
      if (!w || w.closed || typeof w.closed === 'undefined') {
        setGoogleCalendarAuthUrl(resp.auth_url);
      }
    }

    // ── Handle mail provider auth needed ──
    if (resp.gmail_auth_needed || resp.mail_auth_needed || isMailProviderAuthError(resp)) {
      console.log('[CHAT] Mail provider auth required');
      const mailAuthAction = resp.gmail_auth_action || resp.mail_auth_action || '';
      const mailAuthResumeSearchId =
        resp.search_id ||
        resp.searchId ||
        (automationSearchId?.startsWith('search_') ? automationSearchId : '') ||
        getSafeSearchId(requestSessionId);
      showToast('Mail authorization required. Opening authorization to continue...', 'warning');
      setLifecycleAwaitingConfirmation(true);
      setTimeout(async () => {
        if (currentSessionRef.current !== requestSessionId) return;
        await handleMailProviderReconnect({
          cid: getCid(),
          error: resp,
          onPopupBlocked: (err, providerLabel, provider) => setMailAuthPrompt({
            authUrl: err.authUrl,
            provider,
            providerLabel,
            action: mailAuthAction || 'outreach',
            sessionId: requestSessionId,
            searchId: mailAuthResumeSearchId,
            retryMessage: 'yes',
          }),
          resumeContext: {
            source: 'chat_lifecycle',
            sessionId: requestSessionId,
            searchId: mailAuthResumeSearchId,
            action: mailAuthAction || 'outreach',
            retryMessage: 'yes',
          },
          onConnecting: (providerLabel) => setAndPersistSystemMessage([`Connecting ${providerLabel}... Please complete authorization in the popup window.`], { phase: 'lifecycle' }),
          onConnected: async (email, providerLabel, _provider, connectionResult) => {
            if (currentSessionRef.current !== requestSessionId) return;
            showToast(`${providerLabel} connected${email ? ` (${email})` : ''}!`, 'success');
            const isSyncRetry = mailAuthAction === 'sync' || /sync/i.test(trimmed || '');
            const isScheduleRetry = mailAuthAction === 'schedule';
            const isOutreachRetry = !isSyncRetry && !isScheduleRetry && isMailAuthOutreachAction(mailAuthAction);
            const _resumeLabel = isSyncRetry ? 'sync' : isScheduleRetry ? 'interview scheduling' : 'outreach';
            setAndPersistSystemMessage([
              `${providerLabel} connected successfully${email ? ` (${email})` : ''}! Resuming ${_resumeLabel}...`
            ], { phase: 'lifecycle' });
            setLifecycleAwaitingConfirmation(false);
            if (isOutreachRetry && hasResumedMailAuthOutreach(connectionResult)) {
              clearMailAuthOutreachResumeState(requestSessionId);
              setAndPersistSystemMessage([
                'Outreach sent successfully after mail authorization.'
              ], { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId });
              return;
            }
            if (isSyncRetry) {
              const resumed = await attachResumedMailAuthSync({
                resumed: getMailOAuthResumed(connectionResult),
                fallbackSearchId: mailAuthResumeSearchId,
                sessionId: requestSessionId,
                providerLabel,
              });
              if (resumed) return;
              const resumeKey = getMailOAuthResumeKey({
                pendingMailAuthActionId: resp?.pending_mail_auth_action_id || resp?.pendingMailAuthActionId,
                sessionId: requestSessionId,
                searchId: mailAuthResumeSearchId,
                action: mailAuthAction || 'sync',
              });
              if (mailOAuthResumeHandledRef.current.has(resumeKey) || mailOAuthResumeInFlightRef.current.has(resumeKey)) return;
            }
            // Wait briefly for OAuth tokens to be committed to DB before retrying
            await new Promise(resolve => setTimeout(resolve, 2000));
            if (currentSessionRef.current !== requestSessionId) return;
            const retryKey = getMailOAuthResumeKey({
              pendingMailAuthActionId: resp?.pending_mail_auth_action_id || resp?.pendingMailAuthActionId,
              sessionId: requestSessionId,
              searchId: mailAuthResumeSearchId,
              action: mailAuthAction || 'outreach',
            });
            if (mailOAuthResumeHandledRef.current.has(retryKey)) return;
            mailOAuthResumeHandledRef.current.add(retryKey);
            // Auto-retry the pending action
            try {
              if (isSyncRetry) {
                const syncSearchId =
                  mailAuthResumeSearchId ||
                  (automationSearchId?.startsWith('search_') ? automationSearchId : '') ||
                  getSafeSearchId(requestSessionId);
                if (syncSearchId) {
                  setActiveHeaderTab('sourcing');
                  setRightPanelMode('sourcing');
                  setSourcingExpandToken((prev) => prev + 1);
                  setAutomationSearchId(syncSearchId);
                  searchIdBySessionRef.current[requestSessionId] = syncSearchId;
                  setCurrentSearchId(syncSearchId);
                  const retrySync = await startSync(syncSearchId, { sessionId: requestSessionId, cid: getCid(), force: true });
                  if (retrySync?.started || retrySync?.alreadySyncing) {
                    setAndPersistSystemMessage([
                      'Sync started. Track live progress in the Sourcing panel on the right.'
                    ], { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId });
                  } else {
                    await markMailAuthSyncResumeComplete({
                      searchId: syncSearchId,
                      sessionId: requestSessionId,
                      providerLabel,
                      resumeKey: retryKey,
                    });
                  }
                  return;
                }
              }
              const retryResp = await aiAutomationApi.post('/chat', {
                message: 'yes',
                session_id: requestSessionId,
                cid: getCid(),
                additional_data: { search_id: (automationSearchId?.startsWith('search_') ? automationSearchId : '') || getSafeSearchId(requestSessionId), action: 'lifecycle', mode: 'agent', cid: getCid() },
              });
              if (retryResp?.success && retryResp?.response) {
                const retryMsg = retryResp.response.message || 'Outreach sent!';
                setAndPersistSystemMessage([retryMsg], { phase: 'lifecycle', skipPersist: true, forSessionId: requestSessionId });
                if (isOutreachRetry) {
                  clearMailAuthOutreachResumeState(requestSessionId);
                } else {
                  if (retryResp.response.stage_label !== undefined) setAgentStageLabel(retryResp.response.stage_label || '');
                  if (retryResp.response.stage_cancellable !== undefined) setAgentStageCancellable(retryResp.response.stage_cancellable || false);
                }
              }
            } catch (retryErr) {
              console.warn('[CHAT] Mail provider reconnect auto-retry failed:', retryErr);
            }
          },
          onError: (msg) => {
            if (currentSessionRef.current !== requestSessionId) return;
            showToast(`Mail connection failed: ${msg}`, 'error');
            setLifecycleAwaitingConfirmation(false);
          },
        });
      }, 1500);
    }

    // ── Smart title update on first message (skip if JD title was just set) ──
    if ((!selectedJob?.title || selectedJob.title === 'New Chat') && frontendAction !== 'jd_created') {
      const titleText = trimmed.length > 40 ? trimmed.substring(0, 40) + '...' : trimmed;
      try {
        await aiAutomationApi.patch(`/sessions/${chatSessionId}/title`, { title: titleText });
      } catch (_) {}
    }

    // Unified flow complete

    } finally {
      // Ensure typing indicator is cleared after all processing completes
      lifecycleInflightRef.current = false;
      try { sessionStorage.removeItem(`lifecycle_inflight_${requestSessionId}`); } catch (_) {}
      setSessionLoader(requestSessionId, 'chatApi', false);
      setSessionLoader(requestSessionId, 'lifecycle', false);
    }
  };

  /* ══════════════════════════════════════════════════════════════════
   * OLD ROUTING CODE REMOVED (~1200 lines)
   * - /detect-intent API calls
   * - Keyword matching (500+ lines of regex)
   * - analyzePostJdFollowup function
   * - Scattered /chat calls (8 different places)
   * - handleChatMessage fallback for JD input
   *
   * ALL replaced by the single unified /chat call above.
   * The backend LangGraph supervisor handles ALL intent classification.
   * ══════════════════════════════════════════════════════════════════ */

  // Kept for backward compat — called nowhere after unified rewrite
  // but other parts of ChatPage may reference it
  const _legacyHandleChatMessage = null;

  // (Old routing code fully removed — see git history for reference)


  // Map field names to user-friendly questions (each field asked separately)
  const getFieldQuestion = (fieldName) => {
    // Guard against null/undefined fieldName
    if (!fieldName) {
      return null;
    }
    
    const fieldQuestions = {
      // Individual fields - each asked separately
      companyName: '🏢 What is the company name for this job posting?',
      company_name: '🏢 What is the company name for this job posting?',
      salaryMin: '💰 What is the minimum salary for this position? (Enter a number, e.g., 50000)',
      salary_min: '💰 What is the minimum salary for this position? (Enter a number, e.g., 50000)',
      salaryMax: '💰 What is the maximum salary for this position? (Enter a number, e.g., 100000)',
      salary_max: '💰 What is the maximum salary for this position? (Enter a number, e.g., 100000)',
      experienceMin: '📊 What is the minimum experience required? (Enter years, e.g., 0 2)',
      experience_min: '📊 What is the minimum experience required? (Enter years, e.g., 0 for fresher, 2)',
      experienceMax: '📊 What is the maximum experience required? (Enter years, e.g., 5)',
      experience_max: '📊 What is the maximum experience required? (Enter years, e.g., 5)',
      allowCandidatesToCall: '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
      allow_call: '📞 Should candidates be allowed to call?\n(Type "Yes" or "No")',
      recruiterName: '👤 What is the recruiter name for this job posting?',
      recruiter_name: '👤 What is the recruiter name for this job posting?',
      recruiterNumber: '📱 What is the recruiter contact number? (10-digit mobile number)',
      recruiter_number: '📱 What is the recruiter contact number? (10-digit mobile number)',
      employment_type: '📋 What is the employment type?\n(Full-time, Part-time, Contract, or Internship)',
      employmentType: '📋 What is the employment type?\n(Full-time, Part-time, Contract, or Internship)',
      workplace_type: '🏠 What is the workplace type?\n(On-site, Hybrid, or Remote)',
      workplaceType: '🏠 What is the workplace type?\n(On-site, Hybrid, or Remote)',
      location: '📍 What is the job location? (City, Country)',
    };
    return fieldQuestions[fieldName] || `Please provide the ${fieldName.replace(/_/g, ' ')}:`;
  };

  // Ask the next field in the job posting fields queue
  const askNextJobPostingField = async () => {
    if (pendingJobPostingFieldsQueue.length === 0) {
      // All fields collected - open confirmation modal
      console.log('[Job Posting] All fields collected, opening confirmation modal');
      setShowJobPostingConfirmationModal(true);
      setSystemMessages([]);
      return;
    }

    // Get the next field to ask
    const nextField = pendingJobPostingFieldsQueue[0];
    const question = getFieldQuestion(nextField);
    
    // Set the current field being asked
    setPendingNaukriField(nextField);
    
    // Show question in chat
    setSystemMessages([question]);
    
    // Store the question in database
    try {
      if (automationSessionId) {
        await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
          message_type: 'assistant',
          content: question,
          message_role: 'assistant',
          metadata: { phase: 'job_posting_field_collection', field_name: nextField },
        });
      }
    } catch (e) {
      console.warn('Failed to persist field question to chat', e);
    }
  };

  // Map backend field names (camelCase) to modal field names (snake_case)
  const fieldNameToModalKey = (fieldName) => {
    const mapping = {
      'companyName': 'company_name',
      'salaryMin': 'salary_min',
      'salaryMax': 'salary_max',
      'experienceMin': 'experience_min',
      'experienceMax': 'experience_max',
      'allowCandidatesToCall': 'allow_call',
      'recruiterName': 'recruiter_name',
      'recruiterNumber': 'recruiter_number',
      'employmentType': 'employment_type',
      'workplaceType': 'workplace_type',
    };
    return mapping[fieldName] || fieldName;
  };

  // Handle job posting field response and ask next question
  const handleJobPostingFieldResponse = async (fieldName, fieldValue) => {
    let validationError = null;
    const valueTrimmed = (fieldValue || '').trim();
    const valueClean = valueTrimmed.replace(/,/g, '').replace(/\s/g, '');
    
    // Object to hold parsed values
    let parsedValues = {};
    
    // Handle each field type individually
    if (fieldName === 'allowCandidatesToCall' || fieldName === 'allow_call') {
      // Parse Yes/No to boolean
      const lower = valueTrimmed.toLowerCase();
      if (lower === 'yes' || lower === 'y' || lower === 'true' || lower === '1') {
        parsedValues = { allow_call: true };
      } else if (lower === 'no' || lower === 'n' || lower === 'false' || lower === '0') {
        parsedValues = { allow_call: false };
      } else {
        validationError = 'Please answer "Yes" or "No".';
      }
    } else if (fieldName === 'companyName' || fieldName === 'company_name') {
      if (/^\d+$/.test(valueClean)) {
        validationError = `"${fieldValue}" is not a valid company name. Company name cannot be only numbers.`;
      } else {
        parsedValues = { company_name: valueTrimmed };
      }
    } else if (fieldName === 'salaryMin' || fieldName === 'salary_min') {
      const numValue = parseInt(valueClean, 10);
      if (isNaN(numValue) || numValue <= 0) {
        validationError = 'Please enter a valid salary amount greater than 0.';
      } else {
        parsedValues = { salary_min: numValue };
      }
    } else if (fieldName === 'salaryMax' || fieldName === 'salary_max') {
      const numValue = parseInt(valueClean, 10);
      if (isNaN(numValue) || numValue <= 0) {
        validationError = 'Please enter a valid salary amount greater than 0.';
      } else {
        // Cross-field validation: salaryMax must be >= salaryMin
        const salaryMin = collectedJobPostingDetails?.salary_min;
        if (salaryMin && numValue < salaryMin) {
          validationError = `Maximum salary (${numValue}) must be greater than or equal to minimum salary (${salaryMin}).`;
        } else {
          parsedValues = { salary_max: numValue };
        }
      }
    } else if (fieldName === 'experienceMin' || fieldName === 'experience_min') {
      const numValue = parseInt(valueClean, 10);
      if (isNaN(numValue) || numValue < 0) {
        validationError = 'Please enter experience in years (e.g., 0 for fresher, 2).';
      } else {
        parsedValues = { experience_min: numValue };
      }
    } else if (fieldName === 'experienceMax' || fieldName === 'experience_max') {
      const numValue = parseInt(valueClean, 10);
      if (isNaN(numValue) || numValue < 0) {
        validationError = 'Please enter experience in years (e.g., 5).';
      } else {
        // Cross-field validation: experienceMax must be >= experienceMin
        const experienceMin = collectedJobPostingDetails?.experience_min;
        if (experienceMin !== undefined && experienceMin !== null && numValue < experienceMin) {
          validationError = `Maximum experience (${numValue} years) must be greater than or equal to minimum experience (${experienceMin} years).`;
        } else {
          parsedValues = { experience_max: numValue };
        }
      }
    } else if (fieldName === 'recruiterNumber' || fieldName === 'recruiter_number') {
      const phoneClean = valueClean.replace(/\D/g, '');
      if (phoneClean.length !== 10) {
        validationError = 'Please enter a valid 10-digit mobile number.';
      } else {
        parsedValues = { recruiter_number: phoneClean };
      }
    } else {
      // For other fields, just use the value as-is
      const modalKey = fieldNameToModalKey(fieldName);
      parsedValues = { [modalKey]: valueTrimmed };
    }
    
    if (validationError) {
      const errorMessage = `❌ ${validationError}`;
      const currentQuestion = getFieldQuestion(fieldName);
      // Show error AND re-show the current question so user knows what to answer
      setSystemMessages([errorMessage, currentQuestion]);
      setSystemMessageToken((prev) => prev + 1); // Force re-render in ChatMain
      
      // Store validation error in database for chat history persistence
      try {
        if (automationSessionId) {
          await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
            message_type: 'assistant',
            content: `${errorMessage}\n${currentQuestion}`,
            message_role: 'assistant',
            metadata: { phase: 'job_posting_field_collection', field_name: fieldName, validation_error: true },
          });
        }
      } catch (e) {
        console.warn('Failed to persist validation error to chat', e);
      }
      
      return false;
    }
    
    // Store parsed values in collected details
    setCollectedJobPostingDetails((prev) => ({
      ...prev,
      ...parsedValues,
    }));

    setPostingDetails((prev) => ({
      ...prev,
      ...collectedDetailsToPostingDetails(parsedValues),
    }));
    
    // Store response in naukriFieldResponses for additional validation
    setNaukriFieldResponses((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
    
    // Store user response in database
    try {
      if (automationSessionId) {
        await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
          message_type: 'user',
          content: fieldValue,
          message_role: 'user',
          metadata: { phase: 'job_posting_field_collection', field_name: fieldName, parsed_values: parsedValues },
        });
      }
    } catch (e) {
      console.warn('Failed to persist field response to chat', e);
    }
    
    // Calculate remaining fields BEFORE updating state (since state update is async)
    const remainingFields = pendingJobPostingFieldsQueue.slice(1);
    
    // Remove the answered field from queue
    setPendingJobPostingFieldsQueue(remainingFields);
    
    // Update ref immediately with remaining queue (keeps isActive true until setTimeout completes)
    fieldCollectionStateRef.current = {
      isActive: remainingFields.length > 0,
      currentField: remainingFields[0] || null, // Next field or null if none left
      queue: remainingFields,
    };
    
    // CRITICAL: Update session store IMMEDIATELY with new queue (don't wait for React state)
    // This ensures session switching during field collection doesn't lose progress
    if (automationSessionId) {
      updateSessionState(automationSessionId, (state) => {
        state.jobPosting.pendingFieldsQueue = remainingFields;
        state.jobPosting.pendingField = remainingFields[0] || null;
        state.jobPosting.fieldCollectionActive = remainingFields.length > 0;
        // Also update collected details with the new parsed values
        state.jobPosting.collectedDetails = {
          ...(state.jobPosting.collectedDetails || {}),
          ...parsedValues,
        };
        return state;
      });
    }
    
    // IMPORTANT: Keep pendingNaukriField non-null during transition to prevent
    // race conditions where user input could trigger other handlers
    // We'll set it to the next field or null after the next question is shown
    
    // Show confirmation of received answer
    let confirmMsg = `✅ Got it - ${fieldName.replace(/_/g, ' ')}: ${fieldValue}`;
    if (fieldName === 'allow_call' || fieldName === 'allowCandidatesToCall') {
      confirmMsg = `✅ Got it - Allow calls: ${parsedValues.allow_call ? 'Yes' : 'No'}`;
    }
    setSystemMessages([confirmMsg]);
    setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
    
    // CRITICAL: Capture session ID to prevent cross-session contamination
    // If user switches sessions during the delay, don't update UI for wrong session
    const capturedSessionId = automationSessionId;
    
    // Small delay before asking next question
    setTimeout(async () => {
      // SESSION GUARD: Check if user is still viewing the same session
      const isStillCurrentSession = currentViewedSessionRef.current === capturedSessionId;
      
      if (remainingFields.length === 0) {
        // All fields collected - update session store and open modal only if still current
        console.log('[Job Posting] All fields collected, opening confirmation modal');
        
        // Always update session store (specific to this session)
        if (capturedSessionId) {
          updateSessionState(capturedSessionId, (state) => {
            state.jobPosting.pendingField = null;
            state.jobPosting.pendingFieldsQueue = [];
            state.jobPosting.fieldCollectionActive = false;
            state.chat.systemMessages = [];
            return state;
          });
        }
        
        // Only update React state if still viewing this session
        if (isStillCurrentSession) {
          setPendingNaukriField(null);
          fieldCollectionStateRef.current = { isActive: false, currentField: null, queue: [] };
          setShowJobPostingConfirmationModal(true);
          setSystemMessages([]);
        }
      } else {
        // Get the next field to ask
        const nextField = remainingFields[0];
        const question = getFieldQuestion(nextField);
        
        // Always update session store (specific to this session)
        if (capturedSessionId) {
          updateSessionState(capturedSessionId, (state) => {
            state.jobPosting.pendingField = nextField;
            state.jobPosting.pendingFieldsQueue = remainingFields;
            state.jobPosting.fieldCollectionActive = true;
            state.chat.systemMessages = [question];
            return state;
          });
        }
        
        // Only update React state if still viewing this session
        if (isStillCurrentSession) {
          setPendingNaukriField(nextField);
          fieldCollectionStateRef.current = {
            isActive: true,
            currentField: nextField,
            queue: remainingFields,
          };
          setSystemMessages([question]);
          setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
        }
        
        // Store the question in database IMMEDIATELY (use captured session ID)
        try {
          if (capturedSessionId) {
            await aiAutomationApi.post(`/sessions/${capturedSessionId}/messages`, {
              message_type: 'assistant',
              content: question,
              message_role: 'assistant',
              metadata: { phase: 'job_posting_field_collection', field_name: nextField },
            });
          }
        } catch (e) {
          console.warn('Failed to persist field question to chat', e);
        }
      }
    }, 500);
    
    return true;
  };

  // Start job posting field collection flow - asks questions one by one
  const openJobPostingConfirmationWithAnalysis = async (channels, seededCollectedDetails = null) => {
    if (blockDisabledFeature('job_posting')) return;
    // Use the captured session ID from when job posting was initiated, not the current session
    // This ensures job posting happens for the correct session even if user switched
    const effectiveSessionId = pendingJobPostingSessionRef.current || pendingJobPostingSessionId || automationSessionId;
    console.log('[openJobPostingConfirmationWithAnalysis] Using session:', effectiveSessionId, 'pending:', pendingJobPostingSessionRef.current, 'current:', automationSessionId);
    
    try {
      setActiveHeaderTab('job_posting');
      setRightPanelMode('auto');
      const persistedCollectedDetails =
        getSessionState(effectiveSessionId)?.jobPosting?.collectedDetails ||
        getPersistedJobPostingState(effectiveSessionId)?.collectedDetails ||
        {};
      const existingCollectedDetails = {
        ...persistedCollectedDetails,
        ...(collectedJobPostingDetails || {}),
        ...(seededCollectedDetails || {}),
      };
      const existingPostingDetails = {
        ...postingDetails,
        ...collectedDetailsToPostingDetails(existingCollectedDetails),
      };

      setSystemMessages(['📋 Analyzing job description to identify required fields...']);
      setChatApiLoading(true);
      setJobPostingPhase('collecting_details');
      setPendingJobPostingChannels(channels);
      
      // CRITICAL: Immediately update session store with phase and selected channels
      // This ensures state is preserved if user switches sessions before analysis completes
      if (effectiveSessionId) {
        updateSessionState(effectiveSessionId, (state) => {
          state.jobPosting.phase = 'collecting_details';
          state.jobPosting.selectedChannels = channels;
          state.jobPosting.pendingChannels = channels;
          state.chat.systemMessages = ['📋 Analyzing job description to identify required fields...'];
          return state;
        });
      }
      
      // Call backend to analyze JD and extract all required fields
      const analysisResponse = await aiAutomationApi.post('/post-job/validate-and-prepare', {
        session_id: effectiveSessionId,
        connection_id: wsConnectionId,  // Send connection_id for targeted OTP messaging
        job_description: jdText,
        job_title: finalRequirements?.title || finalRequirements?.role || '',
        company_name: existingCollectedDetails.company_name || existingPostingDetails.companyName || '',
        salary_min: existingCollectedDetails.salary_min || existingPostingDetails.salaryMin || 0,
        salary_max: existingCollectedDetails.salary_max || existingPostingDetails.salaryMax || 0,
        experience_min: existingCollectedDetails.experience_min || existingPostingDetails.experienceMin || 0,
        experience_max: existingCollectedDetails.experience_max || existingPostingDetails.experienceMax || 0,
        allow_candidates_to_call: existingCollectedDetails.allow_call ?? existingPostingDetails.allowCandidatesToCall ?? null,
        recruiter_name: existingCollectedDetails.recruiter_name || existingPostingDetails.recruiterName || '',
        recruiter_number: existingCollectedDetails.recruiter_number || existingPostingDetails.recruiterNumber || '',
        department: existingCollectedDetails.department || existingPostingDetails.department || '',
        additional_data: {
          channels: channels,
          intent_message: `Post job to ${channels.join(', ')}`,
          employment_type: existingCollectedDetails.employment_type || existingPostingDetails.employmentType || '',
          workplace_type: existingCollectedDetails.workplace_type || existingPostingDetails.workplaceType || '',
          location: existingCollectedDetails.location || existingPostingDetails.jobLocation || finalRequirements?.location || '',
        },
      });
      
      setChatApiLoading(false);
      if (analysisResponse?.success) {
        // Use collected_details from backend
        const collected = analysisResponse?.collected_details || {};
        const missingFieldsObj = analysisResponse?.missing_fields || {};
        const missingFieldNames = Object.keys(missingFieldsObj);
        
        // Initialize collected details with what we already have
        const preparedDetails = buildJobPostingCollectedDetails({
          collectedDetails: existingCollectedDetails,
          postingValues: existingPostingDetails,
          preparedValues: {
            ...collected,
            _prepared_job_details: analysisResponse?.prepared_job_details,
            _platforms: analysisResponse?.platforms || channels,
          },
          channels,
          finalRequirements,
        });

        setCollectedJobPostingDetails(preparedDetails);
        
        const backendMissingFields = missingFieldNames
          .map((fieldName) => normalizeJobPostingFieldKey(fieldName))
          .filter((fieldName) => fieldName !== 'department');
        const actualMissingFields = getMissingJobPostingFields(preparedDetails);
        const actualMissingFieldSet = new Set(actualMissingFields);
        const fieldsToAsk = [
          ...backendMissingFields.filter((fieldName) => actualMissingFieldSet.has(fieldName)),
          ...actualMissingFields.filter((fieldName) => !backendMissingFields.includes(fieldName)),
        ];
        
        if (fieldsToAsk.length > 0) {
          // Start asking the first question with header message
          const firstField = fieldsToAsk[0];
          const question = getFieldQuestion(firstField);
          const headerMessage = `📝 **Job Posting Details Required**\nI need a few more details to complete your job posting. Type "cancel" at any time to stop.\n\n${question}`;
          
          // CRITICAL: Only update React state if user is still viewing the session where job posting was initiated
          const isStillViewingThisSession = currentViewedSessionRef.current === effectiveSessionId || 
                                            currentViewedSessionRef.current === automationSessionId;
          
          if (isStillViewingThisSession) {
            // Set the queue of fields to ask
            setPendingJobPostingFieldsQueue(fieldsToAsk);
            setPendingNaukriField(firstField);
            setSystemMessages([headerMessage]);
            setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
            
            // Update ref synchronously for immediate availability
            fieldCollectionStateRef.current = {
              isActive: true,
              currentField: firstField,
              queue: fieldsToAsk,
            };
          }
          
          // CRITICAL: ALWAYS update session store regardless of current view
          // This ensures state is saved for when user switches back to this session
          if (effectiveSessionId) {
            updateSessionState(effectiveSessionId, (state) => {
              state.jobPosting.pendingFieldsQueue = fieldsToAsk;
              state.jobPosting.pendingField = firstField;
              state.jobPosting.fieldCollectionActive = true;
              state.jobPosting.collectedDetails = preparedDetails;
              state.chat.systemMessages = [headerMessage];
              return state;
            });
          }
          
          // Store question in database IMMEDIATELY when displayed
          try {
            if (effectiveSessionId) {
              await aiAutomationApi.post(`/sessions/${effectiveSessionId}/messages`, {
                message_type: 'assistant',
                content: headerMessage,
                message_role: 'assistant',
                metadata: { phase: 'job_posting_field_collection', field_name: firstField },
              });
              console.log('[Job Posting] Saved question to DB:', firstField);
            }
          } catch (e) {
            console.warn('Failed to persist field question to chat', e);
          }
        } else {
          // No missing fields except department - go directly to modal
          if (effectiveSessionId) {
            updateSessionState(effectiveSessionId, (state) => {
              state.jobPosting.pendingFieldsQueue = [];
              state.jobPosting.pendingField = null;
              state.jobPosting.fieldCollectionActive = false;
              state.jobPosting.collectedDetails = preparedDetails;
              state.chat.systemMessages = [];
              return state;
            });
          }
          // Only show modal if still viewing this session
          const isStillViewing = currentViewedSessionRef.current === effectiveSessionId || 
                                 currentViewedSessionRef.current === automationSessionId;
          if (isStillViewing) {
            setPendingJobPostingFieldsQueue([]);
            setPendingNaukriField(null);
            setShowJobPostingConfirmationModal(true);
            setSystemMessages([]);
          }
        }
      } else {
        const preparedDetails = buildJobPostingCollectedDetails({
          collectedDetails: existingCollectedDetails,
          postingValues: existingPostingDetails,
          channels,
          finalRequirements,
        });
        const defaultFieldsToAsk = getMissingJobPostingFields(preparedDetails);

        setChatApiLoading(false);
        setCollectedJobPostingDetails(preparedDetails);

        if (defaultFieldsToAsk.length === 0) {
          if (effectiveSessionId) {
            updateSessionState(effectiveSessionId, (state) => {
              state.jobPosting.pendingFieldsQueue = [];
              state.jobPosting.pendingField = null;
              state.jobPosting.fieldCollectionActive = false;
              state.jobPosting.collectedDetails = preparedDetails;
              state.chat.systemMessages = [];
              return state;
            });
          }
          setPendingJobPostingFieldsQueue([]);
          setPendingNaukriField(null);
          setShowJobPostingConfirmationModal(true);
          setSystemMessages([]);
          return;
        }

        setPendingJobPostingFieldsQueue(defaultFieldsToAsk);
        
        // Start asking the first question with header message
        const firstField = defaultFieldsToAsk[0];
        const question = getFieldQuestion(firstField);
        const headerMessage = `📝 **Job Posting Details Required**\nI need a few more details to complete your job posting. Type "cancel" at any time to stop.\n\n${question}`;
        setPendingNaukriField(firstField);
        setSystemMessages([headerMessage]);
        setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
        
        // CRITICAL: Update ref synchronously for immediate availability
        fieldCollectionStateRef.current = {
          isActive: true,
          currentField: firstField,
          queue: defaultFieldsToAsk,
        };
        
        // Store the question in database IMMEDIATELY
        try {
          if (automationSessionId) {
            await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
              message_type: 'assistant',
              content: headerMessage,
              message_role: 'assistant',
              metadata: { phase: 'job_posting_field_collection', field_name: firstField },
            });
          }
        } catch (e) {
          console.warn('Failed to persist field question to chat', e);
        }
      }
    } catch (error) {
      setChatApiLoading(false);
      console.error('Error analyzing JD for job posting:', error);
      const existingCollectedDetails = seededCollectedDetails || collectedJobPostingDetails || {};
      const existingPostingDetails = {
        ...postingDetails,
        ...collectedDetailsToPostingDetails(existingCollectedDetails),
      };
      const preparedDetails = buildJobPostingCollectedDetails({
        collectedDetails: existingCollectedDetails,
        postingValues: existingPostingDetails,
        channels,
        finalRequirements,
      });
      const defaultFieldsToAsk = getMissingJobPostingFields(preparedDetails);

      setCollectedJobPostingDetails(preparedDetails);

      if (defaultFieldsToAsk.length === 0) {
        if (effectiveSessionId) {
          updateSessionState(effectiveSessionId, (state) => {
            state.jobPosting.pendingFieldsQueue = [];
            state.jobPosting.pendingField = null;
            state.jobPosting.fieldCollectionActive = false;
            state.jobPosting.collectedDetails = preparedDetails;
            state.chat.systemMessages = [];
            return state;
          });
        }
        setPendingJobPostingFieldsQueue([]);
        setPendingNaukriField(null);
        setShowJobPostingConfirmationModal(true);
        setSystemMessages([]);
        return;
      }

      setPendingJobPostingFieldsQueue(defaultFieldsToAsk);
      
      const firstField = defaultFieldsToAsk[0];
      const question = getFieldQuestion(firstField);
      const headerMessage = `📝 **Job Posting Details Required**\nI need a few more details to complete your job posting. Type "cancel" at any time to stop.\n\n${question}`;
      setPendingNaukriField(firstField);
      setSystemMessages([headerMessage]);
      setSystemMessageToken((prev) => prev + 1); // Force re-add even if duplicate exists in chat history
      
      // CRITICAL: Update ref synchronously for immediate availability
      fieldCollectionStateRef.current = {
        isActive: true,
        currentField: firstField,
        queue: defaultFieldsToAsk,
      };
      
      // Store the question in database IMMEDIATELY
      try {
        if (automationSessionId) {
          await aiAutomationApi.post(`/sessions/${automationSessionId}/messages`, {
            message_type: 'assistant',
            content: headerMessage,
            message_role: 'assistant',
            metadata: { phase: 'job_posting_field_collection', field_name: firstField },
          });
        }
      } catch (e) {
        console.warn('Failed to persist field question to chat', e);
      }
    }
  };

  // Handle job posting confirmation when user clicks "Proceed" in modal
  const handleJobPostingConfirmationProceed = async (confirmedData) => {
    if (blockDisabledFeature('job_posting')) return;
    // Use the captured session ID from when job posting was initiated, not the current session
    const effectiveSessionId = pendingJobPostingSessionRef.current || pendingJobPostingSessionId || automationSessionId;
    console.log('[handleJobPostingConfirmationProceed] Using session:', effectiveSessionId, 'current:', automationSessionId);
    
    // CRITICAL: Clear previous logs for this session before starting new job posting
    // This ensures logs from previous failed/completed job posts don't show
    const isViewingEffectiveSession = currentViewedSessionRef.current === effectiveSessionId;
    
    try {
      setShowJobPostingConfirmationModal(false);
      const channels = confirmedData._channels || pendingJobPostingChannels || selectedChannels || [];
      const shouldResetLinkedin = channels.includes('LinkedIn');
      const shouldResetNaukri = channels.includes('Naukri');
      const confirmedCollectedDetails = buildJobPostingCollectedDetails({
        collectedDetails: {
          ...(getSessionState(effectiveSessionId)?.jobPosting?.collectedDetails || {}),
          ...(getPersistedJobPostingState(effectiveSessionId)?.collectedDetails || {}),
          ...(collectedJobPostingDetails || {}),
        },
        postingValues: {
          ...postingDetails,
          ...collectedDetailsToPostingDetails(confirmedData || {}),
        },
        preparedValues: confirmedData || {},
        channels,
        finalRequirements,
      });

      setCollectedJobPostingDetails(confirmedCollectedDetails);
      
      // Only update React state if viewing the session where job posting is happening
      if (isViewingEffectiveSession) {
        setSystemMessages(['✅ Confirmed! Starting job posting automation...']);
        if (shouldResetLinkedin) {
          setLinkedinLogs([]);
        }
        if (shouldResetNaukri) {
          setNaukriLogs([]);
        }
      }
      logsInitializedRef.current = { linkedin: false, naukri: false };
      
      // CRITICAL: Store "Starting job posting automation" message in database for persistence
      try {
        aiAutomationApi.post(`/sessions/${effectiveSessionId}/messages`, {
          message_type: 'assistant',
          content: '✅ Confirmed! Starting job posting automation...',
          message_role: 'assistant',
          metadata: { 
            phase: 'job_posting_started', 
            channels: channels,
            type: 'job_posting_start'
          },
        }).catch(e => console.warn('Failed to store job posting start message in DB:', e));
      } catch (e) {
        console.warn('Failed to store job posting start message in DB:', e);
      }
      
      // Clear the pending fields queue and session tracking
      setPendingJobPostingFieldsQueue([]);
      setPendingJobPostingChannels([]);
      // Don't clear pendingJobPostingSessionId yet - we still need it for the API call
      
      // ALWAYS update session store regardless of current view
      // Clear logs only for the channels being started now.
      if (effectiveSessionId) {
        updateSessionState(effectiveSessionId, (state) => {
          if (shouldResetLinkedin) {
            state.jobPosting.linkedinLogs = [];
            state.jobPosting.linkedinProcessing = true;
          }
          if (shouldResetNaukri) {
            state.jobPosting.naukriLogs = [];
            state.jobPosting.naukriProcessing = true;
          }
          state.jobPosting.processing = true;
          state.jobPosting.phase = 'posting';
          state.jobPosting.complete = false;
          state.jobPosting.failed = false;
          state.jobPosting.collectedDetails = confirmedCollectedDetails;
          state.jobPosting.selectedChannels = channels;
          state.chat.systemMessages = ['✅ Confirmed! Starting job posting automation...'];
          return state;
        });

        // NOTE: Processing/logs state comes from DB/file API — no localStorage persistence needed for posting start
      }
      
      // Update UI state for posting - only if viewing this session
      if (isViewingEffectiveSession) {
        setJobPostingPhase('posting');
        setJobPostingProcessing(true);
        setJobPostingComplete(false);
        setJobPostingFailed(false);
        setWaitingForBackendQuestion(false);
        // Reset outreach phase so job posting logs can show in right panel
        setOutreachPhase('idle');
        // Ensure job posting header tab is active for correct right panel
        manualTabOverrideRef.current = false;
        setActiveHeaderTab('job_posting');
        // Ensure right panel mode is set to auto for job posting logs
        setRightPanelMode('auto');

        // Exit agent — posting runs in background via WebSocket, no more agent interaction needed
        const exitJobPostAgent = agentStageLabel || 'Job Posting';
        if (agentStageLabel) {
          showToast(`Exited: ${exitJobPostAgent}`, 'info', 2000);
          setSystemMessages((prev) => [...prev, `__AGENT_TRANSITION__Exited ${exitJobPostAgent}`]);
          setSystemMessageToken((prev) => prev + 1);
          try {
            aiAutomationApi.post(`/sessions/${effectiveSessionId}/messages`, {
              message_type: 'assistant',
              content: `Exited ${exitJobPostAgent}`,
              message_role: 'assistant',
              metadata: { type: 'agent_transition', agent: exitJobPostAgent, action: 'exited' },
            }).catch(() => {});
          } catch (_) {}
        }
        setAgentStageLabel('');
        setAgentStageCancellable(false);

        // Notify backend LangGraph to clear agent state (fire-and-forget)
        try {
          aiAutomationApi.post('/chat', {
            message: '__job_posting_started__',
            session_id: effectiveSessionId,
            cid: getCid(),
            additional_data: {
              search_id: (automationSearchId?.startsWith('search_') ? automationSearchId : '') || getSafeSearchId(effectiveSessionId),
              action: 'lifecycle',
              _internal: 'job_posting_started_signal',
            },
          }).catch(() => {});
        } catch (_) {}
      }
      addActiveJobPostingSession(effectiveSessionId); // Track active session with CORRECT session ID
      
      // Set processing state based on selected channels - only if viewing this session
      if (isViewingEffectiveSession) {
        if (shouldResetLinkedin) {
          setLinkedinProcessing(true);
        }
        if (shouldResetNaukri) {
          setNaukriProcessing(true);
        }
      }
      
      const confirmJobPostingPayload = {
        session_id: effectiveSessionId,
        // Send user-edited values from the confirmation modal
        company_name: confirmedData.company_name,
        employment_type: confirmedData.employment_type,
        workplace_type: confirmedData.workplace_type,
        location: confirmedData.location,
        salary_min: String(confirmedData.salary_min ?? ''),
        salary_max: String(confirmedData.salary_max ?? ''),
        experience_min: parseInt(confirmedData.experience_min) || 0,
        experience_max: parseInt(confirmedData.experience_max) || 0,
        allow_call: confirmedData.allow_call,
        recruiter_name: confirmedData.recruiter_name,
        recruiter_number: confirmedData.recruiter_number,
        department: confirmedData.department, // Include department from frontend dropdown
        channels: channels, // Include channels for backend to know which platforms to post to
        additional_data: {
          intent_message: `Post job to ${channels.join(', ')}`,
        },
      };

      await aiAutomationApi.post('/post-job/confirm-job-posting', confirmJobPostingPayload);

      // Clear the pending session tracker only after backend acknowledges the run.
      setPendingJobPostingSessionId(null);
      pendingJobPostingSessionRef.current = null;

      try {
        const processStatus = await aiAutomationApi.get(`/process-status/${effectiveSessionId}`);
        const trackerLinkedinProcessing = processStatus?.job_post_linkedin === 'processing';
        const trackerNaukriProcessing = processStatus?.job_post_naukri === 'processing';
        const trackerJobPostingProcessing =
          processStatus?.job_posting === true ||
          trackerLinkedinProcessing ||
          trackerNaukriProcessing;

        updateSessionState(effectiveSessionId, (state) => {
          state.jobPosting.processing = trackerJobPostingProcessing;
          state.jobPosting.linkedinProcessing = trackerLinkedinProcessing;
          state.jobPosting.naukriProcessing = trackerNaukriProcessing;
          return state;
        });

        if (isViewingEffectiveSession) {
          setJobPostingProcessing(trackerJobPostingProcessing);
          setLinkedinProcessing(trackerLinkedinProcessing);
          setNaukriProcessing(trackerNaukriProcessing);
        }
      } catch (processStatusError) {
        console.warn('Failed to refresh process status after starting job posting:', processStatusError);
      }
      
    } catch (error) {
      if (error?.response?.status === 409) {
        setSystemMessages(['Job posting is already in progress for this session.']);
        return;
      }
      console.error('Error confirming job posting:', error);
      setSystemMessages(['Failed to confirm job posting. Please try again.']);
      setJobPostingProcessing(false);
      setJobPostingFailed(true);
    }
  };

  // Handle job posting preview confirmation
  const handleJobPostingPreviewProceed = async (confirmedData) => {
    if (blockDisabledFeature('job_posting')) return;
    setShowJobPostingPreview(false);
    
    // Extract stored channels and details from the preview data
    const channels = jobPostingPreviewData._channels || selectedChannels;
    const storedDetails = jobPostingPreviewData._details || postingDetails;
    
    if (channels.length > 0 && automationSessionId && jdText) {
      const parsedAllowCalls =
        confirmedData.allowCandidatesToCall === 'Yes'
          ? true
          : confirmedData.allowCandidatesToCall === 'No'
          ? false
          : null;

      const nextPostingDetails = {
        ...storedDetails,
        companyName: confirmedData.companyName || storedDetails.companyName || '',
        salaryMin: confirmedData.salaryMin || storedDetails.salaryMin || '',
        salaryMax: confirmedData.salaryMax || storedDetails.salaryMax || '',
        experienceMin: storedDetails.experienceMin ?? storedDetails.experience_min ?? '',
        experienceMax: storedDetails.experienceMax ?? storedDetails.experience_max ?? '',
        recruiterName: confirmedData.recruiterName || storedDetails.recruiterName || '',
        recruiterNumber: confirmedData.recruiterNumber || storedDetails.recruiterNumber || '',
        allowCandidatesToCall: parsedAllowCalls,
      };

      setPostingDetails((prev) => ({
        ...prev,
        companyName: nextPostingDetails.companyName || prev.companyName || '',
        salaryMin: nextPostingDetails.salaryMin || prev.salaryMin || '',
        salaryMax: nextPostingDetails.salaryMax || prev.salaryMax || '',
        experienceMin: nextPostingDetails.experienceMin ?? prev.experienceMin ?? '',
        experienceMax: nextPostingDetails.experienceMax ?? prev.experienceMax ?? '',
        recruiterName: nextPostingDetails.recruiterName || prev.recruiterName || '',
        recruiterNumber: nextPostingDetails.recruiterNumber || prev.recruiterNumber || '',
        allowCandidatesToCall: parsedAllowCalls,
      }));

      // ✅ NEW FLOW: Use validate-and-prepare API to analyze JD and get missing fields
      // Then ask questions one by one in frontend (no WebSocket)
      // Finally call confirm-job-posting to start automation
      
      // Store initial data from preview for later use
      const seededCollectedDetails = buildJobPostingCollectedDetails({
        collectedDetails: collectedJobPostingDetails || {},
        postingValues: nextPostingDetails,
        preparedValues: {
          company_name: confirmedData.companyName || '',
          salary_min: confirmedData.salaryMin || '',
          salary_max: confirmedData.salaryMax || '',
          allow_call: parsedAllowCalls,
          recruiter_name: confirmedData.recruiterName || '',
          recruiter_number: confirmedData.recruiterNumber || '',
          employment_type: storedDetails?.employmentType || '',
          workplace_type: storedDetails?.workplaceType || '',
          location: storedDetails?.jobLocation || finalRequirements?.location || '',
          experience_min: storedDetails?.experienceMin ?? '',
          experience_max: storedDetails?.experienceMax ?? '',
        },
        channels,
        finalRequirements,
      });

      setCollectedJobPostingDetails(seededCollectedDetails);
      
      // Store pending session and channels for tracking
      setPendingJobPostingSessionId(automationSessionId);
      pendingJobPostingSessionRef.current = automationSessionId;
      setPendingJobPostingChannels(channels);
      
      // Use the new flow - call validate-and-prepare and ask missing fields one by one
      await openJobPostingConfirmationWithAnalysis(channels, seededCollectedDetails);
    } else {
      setSystemMessages(['Missing required data for job posting. Please try again.']);
    }
  };

  const handleChatMessage = async (trimmed, alreadyPersisted = false) => {
    // Hard guard: after JD is created (jdText exists), never call /search/{id}/analyse again.
    // Keep existing JD intact and ask user for supported action commands only.
    // But allow changes during waiting_confirmation phase (when user can still modify requirements)
    const hasJdText = typeof jdText === 'string' && jdText.trim().length > 0;
    const jdActuallyCreated = hasJdText || analysisUxPhase === 'done';
    console.log('[DEBUG] Guard check:', { jdActuallyCreated, hasJdText, analysisUxPhase, waitingForConfirmation });
    if (jdActuallyCreated) {
      setSystemMessages([
        "I can't understand this command. Please enter like: post the job, show candidates, view jd, start sourcing, or start outreach.",
      ]);
      return;
    }

    // Otherwise, treat as new details and analyze
    try {
      // Track which session started the JD loading for session isolation
      const currentSessionForTracking = automationSessionId || selectedJobId || `pending_${Date.now()}`;
      jdLoadingSessionRef.current = currentSessionForTracking;
      
      // CRITICAL: Update currentViewedSessionRef IMMEDIATELY so loader state updates work
      currentViewedSessionRef.current = currentSessionForTracking;
      
      // CRITICAL: Use session store for loader state
      setSessionLoader(currentSessionForTracking, 'jd', true);
      
      // CRITICAL: Also set React state DIRECTLY to ensure loader shows immediately
      // This is needed because setSessionLoader only updates if session matches ref,
      // but we need the loader visible before any async operations
      setIsJDLoading(true);
      setAnalysisUxPhase('analysing');
      
      // Update session store with initial state
      const initialState = sessionStateStoreRef.current.get(currentSessionForTracking) || createEmptySessionState();
      initialState.jdError = '';
      initialState.systemMessage = '';
      initialState.systemMessages = [];
      initialState.analysisUxPhase = 'analysing';
      initialState.loaders = initialState.loaders || {};
      initialState.loaders.jd = true;
      sessionStateStoreRef.current.set(currentSessionForTracking, initialState);
      
      // Only update React state if this is the current session
      if (currentViewedSessionRef.current === currentSessionForTracking) {
        setJdError('');
        setSystemMessage('');
        setSystemMessages([]);
        setAnalysisUxPhase('analysing');
      }

      // Track variables for session initialization
      const forceNewSessionFromDashboard = forceNewSessionFromDashboardRef.current;
      // Use alreadyPersisted parameter to avoid duplicate saves
      let persistedUserMessage = alreadyPersisted;

      // Ensure we have a stable automation session ID
      let sessionId = automationSessionId || selectedJobId;
      if (forceNewSessionFromDashboard || !sessionId) {
        sessionId =
          'session_' +
          Date.now() +
          '_' +
          Math.random().toString(36).slice(2, 10);
        setAutomationSessionId(sessionId);
        setAutomationSearchId(null);
        // CRITICAL: Update currentViewedSessionRef IMMEDIATELY when new session is created
        // This ensures API responses are validated against the correct session
        currentViewedSessionRef.current = sessionId;
        // Update loading session ref with actual session ID (was set to pending_* earlier)
        jdLoadingSessionRef.current = sessionId;
        // Fresh session must not reuse any old search id.
        delete searchIdBySessionRef.current[sessionId];

        // Create initial job posting entry when user sends first message
        const initialTitle =
          trimmed.split(' ').slice(0, 5).join(' ') || 'New Job Posting';
        const MAX_TITLE_LENGTH = 80;
        const shortTitle =
          initialTitle.length > MAX_TITLE_LENGTH
            ? `${initialTitle.slice(0, MAX_TITLE_LENGTH - 1)}…`
            : initialTitle;

        const initialJobPosting = {
          id: sessionId,
          title: shortTitle,
          lastUpdated: formatLastUpdated(),
          duration: 'In progress',
          cid: getCid() ? String(getCid()) : null,
        };

        // Add to job postings list (prepend so it appears at top)
        setJobPostings((prev) => [initialJobPosting, ...prev]);

        // Select this new job so header updates
        setSelectedJobId(sessionId);
        // Persist in URL for page refresh
        setSearchParams(buildSearchParams({ session_id: sessionId }), { replace: true });
      }
      forceNewSessionFromDashboardRef.current = false;

      // Ensure first dashboard/new-chat message is saved after session creation.
      if (trimmed && !persistedUserMessage && sessionId) {
        try {
          await aiAutomationApi.post(`/sessions/${sessionId}/messages`, {
            message_type: 'user',
            content: trimmed,
            message_role: 'user',
          });
          persistedUserMessage = true; // Mark as saved to prevent duplicate saves
          updateJobPostingLastUpdated(sessionId, new Date());
        } catch (e) {
          console.warn('Failed to persist user chat message after session init', e);
        }
      }

      // Phase 0: preliminary search should be called only once per session.
      // Reuse existing search_id for all subsequent messages in the same job post.
      let searchId =
        searchIdBySessionRef.current[sessionId] ||
        (automationSessionId === sessionId ? automationSearchId : null);

      if (!searchId) {
        const currentCid = getCid();
        const prelimData = await aiAutomationApi.post('/search/preliminary', {
          query: trimmed,
          session_id: sessionId,
          cid: currentCid != null ? String(currentCid) : null,
        });
        console.log("222")

        searchId = prelimData?.searchId || prelimData?.search_id;
        if (!searchId) {
          console.error('Missing searchId in preliminary response:', prelimData);
          // CRITICAL: Update session store FIRST
          setSessionLoader(sessionId, 'jd', false);
          const errorState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
          errorState.jd.error = 'Unable to start analysis. Please try again.';
          sessionStateStoreRef.current.set(sessionId, errorState);
          
          // Only update React state if current session
          if (currentViewedSessionRef.current === sessionId) {
            setJdError('Unable to start analysis. Please try again.');
            setAnalysisUxPhase('idle');
          }
          jdLoadingSessionRef.current = null; // Clear session tracking
          return;
        }
        setAutomationSearchId(searchId);
        searchIdBySessionRef.current[sessionId] = searchId;
        console.log(searchIdBySessionRef,"111")
      }

      // If initial persistence happened before backend session creation and failed,
      // retry now after preliminary has initialized the session.
      if (trimmed && !persistedUserMessage && sessionId) {
        try {
          await aiAutomationApi.post(`/sessions/${sessionId}/messages`, {
            message_type: 'user',
            content: trimmed,
            message_role: 'user',
          });
          persistedUserMessage = true;
          updateJobPostingLastUpdated(sessionId, new Date());
        } catch (e) {
          console.warn('Failed to persist user chat message after preliminary init', e);
        }
      }

      // Build chat history for context - extract user and assistant messages
      // This allows AI to understand the full conversation when user returns to a session
      const chatHistoryForContext = chatHistory
        .filter(msg => msg && (msg.sender === 'user' || msg.sender === 'agent'))
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.message || msg.content || ''
        }))
        .filter(msg => msg.content.trim().length > 0)
        .slice(-10); // Last 10 messages for context

      // Phase 1: analyse request (extract requirements + missing fields)
      const contextToSend = Object.keys(accumulatedRequirements).length > 0 || waitingForConfirmation || chatHistoryForContext.length > 0
        ? {
            conversation_stage: waitingForConfirmation
              ? 'confirmation_edit'
              : 'awaiting_details',
            missing_fields: backendMissingFields?.length
              ? backendMissingFields
              : computeMissingFieldsFromAccumulated(accumulatedRequirements),
            current_requirements: accumulatedRequirements,
            chat_history: chatHistoryForContext, // Include chat history for full context
          }
        : undefined;
      console.log('[DEBUG] Sending analyse request:', {
        query: trimmed,
        session_id: sessionId,
        context: contextToSend,
      });
      const analyseData = await aiAutomationApi.post(
        `/search/${searchId}/analyse`,
        {
          query: trimmed,
          session_id: sessionId,
          context: contextToSend,
        }
      );
      console.log('[DEBUG] Analyse response:', {
        parsed_requirements: analyseData.parsed_requirements,
        ready_for_confirmation: analyseData.ready_for_confirmation,
        system_messages: analyseData.system_messages,
        proceed_to_create_jd: analyseData.proceed_to_create_jd,
        agent_state: analyseData.agent_state,
      });

      // ===================== SESSION VALIDATION =====================
      // Validate that response belongs to the currently viewed session
      // If user switched sessions during API call, store response but don't render
      const responseSessionId = analyseData.session_id || sessionId;
      
      // CRITICAL: Always store response in session state FIRST
      const responseState = sessionStateStoreRef.current.get(responseSessionId) || createEmptySessionState();
      responseState.analysisResponse = analyseData;
      responseState.accumulatedRequirements = analyseData.parsed_requirements;
      responseState.backendMissingFields = analyseData.missing_fields || [];
      sessionStateStoreRef.current.set(responseSessionId, responseState);
      
      if (!validateResponseSession(responseSessionId)) {
        console.log('[Session Validation] Response belongs to different session, stored without rendering:', {
          responseSession: responseSessionId,
          currentSession: currentViewedSessionRef.current
        });
        // Clear loading state for the response session
        setSessionLoader(responseSessionId, 'jd', false);
        jdLoadingSessionRef.current = null;
        return;
      }
      // ===================== END SESSION VALIDATION =====================

      // Store agent state from backend response for session-isolated state management
      if (analyseData.agent_state && sessionId) {
        agentStatePerSessionRef.current.set(sessionId, analyseData.agent_state);
        console.log('[Agent State] Stored agent state for session:', sessionId, analyseData.agent_state);
      }

      // Handle backend signal to proceed with JD creation (user confirmed in confirmation_edit stage)
      if (analyseData.proceed_to_create_jd && analyseData.parsed_requirements) {
        console.log('[DEBUG] Backend signaled to proceed with JD creation');
        const reqs = analyseData.parsed_requirements;
        setAccumulatedRequirements(reqs);
        setWaitingForConfirmation(false);
        
        // Call createJobDescription with the accumulated requirements
        if (searchId && sessionId) {
          await createJobDescription(searchId, sessionId, reqs, trimmed);
        } else {
          // If IDs still not available, show error message
          console.warn('[DEBUG] Cannot create JD - search/session IDs not available');
          // CRITICAL: Update session store FIRST
          setSessionLoader(sessionId || currentSessionForTracking, 'jd', false);
          const errorState = sessionStateStoreRef.current.get(sessionId || currentSessionForTracking) || createEmptySessionState();
          errorState.chat.systemMessage = 'Session is still loading. Please try again in a moment.';
          sessionStateStoreRef.current.set(sessionId || currentSessionForTracking, errorState);
          
          // Only update React state if current session
          if (currentViewedSessionRef.current === (sessionId || currentSessionForTracking)) {
            setSystemMessage('Session is still loading. Please try again in a moment.');
          }
          jdLoadingSessionRef.current = null; // Clear session tracking
        }
        return;
      }

      const parsedRequirements = analyseData.parsed_requirements || {};
      const missingFields = Array.isArray(analyseData.missing_fields)
        ? analyseData.missing_fields
        : [];

      // Merge with accumulated requirements (for incremental details)
      const mergedRequirements = {
        ...accumulatedRequirements,
        ...parsedRequirements,
        // Merge skills arrays
        skills: [
          ...(accumulatedRequirements.skills || []),
          ...(parsedRequirements.skills || []),
        ].filter((v, i, a) => a.indexOf(v) === i), // Remove duplicates
      };
      console.log('[DEBUG] Merged requirements:', mergedRequirements);
      setAccumulatedRequirements(mergedRequirements);
      setBackendMissingFields(missingFields);

      // If title/role is missing after merge, ask user for it instead of showing generic error
      const hasRoleAfterMerge = !!(mergedRequirements?.title || mergedRequirements?.role);
      if (missingFields.includes('title') && !hasRoleAfterMerge) {
        // Use backend's system_message if available, otherwise provide helpful message
        const backendMessage = analyseData?.system_message;
        const helpfulMessage = backendMessage || 
          "I couldn't identify the job role from your prompt. Could you please specify the job title? (For example: React Developer, Home Maid, Accountant, etc.)";
        
        // CRITICAL: Update session store FIRST
        setSessionLoader(sessionId, 'jd', false);
        const missingTitleState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
        missingTitleState.chat.systemMessage = helpfulMessage;
        missingTitleState.chat.systemMessages = [];
        missingTitleState.jd.waitingForConfirmation = false;
        missingTitleState.jd.analysisPhase = 'analysing';
        sessionStateStoreRef.current.set(sessionId, missingTitleState);
        
        // Only update React state if current session
        if (currentViewedSessionRef.current === sessionId) {
          setSystemMessage(helpfulMessage);
          setSystemMessages([]);
          setWaitingForConfirmation(false);
          setAnalysisUxPhase('analysing');
        }
        jdLoadingSessionRef.current = null; // Clear session tracking
        return;
      }

      // PRIORITY: If backend explicitly says ready_for_confirmation with system_messages, show those
      // This handles the confirmation_edit flow where user changes details
      if (analyseData.ready_for_confirmation && analyseData.system_messages && Array.isArray(analyseData.system_messages)) {
        // CRITICAL: Update session store FIRST
        setSessionLoader(sessionId, 'jd', false);
        const confState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
        confState.chat.systemMessages = analyseData.system_messages;
        confState.chat.systemMessage = '';
        confState.jd.requirements = mergedRequirements;
        confState.jd.waitingForConfirmation = true;
        confState.jd.missingFields = [];
        confState.jd.analysisPhase = 'analysed';
        sessionStateStoreRef.current.set(sessionId, confState);
        
        // Only update React state if current session
        if (currentViewedSessionRef.current === sessionId) {
          setSystemMessages(analyseData.system_messages);
          setSystemMessage(''); // Clear single message
          setAccumulatedRequirements(mergedRequirements);
          setWaitingForConfirmation(true);
          setBackendMissingFields([]);
          setAnalysisUxPhase('analysed');
        }
        jdLoadingSessionRef.current = null; // Clear session tracking
        return;
      }

      // If backend says details are missing (and not ready for confirmation), show system message from backend
      if (missingFields.length > 0 && !analyseData.ready_for_confirmation) {
        // Backend already saved the system message to database
        // Get it from response to display in chat
        const backendSystemMessage = analyseData.system_message || 
          `To generate a complete job description, please also mention the ${missingFields.join(', ')} in your description.`;
        
        // CRITICAL: Update session store FIRST
        setSessionLoader(sessionId, 'jd', false);
        const missingState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
        missingState.chat.systemMessage = backendSystemMessage;
        missingState.chat.systemMessages = [];
        missingState.jd.waitingForConfirmation = false;
        missingState.jd.analysisPhase = 'analysing';
        missingState.jd.missingFields = missingFields;  // CRITICAL: Store missing fields for session isolation
        missingState.jd.requirements = mergedRequirements;  // Also store current requirements
        sessionStateStoreRef.current.set(sessionId, missingState);
        
        // Only update React state if current session
        if (currentViewedSessionRef.current === sessionId) {
          setSystemMessage(backendSystemMessage);
          setSystemMessages([]); // Clear system messages when asking for missing fields
          setWaitingForConfirmation(false);
          setAnalysisUxPhase('analysing'); // still collecting details
        }
        jdLoadingSessionRef.current = null; // Clear session tracking

        // Update job posting entry to show it's waiting for more info
        if (sessionId) {
          setJobPostings((prev) => {
            return prev.map((job) =>
              job.id === sessionId
                ? {
                    ...job,
                    lastUpdated: formatLastUpdated(),
                    duration: 'Waiting for details',
                  }
                : job
            );
          });
        }
        return;
      }

      // All fields present - show confirmation messages from backend
      // Check if backend says ready for confirmation OR if we have all required fields
      const hasAllFields = 
        (mergedRequirements.title || mergedRequirements.role) &&
        mergedRequirements.location &&
        mergedRequirements.experience &&
        mergedRequirements.skills && 
        mergedRequirements.skills.length > 0;

      if ((analyseData.ready_for_confirmation || hasAllFields)) {
        // Prepare confirmation messages
        let confirmSystemMessages = [];
        let confirmSystemMessage = '';
        
        // Check if backend returned multiple messages (new format)
        if (analyseData.system_messages && Array.isArray(analyseData.system_messages)) {
          confirmSystemMessages = analyseData.system_messages;
          confirmSystemMessage = '';
        } else if (analyseData.system_message) {
          // Fallback: split single message into two
          const messageParts = analyseData.system_message.split('\n\n\nDo you want to make any changes');
          if (messageParts.length === 2) {
            confirmSystemMessages = [messageParts[0], 'Do you want to make any changes' + messageParts[1]];
            confirmSystemMessage = '';
          } else {
            // If can't split, use as single message
            confirmSystemMessage = analyseData.system_message;
            confirmSystemMessages = [];
          }
        } else if (hasAllFields) {
          // Generate fallback messages
          const confirmationParts = [];
          if (mergedRequirements.title || mergedRequirements.role) {
            confirmationParts.push(`Role: ${mergedRequirements.title || mergedRequirements.role}`);
          }
          if (mergedRequirements.experience) {
            confirmationParts.push(`Experience: ${mergedRequirements.experience}`);
          }
          if (mergedRequirements.location) {
            confirmationParts.push(`Location: ${mergedRequirements.location}`);
          }
          if (mergedRequirements.skills && mergedRequirements.skills.length > 0) {
            confirmationParts.push(`Skills: ${mergedRequirements.skills.join(', ')}`);
          }
          
          const detailsMsg = `Got it - here's what I understood:\n\n${confirmationParts.join('\n')}`;
          const questionMsg = "Do you want to make any changes or should I proceed with the Sourcing Strategy and JD?";
          confirmSystemMessages = [detailsMsg, questionMsg];
          confirmSystemMessage = '';
        }
        
        // CRITICAL: Update session store FIRST
        setSessionLoader(sessionId, 'jd', false);
        const allFieldsState = sessionStateStoreRef.current.get(sessionId) || createEmptySessionState();
        allFieldsState.chat.systemMessages = confirmSystemMessages;
        allFieldsState.chat.systemMessage = confirmSystemMessage;
        allFieldsState.jd.waitingForConfirmation = true;
        allFieldsState.jd.missingFields = [];
        allFieldsState.jd.analysisPhase = 'analysed';
        sessionStateStoreRef.current.set(sessionId, allFieldsState);
        
        // Only update React state if current session
        if (currentViewedSessionRef.current === sessionId) {
          setSystemMessages(confirmSystemMessages);
          setSystemMessage(confirmSystemMessage);
          setWaitingForConfirmation(true);
          setBackendMissingFields([]);
          setAnalysisUxPhase('analysed');
        }
        jdLoadingSessionRef.current = null; // Clear session tracking
        return; // Wait for user confirmation
      }

      // User confirmed - proceed to create JD
      await createJobDescription(searchId, sessionId, mergedRequirements, trimmed);
    } catch (error) {
      console.error('Error during JD analysis flow:', error);
      const errorMessage =
        error?.message ||
        "Please give the correct prompt. We can't proceed with this input.";
      
      // Get the session ID from the loading ref (set at start of try block)
      const errorSessionId = jdLoadingSessionRef.current;
      
      // CRITICAL: Update session store FIRST
      if (errorSessionId) {
        setSessionLoader(errorSessionId, 'jd', false);
        const errorState = sessionStateStoreRef.current.get(errorSessionId) || createEmptySessionState();
        errorState.jd.error = errorMessage;
        errorState.chat.systemMessage = "Please give the correct prompt. We can't proceed with this input.";
        errorState.chat.systemMessages = [];
        errorState.jd.waitingForConfirmation = false;
        errorState.jd.analysisPhase = 'idle';
        sessionStateStoreRef.current.set(errorSessionId, errorState);
      }
      
      // Only update React state if current session
      if (!errorSessionId || currentViewedSessionRef.current === errorSessionId) {
        setJdError(errorMessage);
        setSystemMessage("Please give the correct prompt. We can't proceed with this input.");
        setSystemMessages([]);
        setIsJDLoading(false);
        setWaitingForConfirmation(false);
        setAnalysisUxPhase('idle');
      }
      jdLoadingSessionRef.current = null; // Clear session tracking on error
    }
  };

  // Dashboard query auto-submit is now handled by the ?new=true URL param flow
  // in the initiateNewChat() function. The old sessionStorage-based handler was
  // removed to prevent duplicate session creation race conditions.


  // -- Google Calendar OAuth callback listener --
  // After the user completes Google Calendar OAuth in a popup, the popup sends
  // a postMessage({ type: 'GOOGLE_CALENDAR_CONNECTED' }). We listen for that
  // message and automatically re-trigger "schedule interview" so the user
  // doesn't lose context.
  useEffect(() => {
    const handleCalendarOAuth = async (event) => {
      if (!event.data || event.data.type !== 'GOOGLE_CALENDAR_CONNECTED') return;

      if (!event.data.success) {
        const errMsg = event.data.error || 'Google Calendar connection failed. Please try again and grant all required permissions.';
        showToast(errMsg, 'warning', 8000);
        return;
      }

      console.log('[CalendarOAuth] Google Calendar connected via popup, re-triggering schedule interview');

      // Show confirmation message to user
      setSystemMessages(['✅ Google Calendar connected successfully! Resuming interview scheduling...']);

      // Re-trigger "schedule interview" lifecycle message automatically
      const lcSessionId = automationSessionId || selectedJobId || sessionId || null;
      const lcSearchId =
        (lcSessionId ? getSafeSearchId(lcSessionId) : null) ||
        (automationSearchId?.startsWith('search_') ? automationSearchId : null) ||
        null;

      if (!lcSessionId) {
        console.warn('[CalendarOAuth] No active session to re-trigger scheduling');
        setSystemMessages(['✅ Google Calendar connected! Please type "schedule interview" to continue.']);
        return;
      }

      try {
        setSessionLoader(lcSessionId, 'lifecycle', true);
        const lifecycleResponse = await aiAutomationApi.post('/chat', {
          message: 'schedule interview',
          session_id: lcSessionId,
          cid: getCid(),
          additional_data: {
            action: 'lifecycle',
            search_id: lcSearchId,
          },
        });

        if (lifecycleResponse && lifecycleResponse.success && lifecycleResponse.response) {
          const resp = lifecycleResponse.response;
          const lifecycleMessage = resp.message || 'Interview scheduling resumed.';
          setAgentStageLabel(resp.stage_label || '');
          setAgentStageCancellable(resp.stage_cancellable || false);
          setSystemMessages([lifecycleMessage]);

          // Handle auth_url in case calendar still needs reconnection
          if (resp.auth_url) {
            const w = window.open(resp.auth_url, 'gcal_auth', 'width=600,height=700');
            if (!w || w.closed || typeof w.closed === 'undefined') {
              setGoogleCalendarAuthUrl(resp.auth_url);
            }
          }

          // Handle lifecycle candidates
          if (resp.lifecycle_candidates && Array.isArray(resp.lifecycle_candidates) && resp.lifecycle_candidates.length > 0) {
            const mappedCandidates = resp.lifecycle_candidates.map((c, idx) => ({
              id: c.id || `lifecycle_${idx}`,
              name: c.name || 'Unknown',
              email: c.email || '',
              phone: c.phone || '',
              title: c.current_title || c.headline || '',
              current_title: c.current_title || '',
              headline: c.headline || '',
              company: c.current_company || '',
              current_company: c.current_company || '',
              location: c.location || '',
              experience: c.experience || '',
              skills: c.skills || '',
              match_percentage: c.match_percentage || 0,
              score_details: c.score_details || c.scoreDetails || {},
              status: c.status || c.funnel_stage || '',
              funnel_stage: c.funnel_stage || '',
              doc_id: c.doc_id || '',
              source: c.source || 'lifecycle',
              _source_type: c.source || 'lifecycle',
              platforms: Array.isArray(c.platforms) && c.platforms.length > 0 ? c.platforms : (c.source ? [c.source] : ['lifecycle']),
              linkedin_url: c.linkedin_url || '',
              linkedin_profile_url: c.linkedin_url || '',
              profile_url: c.profile_url || c.linkedin_url || c.email || `lifecycle_${c.id || idx}`,
              priority: c.priority || '',
              search_id: automationSearchId || '',
              highlights: c.highlights || [],
              key_skills: c.highlights || [],
              summary: c.summary || c.summary_text || '',
              summary_text: c.summary_text || c.summary || '',
              match_breakdown: c.match_breakdown || {},
              experience_years: c.experience_years || '',
              cv_filename: c.cv_filename || '',
            }));
            setCandidates(mappedCandidates);
            setRightPanelMode('candidates');
            setActiveHeaderTab('sourcing');
            setTalentPoolChannelFilter([]);
          }

          // Update lifecycle action suggestions
          if (resp.suggestions && Array.isArray(resp.suggestions) && resp.suggestions.length > 0) {
            setLifecycleActions(resp.suggestions.map((s, idx) => ({
              id: `lifecycle_${s.action || idx}`,
              label: s.label,
              command: s.command || s.label,
            })));
          }
          if (resp.pipeline_snapshot) setPipelineSnapshot(resp.pipeline_snapshot);
        } else {
          setSystemMessages(['✅ Google Calendar connected! Please type "schedule interview" to continue.']);
        }
      } catch (error) {
        console.error('[CalendarOAuth] Failed to re-trigger scheduling:', error);
        setSystemMessages(['✅ Google Calendar connected! Please type "schedule interview" to continue.']);
      } finally {
        setLifecycleLoading(false);
      }
    };

    window.addEventListener('message', handleCalendarOAuth);
    return () => window.removeEventListener('message', handleCalendarOAuth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationSessionId, selectedJobId, sessionId, automationSearchId]);

  useEffect(() => {
    let cancelled = false;

    const readMailOAuthResumePayload = ({ remove = false } = {}) => {
      let raw = null;
      try {
        raw = sessionStorage.getItem('mail_oauth_resume') || localStorage.getItem('mail_oauth_resume');
        if (remove) {
          sessionStorage.removeItem('mail_oauth_resume');
          localStorage.removeItem('mail_oauth_resume');
        }
      } catch (_) {
        try {
          sessionStorage.removeItem('mail_oauth_resume');
          localStorage.removeItem('mail_oauth_resume');
        } catch (e) { /* ignore */ }
        return null;
      }
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (_) {
        return null;
      }
    };

    const isFreshMailOAuthPayload = (payload) => (
      payload &&
      payload.source === 'chat_lifecycle' &&
      (!payload.createdAt || Date.now() - Number(payload.createdAt) <= 10 * 60 * 1000)
    );

    const getFreshMailOAuthCompleted = (event) => {
      let completed = event?.detail || event?.data || null;
      if (!completed && event?.key === 'mail_oauth_completed' && event.newValue) {
        try { completed = JSON.parse(event.newValue); } catch (_) { completed = null; }
      }
      if (!completed) {
        try {
          const raw = localStorage.getItem('mail_oauth_completed');
          completed = raw ? JSON.parse(raw) : null;
        } catch (_) { completed = null; }
      }
      if (!completed?.success) return null;
      if (completed.createdAt && Date.now() - Number(completed.createdAt) > 10 * 60 * 1000) return null;
      return completed;
    };

    const handleSharedMailOAuthCompleted = async (event) => {
      const completed = getFreshMailOAuthCompleted(event);
      if (!completed) return;
      const payload = readMailOAuthResumePayload({ remove: true });
      if (!isFreshMailOAuthPayload(payload)) return;
      if (payload.createdAt && completed.createdAt && Number(completed.createdAt) < Number(payload.createdAt)) {
        try {
          sessionStorage.setItem('mail_oauth_resume', JSON.stringify(payload));
          localStorage.setItem('mail_oauth_resume', JSON.stringify(payload));
        } catch (_) { /* ignore */ }
        return;
      }
      const resumeKey = getMailOAuthResumeKey(payload);
      if (mailOAuthResumeHandledRef.current.has(resumeKey)) return;
      if (mailOAuthResumeInFlightRef.current.has(resumeKey)) return;
      mailOAuthResumeInFlightRef.current.add(resumeKey);
      try { localStorage.removeItem('mail_oauth_completed'); } catch (_) { /* ignore */ }
      const providerLabel = String(completed.provider || '').toLowerCase() === 'outlook' ? 'Outlook' : 'Gmail';
      try {
        await resumeAfterMailProviderAuth(payload, completed.email || '', providerLabel, completed);
      } finally {
        mailOAuthResumeInFlightRef.current.delete(resumeKey);
      }
    };

    handleSharedMailOAuthCompleted();
    let mailOAuthChannel = null;
    if ('BroadcastChannel' in window) {
      mailOAuthChannel = new BroadcastChannel('mail_oauth');
      mailOAuthChannel.onmessage = handleSharedMailOAuthCompleted;
    }
    window.addEventListener('storage', handleSharedMailOAuthCompleted);
    window.addEventListener('mail_oauth_completed', handleSharedMailOAuthCompleted);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handleSharedMailOAuthCompleted);
      window.removeEventListener('mail_oauth_completed', handleSharedMailOAuthCompleted);
      if (mailOAuthChannel) {
        mailOAuthChannel.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationSessionId, selectedJobId, sessionId]);

  const getMailOAuthResumeKey = (payload = {}) => (
    payload.pendingMailAuthActionId ||
    payload.pending_mail_auth_action_id ||
    `${payload.sessionId || ''}:${payload.searchId || ''}:${payload.action || ''}`
  );

  const getStoredMailOAuthResumed = () => {
    try {
      const raw = localStorage.getItem('mail_oauth_completed');
      if (!raw) return [];
      const completed = JSON.parse(raw);
      if (!completed?.success) return [];
      if (completed.createdAt && Date.now() - Number(completed.createdAt) > 10 * 60 * 1000) return [];
      return Array.isArray(completed.resumed) ? completed.resumed : [];
    } catch (_) {
      return [];
    }
  };

  const getMailOAuthResumed = (completed = null) => {
    const direct = Array.isArray(completed?.resumed) ? completed.resumed : [];
    if (direct.length > 0) return direct;
    return getStoredMailOAuthResumed();
  };

  const isMailAuthOutreachAction = (action = '') => {
    const value = String(action || '').toLowerCase();
    return !value || value === 'outreach' || value === 'outreach_send' || value === 'send_outreach';
  };

  const hasResumedMailAuthOutreach = (completed = null) => {
    const resumed = getMailOAuthResumed(completed);
    return Array.isArray(resumed) && resumed.some((item) => {
      if (item?.sync_id) return false;
      return isMailAuthOutreachAction(item?.action);
    });
  };

  const clearMailAuthOutreachResumeState = (resumeSessionId) => {
    setAgentStageLabel('');
    setAgentStageCancellable(false);
    setLifecycleAwaitingConfirmation(false);
    setDraftEditActions([]);
    setLifecycleActions([]);
    if (!resumeSessionId) return;
    updateSessionState(resumeSessionId, (state) => {
      state.agentStage = { label: '', cancellable: false };
      state.lifecycle = {
        ...(state.lifecycle || {}),
        awaitingConfirmation: false,
        actions: [],
      };
      return state;
    });
  };

  const markMailAuthSyncResumeComplete = async ({
    searchId,
    sessionId: resumeSessionId,
    providerLabel,
    resumeKey,
  } = {}) => {
    if (!searchId) return;
    if (resumeKey) mailOAuthResumeHandledRef.current.add(resumeKey);
    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    setSourcingExpandToken((prev) => prev + 1);
    setAutomationSearchId(searchId);
    setCurrentSearchId(searchId);
    if (resumeSessionId) {
      searchIdBySessionRef.current[resumeSessionId] = searchId;
    }
    const completeMessage = `Sync resumed after ${providerLabel || 'mail'} authorization. Track results in the Sourcing panel on the right.`;
    updateSyncState(searchId, {
      syncing: false,
      phase: 'complete',
      syncMessage: '',
      logs: [completeMessage],
      progress: completeMessage,
      completedAt: Date.now(),
      mailAuthNeeded: false,
      gmailAuthNeeded: false,
      mailNotConnected: false,
      gmailNotConnected: false,
    });
    setAndPersistSystemMessage(
      [completeMessage],
      { phase: 'lifecycle', skipPersist: true, forSessionId: resumeSessionId }
    );
    try {
      await refreshSyncState(searchId);
    } catch (refreshError) {
      console.warn('[mail-auth-resume] Sync state refresh after auth resume failed', refreshError);
    }
  };

  const attachResumedMailAuthSync = async ({ resumed, fallbackSearchId, sessionId, providerLabel } = {}) => {
    const resumedSync = Array.isArray(resumed)
      ? resumed.find((item) => item?.sync_id && (item?.action === 'sync' || item?.action === 'subject_sync' || !item?.action))
      : null;
    if (!resumedSync?.sync_id) return false;

    const syncSearchId = resumedSync.search_id || fallbackSearchId;
    if (!syncSearchId) return false;

    const syncSessionId = resumedSync.session_id || sessionId || automationSessionId || selectedJobId || sessionId;
    const resumeKey = getMailOAuthResumeKey({
      pendingMailAuthActionId: resumedSync.id,
      sessionId: syncSessionId,
      searchId: syncSearchId,
      action: resumedSync.action || 'sync',
    });
    mailOAuthResumeHandledRef.current.add(resumeKey);
    mailOAuthResumeHandledRef.current.add(`${syncSessionId || ''}:${syncSearchId || ''}:${resumedSync.action || 'sync'}`);
    setActiveHeaderTab('sourcing');
    setRightPanelMode('sourcing');
    setSourcingExpandToken((prev) => prev + 1);
    setAutomationSearchId(syncSearchId);
    searchIdBySessionRef.current[syncSessionId] = syncSearchId;
    setCurrentSearchId(syncSearchId);
    const resumeLog = `Sync resumed after ${providerLabel || 'mail'} authorization...`;
    updateSyncState(syncSearchId, {
      syncing: true,
      phase: 'syncing',
      logs: [resumeLog],
      progress: resumeLog,
      completedAt: null,
      mailAuthNeeded: false,
      gmailAuthNeeded: false,
      mailNotConnected: false,
      gmailNotConnected: false,
    });
    connectToExistingSync(syncSearchId, resumedSync.sync_id, {
      force: true,
      replaceLogs: true,
      initialLogs: [resumeLog],
      progress: resumeLog,
      onComplete: () => {
        setAndPersistSystemMessage(
          ['Sync completed. Track results in the Sourcing panel on the right.'],
          { phase: 'lifecycle', skipPersist: true, forSessionId: syncSessionId }
        );
      },
    });
    setAndPersistSystemMessage(
      ['Sync resumed. Track live progress in the Sourcing panel on the right.'],
      { phase: 'lifecycle', skipPersist: true, forSessionId: syncSessionId }
    );
    return true;
  };

  const resumeAfterMailProviderAuth = async (prompt, email, providerLabel, completed = null) => {
    if (!prompt) return;
    const resumeSessionId = String(prompt.sessionId || automationSessionId || selectedJobId || sessionId || '');
    const resumeSearchId = prompt.searchId || getSafeSearchId(resumeSessionId);
    const resumeAction = String(prompt.action || '').toLowerCase();
    showToast(`${providerLabel} connected${email ? ` (${email})` : ''}!`, 'success');
    setAndPersistSystemMessage(
      [`${providerLabel} connected successfully${email ? ` (${email})` : ''}! Resuming ${prompt.action === 'sync' ? 'sync' : 'process'}...`],
      { phase: 'lifecycle', skipPersist: true, forSessionId: resumeSessionId }
    );

    await new Promise(resolve => setTimeout(resolve, 1200));
    if (resumeSearchId) {
      setAutomationSearchId(resumeSearchId);
      searchIdBySessionRef.current[resumeSessionId] = resumeSearchId;
      setCurrentSearchId(resumeSearchId);
    }
    if (resumeSessionId) {
      setAutomationSessionId(resumeSessionId);
      setSelectedJobId(resumeSessionId);
      currentSessionRef.current = resumeSessionId;
    }

    if (resumeAction === 'sync') {
      if (resumeSearchId) {
        const resumed = await attachResumedMailAuthSync({
          resumed: getMailOAuthResumed(completed),
          fallbackSearchId: resumeSearchId,
          sessionId: resumeSessionId,
          providerLabel,
        });
        if (resumed) return;
        const resumeKey = getMailOAuthResumeKey({
          pendingMailAuthActionId: prompt.pendingMailAuthActionId || prompt.pending_mail_auth_action_id,
          sessionId: resumeSessionId,
          searchId: resumeSearchId,
          action: 'sync',
        });
        if (mailOAuthResumeHandledRef.current.has(resumeKey) || mailOAuthResumeInFlightRef.current.has(resumeKey)) return;

        setActiveHeaderTab('sourcing');
        setRightPanelMode('sourcing');
        setSourcingExpandToken((prev) => prev + 1);
        const retrySync = await startSync(resumeSearchId, { sessionId: resumeSessionId, cid: getCid(), force: true });
        if (retrySync?.started || retrySync?.alreadySyncing) {
          setAndPersistSystemMessage([
            'Sync started. Track live progress in the Sourcing panel on the right.'
          ], { phase: 'lifecycle', skipPersist: true, forSessionId: resumeSessionId });
        } else {
          await markMailAuthSyncResumeComplete({
            searchId: resumeSearchId,
            sessionId: resumeSessionId,
            providerLabel,
            resumeKey,
          });
        }
      }
      return;
    }

    if (isMailAuthOutreachAction(resumeAction) && hasResumedMailAuthOutreach(completed)) {
      clearMailAuthOutreachResumeState(resumeSessionId);
      setAndPersistSystemMessage(
        ['Outreach sent successfully after mail authorization.'],
        { phase: 'lifecycle', skipPersist: true, forSessionId: resumeSessionId }
      );
      return;
    }

    await handleUserQueryForJD(prompt.retryMessage || 'yes', 'agent', {
      sessionId: resumeSessionId,
      bypassLoadingGuard: true,
    });

    if (isMailAuthOutreachAction(resumeAction)) {
      clearMailAuthOutreachResumeState(resumeSessionId);
    }
  };

  const handleMailAuthPromptConnect = async () => {
    const prompt = mailAuthPrompt;
    if (!prompt?.authUrl) return;
    const provider = String(prompt.provider || 'gmail').toLowerCase() === 'outlook' ? 'outlook' : 'gmail';
    const providerLabel = provider === 'outlook' ? 'Outlook' : 'Gmail';
    setMailAuthPrompt({ ...prompt, connecting: true, error: '' });
    try {
      const result = await openMailOAuthUrlPopup({ authUrl: prompt.authUrl, provider });
      let email = result.email || '';
      if (provider === 'gmail' && result.pendingSwitch) {
        const confirmed = window.confirm(
          `Switch Gmail from ${result.previousEmail} to ${result.newEmail}?\n\n` +
          'Past email conversations from the previous account will no longer sync.\n\n' +
          'Click OK to confirm the switch, or Cancel to keep the current account.'
        );
        if (confirmed) {
          const confirmResult = await confirmGmailAccountSwitch(getCid());
          email = confirmResult.gmail_email || result.newEmail || '';
        } else {
          await cancelGmailAccountSwitch(getCid());
          setMailAuthPrompt({ ...prompt, connecting: false, error: 'Gmail account switch was not confirmed.' });
          return;
        }
      }
      setMailAuthPrompt(null);
      await resumeAfterMailProviderAuth(prompt, email, providerLabel, result);
    } catch (err) {
      setMailAuthPrompt({
        ...prompt,
        connecting: false,
        error: err?.message || `Failed to connect ${providerLabel}.`,
      });
    }
  };

  // Track viewport breakpoints for responsive split layout behavior
  useEffect(() => {
    const updateLayoutMode = () => {
      setIsCompactLayout(window.innerWidth < 1536);
      setIsStackedPanels(window.innerWidth < 1280);
      setIsMobileLayout(window.innerWidth < 768);
    };

    updateLayoutMode();
    window.addEventListener('resize', updateLayoutMode);
    return () => window.removeEventListener('resize', updateLayoutMode);
  }, []);

  // Track split ratio (0-1, proportion of center panel relative to available space)
  const splitRatioRef = useRef(0.6); // default chat/right split: 60/40
  const previousSidebarWidthRef = useRef(effectiveSidebarWidthPx);

  // Set initial 60/40 split on mount
  useEffect(() => {
    if (isStackedPanels) {
      setCenterWidth(null);
      return;
    }

    if (containerRef.current && centerWidth === null) {
      const containerWidth = containerRef.current.offsetWidth;
      const availableWidth = containerWidth - effectiveSidebarWidthPx;
      setCenterWidth(availableWidth * splitRatioRef.current);
    }
  }, [centerWidth, isStackedPanels, effectiveSidebarWidthPx]);

  // Keep sidebar collapsed on tablet/mobile for better usable content width
  useEffect(() => {
    if (isStackedPanels) {
      setSidebarCollapsed(true);
    }
  }, [isStackedPanels]);

  // On mobile/tablet, move focus to results panel when result-heavy content is active
  useEffect(() => {
    if (!isStackedPanels) {
      setMobilePanelView('chat');
      return;
    }

    const hasResultsContent =
      rightPanelMode !== 'auto' ||
      outreachPhase !== 'idle' ||
      talentScoutingStarted ||
      jobPostingPhase !== 'idle';

    if (hasResultsContent) {
      setMobilePanelView('results');
    }
  }, [
    isStackedPanels,
    rightPanelMode,
    outreachPhase,
    talentScoutingStarted,
    jobPostingPhase,
  ]);

  const shouldForceJobPostingPanel =
    activeHeaderTab === 'job_posting' &&
    (
      jobPostingPhase === 'posting' ||
      jobPostingPhase === 'collecting_details' ||
      jobPostingPhase === 'channel_selection' ||
      jobPostingProcessing ||
      linkedinProcessing ||
      naukriProcessing ||
      waitingForBackendQuestion ||
      pendingNaukriField ||
      fieldCollectionStateRef.current?.isActive
    );

  const jobPostingPanelActive = shouldForceJobPostingPanel;

  const shouldPreserveJobPostingView =
    shouldForceJobPostingPanel ||
    activeHeaderTab === 'job_posting';

  const hasJobPostingPlatformActivity =
    jobPostingProcessing ||
    linkedinProcessing ||
    naukriProcessing ||
    linkedinLogs.length > 0 ||
    naukriLogs.length > 0 ||
    postedChannels.length > 0;

  const showJobPostingJdPanel =
    activeHeaderTab === 'job_posting' &&
    rightPanelMode === 'jd' &&
    !!jdText;

  const showJobPostingPlanPanel =
    activeHeaderTab === 'job_posting' &&
    !showJobPostingJdPanel &&
    !hasJobPostingPlatformActivity &&
    (jobPostingPhase === 'channel_selection' || jobPostingPhase === 'collecting_details');

  const previousPanelExpansionRef = useRef({
    jobPosting: false,
    sourcing: false,
  });

  useEffect(() => {
    const shouldExpandJobPosting =
      activeHeaderTab === 'job_posting' &&
      (
        shouldForceJobPostingPanel ||
        linkedinLogs.length > 0 ||
        naukriLogs.length > 0 ||
        jobPostingPhase === 'posting'
      );

    if (shouldExpandJobPosting && !previousPanelExpansionRef.current.jobPosting) {
      setJobPostingExpandToken((prev) => prev + 1);
    }

    previousPanelExpansionRef.current.jobPosting = shouldExpandJobPosting;
  }, [
    activeHeaderTab,
    shouldForceJobPostingPanel,
    linkedinLogs.length,
    naukriLogs.length,
    jobPostingPhase,
  ]);

  useEffect(() => {
    const shouldExpandSourcing =
      activeHeaderTab === 'sourcing' &&
      (
        googleSourcingProcessing ||
        linkedinSourcingProcessing ||
        resumeBoxSourcingProcessing ||
        googleSourcingLogs.length > 0 ||
        linkedinSourcingLogs.length > 0 ||
        resumeBoxSourcingLogs.length > 0 ||
        candidates.length > 0
      );

    if (shouldExpandSourcing && !previousPanelExpansionRef.current.sourcing) {
      setSourcingExpandToken((prev) => prev + 1);
    }

    previousPanelExpansionRef.current.sourcing = shouldExpandSourcing;
  }, [
    activeHeaderTab,
    googleSourcingProcessing,
    linkedinSourcingProcessing,
    resumeBoxSourcingProcessing,
    googleSourcingLogs.length,
    linkedinSourcingLogs.length,
    resumeBoxSourcingLogs.length,
    candidates.length,
  ]);

  // Recalculate when sidebar toggles — preserve user's split ratio
  useEffect(() => {
    if (isResizing) return;

    if (isStackedPanels) {
      setCenterWidth(null);
      previousSidebarWidthRef.current = effectiveSidebarWidthPx;
      return;
    }

    const recalcWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const previousAvailableWidth = containerWidth - previousSidebarWidthRef.current;
        const availableWidth = containerWidth - effectiveSidebarWidthPx;
        const previousCenterWidth = centerWidth ?? previousAvailableWidth * splitRatioRef.current;
        const previousRightWidth = Math.max(
          rightPanelMinWidthPx,
          previousAvailableWidth - previousCenterWidth - 4
        );
        const maxCenterWidth = Math.max(centerMinWidthPx, availableWidth - rightPanelMinWidthPx - 4);
        const nextCenterWidth = Math.min(
          Math.max(centerMinWidthPx, availableWidth - previousRightWidth - 4),
          maxCenterWidth
        );

        setCenterWidth(nextCenterWidth);
        splitRatioRef.current = availableWidth > 0 ? nextCenterWidth / availableWidth : splitRatioRef.current;
        previousSidebarWidthRef.current = effectiveSidebarWidthPx;
      }
    };

    const timeoutId = setTimeout(recalcWidth, 100);
    return () => clearTimeout(timeoutId);
  }, [isResizing, isStackedPanels, effectiveSidebarWidthPx, centerWidth, centerMinWidthPx, rightPanelMinWidthPx]);

  useEffect(() => {
    if (isStackedPanels) return;

    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const deltaX = e.clientX - resizeStartX.current;
      const rawWidth = resizeStartWidth.current + deltaX;

      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const availableWidth = containerWidth - effectiveSidebarWidthPx;
        const minWidth = availableWidth * 0.2;
        const maxWidth = availableWidth * 0.8;
        // Free drag during move (smooth visual feedback)
        if (rawWidth >= minWidth && rawWidth <= maxWidth) {
          setCenterWidth(rawWidth);
        }
      }
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';

      // Snap to nearest quarter on release: 1/4, 2/4, 3/4
      if (containerRef.current && centerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const availableWidth = containerWidth - effectiveSidebarWidthPx;
        const currentWidth = centerRef.current.offsetWidth;
        const currentRatio = currentWidth / availableWidth;
        const snapPoints = [0.4, 0.6, 0.75];
        const snappedRatio = snapPoints.reduce((closest, point) =>
          Math.abs(point - currentRatio) < Math.abs(closest - currentRatio) ? point : closest
        );
        setCenterWidth(availableWidth * snappedRatio);
        splitRatioRef.current = snappedRatio;
      }
    };

    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.pointerEvents = 'none';
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isResizing, isStackedPanels, effectiveSidebarWidthPx, centerMinWidthPx, rightPanelMinWidthPx]);

  const handleResizeStart = (e) => {
    if (isStackedPanels) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    if (centerRef.current) {
      resizeStartWidth.current = centerRef.current.offsetWidth;
    }
  };

  const isReusableEmptyChatSession = (session) => {
    if (!session?.id || session.status === 'archived') return false;
    const title = String(
      session.title ||
      session.custom_title ||
      session.initial_query ||
      session.query ||
      ''
    ).trim();
    const hasDefaultTitle = !title || title === 'New Chat' || /^session[_\-\s]/i.test(title);
    return (
      hasDefaultTitle &&
      Number(session.candidate_count || session.candidateCount || 0) === 0 &&
      Number(session.search_count || session.searchCount || 0) === 0 &&
      !session.has_posted_job &&
      !session.hasPostedJob &&
      !session.parsed_requirements &&
      !session.initial_query &&
      !session.query
    );
  };

  const getEmptyChatJob = (session) => ({
    id: session.id,
    title: 'New Chat',
    lastUpdated: session.lastUpdated || formatLastUpdated(session.updated_at || session.created_at || undefined),
    duration: 'Session',
    candidateCount: 0,
    hasPostedJob: false,
    cid: session.cid != null ? String(session.cid) : (getCid() ? String(getCid()) : null),
  });

  const hasNoPersistedChatMessages = async (sessionId) => {
    if (!sessionId) return false;
    try {
      const data = await aiAutomationApi.get(`/sessions/${encodeURIComponent(sessionId)}/chat-messages`);
      const messages = Array.isArray(data?.messages) ? data.messages : [];
      return messages.length === 0 || messages.every((msg) => !String(msg?.content || '').trim());
    } catch (error) {
      console.warn('Could not verify empty chat messages for session', sessionId, error);
      return false;
    }
  };

  const findReusableEmptyChatSession = async (sessions = []) => {
    const candidates = (Array.isArray(sessions) ? sessions : []).filter(isReusableEmptyChatSession);
    for (const session of candidates) {
      if (await hasNoPersistedChatMessages(session.id)) {
        return session;
      }
    }
    return null;
  };

  // ── Shared "New Chat" initializer — used by New Job Posting button AND ?new=true URL param ──
  const initiateNewChat = useCallback(async (options = {}) => {
    const { autoQuery } = options; // optional query to auto-send after creating chat

    if (newChatCreatingRef.current && !autoQuery) {
      return;
    }
    if (!autoQuery) {
      newChatCreatingRef.current = true;
      setNewChatCreating(true);
    }

    // If already in an unused new chat and no auto-query, do nothing
    if (newChatPendingRef.current && !autoQuery && currentViewedSessionRef.current === newChatPendingRef.current) {
      newChatCreatingRef.current = false;
      setNewChatCreating(false);
      return;
    }

    if (!autoQuery) {
      let reusableSession = await findReusableEmptyChatSession(jobPostings);
      if (!reusableSession) {
        try {
          const cid = getCid();
          const sessionsEndpoint = cid
            ? `/sessions?limit=100&cid=${encodeURIComponent(String(cid))}`
            : '/sessions?limit=100';
          const sessionsData = await aiAutomationApi.get(sessionsEndpoint);
          const rows = Array.isArray(sessionsData?.sessions || sessionsData?.data || sessionsData)
            ? (sessionsData?.sessions || sessionsData?.data || sessionsData)
            : [];
          reusableSession = await findReusableEmptyChatSession(rows);
        } catch (emptyLookupError) {
          console.warn('Could not check for reusable empty chat session', emptyLookupError);
        }
      }

      if (reusableSession?.id) {
        const reusableJob = getEmptyChatJob(reusableSession);
        setJobPostings((prev) => {
          const withoutDuplicate = (Array.isArray(prev) ? prev : []).filter((job) => job?.id !== reusableJob.id);
          return [reusableJob, ...withoutDuplicate];
        });
        setSelectedJobId(reusableJob.id);
        setAutomationSessionId(reusableJob.id);
        currentViewedSessionRef.current = reusableJob.id;
        setSearchParams(buildSearchParams({ session_id: reusableJob.id }), { replace: true });
        initialGreetingSavedRef.current = false;
        newChatPendingRef.current = reusableJob.id;
        setActiveHeaderTab('status');
        setChatHistory([]);
        setChatResetToken((prev) => prev + 1);
        setJdText(''); setJdError('');
        setSystemMessage(''); setSystemMessages([]);
        setWaitingForConfirmation(false); setAccumulatedRequirements({});
        setBackendMissingFields([]); setAnalysisUxPhase('idle');
        setShowResultsPanel(false); setFinalRequirements({});
        setTalentScoutingStarted(false); setSourcingComplete(false);
        setCandidates([]); setSourcingStats({});
        setGoogleSourcingLogs([]); setLinkedinSourcingLogs([]); setResumeBoxSourcingLogs([]);
        setGoogleSourcingProcessing(false); setLinkedinSourcingProcessing(false); setResumeBoxSourcingProcessing(false);
        setSelectedSourcingChannels([]);
        setPendingSourcingSessionId(null); pendingSourcingSessionRef.current = null;
        setActiveSourcingSessions(new Set());
        setJobPostingPhase('idle'); setSelectedChannels([]);
        setPostedChannels([]); setPostingDetails({});
        setPendingNaukriField(null); setPendingNaukriFieldOptions([]);
        setPendingJobPostingFieldsQueue([]); setPendingJobPostingChannels([]);
        setJobPostingIntroShown(false);
        setLinkedinLogs([]); setNaukriLogs([]);
        setLinkedinProcessing(false); setNaukriProcessing(false);
        setJobPostingProcessing(false); setJobPostingComplete(false);
        setJobPostingFailed(false); setWaitingForBackendQuestion(false);
        logsInitializedRef.current = { linkedin: false, naukri: false };
        setSelectedCandidate(null);
        setOutreachPhase('idle'); setOutreachTemplate('');
        setOutreachWorkspaceName('');
        setOutreachCandidates([]); setOutreachCandidateIds([]);
        setIsLoadingOutreachTemplate(false);
        setRightPanelMode('auto');
        setAgentStageLabel(''); setAgentStageCancellable(false);
        setDraftEditActions([]); setLifecycleAwaitingConfirmation(false);
        setWorkspaceOptions([]);
        setWorkspaceSelectionRequired(false);
        setLifecycleScheduleActive(false); setLifecycleLoading(false);
        setChatApiLoading(false); setLifecycleActions([]);
        setLastQueryResults(null); lastLifecycleInteractionRef.current = 0;
        lifecycleInflightRef.current = false;
        if (lifecycleAbortRef.current) { lifecycleAbortRef.current.abort(); lifecycleAbortRef.current = null; }
        newChatCreatingRef.current = false;
        setNewChatCreating(false);
        return reusableJob.id;
      }
    }

    // Clean up previous pending "New Chat" that was never used
    if (newChatPendingRef.current) {
      const abandonId = newChatPendingRef.current;
      newChatPendingRef.current = null;
      setJobPostings((prev) => prev.filter((p) => p.id !== abandonId));
      aiAutomationApi.delete(`/sessions/${abandonId}`).catch(() => {});
    }

    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);

    // Add to sidebar immediately
    setJobPostings((prev) => [{
      id: newSessionId,
      title: 'New Chat',
      lastUpdated: formatLastUpdated(),
      duration: 'Session',
      cid: getCid() ? String(getCid()) : null,
    }, ...prev]);

    // Update state
    setSelectedJobId(newSessionId);
    setAutomationSessionId(newSessionId);
    currentViewedSessionRef.current = newSessionId;
    setSearchParams(buildSearchParams({ session_id: newSessionId }), { replace: true });
    initialGreetingSavedRef.current = false;
    newChatPendingRef.current = newSessionId;
    setActiveHeaderTab('status');
    newChatCreatingRef.current = false;
    setNewChatCreating(false);

    // Persist to DB — don't set initial_query to avoid it showing as a message
    aiAutomationApi.post('/sessions', {
      session_id: newSessionId,
      cid: getCid() ? String(getCid()) : null,
      status: 'active',
    }).then(() => {
      aiAutomationApi.patch(`/sessions/${newSessionId}/title`, { title: 'New Chat' }).catch(() => {});
    }).catch((e) => console.warn('Failed to create new session', e));

    // Clear chat and reset all state
    setChatHistory([]);
    setChatResetToken((prev) => prev + 1);
    setJdText(''); setJdError('');
    setSystemMessage(''); setSystemMessages([]);
    setWaitingForConfirmation(false); setAccumulatedRequirements({});
    setBackendMissingFields([]); setAnalysisUxPhase('idle');
    setShowResultsPanel(false); setFinalRequirements({});
    setTalentScoutingStarted(false); setSourcingComplete(false);
    setCandidates([]); setSourcingStats({});
    setGoogleSourcingLogs([]); setLinkedinSourcingLogs([]); setResumeBoxSourcingLogs([]);
    setGoogleSourcingProcessing(false); setLinkedinSourcingProcessing(false); setResumeBoxSourcingProcessing(false);
    setSelectedSourcingChannels([]);
    setPendingSourcingSessionId(null); pendingSourcingSessionRef.current = null;
    setActiveSourcingSessions(new Set());
    setJobPostingPhase('idle'); setSelectedChannels([]);
    setPostedChannels([]); setPostingDetails({});
    setPendingNaukriField(null); setPendingNaukriFieldOptions([]);
    setPendingJobPostingFieldsQueue([]); setPendingJobPostingChannels([]);
    setJobPostingIntroShown(false);
    setLinkedinLogs([]); setNaukriLogs([]);
    setLinkedinProcessing(false); setNaukriProcessing(false);
    setJobPostingProcessing(false); setJobPostingComplete(false);
    setJobPostingFailed(false); setWaitingForBackendQuestion(false);
    logsInitializedRef.current = { linkedin: false, naukri: false };
    setSelectedCandidate(null);
    setOutreachPhase('idle'); setOutreachTemplate('');
    setOutreachWorkspaceName('');
    setOutreachCandidates([]); setOutreachCandidateIds([]);
    setIsLoadingOutreachTemplate(false);
    setRightPanelMode('auto');
    setAgentStageLabel(''); setAgentStageCancellable(false);
    setDraftEditActions([]); setLifecycleAwaitingConfirmation(false);
    setWorkspaceOptions([]);
    setWorkspaceSelectionRequired(false);
    setLifecycleScheduleActive(false); setLifecycleLoading(false);
    setChatApiLoading(false); setLifecycleActions([]);
    setLastQueryResults(null); lastLifecycleInteractionRef.current = 0;
    lifecycleInflightRef.current = false;
    if (lifecycleAbortRef.current) { lifecycleAbortRef.current.abort(); lifecycleAbortRef.current = null; }

    // If there's a query to auto-send (from dashboard search)
    // Use a delayed trigger so all state from initiateNewChat settles first
    if (autoQuery) {
      forceNewSessionFromDashboardRef.current = true;
      setTimeout(() => {
        setDashboardInitialQuery(autoQuery);
        setDashboardInitialQueryToken(Date.now());
      }, 300);
    }

    return newSessionId;
  }, [setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detect ?new=true URL param (from dashboard, campaigns, AppHeader, etc.) ──
  const lastNewParamTokenRef = useRef(null);
  useEffect(() => {
    if (searchParams.get('new') !== 'true') return;

    // Use a token to deduplicate — only fire once per unique navigation
    const token = searchParams.get('t') || 'default';
    if (lastNewParamTokenRef.current === token) return;
    lastNewParamTokenRef.current = token;

    // Check for pending dashboard query
    let pendingQuery = '';
    try {
      pendingQuery = sessionStorage.getItem('dashboard_pending_query') || '';
      sessionStorage.removeItem('dashboard_pending_query');
    } catch {}

    // Small delay to ensure ChatPage is mounted and state is ready
    setTimeout(() => {
      initiateNewChat({ autoQuery: pendingQuery || undefined });
    }, 100);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastDashboardSessionQueryTokenRef = useRef(null);
  useEffect(() => {
    if (!sessionId) return;
    let payload = null;

    try {
      payload = JSON.parse(sessionStorage.getItem('dashboard_pending_session_query') || 'null');
    } catch {
      payload = null;
    }

    if (!payload?.query) return;
    if (String(payload.sessionId || '') !== String(sessionId)) return;

    const token = String(payload.token || `${payload.sessionId}:${payload.query}`);
    if (lastDashboardSessionQueryTokenRef.current === token) return;
    lastDashboardSessionQueryTokenRef.current = token;

    try {
      sessionStorage.removeItem('dashboard_pending_session_query');
    } catch {}

    if (payload.localOnly === true) {
      const localPrompt = String(payload.query || '').trim();
      if (localPrompt) {
        setSystemMessages([localPrompt]);
        setSystemMessageToken((prev) => prev + 1);
        updateSessionState(String(payload.sessionId), (state) => {
          state.chat.systemMessages = [localPrompt];
          state.chat.apiLoading = false;
          state.chat.lifecycleLoading = false;
          return state;
        });
      }
      setChatApiLoading(false);
      setLifecycleLoading(false);
      setSessionLoader(String(payload.sessionId), 'chatApi', false);
      setSessionLoader(String(payload.sessionId), 'lifecycle', false);
      setDashboardInitialQuery('');
      setDashboardInitialQueryToken(0);
      setDashboardInitialQuerySessionId('');
      return;
    }

    setChatApiLoading(true);
    setSessionLoader(String(payload.sessionId), 'chatApi', true);
    setTimeout(() => {
      setDashboardInitialQuery(String(payload.query || ''));
      setDashboardInitialQuerySessionId(String(payload.sessionId || ''));
      setDashboardInitialQueryToken(Date.now());
    }, 250);
  }, [sessionId]);

  const workspaceCanUseInterviews = canUseFeature('manual_interview') || canUseFeature('ai_interview');
  const workspaceVisibleTabs = [
    'status',
    ...(canUseFeature('job_posting') ? ['job_posting'] : []),
    ...(canUseFeature('sourcing') ? ['sourcing'] : []),
    ...(workspaceCanUseInterviews ? ['interview'] : []),
    ...((jdText || analysisUxPhase === 'done') ? ['overview'] : []),
  ];

  return (
    <div
      className={`${embedded ? 'min-h-0 h-full overflow-hidden' : 'min-h-screen h-screen'} flex flex-col transition-colors duration-250 ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}
    >
      {/* Google Calendar connect prompt — shown when the auto-open popup was blocked. Clicking is a
          fresh user gesture, so the popup opens reliably. */}
      {googleCalendarAuthUrl && (
        <div className="fixed inset-x-0 top-4 z-[300] flex justify-center px-4">
          <div className="flex w-full max-w-xl items-center gap-3 rounded-xl border border-amber-300 bg-white px-4 py-3 shadow-xl dark:border-amber-700 dark:bg-gray-900">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" strokeLinecap="round" /></svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Connect Google Calendar</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Interview scheduling needs calendar access. Click to authorize — scheduling resumes automatically.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                window.open(googleCalendarAuthUrl, 'gcal_auth', 'width=600,height=700,scrollbars=yes,resizable=yes');
                setGoogleCalendarAuthUrl(null);
              }}
              className="shrink-0 rounded-lg bg-amber-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700"
            >
              Connect
            </button>
            <button
              type="button"
              onClick={() => setGoogleCalendarAuthUrl(null)}
              className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
              aria-label="Dismiss"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>
      )}

      {mailAuthPrompt?.authUrl && (
        <div className="fixed inset-x-0 top-4 z-[10050] flex justify-center px-4">
          <div className="flex w-full max-w-xl items-center gap-3 rounded-xl border border-blue-300 bg-white px-4 py-3 shadow-xl dark:border-blue-700 dark:bg-gray-900">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 6h16v12H4z" rx="2" /><path d="m4 7 8 6 8-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Connect {mailAuthPrompt.providerLabel || 'Mail Provider'}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Mail authorization is required. Click to authorize — the current process resumes automatically.
              </p>
              {mailAuthPrompt.error && (
                <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{mailAuthPrompt.error}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleMailAuthPromptConnect}
              disabled={mailAuthPrompt.connecting}
              className="shrink-0 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mailAuthPrompt.connecting ? 'Connecting...' : 'Connect'}
            </button>
            <button
              type="button"
              onClick={() => setMailAuthPrompt(null)}
              className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
              aria-label="Dismiss"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* Cross-Session Notification Modal for OTP/Questions from background jobs */}
      {crossSessionNotification && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        >
          <div 
            className={`relative max-w-md w-full mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="px-6 py-4 border-b rounded-t-xl"
              style={{ 
                borderColor: isDark ? '#374151' : '#E5E7EB',
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)` 
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}30` }}
                >
                  <span className="text-lg">
                    {crossSessionNotification.type === 'otp_request' ? '🔐' : '❓'}
                  </span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {crossSessionNotification.platform} - {crossSessionNotification.type === 'otp_request' ? 'OTP Required' : 'Input Required'}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Job: {crossSessionNotification.jobTitle}
                  </p>
                </div>
                <button
                  onClick={() => setCrossSessionNotification(null)}
                  className={`ml-auto p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {crossSessionNotification.message}
              </p>
              
              {/* Options for dropdown fields */}
              {crossSessionNotification.options && crossSessionNotification.options.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {crossSessionNotification.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={async () => {
                        try {
                          await aiAutomationApi.post('/post-job/submit-field-response', {
                            session_id: crossSessionNotification.sessionId,
                            field_name: crossSessionNotification.fieldName,
                            field_value: option,
                          });
                          setCrossSessionNotification(null);
                        } catch (error) {
                          console.error('Failed to submit field response from modal', error);
                        }
                      }}
                      className="px-4 py-2 rounded-lg border transition-colors hover:opacity-80"
                      style={{
                        borderColor: primaryColor,
                        color: primaryColor,
                        backgroundColor: isDark ? '#1F2937' : '#ffffff',
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Input field for OTP or free-text */}
              {(crossSessionNotification.type === 'otp_request' || 
                !crossSessionNotification.options || 
                crossSessionNotification.options.length === 0) && (
                <form 
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const input = e.target.elements.crossSessionInput.value.trim();
                    if (!input) return;
                    
                    try {
                      if (crossSessionNotification.type === 'otp_request') {
                        // Include platform for correct OTP routing
                        const platform = crossSessionNotification.platform?.toLowerCase() || '';
                        await aiAutomationApi.post('/submit-otp', {
                          session_id: crossSessionNotification.sessionId,
                          otp_code: input,
                          platform: platform,
                        });
                      } else {
                        await aiAutomationApi.post('/post-job/submit-field-response', {
                          session_id: crossSessionNotification.sessionId,
                          field_name: crossSessionNotification.fieldName,
                          field_value: input,
                        });
                      }
                      setCrossSessionNotification(null);
                    } catch (error) {
                      console.error('Failed to submit from modal', error);
                    }
                  }}
                  className="flex gap-2"
                >
                  <input
                    name="crossSessionInput"
                    type={crossSessionNotification.type === 'otp_request' ? 'text' : 'text'}
                    placeholder={crossSessionNotification.type === 'otp_request' ? 'Enter OTP code...' : 'Enter your response...'}
                    autoFocus
                    className={`flex-1 px-4 py-2 rounded-lg border outline-none focus:ring-2 ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-400'
                    }`}
                  />
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-lg text-white font-medium transition-opacity hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    Submit
                  </button>
                </form>
              )}
            </div>
            
            {/* Modal Footer - Go to Session Button */}
            <div 
              className="px-6 py-3 border-t flex justify-between items-center"
              style={{ borderColor: isDark ? '#374151' : '#E5E7EB' }}
            >
              <button
                onClick={() => {
                  const newSessionId = crossSessionNotification.sessionId;
                  // CRITICAL: Save current session state before switching
                  const prevSession = automationSessionId;
                  if (prevSession && prevSession !== newSessionId) {
                    saveCurrentSessionState(prevSession);
                  }
                  
                  // Update ref BEFORE state updates
                  currentViewedSessionRef.current = newSessionId;
                  
                  // Navigate to the session
                  setSelectedJobId(newSessionId);
                  setAutomationSessionId(newSessionId);
                  setSearchParams(buildSearchParams({ session_id: newSessionId }), { replace: true });
                  
                  // Hydrate UI from session store
                  hydrateUIFromSession(newSessionId);
                  
                  // Load full session data from backend
                  loadSessionData(newSessionId);
                  
                  setCrossSessionNotification(null);
                }}
                className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'} hover:underline`}
              >
                Go to this session →
              </button>
              <button
                onClick={() => setCrossSessionNotification(null)}
                className={`px-4 py-1.5 rounded-lg text-sm ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal for LinkedIn/Naukri login */}
      {credentialsModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div 
            className={`relative max-w-md w-full mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="px-6 py-4 border-b rounded-t-xl"
              style={{ 
                borderColor: isDark ? '#374151' : '#E5E7EB',
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)` 
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}30` }}
                >
                  <span className="text-lg">🔐</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {credentialsModal.platform} Login Required
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {credentialsModal.attemptMessage}
                  </p>
                </div>
                <button
                  onClick={() => setCredentialsModal(null)}
                  className={`ml-auto p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              {/* Error message for retry */}
              {credentialsModal.isRetry && credentialsModal.errorMessage && (
                <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {credentialsModal.errorMessage}
                  </p>
                </div>
              )}
              
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {credentialsModal.prompt}
              </p>
              
              {/* Credentials Form */}
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const username = e.target.elements.credUsername.value.trim();
                  const password = e.target.elements.credPassword.value;
                  const saveCredentials = e.target.elements.saveCredentials?.checked || false;
                  const errorDiv = e.target.querySelector('.validation-error');
                  
                  if (!username || !password) {
                    if (errorDiv) errorDiv.textContent = 'Please enter both username and password';
                    return;
                  }
                  
                  // Email validation regex
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(username)) {
                    if (errorDiv) errorDiv.textContent = 'Please enter a valid email address (e.g., career@aimplify.tech)';
                    return;
                  }
                  
                  // Clear any previous error
                  if (errorDiv) errorDiv.textContent = '';
                  
                  try {
                    // Submit credentials for automation
                    await aiAutomationApi.post('/post-job/submit-field-response', {
                      session_id: credentialsModal.sessionId,
                      field_name: 'credentials',
                      field_value: {
                        username,
                        password,
                        save_to_db: saveCredentials,
                        platform: credentialsModal.platform?.toLowerCase() || 'naukri',
                      },
                    });
                    setCredentialsModal(null);
                  } catch (error) {
                    console.error('Failed to submit credentials from modal', error);
                    if (errorDiv) errorDiv.textContent = 'Failed to submit credentials. Please try again.';
                  }
                }}
                className="space-y-4"
              >
                {/* Validation Error Display */}
                <div className="validation-error text-sm text-red-600 dark:text-red-400 min-h-[1.25rem]"></div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {credentialsModal.platform} Username/Email
                  </label>
                  <input
                    name="credUsername"
                    type="text"
                    placeholder={`Enter your ${credentialsModal.platform} email or username`}
                    autoFocus
                    required
                    className={`w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-400'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {credentialsModal.platform} Password
                  </label>
                  <input
                    name="credPassword"
                    type="password"
                    placeholder="Enter your password"
                    required
                    className={`w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-400'
                    }`}
                  />
                </div>
                
                {/* Save credentials checkbox */}
                <div className="flex items-center gap-2">
                  <input
                    name="saveCredentials"
                    type="checkbox"
                    id="saveCredentialsCheckbox"
                    className={`w-4 h-4 rounded border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    defaultChecked={true}
                  />
                  <label htmlFor="saveCredentialsCheckbox" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Save credentials for future use
                  </label>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 px-5 py-2.5 rounded-lg text-white font-medium transition-opacity hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => setCredentialsModal(null)}
                    className={`px-5 py-2.5 rounded-lg font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Work Email Modal for LinkedIn Company Email Verification */}
      {workEmailModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div 
            className={`relative max-w-md w-full mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="px-6 py-4 border-b rounded-t-xl"
              style={{ 
                borderColor: isDark ? '#374151' : '#E5E7EB',
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)` 
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}30` }}
                >
                  <span className="text-lg">📧</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Company Email Verification Required
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {workEmailModal.attemptMessage}
                  </p>
                </div>
                <button
                  onClick={() => setWorkEmailModal(null)}
                  className={`ml-auto p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              {/* Error message for retry */}
              {workEmailModal.isRetry && workEmailModal.errorMessage && (
                <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {workEmailModal.errorMessage}
                  </p>
                </div>
              )}
              
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {workEmailModal.prompt || `LinkedIn requires your ${workEmailModal.companyName ? workEmailModal.companyName + ' ' : ''}work email address to verify you work at this company.`}
              </p>
              
              {workEmailModal.companyName && (
                <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border`}>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    <span className="font-medium">Company:</span> {workEmailModal.companyName}
                  </p>
                </div>
              )}
              
              {/* Work Email Form */}
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const workEmail = e.target.elements.workEmail.value.trim();
                  const errorDiv = e.target.querySelector('.validation-error');
                  
                  if (!workEmail) {
                    if (errorDiv) errorDiv.textContent = 'Please enter your work email address';
                    return;
                  }
                  
                  // Email validation regex
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(workEmail)) {
                    if (errorDiv) errorDiv.textContent = 'Please enter a valid email address';
                    return;
                  }
                  
                  // Clear any previous error
                  if (errorDiv) errorDiv.textContent = '';
                  
                  try {
                    // Submit work email for automation
                    await aiAutomationApi.post('/post-job/submit-field-response', {
                      session_id: workEmailModal.sessionId,
                      field_name: 'work_email',
                      field_value: {
                        work_email: workEmail,
                      },
                    });
                    setWorkEmailModal(null);
                  } catch (error) {
                    console.error('Failed to submit work email from modal', error);
                    if (errorDiv) errorDiv.textContent = 'Failed to submit email. Please try again.';
                  }
                }}
                className="space-y-4"
              >
                {/* Validation Error Display */}
                <div className="validation-error text-sm text-red-600 dark:text-red-400 min-h-[1.25rem]"></div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Work Email Address{workEmailModal.companyName ? ` for ${workEmailModal.companyName}` : ''}
                  </label>
                  <input
                    name="workEmail"
                    type="email"
                    placeholder={`Enter your ${workEmailModal.companyName ? workEmailModal.companyName + ' ' : ''}work email`}
                    autoFocus
                    required
                    className={`w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-400'
                    }`}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 px-5 py-2.5 rounded-lg text-white font-medium transition-opacity hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    Verify Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setWorkEmailModal(null)}
                    className={`px-5 py-2.5 rounded-lg font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Site Error Recovery Modal for Naukri "Something Went Wrong" errors */}
      {siteErrorRecoveryModal.open && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        >
          <div 
            className={`relative max-w-md w-full mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="px-6 py-4 border-b rounded-t-xl"
              style={{ 
                borderColor: isDark ? '#374151' : '#E5E7EB',
                background: `linear-gradient(135deg, #EF444420, #F9731620)` 
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#EF444430' }}
                >
                  <span className="text-lg">⚠️</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Naukri Site Error
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Temporary issue from Naukri
                  </p>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              <div className="mb-4 p-4 rounded-lg bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700">
                <p className={`text-sm ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                  {siteErrorRecoveryModal.message}
                </p>
              </div>
              
              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {siteErrorRecoveryModal.prompt || 'Would you like to retry the job posting or cancel?'}
              </p>
              
              {siteErrorRecoveryModal.clearLogsOnRetry && (
                <p className={`mb-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-medium">Note:</span> Retrying will clear the current progress logs and start fresh.
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={siteErrorRecoveryModal.onRetry}
                  className="flex-1 px-5 py-2.5 rounded-lg text-white font-medium transition-opacity hover:opacity-90"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  🔄 Retry
                </button>
                <button
                  onClick={siteErrorRecoveryModal.onCancel}
                  className={`flex-1 px-5 py-2.5 rounded-lg font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  ✕ Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Channel Selection Modal for Sourcing/Outreach */}
      {showChannelSelectionModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowChannelSelectionModal(false)}
        >
          <div 
            className={`relative max-w-md w-full mx-4 rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="px-6 py-4 border-b rounded-t-xl"
              style={{ 
                borderColor: isDark ? '#374151' : '#E5E7EB',
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)` 
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}30` }}
                >
                  <span className="text-lg">{channelSelectionType === 'sourcing' ? '🔍' : '📤'}</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Select {channelSelectionType === 'sourcing' ? 'Sourcing' : 'Outreach'} Channels
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {channelSelectionType === 'sourcing' 
                      ? 'Choose where to search for candidates'
                      : 'Choose how to reach out to candidates'}
                  </p>
                </div>
                <button
                  onClick={() => setShowChannelSelectionModal(false)}
                  className={`ml-auto p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {channelSelectionType === 'sourcing' 
                  ? 'Select the platforms to search for candidates:'
                  : 'Select the channels to use for outreach:'}
              </p>
              
              {/* Channel Checkboxes */}
              <div className="space-y-3 mb-4">
                {channelSelectionType === 'sourcing' ? (
                  <>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedSourcingChannels.includes('google')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedSourcingChannels.includes('google')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSourcingChannels([...selectedSourcingChannels, 'google']);
                          } else {
                            setSelectedSourcingChannels(selectedSourcingChannels.filter(c => c !== 'google'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">🔍</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Online Sourcing</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Search LinkedIn profiles via multiple search engines</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedSourcingChannels.includes('linkedin')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedSourcingChannels.includes('linkedin')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSourcingChannels([...selectedSourcingChannels, 'linkedin']);
                          } else {
                            setSelectedSourcingChannels(selectedSourcingChannels.filter(c => c !== 'linkedin'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">💼</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>LinkedIn</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Direct LinkedIn search</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedSourcingChannels.includes('resumebox')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedSourcingChannels.includes('resumebox')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSourcingChannels([...selectedSourcingChannels, 'resumebox']);
                          } else {
                            setSelectedSourcingChannels(selectedSourcingChannels.filter(c => c !== 'resumebox'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">📂</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{RESUME_LIBRARY_LABEL}</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Search local resume database</div>
                      </div>
                    </label>
                  </>
                ) : (
                  <>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedOutreachChannels.includes('gmail')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedOutreachChannels.includes('gmail')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOutreachChannels([...selectedOutreachChannels, 'gmail']);
                          } else {
                            setSelectedOutreachChannels(selectedOutreachChannels.filter(c => c !== 'gmail'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">📧</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Gmail/Email</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Send email outreach</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedOutreachChannels.includes('linkedin')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedOutreachChannels.includes('linkedin')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOutreachChannels([...selectedOutreachChannels, 'linkedin']);
                          } else {
                            setSelectedOutreachChannels(selectedOutreachChannels.filter(c => c !== 'linkedin'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">💼</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>LinkedIn Message</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Send LinkedIn InMail/message</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedOutreachChannels.includes('whatsapp')
                        ? isDark ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                        : isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedOutreachChannels.includes('whatsapp')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOutreachChannels([...selectedOutreachChannels, 'whatsapp']);
                          } else {
                            setSelectedOutreachChannels(selectedOutreachChannels.filter(c => c !== 'whatsapp'));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <span className="text-xl">💬</span>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>WhatsApp</div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Send WhatsApp message</div>
                      </div>
                    </label>
                  </>
                )}
              </div>
              
              {/* Save to Database Checkbox */}
              <div className={`flex items-center gap-2 mb-4 p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <input
                  type="checkbox"
                  id="saveChannelPreferences"
                  checked={saveChannelPreferences}
                  onChange={(e) => setSaveChannelPreferences(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label htmlFor="saveChannelPreferences" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Save these preferences for future use
                </label>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const channels = channelSelectionType === 'sourcing' 
                      ? selectedSourcingChannels 
                      : selectedOutreachChannels;
                    
                    if (channels.length === 0) {
                      // Show error that at least one channel must be selected
                      return;
                    }
                    
                    handleChannelSelectionConfirm(channels);
                  }}
                  disabled={(channelSelectionType === 'sourcing' && selectedSourcingChannels.length === 0) ||
                           (channelSelectionType === 'outreach' && selectedOutreachChannels.length === 0)}
                  className={`flex-1 px-5 py-2.5 rounded-lg text-white font-medium transition-opacity ${
                    ((channelSelectionType === 'sourcing' && selectedSourcingChannels.length === 0) ||
                     (channelSelectionType === 'outreach' && selectedOutreachChannels.length === 0))
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:opacity-90'
                  }`}
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  {channelSelectionType === 'sourcing' ? 'Start Sourcing' : 'Start Outreach'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowChannelSelectionModal(false)}
                  className={`px-5 py-2.5 rounded-lg font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-page loading overlay when restoring candidate from URL */}
      {!candidateRestored && !suppressFullPageRestoreOverlay && (
        <div className="flex-1 min-h-0 relative" style={{ minWidth: 0 }}>
          <PageLoadingOverlay />
        </div>
      )}

      {/* Full-page Candidate Detail — hides sidebar + chat when viewing a candidate */}
      {/* Filter clear confirmation modal */}
      {showFilterClearModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Switch to Agent Mode?
            </h3>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#4B5563', lineHeight: '20px' }}>
              You have active filters:
            </p>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {filterBreadcrumbs.map((crumb, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px',
                    backgroundColor: '#DBEAFE', color: '#1E40AF', fontSize: '12px', fontWeight: 500,
                  }}>{crumb}</span>
                  {i < filterBreadcrumbs.length - 1 && <span style={{ color: '#9CA3AF' }}>→</span>}
                </span>
              ))}
            </div>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6B7280' }}>
              Switching to Agent mode will clear these filters. You can always come back to Pipeline mode to filter again.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowFilterClearModal(false)}
                style={{
                  padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
                  border: '1px solid #D1D5DB', backgroundColor: '#F9FAFB', color: '#374151', cursor: 'pointer',
                }}
              >
                Stay in Ask Mode
              </button>
              <button
                onClick={() => {
                  setShowFilterClearModal(false);
                  setChatMode('agent');
                  setFilterBreadcrumbs([]);
                  setFilterActions([]);
                  setWorkingCandidates([]);
                  setFollowUpSuggestions([]);
                  // Persistent transition entry (same shape as the
                  // toggle's own handler). The previous transient
                  // pill via filterPillText was intentionally removed
                  // because it disappeared after the next render cycle
                  // and didn't survive refresh. Toast remains for
                  // corner feedback only.
                  showToast('Exited Pipeline Agent', 'info', 2000);
                  const exitTransitionEntry = {
                    id: `transition_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    sender: 'agent',
                    message: 'Exited Pipeline Agent',
                    timestamp: new Date().toISOString(),
                    metadata: {
                      type: 'agent_transition',
                      agent: 'Pipeline Agent',
                      action: 'exited',
                    },
                  };
                  setChatHistory((prev) => [...prev, exitTransitionEntry]);
                  // Persist cleared state to in-memory + backend DB
                  const sid = selectedJobId || automationSessionId || sessionId;
                  if (sid) {
                    updateSessionState(sid, (s) => {
                      s.chat = s.chat || {};
                      s.chat.chatHistory = [
                        ...(s.chat.chatHistory || []),
                        exitTransitionEntry,
                      ];
                      s.filtering = { chatMode: 'agent', breadcrumbs: [], actions: [], workingCandidates: [], followUpSuggestions: [] };
                      return s;
                    });
                    // Persist transition entry so it survives refresh.
                    aiAutomationApi.post(`/sessions/${sid}/messages`, {
                      message_type: 'assistant',
                      content: 'Exited Pipeline Agent',
                      message_role: 'assistant',
                      metadata: {
                        type: 'agent_transition',
                        agent: 'Pipeline Agent',
                        action: 'exited',
                      },
                    }).catch((e) => {
                      console.warn('Failed to persist exit transition pill', e);
                    });
                    // Persist mode change (closes the active filter flow on BE).
                    aiAutomationApi.post('/chat/mode-switch', {
                      session_id: sid,
                      cid: getCid(),
                      additional_data: { mode: 'agent' },
                    }).catch(() => {});
                  }
                }}
                style={{
                  padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
                  border: 'none', backgroundColor: '#3B82F6', color: 'white', cursor: 'pointer',
                }}
              >
                Switch to Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CandidateDetail modal — only for Job Posting tab (Overview uses inline accordion) */}
      {candidateRestored && selectedCandidate && activeHeaderTab !== 'overview' && (
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ minWidth: 0 }}>
          <CandidateDetail
            candidate={selectedCandidate}
            searchId={selectedCandidate?._searchId || automationSearchId}
            sessionId={selectedJobId || automationSessionId || sessionId}
            onBack={clearCandidate}
            onCandidateUpdate={updateCandidateData}
            onRejected={handleCandidateRejected}
            jobTitle={currentInterviewJobTitle}
            backLabel='Candidate List'
            featureAccess={companyFeatureAccess}
          />
        </div>
      )}

      {candidateRestored && !selectedCandidate && (
      <>
      {/* Main Layout */}
      <div className="flex flex-1 min-h-0 h-full overflow-hidden" ref={containerRef}>
        {/* Left Sidebar */}
        {!embedded && (
        <div
          aria-hidden={!sidebarVisible}
          style={{
            width: sidebarVisible ? (isMobileLayout ? '100%' : `${isCompactLayout ? 276 : 296}px`) : '0px',
            height: '100%',
            overflow: 'hidden',
            flexShrink: 0,
            pointerEvents: sidebarVisible ? 'auto' : 'none',
            transition: 'width .28s cubic-bezier(.16,1,.3,1)',
            position: isMobileLayout && sidebarVisible ? 'absolute' : 'relative',
            inset: isMobileLayout && sidebarVisible ? 0 : undefined,
            zIndex: isMobileLayout && sidebarVisible ? 60 : undefined,
          }}
        >
        <ChatSidebar
          isCollapsed={false}
          isCompactLayout={isCompactLayout}
          isMobileLayout={isMobileLayout}
          onToggleCollapse={() => setSidebarVisible(false)}
          onRequestClose={() => setSidebarVisible(false)}
          appName={appName}
          appLogo={appLogo}
          jobPostings={jobPostings}
          selectedJobId={selectedJobId}
          loading={sidebarLoading}
          newChatCreating={newChatCreating}
          onArchiveJob={handleArchiveJob}
          onDeleteJob={handleDeleteJob}
          onRenameJob={handleRenameJob}
          onRestoreJob={handleRestoreJob}
          archivedJobs={archivedJobs}
          showArchived={showArchived}
          onToggleArchived={handleToggleArchived}
          archivedCount={archivedCount}
          onSelectJob={(job) => {
            // ===================== SESSION SWITCH =====================
            // Save current session state, update refs, restore new session state

            // Abandon "New Chat" if user never sent a message
            if (newChatPendingRef.current) {
              const abandonId = newChatPendingRef.current;
              newChatPendingRef.current = null;
              // Remove from sidebar
              setJobPostings((prev) => prev.filter((p) => p.id !== abandonId));
              // Delete from DB (fire-and-forget)
              aiAutomationApi.delete(`/sessions/${abandonId}`).catch(() => {});
            }

            const prevSession = automationSessionId;

            // 1. Save current session state to centralized store BEFORE switching
            if (prevSession && prevSession !== job.id) {
              console.log('[Session Switch] Saving state for previous session:', prevSession);
              saveCurrentSessionState(prevSession);
              
              // Also save to legacy job posting state ref for backward compatibility
              const hasActiveJobPosting = jobPostingPhase !== 'idle' && jobPostingPhase !== 'posted';
              const hasPendingQuestion = pendingNaukriField || systemMessages.length > 0;
              
              if (hasActiveJobPosting || hasPendingQuestion || activeJobPostingSessions.has(prevSession)) {
                jobPostingStatePerSessionRef.current.set(prevSession, {
                  systemMessages: sanitizeSystemMessages([...systemMessages]),
                  pendingNaukriField,
                  pendingNaukriFieldOptions: [...pendingNaukriFieldOptions],
                  jobPostingPhase,
                  selectedChannels: [...selectedChannels],
                  collectedJobPostingDetails,
                  waitingForBackendQuestion,
                  jobPostingProcessing,
                  linkedinLogs: [...linkedinLogs],
                  naukriLogs: [...naukriLogs],
                  linkedinProcessing,
                  naukriProcessing,
                });
              }
            }
            
            // 2. Update currentViewedSessionRef BEFORE any state updates
            currentViewedSessionRef.current = job.id;
            
            // 3. Fetch agent state for the new session from backend
            // This ensures session-isolated state even after page refresh
            const fetchAgentState = async (newSessionId) => {
              try {
                // Check if we have cached agent state first
                if (agentStatePerSessionRef.current.has(newSessionId)) {
                  console.log('[Agent State] Using cached agent state for session:', newSessionId);
                  return;
                }
                
                // Fetch from backend
                const response = await aiAutomationApi.get(`/session-agent-state/${newSessionId}`);
                if (response?.success && response.agent_state) {
                  agentStatePerSessionRef.current.set(newSessionId, response.agent_state);
                  console.log('[Agent State] Fetched agent state from backend for session:', newSessionId, response.agent_state);
                }
              } catch (err) {
                console.warn('[Agent State] Could not fetch agent state for session:', newSessionId, err);
              }
            };
            
            // Fire-and-forget fetch of agent state (don't block UI)
            fetchAgentState(job.id);
            
            // 4. Update React state to reflect new session
            setSelectedJobId(job.id);
            setAutomationSessionId(job.id);
            setSidebarCollapsed(true);
            // Always start on status tab when switching sessions
            setActiveHeaderTab('status');
            // Persist session ID in URL for page refresh persistence
            setSearchParams(buildSearchParams({ session_id: job.id }), { replace: true });
            
            // 5. CRITICAL: Hydrate UI from session store (single source of truth)
            // This replaces all the complex manual state restoration
            hydrateUIFromSession(job.id);
            
            // 6. Also check legacy state ref (backward compatibility)
            const hasLegacyState = jobPostingStatePerSessionRef.current.has(job.id);
            if (hasLegacyState && !sessionStateStoreRef.current.has(job.id)) {
              const savedState = jobPostingStatePerSessionRef.current.get(job.id);
              console.log('[Session Switch] Migrating from legacy state:', job.id);
              // Migrate to new session store
              const migrationState = createEmptySessionState();
              Object.assign(migrationState, savedState);
              sessionStateStoreRef.current.set(job.id, migrationState);
              // Hydrate from migrated state
              hydrateUIFromSession(job.id);
              // Clear legacy state
              jobPostingStatePerSessionRef.current.delete(job.id);
            }
            
            // CRITICAL: Do NOT reset state after hydration - hydration restores from session store
            // Only reset non-state refs and items not in session store
            logsInitializedRef.current = { linkedin: false, naukri: false };
            setSelectedCandidate(null);
            // Clear outreach items not persisted in session store
            setOutreachTemplate('');
            setOutreachWorkspaceName('');
            setOutreachCandidates([]);
            setOutreachCandidateIds([]);
            setIsLoadingOutreachTemplate(false);
            // Clear lifecycle draft actions (not part of session store)
            setDraftEditActions([]);
            
            // 9. Load session data from API (will override with DB state)
            setLifecycleAwaitingConfirmation(false);
            setLifecycleScheduleActive(false);
            // -- Cross-session leak guards: reset loading states & abort in-flight calls --
            // Clear loading indicators for the NEW session (they'll be restored from
            // sessionStorage in loadSessionData if a request was in-flight for this session)
            setLifecycleLoading(false);
            setChatApiLoading(false);
            setLifecycleActions([]);
            setLastQueryResults(null);
            lastLifecycleInteractionRef.current = 0;
            // IMPORTANT: Do NOT remove lifecycle_inflight from sessionStorage here.
            // It may belong to a different session and needs to survive session switching
            // so the typing loader can be restored when the user navigates back.
            // The inflight flag is scoped by sessionId and checked during loadSessionData.
            lifecycleInflightRef.current = false;
            // Abort any in-flight lifecycle/outreach API calls from the previous session
            if (lifecycleAbortRef.current) {
              lifecycleAbortRef.current.abort();
              lifecycleAbortRef.current = null;
            }
            // Load session data (JD, candidates, requirements, chat) from API
            loadSessionData(job.id);
            if (isMobileLayout) setSidebarVisible(false);
          }}
          onNewJobPosting={() => {
            // Save current session's job posting state before starting new job
            const prevSession = automationSessionId;
            if (prevSession) {
              const hasActiveJobPosting = jobPostingPhase !== 'idle' && jobPostingPhase !== 'posted';
              const hasPendingQuestion = pendingNaukriField || systemMessages.length > 0;

              if (hasActiveJobPosting || hasPendingQuestion || activeJobPostingSessions.has(prevSession)) {
                console.log('[onNewJobPosting] Saving job posting state for session:', prevSession);
                jobPostingStatePerSessionRef.current.set(prevSession, {
                  systemMessages: sanitizeSystemMessages([...systemMessages]),
                  pendingNaukriField,
                  pendingNaukriFieldOptions: [...pendingNaukriFieldOptions],
                  jobPostingPhase,
                  selectedChannels: [...selectedChannels],
                  collectedJobPostingDetails,
                  waitingForBackendQuestion,
                  jobPostingProcessing,
                  linkedinLogs: [...linkedinLogs],
                  naukriLogs: [...naukriLogs],
                  linkedinProcessing,
                  naukriProcessing,
                });
              }
            }
            initiateNewChat();
            if (isMobileLayout) setSidebarVisible(false);
          }}
        />
        </div>
        )}

        {/* Content (shared top row + split panels) */}
        <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden" style={{ minWidth: 0 }}>
          {/* Loading overlay for session transitions or initial page restore */}
          {(sessionLoading || !sessionRestored) && !suppressFullPageRestoreOverlay && <PageLoadingOverlay />}

          {!embedded && !sidebarVisible && !isMobileLayout && (
            <div
              className="absolute bottom-0 left-0 z-10"
              style={{
                top: `${campaignHeaderHeightPx}px`,
                width: `${collapsedSidebarRailWidth}px`,
              }}
            >
              <ChatSidebar
                isCollapsed
                isCompactLayout
                isMobileLayout={isMobileLayout}
                onToggleCollapse={() => setSidebarVisible(true)}
                onRequestClose={() => setSidebarVisible(false)}
                appName={appName}
                appLogo={appLogo}
                newChatCreating={newChatCreating}
                onNewJobPosting={() => {
                  initiateNewChat();
                  if (isMobileLayout) setSidebarVisible(false);
                }}
              />
            </div>
          )}

          {/* Campaign Workspace Header */}
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 z-10 border-b border-gray-200 dark:border-gray-800"
            style={{
              backgroundColor: isDark ? '#111827' : '#FFFFFF',
              height: `${campaignHeaderHeightPx}px`,
              width: '100%',
              display: activeHeaderTab !== 'overview' && isStackedPanels && mobilePanelView !== 'chat' && !isMobileLayout ? 'none' : undefined,
            }}
          />
          <div
            className={`${isMobileLayout && activeHeaderTab !== 'overview' ? 'px-0' : 'px-4'} absolute left-0 top-0 z-20 border-b border-gray-200 py-0 dark:border-gray-800`}
            style={{
              backgroundColor: isDark ? '#111827' : '#FFFFFF',
              height: `${campaignHeaderHeightPx}px`,
              width: isStackedPanels ? '100%' : centerWidth !== null ? `${centerWidth}px` : '60%',
              display: activeHeaderTab !== 'overview' && isStackedPanels && mobilePanelView !== 'chat' && !isMobileLayout ? 'none' : undefined,
            }}
          >
            {!sidebarVisible && !isMobileLayout && (
              <span
                aria-hidden="true"
                className="absolute top-3 h-8 w-px bg-gray-200 dark:bg-gray-800"
                style={{ left: `${collapsedSidebarRailWidth}px` }}
              />
            )}
            {isMobileLayout && activeHeaderTab !== 'overview' && (
              <div className="flex h-full flex-col">
                <div className="flex h-11 items-center justify-between gap-3 px-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="-ml-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    title="Go to dashboard"
                    aria-label="Go to dashboard"
                  >
                    <img
                      src={appLogo}
                      alt={appName}
                      className="h-8 w-8 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement.textContent = 'W';
                      }}
                    />
                  </button>
                  <div className="flex items-center gap-5 text-sm font-semibold">
                    <button
                      type="button"
                      onClick={() => setMobilePanelView('chat')}
                      className="relative pb-1 transition-colors"
                      style={{ color: mobilePanelView === 'chat' ? primaryColor : (isDark ? '#CBD5E1' : '#64748B') }}
                    >
                      Chat
                      {mobilePanelView === 'chat' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMobilePanelView('results')}
                      className="relative pb-1 transition-colors"
                      style={{ color: mobilePanelView === 'results' ? primaryColor : (isDark ? '#CBD5E1' : '#64748B') }}
                    >
                      Results
                      {mobilePanelView === 'results' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex h-[52px] min-w-0 items-center gap-2 border-t border-gray-100 pl-2 pr-4 dark:border-gray-800">
                  {mobilePanelView === 'results' ? (
                    <>
                      {!embedded && !sidebarVisible && (
                        <button
                          type="button"
                          onClick={() => setSidebarVisible(true)}
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-gray-900 transition-colors hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                          aria-label="Show campaigns sidebar"
                          title="Show sidebar"
                        >
                          <ArrowRightIcon />
                        </button>
                      )}
                      {!embedded && !sidebarVisible && (
                        <span className="h-7 w-px flex-shrink-0 bg-gray-200 dark:bg-gray-800" aria-hidden="true" />
                      )}
                      <div className="min-w-0 flex-1">
                        <ChatHeaderControls
                          activeTab={activeHeaderTab}
                          onTabChange={handleHeaderTabChange}
                          onSearch={() => {}}
                          disabledTabs={(!jdText && analysisUxPhase !== 'done') ? ['overview'] : []}
                          visibleTabs={workspaceVisibleTabs}
                          fromDashboard={fromDashboard}
                          variant="workspace"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {!embedded && !sidebarVisible && (
                        <button
                          type="button"
                          onClick={() => setSidebarVisible(true)}
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-gray-900 transition-colors hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                          aria-label="Show campaigns sidebar"
                          title="Show sidebar"
                        >
                          <ArrowRightIcon />
                        </button>
                      )}
                      {!embedded && !sidebarVisible && selectedJobId && (
                        <span className="h-7 w-px flex-shrink-0 bg-gray-200 dark:bg-gray-800" aria-hidden="true" />
                      )}
                      {!embedded && selectedJobId && (
                        <div className="relative flex-shrink-0" ref={headerMenuRef}>
                          <button
                            onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                            style={{ color: '#536987' }}
                            title="Campaign options"
                            aria-label="Campaign options"
                          >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          {headerMenuOpen && (
                            <div className="absolute left-0 top-8 z-50 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 text-sm">
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
                                onClick={() => { setEditTitleValue(selectedJob?.title || ''); setEditingTitle(true); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                Rename
                              </button>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
                                onClick={() => { handleArchiveJob(selectedJobId); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                                Archive
                              </button>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
                                onClick={() => { handleDeleteJob(selectedJobId); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {editingTitle && !embedded ? (
                        <input
                          type="text"
                          value={editTitleValue}
                          onChange={(e) => setEditTitleValue(e.target.value)}
                          onBlur={() => handleSaveSessionTitle(editTitleValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSessionTitle(editTitleValue);
                            if (e.key === 'Escape') setEditingTitle(false);
                          }}
                          autoFocus
                          className="min-w-0 flex-1 bg-transparent text-base font-semibold text-gray-900 outline-none caret-current dark:text-white"
                          style={{
                            borderBottom: `1px solid ${primaryColor}`,
                            padding: '0 0 2px 0',
                            caretColor: primaryColor,
                          }}
                        />
                      ) : (
                        <span className="min-w-0 truncate text-base font-semibold text-gray-950 dark:text-white">{selectedJob?.title || 'New Campaign'}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <div className={`${isMobileLayout && activeHeaderTab !== 'overview' ? 'hidden' : 'flex'} h-full items-center justify-between`}>
              <div className="min-w-0 flex flex-1 items-center gap-1">
                {!sidebarVisible && (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="-ml-4 flex h-[58px] w-14 flex-shrink-0 items-center justify-center text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      title="Go to dashboard"
                      aria-label="Go to dashboard"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl">
                        <img
                          src={appLogo}
                          alt={appName}
                          className="h-8 w-8 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement.textContent = 'W';
                          }}
                        />
                      </div>
                      <span className="hidden truncate text-lg font-semibold text-gray-950 dark:text-white">{appName}</span>
                    </button>
                  </>
                )}
                <div className="min-w-0 flex flex-1 flex-col">
                <h1 className="min-w-0 flex w-full items-center gap-2 text-base font-semibold leading-snug text-gray-950 dark:text-white">
                  {/* {(embedded || fromDashboard) && (
                    <button
                      onClick={() => { navigate('/dashboard'); searchParams.delete('from_dashboard'); setSearchParams(searchParams); }}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400 flex-shrink-0"
                      title="Back to Dashboard"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </button>
                  )} */}
                  {editingTitle && !embedded ? (
                    <input
                      type="text"
                      value={editTitleValue}
                      onChange={(e) => setEditTitleValue(e.target.value)}
                      onBlur={() => handleSaveSessionTitle(editTitleValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveSessionTitle(editTitleValue);
                        if (e.key === 'Escape') setEditingTitle(false);
                      }}
                      autoFocus
                      className="bg-transparent outline-none text-gray-900 dark:text-white caret-current"
                      style={{
                        borderBottom: `1px solid ${primaryColor}`,
                        minWidth: '200px',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        padding: '0 0 2px 0',
                        caretColor: primaryColor,
                      }}
                    />
                  ) : (
                    <>
                      {/* Three-dot menu before title */}
                      {!embedded && selectedJobId && (
                        <div className="relative flex-shrink-0" ref={headerMenuRef}>
                          <button
                            onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                            style={{ color: '#536987' }}
                            title="Campaign options"
                            aria-label="Campaign options"
                          >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          {headerMenuOpen && (
                            <div className="absolute left-0 top-8 z-50 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 text-sm">
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
                                onClick={() => { setEditTitleValue(selectedJob?.title || ''); setEditingTitle(true); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                Rename
                              </button>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
                                onClick={() => { handleArchiveJob(selectedJobId); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                                Archive
                              </button>
                              <button
                                className="w-full text-left px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
                                onClick={() => { handleDeleteJob(selectedJobId); setHeaderMenuOpen(false); }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <span className="min-w-0 truncate">{selectedJob?.title || 'New Campaign'}</span>
                    </>
                  )}
                  {activeHeaderTab === 'job_posting' && !editingTitle && (
                    <span
                      className="flex-shrink-0 w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: primaryColor }}
                      title="Active chat"
                    />
                  )}
                  {!editingTitle && selectedJobId && isStackedPanels && activeHeaderTab !== 'overview' && (
                    <div className="ml-auto flex flex-shrink-0 items-center gap-3">
                      <div className="hidden min-w-0 items-center gap-1.5 text-xs font-normal leading-4 text-gray-600 dark:text-gray-300 md:flex">
                        <CalendarIcon />
                        <span className="truncate">Updated {selectedJob?.lastUpdated || 'No timestamp yet'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMobilePanelView('chat')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          mobilePanelView === 'chat'
                            ? 'text-white'
                            : isDark
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                        style={mobilePanelView === 'chat' ? { backgroundColor: primaryColor } : {}}
                      >
                        Chat
                      </button>
                      <button
                        type="button"
                        onClick={() => setMobilePanelView('results')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          mobilePanelView === 'results'
                            ? 'text-white'
                            : isDark
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                        style={mobilePanelView === 'results' ? { backgroundColor: primaryColor } : {}}
                      >
                        Results
                      </button>
                    </div>
                  )}
                  {!editingTitle && selectedJobId && !isStackedPanels && (
                    <div
                      className="ml-auto flex min-w-0 items-center gap-1.5 text-xs font-normal leading-4 text-gray-600 dark:text-gray-300"
                    >
                      <CalendarIcon />
                      <span className="truncate">Updated {selectedJob?.lastUpdated || 'No timestamp yet'}</span>
                    </div>
                  )}
                </h1>
                </div>
              </div>

              <div className=" hidden flex w-full flex-shrink-0 items-center justify-start gap-3 xl:w-auto xl:justify-end">
                <button
                  type="button"
                  onClick={initiateNewChat}
                  className="flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  style={{ color: isDark ? '#D1D5DB' : '#536987' }}
                  aria-label="New Job Posting"
                  title="New Job Posting"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5H6C4.89543 5 4 5.89543 4 7V18C4 19.1046 4.89543 20 6 20H17C18.1046 20 19 19.1046 19 18V13M17.5858 3.58579C18.3668 2.80474 19.6332 2.80474 20.4142 3.58579C21.1953 4.36683 21.1953 5.63316 20.4142 6.41421L11.8284 15H9L9 12.1716L17.5858 3.58579Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

          </div>

          {activeHeaderTab === 'overview' && !isStackedPanels && (
            <div
              className="absolute right-0 top-0 z-20 flex h-[58px] items-center justify-center border-b border-l border-gray-200 px-3 py-0 dark:border-gray-700 sm:px-4"
              style={{
                backgroundColor: isDark ? '#111827' : '#FFFFFF',
                width: centerWidth !== null ? `calc(100% - ${centerWidth}px - 4px)` : '40%',
                minWidth: `${rightPanelMinWidthPx}px`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              <ChatHeaderControls
                activeTab={activeHeaderTab}
                onTabChange={handleHeaderTabChange}
                onSearch={() => {}}
                disabledTabs={(!jdText && analysisUxPhase !== 'done') ? ['overview'] : []}
                visibleTabs={workspaceVisibleTabs}
                fromDashboard={fromDashboard}
                variant="workspace"
              />
            </div>
          )}

          {/* Content Area: Both always rendered, hidden with display:none to preserve state across tab switches */}
          <div
            style={{
              display: activeHeaderTab === 'overview' ? undefined : 'none',
              paddingTop: `${campaignHeaderHeightPx}px`,
              paddingLeft: !embedded && !sidebarVisible && !isMobileLayout ? `${collapsedSidebarRailWidth}px` : undefined,
              boxSizing: 'border-box',
            }}
            className="flex-1 min-h-0 overflow-y-auto w-full"
          >
            <OverviewSection
              key={selectedJobId || automationSessionId || sessionId || 'no-session'}
              jobTitle={currentInterviewJobTitle}
              sessionId={selectedJobId || automationSessionId || sessionId}
              onCandidateClick={(candidate) => selectCandidate(candidate)}
              scrollToCandidatesToken={overviewScrollToCandidatesToken}
              activeTab={activeHeaderTab}
              onSidebarCollapse={(collapsed) => setSidebarCollapsed(collapsed)}
              onCandidateUpdate={updateCandidateData}
              onBack={() => { setUserActiveHeaderTab('job_posting'); clearCandidate(); }}
              featureAccess={companyFeatureAccess}
            />
          </div>
          <div
            style={{
              display: activeHeaderTab === 'overview' ? 'none' : undefined,
              minWidth: 0,
              paddingTop: isMobileLayout ? `${campaignHeaderHeightPx}px` : undefined,
              paddingLeft: !embedded && !sidebarVisible && !isMobileLayout ? `${collapsedSidebarRailWidth}px` : undefined,
              boxSizing: 'border-box',
            }}
            className="flex-1 flex min-h-0"
          >
            {/* Center Chat Area */}
            <div
              ref={centerRef}
                className="flex flex-col min-h-0 flex-shrink-0 h-full"
	              style={{
                width: isStackedPanels ? '100%' : centerWidth !== null ? `${centerWidth}px` : '60%',
	                minWidth: isStackedPanels ? '0px' : `${centerMinWidthPx}px`,
	                flex: isStackedPanels ? '1 1 auto' : '0 0 auto',
	                display: isStackedPanels && mobilePanelView !== 'chat' ? 'none' : 'flex',
                paddingTop: isMobileLayout ? '0px' : '58px',
                boxSizing: 'border-box',
	                background: isDark
	                  ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.94) 100%)'
	                  : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,251,255,0.98) 100%)',
	              }}
            >
              <ChatMain
                key={selectedJobId || automationSessionId || sessionId || 'chat-main'}
                  jobTitle={currentInterviewJobTitle}
                  lastUpdated={selectedJob?.lastUpdated}
                  duration={selectedJob?.duration}
                  showHeader={false}
                  onUserQuery={handleUserQueryForJD}
                  onOpenScorecardEditor={() => {
                    if (!scorecardSetupSessionId || scorecardSetupSessionId !== currentViewedSessionRef.current) return;
                    setScorecardSelectedSetupId(null);
                    setScorecardEditorSessionId(scorecardSetupSessionId);
                    setScorecardEditorOpen(true);
                  }}
                  scorecardEditorAvailable={scorecardSetupSessionId === currentViewedSessionRef.current &&
                    ['required_competencies', 'setup_review'].includes(scorecardSetupStep)}
                  scorecardSavedSummary={scorecardSavedSummary}
                  selectedJobId={selectedJobId}
                  syncSearchId={getCurrentSessionSearchId()}
                  onCandidateRejected={handleCandidateRejected}
                  onSystemMessage={systemMessage}
                  onSystemMessages={systemMessages}
                  systemMessageToken={systemMessageToken}
                  lastResponseMode={lastResponseMode}
                  chatMode={chatMode}
                  showFilterMode={canUseFeature('filter')}
                  onChatModeChange={(newMode) => {
                  // Skip if mode isn't actually changing
                  if (newMode === chatMode) return;
                  if (newMode === 'ask' && blockDisabledFeature('filter')) return;
                  // Block mode switch if an agent flow or process is active
                  if (newMode === 'ask') {
                    if (agentStageLabel) {
                      showToast(`You are in the middle of "${agentStageLabel}". Please complete it or cancel before switching to Pipeline mode.`, 'warning', 4000);
                      return;
                    }
                    if (talentScoutingStarted && !sourcingComplete) {
                      showToast('Candidate sourcing is in progress. Please wait for it to complete before switching to Pipeline mode.', 'warning', 4000);
                      return;
                    }
                    if (jobPostingPhase === 'posting' || jobPostingProcessing) {
                      showToast('Job posting is in progress. Please wait for it to complete before switching to Pipeline mode.', 'warning', 4000);
                      return;
                    }
                    if (outreachSending && !outreachCompleted) {
                      showToast('Outreach is being sent. Please wait for it to complete before switching to Pipeline mode.', 'warning', 4000);
                      return;
                    }
                  }
                  // If switching to Agent mode with active filters, show confirmation modal
                  if (newMode === 'agent' && filterBreadcrumbs.length > 0) {
                    setShowFilterClearModal(true);
                    return;
                  }

                  // Single source of truth for the transition pill:
                  // a permanent chat history entry with `agent_transition`
                  // metadata that ChatMain renders as a centered pill.
                  // The toast still fires for corner feedback, but the
                  // in-chat transient pill (filterPillText / token) is
                  // intentionally NOT set here — using both produced
                  // two stacked pills, then the transient one
                  // disappeared after its timer, which looked like a
                  // disappearing message and confused users.
                  const pillText = newMode === 'ask' ? 'Pipeline Agent activated' : 'Exited Pipeline Agent';
                  showToast(pillText, 'info', 2000);

                  const transitionEntry = {
                    id: `transition_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    sender: 'agent',
                    message: pillText,
                    timestamp: new Date().toISOString(),
                    metadata: {
                      type: 'agent_transition',
                      agent: 'Pipeline Agent',
                      action: newMode === 'ask' ? 'activated' : 'exited',
                    },
                  };
                  setChatHistory((prev) => [...prev, transitionEntry]);
                  const _transitionSid = selectedJobId || automationSessionId || sessionId;
                  if (_transitionSid) {
                    updateSessionState(_transitionSid, (s) => {
                      s.chat = s.chat || {};
                      s.chat.chatHistory = [
                        ...(s.chat.chatHistory || []),
                        transitionEntry,
                      ];
                      return s;
                    });
                    aiAutomationApi.post(`/sessions/${_transitionSid}/messages`, {
                      message_type: 'assistant',
                      content: pillText,
                      message_role: 'assistant',
                      metadata: {
                        type: 'agent_transition',
                        agent: 'Pipeline Agent',
                        action: newMode === 'ask' ? 'activated' : 'exited',
                      },
                    }).catch((e) => {
                      console.warn('Failed to persist transition pill', e);
                    });
                  }
                  // Note: filter pills are transient UI only — not persisted to DB
                  // They don't survive refresh (mode state does via /chat/mode-switch)
                  if (newMode === 'agent') {
                    // Clear Ask-mode filter state
                    setFilterBreadcrumbs([]);
                    setFilterActions([]);
                    setWorkingCandidates([]);
                    setFollowUpSuggestions([]);
                    setNewCandidatesCount(0);
                  }
                  setChatMode(newMode);
                  // Persist filter state to session store (in-memory)
                  const sid = selectedJobId || automationSessionId || sessionId;
                  if (sid) {
                    updateSessionState(sid, (s) => {
                      s.filtering = {
                        chatMode: newMode,
                        breadcrumbs: newMode === 'agent' ? [] : filterBreadcrumbs,
                        actions: newMode === 'agent' ? [] : filterActions,
                        workingCandidates: newMode === 'agent' ? [] : workingCandidates,
                        followUpSuggestions: newMode === 'agent' ? [] : followUpSuggestions,
                      };
                      return s;
                    });
                    // Persist mode switch to backend. For Filter mode the
                    // backend also runs filter_mode_welcome and returns a
                    // welcome payload with pre-loaded candidates so the
                    // user has context from turn one (instead of needing
                    // to type "show all candidates" first). For Agent
                    // mode the response is a minimal ack.
                    aiAutomationApi.post('/chat/mode-switch', {
                      session_id: sid,
                      cid: getCid(),
                      additional_data: { mode: newMode },
                    }).then((response) => {
                      if (newMode !== 'ask') return;
                      const welcome = response?.welcome;
                      if (!welcome || !welcome.message) return;

                      // Render the welcome as an agent chat message so
                      // the conversation transcript reflects the toggle.
                      const welcomeMsg = {
                        id: `welcome_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                        sender: 'agent',
                        message: welcome.message,
                        timestamp: new Date().toISOString(),
                        metadata: {
                          type: welcome.type || 'filter_welcome',
                          welcome_kind: welcome.welcome_kind,
                          candidate_count: welcome.candidate_count,
                        },
                      };
                      setChatHistory((prev) => [...prev, welcomeMsg]);

                      // Hydrate the filter UI immediately — breadcrumbs,
                      // working set, action buttons, follow-up chips —
                      // so subsequent messages refine off this set.
                      const wbc = Array.isArray(welcome.filter_breadcrumbs)
                        ? welcome.filter_breadcrumbs
                        : [];
                      const wwc = Array.isArray(welcome.working_candidates)
                        ? welcome.working_candidates
                        : [];
                      const wfa = Array.isArray(welcome.filter_actions)
                        ? welcome.filter_actions
                        : [];
                      const wfu = Array.isArray(welcome.follow_up_suggestions)
                        ? welcome.follow_up_suggestions
                        : [];
                      // Mode-switch can also surface a new-candidates hint
                      // at the top level (response.new_candidates_count) or
                      // inside welcome.* — read the top-level path the BE
                      // currently emits.
                      const wNewCount = Number.isInteger(welcome.new_candidates_count)
                        ? welcome.new_candidates_count
                        : 0;
                      setFilterBreadcrumbs(wbc);
                      setWorkingCandidates(wwc);
                      setFilterActions(wfa);
                      setFollowUpSuggestions(wfu);
                      setNewCandidatesCount(wNewCount);

                      // Re-sync the per-session store so refresh / session
                      // switch round-trip keeps the welcome state intact.
                      updateSessionState(sid, (s) => {
                        s.chat = s.chat || {};
                        s.chat.chatHistory = [
                          ...(s.chat.chatHistory || []),
                          welcomeMsg,
                        ];
                        s.filtering = {
                          chatMode: 'ask',
                          breadcrumbs: wbc,
                          actions: wfa,
                          workingCandidates: wwc,
                          followUpSuggestions: wfu,
                        };
                        return s;
                      });
                    }).catch((err) => {
                      // Welcome failures fall back to silent toggle —
                      // user is still in Filter mode, they just don't
                      // get the pre-loaded set. They can type "show all"
                      // to reach the same state.
                      console.warn('mode-switch welcome failed', err);
                    });
                  }
                }}
                isLoading={
                  // Session-aware loader: only show JD loading if current session matches the loading session
                  ((analysisUxPhase === 'analysing' && isJDLoading && 
                    (!jdLoadingSessionRef.current || jdLoadingSessionRef.current === automationSessionId)) ||
                  lifecycleLoading || chatApiLoading)
                }
                transitionPill={filterPillText}
                transitionPillToken={filterPillToken}
                filterBreadcrumbs={filterBreadcrumbs}
                filterActions={filterActions}
                followUpSuggestions={followUpSuggestions}
                newCandidatesCount={newCandidatesCount}
                onRefreshFilter={async () => {
                  if (blockDisabledFeature('filter')) return;
                  // Phase 2 — "X new candidates match" chip click. Calls
                  // /filter/refresh which re-runs the active AST chain
                  // server-side and persists the fresh working set. The
                  // response is shaped like a normal filter result so we
                  // drop it onto the transcript via the same handler that
                  // processes /chat replies. On success the chip clears
                  // because the BE returns new_candidates_count: 0.
                  const sid = selectedJobId || automationSessionId || sessionId;
                  if (!sid) return;
                  // Optimistically clear the chip so the user gets
                  // immediate feedback even before the request returns.
                  setNewCandidatesCount(0);
                  try {
                    const r = await aiAutomationApi.post('/filter/refresh', {
                      session_id: sid,
                    });
                    if (!r?.success) {
                      console.warn('[filter/refresh] non-success response', r);
                      return;
                    }
                    if (r.refreshed === false) return;
                    // Push the assistant message into the transcript.
                    const refreshMsg = {
                      id: `filter_refresh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                      type: 'assistant',
                      content: r.message || 'Filter refreshed.',
                      metadata: { type: r.type || 'filter_result' },
                      timestamp: new Date().toISOString(),
                    };
                    setChatHistory((prev) => [...prev, refreshMsg]);
                    // Apply working set + chips just like /chat would.
                    if (Array.isArray(r.working_candidates)) {
                      setWorkingCandidates(r.working_candidates);
                    }
                    if (Array.isArray(r.filter_breadcrumbs)) {
                      setFilterBreadcrumbs(r.filter_breadcrumbs);
                    }
                    if (Array.isArray(r.filter_actions)) {
                      setFilterActions(r.filter_actions);
                    }
                    if (Array.isArray(r.follow_up_suggestions)) {
                      setFollowUpSuggestions(r.follow_up_suggestions);
                    }
                    // Persist into the per-session store so the
                    // refreshed set survives a quick session switch.
                    updateSessionState(sid, (s) => {
                      s.chat = s.chat || {};
                      s.chat.chatHistory = [
                        ...(s.chat.chatHistory || []),
                        refreshMsg,
                      ];
                      s.filtering = {
                        chatMode: 'ask',
                        breadcrumbs: Array.isArray(r.filter_breadcrumbs) ? r.filter_breadcrumbs : (s.filtering?.breadcrumbs || []),
                        actions: Array.isArray(r.filter_actions) ? r.filter_actions : (s.filtering?.actions || []),
                        workingCandidates: Array.isArray(r.working_candidates) ? r.working_candidates : (s.filtering?.workingCandidates || []),
                        followUpSuggestions: Array.isArray(r.follow_up_suggestions) ? r.follow_up_suggestions : (s.filtering?.followUpSuggestions || []),
                      };
                      return s;
                    });
                  } catch (err) {
                    console.warn('[filter/refresh] failed', err);
                  }
                }}
                onFilterAction={handleFilterAction}
                onClearFilters={() => {
                  if (blockDisabledFeature('filter')) return;
                  setFilterBreadcrumbs([]);
                  setFilterActions([]);
                  setWorkingCandidates([]);
                  setFollowUpSuggestions([]);
                  setNewCandidatesCount(0);
                  // Send clear signal to backend
                  const sid = selectedJobId || automationSessionId || sessionId;
                  const srid = getSafeSearchId(sid) || (automationSearchId?.startsWith('search_') ? automationSearchId : '');
                  handleUserQueryForJD('clear filters', 'ask');
                }}
                sourcingComplete={sourcingComplete}
                jobPostingPhase={jobPostingPhase}
                selectedChannels={selectedChannels}
                channelOptions={availablePostingChannels}
                postedChannels={effectivePostedChannels}
                onChannelSelect={(channels) => {
                  setSelectedChannels(channels);
                  // CRITICAL: Persist to session store so selection survives session switch/refresh
                  updateSessionState(automationSessionId, (state) => {
                    state.jobPosting.selectedChannels = channels;
                    return state;
                  });
                  // Don't change phase here - wait for user to press Enter
                }}
                jobPostingProcessing={jobPostingProcessing}
                jobPostingComplete={jobPostingComplete}
                jobPostingFailed={jobPostingFailed}
                waitingForBackendQuestion={waitingForBackendQuestion}
                waitingForConfirmation={waitingForConfirmation}
                pendingNaukriFieldOptions={pendingNaukriFieldOptions}
                onNaukriFieldOptionSelect={handleNaukriFieldOptionSelect}
                initialMessages={chatHistory}
                historyLoading={chatHistoryLoading}
                resetToken={chatResetToken}
                contextActions={(() => {
                  // Gate: hide during field collection or active agent flows
                  const currentSessionState = sessionStateStoreRef.current.get(automationSessionId);
                  const isFieldCollectionActiveForSession =
                    currentSessionState?.jobPosting?.fieldCollectionActive ||
                    currentSessionState?.jobPosting?.pendingField;
                  const isConfirmationModalOpen = showJobPostingConfirmationModal;
                  if (isFieldCollectionActiveForSession && !isConfirmationModalOpen) return [];
                  if (agentStageLabel) {
                    // During an active agent flow (e.g. interview setup), show ONLY the flow's own
                    // suggestion chips from the backend (Fully AI / Hybrid / Confirm …), not the
                    // generic context buttons. Without this, staged flows render no clickable options.
                    return (lifecycleActions || []).map((a) => ({
                      id: a.id || `suggestion_${a.action}`,
                      label: a.label,
                      command: a.command || a.label,
                    }));
                  }

                  const hasJd = !!jdText;
                  const hasCandidates = Array.isArray(candidates) && candidates.filter(Boolean).length > 0;
                  const isSourcingDone = sourcingComplete;
                  const isSourcingRunning = talentScoutingStarted && !sourcingComplete;

                  // 1. Start with backend suggestions (flow-aware, DB-driven)
                  let buttons = (lifecycleActions || []).map(a => ({
                    id: a.id || `suggestion_${a.action}`,
                    label: a.label,
                    command: a.command || a.label,
                  }));

                  // 2. Frontend UI-visibility filters (things backend can't know)
                  buttons = buttons.filter(b => {
                    if (!hasJd && b.id.includes('post_job')) return false;
                    if (b.id.includes('post_job') && !canUseFeature('job_posting')) return false;
                    if (b.id.includes('source') && !canUseFeature('sourcing')) return false;
                    if (b.id.includes('outreach') && !canUseFeature('outreach')) return false;
                    if ((b.id.includes('schedule') || b.id.includes('interview')) && !canUseFeature('scheduling')) return false;
                    if (b.id.includes('view_jd') && rightPanelMode === 'jd') return false;
                    if (b.id.includes('source') && isSourcingRunning) return false;
                    return true;
                  });
                  buttons = buttons.map((button) =>
                    button.id.includes('post_job')
                      ? { ...button, label: getPostJobButtonLabel(), command: getPostJobCommand() }
                      : button
                  );

                  // 3. MANDATORY buttons — always present regardless of backend
                  if (!hasJd) {
                    // No JD yet — Create JD is the only mandatory button
                    if (!buttons.some(b => b.id.includes('jd'))) {
                      if (lastResponseType === 'jd_collecting' || lastResponseType === 'jd_confirm_fields') {
                        buttons.unshift({ id: 'continue_jd', label: 'Continue JD Creation', command: 'create jd' });
                      } else {
                        buttons.unshift({ id: 'create_jd', label: 'Create JD', command: 'create jd' });
                      }
                    }
                  } else {
                    // JD exists — Source Candidates and Post Job are MANDATORY
                    if (canUseFeature('sourcing') && !isSourcingDone && !isSourcingRunning && !buttons.some(b => b.id.includes('source'))) {
                      buttons.unshift({ id: 'source_candidates', label: 'Source Candidates', command: 'search candidates' });
                    }
                    if (canUseFeature('job_posting') && actionablePostingChannels.length > 0 && !buttons.some(b => b.id.includes('post_job'))) {
                      buttons.push({ id: 'post_job', label: getPostJobButtonLabel(), command: getPostJobCommand() });
                    }
                    // View JD — show if not already in panel
                    if (rightPanelMode !== 'jd' && !buttons.some(b => b.id.includes('view_jd'))) {
                      buttons.push({ id: 'view_jd', label: 'View JD', command: 'view jd' });
                    }
                    // Send Outreach — show when sourcing done and candidates exist
                    if (canUseFeature('outreach') && isSourcingDone && hasCandidates && !buttons.some(b => b.id.includes('outreach'))) {
                      buttons.push({ id: 'send_outreach', label: 'Send Outreach', command: 'send outreach' });
                    }
                  }

                  // 4. Status/Candidates — MANDATORY when candidates exist (never hidden)
                  const showCandidateCount = activeSessionForCandidateCount ? activeSessionCandidateCount : candidates.filter(Boolean).length;
                  if (showCandidateCount > 0) {
                    buttons = buttons.filter(b => !b.id.includes('show_status'));
                    buttons.unshift({
                      id: 'show_status',
                      label: 'Show Status',
                      command: 'show status',
                    });
                    // Replace any backend version with count-enriched one
                    buttons = buttons.filter(b => !b.id.includes('candidates'));
                    buttons.push({
                      id: 'show_candidates',
                      label: `Show Candidates (${showCandidateCount})`,
                      command: 'show candidates',
                    });
                  }

                  return buttons;
                })()}
                autoSendMessage={dashboardInitialQuery}
                autoSendToken={dashboardInitialQueryToken}
                autoSendOptions={dashboardInitialQuerySessionId ? { sessionId: dashboardInitialQuerySessionId } : null}
                onAutoSendConsumed={() => {
                  setDashboardInitialQuery('');
                  setDashboardInitialQueryToken(0);
                  setDashboardInitialQuerySessionId('');
                }}
                draftEditActions={draftEditActions}
                workspaceOptions={workspaceOptions}
                workspaceSelectionRequired={workspaceSelectionRequired}
                onDraftEditAction={(command) => {
                  setDraftEditActions([]); // Clear buttons after click
                }}
                stageLabel={agentStageLabel}
                stageCancellable={agentStageCancellable}
                onStageCancel={() => {
                  // Send cancel to the chat as if user typed it
                  // Don't clear agentStageLabel here — the /chat response handler
                  // or field-collection cancel handler will clear it AND show the
                  // "Exited" transition pill. Clearing here would prevent pill from showing.
                  handleUserQueryForJD('cancel');
                }}
                onStartOutreach={initiateOutreach}
                canUseOutreach={canUseFeature('outreach')}
                onViewCandidateDetails={handleViewCandidateDetailsFromChat}
                onCandidateAction={handleCandidateCardAction}
                onMarkPriority={handleMarkPriority}
                onSendFollowup={handleSendFollowup}
                onBookInterview={handleBookInterview}
                onViewResume={handleViewResume}
                onGenerateInterview={handleGenerateInterview}
                onMeetingAnalysis={handleMeetingAnalysis}
                hasJd={!!jdText}
                jdChecking={sessionLoading}
                candidates={candidates}
                renderInterviewCreation={(payload, controls = {}) => (
                  <InterviewWorkspace
                    creationOnly
                    candidates={payload.candidates || []}
                    searchId={payload.searchId || getCurrentSessionSearchId() || automationSearchId}
                    sourceSessionId={payload.sourceSessionId || selectedJobId || automationSessionId || sessionId}
                    jobTitle={payload.jobTitle || currentInterviewJobTitle}
                    jobContext={payload.jobContext || {
                      ...(accumulatedRequirements || {}),
                      ...(finalRequirements || {}),
                      jdText,
                      jobTitle: currentInterviewJobTitle,
                    }}
                    externalMessage={controls.externalMessage}
                    onExternalMessageProcessed={controls.onExternalMessageProcessed}
                    onStartSchedulingAgent={handleInterviewCreationSchedulingAgent}
                    onCloseCreation={controls.dismiss}
                    featureAccess={companyFeatureAccess}
                  />
                )}
              />
            </div>

            {/* Resize Handle */}
            {!isStackedPanels && (
              <div
                className="w-1 bg-gray-200 dark:bg-gray-700 hover:bg-primary-400 dark:hover:bg-primary-500 transition-colors duration-200 flex-shrink-0 relative group"
                onMouseDown={handleResizeStart}
                style={{ cursor: 'col-resize', zIndex: 10 }}
              >
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 group-hover:w-1.5 bg-transparent group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-all duration-200" />
              </div>
            )}

            <ChatRightPanel
              featureAccess={companyFeatureAccess}
              pipelineSnapshot={pipelineSnapshot}
              layout={{
                isStackedPanels,
                mobilePanelView,
                isMobileLayout,
                centerWidth,
                rightPanelMinWidthPx,
                isDark,
                rightPanelTransitioning,
                primaryColor,
                secondaryColor,
                onMobilePanelViewChange: setMobilePanelView,
              }}
              header={{
                activeHeaderTab,
                agentStageLabel,
                lastResponseType,
                onNavigateTab: handleHeaderTabChange,
                onOpenPipelineFilter: handleOpenPipelineFilter,
                fromDashboard,
                interviewRefreshKey,
                interviewSetupDraft,
              }}
              jobPosting={{
                jobTitle: currentInterviewJobTitle,
                showJobPostingJdPanel,
                showJobPostingPlanPanel,
                jdText,
                finalRequirements,
                availablePostingChannels,
                sourcingComplete,
                talentScoutingStarted,
                jobPostingPhase,
                jobPostingProcessing,
                jobPostingComplete,
                jobPostingFailed,
                selectedChannels,
                selectedJobCandidateCount: activeSessionCandidateCount,
                selectedJobHasPostedJob: Boolean(selectedJob?.hasPostedJob),
                selectedJobLastUpdated: selectedJob?.lastUpdated || selectedJob?.rawTimestamp || null,
                postingDetails,
                collectedJobPostingDetails,
                jobPostingLogs: [...(linkedinLogs || []), ...(naukriLogs || [])],
                linkedinLogs,
                naukriLogs,
                linkedinProcessing: effectiveLinkedinPostingProcessing,
                naukriProcessing: effectiveNaukriPostingProcessing,
                shouldForceJobPostingPanel,
                postedChannels: effectivePostedChannels,
                syncingPostedApplicants,
                postedApplicantsLastSyncTime,
                jobPostingExpandToken,
              }}
              sourcing={{
                candidates,
                sourcingStats,
                automationSessionId,
                automationSearchId,
                panelSessionId: selectedJobId || automationSessionId || sessionId,
                panelSearchId: getCurrentSessionSearchId() || automationSearchId,
                talentPoolChannelFilter,
                googleCandidateCount,
                linkedinCandidateCount,
                googleSourcingLogs,
                linkedinSourcingLogs,
                resumeBoxSourcingLogs,
                sourcingLogs: [...(googleSourcingLogs || []), ...(linkedinSourcingLogs || []), ...(resumeBoxSourcingLogs || [])],
                googleSourcingProcessing,
                linkedinSourcingProcessing,
                resumeBoxSourcingProcessing,
                resumeBoxCandidateCount,
                googleSearchFoundCount,
                linkedinSearchFoundCount,
                enrichedCandidateCount,
                matchedCandidateCount: activeSessionCandidateCount || matchedCandidateCount,
                sourcingExpandToken,
                sourcingRefreshToken,
                selectedSourcingChannels,
                pipelineCandidateStatusFilter,
              }}
              outreach={{
                outreachPhase,
                rightPanelMode,
                outreachCompleted,
                outreachLogs,
                outreachSummary,
                outreachSending,
                outreachCandidateIds,
                outreachCandidates,
                selectedOutreachChannels,
                outreachTemplate,
                outreachWorkspaceName,
                outreachTemplatesByChannel,
                isLoadingOutreachTemplate,
                emailSyncState: getSyncState(getCurrentSessionSearchId() || automationSearchId),
              }}
              analysis={{
                showResultsPanel,
                analysisUxPhase,
                chatApiLoading,
                lifecycleLoading,
                isJDLoading,
                jdError,
              }}
              handlers={{
                handleUserQueryForJD,
                getPostJobCommand,
                onPostingDetailsChange: (details) => {
                  setPostingDetails(details);
                },
                onPostingDetailsTriggerMessage: (message) => {
                  if (handleUserQueryForJD) {
                    handleUserQueryForJD(message);
                  }
                },
                onOpenJobPostingPreview: async (channels, details) => {
                  const mergedCollectedDetails = buildJobPostingCollectedDetails({
                    collectedDetails: collectedJobPostingDetails || {},
                    postingValues: {
                      ...postingDetails,
                      ...details,
                    },
                    channels,
                    finalRequirements,
                  });
                  const reusablePostingDetails = collectedDetailsToPostingDetails(mergedCollectedDetails);
                  setJobPostingPreviewData({
                    companyName: reusablePostingDetails.companyName || '',
                    salaryMin: reusablePostingDetails.salaryMin || '',
                    salaryMax: reusablePostingDetails.salaryMax || '',
                    experienceMin: reusablePostingDetails.experienceMin ?? '',
                    experienceMax: reusablePostingDetails.experienceMax ?? '',
                    allowCandidatesToCall:
                      reusablePostingDetails.allowCandidatesToCall === true
                        ? 'Yes'
                        : reusablePostingDetails.allowCandidatesToCall === false
                        ? 'No'
                        : '',
                    recruiterName: reusablePostingDetails.recruiterName || '',
                    recruiterNumber: reusablePostingDetails.recruiterNumber || '',
                    _channels: channels,
                    _details: reusablePostingDetails,
                  });
                  setShowJobPostingPreview(true);
                },
                onRequestPostJob: () => {
                  if (blockDisabledFeature('job_posting')) return;
                  if (!jdText || !String(jdText).trim()) {
                    setSystemMessages(['Please create the JD first. Job posting is available only after the JD is created.']);
                    return;
                  }
                  setActiveHeaderTab('job_posting');
                  setRightPanelMode('auto');
                  setJobPostingExpandToken((prev) => prev + 1);
                  handleUserQueryForJD(getPostJobCommand());
                },
                onRetryPostChannel: async (channel) => {
                  if (blockDisabledFeature('job_posting')) return;
                  if (!jdText || !String(jdText).trim()) {
                    setSystemMessages(['Please create the JD first. Job posting is available only after the JD is created.']);
                    return;
                  }
                  setActiveHeaderTab('job_posting');
                  setRightPanelMode('auto');
                  setJobPostingExpandToken((prev) => prev + 1);
                  const normalizedChannel = channel === 'linkedin' ? 'LinkedIn' : 'Naukri';
                  // Reset so next posting_started WS message clears old logs for this channel
                  logsInitializedRef.current[channel.toLowerCase()] = false;
                  // Clear old logs immediately so the inline panel shows fresh state
                  if (channel.toLowerCase() === 'linkedin') setLinkedinLogs([]);
                  else setNaukriLogs([]);
                  setSelectedChannels([normalizedChannel]);
                  // Also clear old logs from in-memory session store so session switches don't show stale failed logs
                  updateSessionState(automationSessionId, (state) => {
                    state.jobPosting.selectedChannels = [normalizedChannel];
                    state.jobPosting.availableChannels = Array.from(new Set([
                      ...(state.jobPosting.availableChannels || []),
                      normalizedChannel,
                    ]));
                    if (channel.toLowerCase() === 'linkedin') {
                      state.jobPosting.linkedinLogs = [];
                      state.jobPosting.linkedinProcessing = false;
                      state.jobPosting.failed = false;
                    } else {
                      state.jobPosting.naukriLogs = [];
                      state.jobPosting.naukriProcessing = false;
                      state.jobPosting.failed = false;
                    }
                    return state;
                  });
                  return openJobPostingConfirmationWithAnalysis([normalizedChannel]);
                },
                onSyncApplicants: handleSyncPostedApplicants,
                initiateOutreach,
                onViewCandidate: (candidate) => {
                  // Add candidate card as a message in chat history instead of overlay
                  if (!candidate) return;
                  const newMessage = {
                    id: Date.now(),
                    sender: 'agent',
                    message: '',
                    timestamp: new Date().toISOString(),
                    metadata: {
                      type: 'candidate_card',
                      candidate: candidate
                    }
                  };
                  setChatHistory(prev => [...prev, newMessage]);
                  updateSessionState(selectedJobId || automationSessionId || sessionId, (state) => {
                    state.chat.chatHistory = [...(state.chat.chatHistory || []), newMessage];
                    return state;
                  });
                },
                onCandidateRowClick: handleOpenOverviewCandidates,
                onChannelFilterChange: setTalentPoolChannelFilter,
                handleBackFromOutreach,
                handleSendOutreach,
                handleStartOutreach,
                onRefreshStatus: async () => {
                  const refreshSearchId = getCurrentSessionSearchId() || automationSearchId;
                  if (refreshSearchId) {
                    try {
                      await refreshSyncState(refreshSearchId);
                    } catch (error) {
                      // Email sync is only one section of the status panel. A sync
                      // endpoint failure must not block the DB-backed panel refresh.
                      console.warn('[StatusPanel] Email sync refresh failed:', error);
                    }
                  }
                  setSourcingRefreshToken((key) => key + 1);
                  setInterviewRefreshKey((key) => key + 1);
                },
                // Interview creation now happens conversationally in chat (no inline setup card).
                onStartInterviewCreation: handleStartConversationalInterview,
                onOpenScorecard: (setupId, sourceSessionId) => {
                  setScorecardSelectedSetupId(setupId);
                  setScorecardEditorSessionId(sourceSessionId || automationSessionId || selectedJobId || sessionId);
                  setScorecardEditorOpen(true);
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Job Posting Preview Modal */}
      <JobPostingPreview
        isOpen={showJobPostingPreview}
        onClose={() => setShowJobPostingPreview(false)}
        onProceed={handleJobPostingPreviewProceed}
        initialData={jobPostingPreviewData}
        isDark={isDark}
      />
      
      {/* Job Posting Confirmation Modal (after collecting all details) */}
      <JobPostingConfirmationModal
        isOpen={showJobPostingConfirmationModal}
        onClose={() => {
          setShowJobPostingConfirmationModal(false);
          // Reset job posting state when user cancels
          setJobPostingPhase('idle');
          setPendingJobPostingFieldsQueue([]);
          setPendingNaukriField(null);
          setCollectedJobPostingDetails(null);
          // Exit agent banner + transition pill
          const exitJobModal = agentStageLabel || 'Job Posting';
          if (agentStageLabel) showToast(`Exited: ${exitJobModal}`, 'info', 2000);
          setAgentStageLabel('');
          setAgentStageCancellable(false);
          setSystemMessages([
            `__AGENT_TRANSITION__Exited ${exitJobModal}`,
            '❌ Job posting cancelled. You can start a new job posting anytime by pasting a job description.',
          ]);
          setSystemMessageToken((prev) => prev + 1);
        }}
        onProceed={handleJobPostingConfirmationProceed}
        collectedDetails={collectedJobPostingDetails}
        isDark={isDark}
      />

      <DynamicNaukriFieldsModal
        isOpen={Boolean(dynamicNaukriFieldRequest)}
        request={dynamicNaukriFieldRequest}
        isDark={isDark}
        onSubmit={handleDynamicNaukriFieldsSubmit}
        onClose={handleDynamicNaukriFieldsClose}
      />

      {/* Re-sourcing Confirmation Modal */}
      {showResourcingConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className={`rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}
          >
            <h3 className="text-lg font-semibold mb-4">Sourcing Already Completed</h3>
            <p className="text-sm mb-6" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
              Candidate sourcing has already been completed for this session. 
              Do you want to search for more candidates? 
              New candidates will be added to your existing pool (duplicates will be automatically removed).
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelResourcing}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                No, Keep Existing
              </button>
              <button
                onClick={confirmResourcing}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                Yes, Search More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal - Shows when OTP is required for any process */}
      <OtpModal
        isOpen={otpModal.isOpen}
        onClose={() => setOtpModal(prev => ({ ...prev, isOpen: false }))}
        onSubmit={async (otpCode) => {
          // Submit OTP directly with platform info for correct routing
          const platform = otpModal.platform?.toLowerCase() || '';
          try {
            await aiAutomationApi.post('/submit-otp', {
              session_id: otpModal.sessionId || automationSessionId,
              otp_code: otpCode,
              platform: platform,  // Include platform for correct routing
            });
          } catch (error) {
            console.error('Failed to submit OTP from modal:', error);
          }
          // Keep the modal mounted until the backend confirms that LinkedIn
          // accepted the code. The otp_status_update terminal event closes it.
        }}
        process={otpModal.process}
        platform={otpModal.platform}
        message={otpModal.message}
        jobTitle={otpModal.jobTitle}
      />

      {linkedinAppApprovalModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 px-4">
          <div
            className={`w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div
                className="h-12 w-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${primaryColor}18`, color: primaryColor }}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-bold">Approve LinkedIn sign-in</h3>
                  <div className="flex items-center gap-2">
                    <span
                      className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{ background: `${primaryColor}14`, color: primaryColor }}
                    >
                      {linkedinAppApprovalModal.attempt}/{linkedinAppApprovalModal.maxAttempts}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLinkedinAppApprovalModal(prev => ({ ...prev, isOpen: false }))}
                      className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                        isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-500'
                      }`}
                      aria-label="Close LinkedIn approval modal"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 6 6 18" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {linkedinAppApprovalModal.message || 'Open your LinkedIn mobile app and tap Yes to approve this sign-in.'}
                </p>
                {linkedinAppApprovalModal.screenshotUrl && (
                  <div className={`mt-4 overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
	                    <img
	                      src={linkedinAppApprovalModal.screenshotUrl}
	                      onError={(event) => {
	                        const fallback = linkedinAppApprovalModal.screenshotFallbackUrl;
	                        if (fallback && event.currentTarget.src !== fallback) {
	                          event.currentTarget.src = fallback;
	                        }
	                      }}
	                      alt="Current LinkedIn verification screen"
	                      className="max-h-64 w-full object-contain"
	                    />
                  </div>
                )}
                <div className={`mt-4 rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full animate-pulse" style={{ background: primaryColor }} />
                    Waiting up to {linkedinAppApprovalModal.timeoutSeconds || 60}s for this attempt
                  </div>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    If it does not move forward, I will remind you again. After 3 attempts the automation will stop.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume View Modal */}
      {resumeViewModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setResumeViewModal({ isOpen: false, loading: false, data: null, error: null, attachmentUrl: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}08)`, border: `1px solid ${primaryColor}30` }}>
                  <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Resume Details</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Analyzed candidate profile</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {resumeViewModal.attachmentUrl && (
                  <a
                    href={resumeViewModal.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
                    title="View original resume PDF"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View PDF
                  </a>
                )}
                <button onClick={() => setResumeViewModal({ isOpen: false, loading: false, data: null, error: null, attachmentUrl: null })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-gray-50/50 dark:bg-gray-900/50">
              {resumeViewModal.loading && (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div className="w-12 h-12 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}20`, borderTopColor: primaryColor }} />
                  <p className="text-sm text-gray-400 font-medium">Loading resume data…</p>
                </div>
              )}

              {resumeViewModal.error && (
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  </div>
                  <p className="text-sm text-red-500 font-medium">{resumeViewModal.error}</p>
                </div>
              )}

              {resumeViewModal.data && (() => {
                const { resume, basic_detail, skills, work_experience, education, certifications, match_score } = resumeViewModal.data;
                const scoreValue = typeof match_score === 'number' ? match_score : match_score?.overall_score;
                const scorePercent = scoreValue != null ? Math.round(scoreValue * 100) : null;
                const scoreColor = scorePercent >= 80 ? '#22C55E' : scorePercent >= 60 ? '#F59E0B' : scorePercent >= 40 ? '#EF8A21' : '#EF4444';
                const expMonths = basic_detail?.total_experience_in_months || 0;
                const expYears = expMonths >= 12 ? `${Math.floor(expMonths / 12)} yr${Math.floor(expMonths / 12) !== 1 ? 's' : ''} ${expMonths % 12 ? `${expMonths % 12} mo` : ''}` : `${expMonths} mo`;

                return (
                  <>
                    {/* Top Section: Candidate Header Card + Resume Match */}
                    <div className="flex gap-4">
                      {/* Candidate Profile Card */}
                      <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0 shadow-md" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}bb)` }}>
                            {(resume?.name || '?')[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white truncate">{resume?.name || 'Unknown'}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{resume?.email || ''}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{resume?.phone || ''}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                {expYears} experience
                              </span>
                              {resume?.location && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {resume.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Resume Match Card */}
                      {scorePercent != null && (
                        <div className="w-48 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm flex flex-col items-center justify-center">
                          <div className="text-4xl font-bold" style={{ color: scoreColor }}>{scorePercent}%</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Resume match</div>
                        </div>
                      )}
                    </div>

                    {/* Skills Section */}
                    {skills && skills.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                        <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Skills</h5>
                        <div className="flex flex-wrap gap-2">
                          {skills.map((skill, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Work Experience Section */}
                    {work_experience && work_experience.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                        <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Work Experience</h5>
                        <div className="space-y-3">
                          {work_experience.map((exp, idx) => (
                            <div key={idx} className="border-b border-gray-100 dark:border-gray-700/50 pb-3 last:border-0 last:pb-0">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h6 className="text-sm font-semibold text-gray-900 dark:text-white">{exp.title || exp.position || 'Unknown Role'}</h6>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{exp.company || 'Unknown Company'}</p>
                                </div>
                                {exp.duration && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">{exp.duration}</span>
                                )}
                              </div>
                              {exp.description && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{exp.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Education Section */}
                    {education && education.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                        <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Education</h5>
                        <div className="space-y-3">
                          {education.map((edu, idx) => (
                            <div key={idx} className="border-b border-gray-100 dark:border-gray-700/50 pb-3 last:border-0 last:pb-0">
                              <h6 className="text-sm font-semibold text-gray-900 dark:text-white">{edu.degree || edu.qualification || 'Unknown Degree'}</h6>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{edu.institution || edu.university || 'Unknown Institution'}</p>
                              {edu.year && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">{edu.year}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Generate AI Interview Modal */}
      {generateInterviewModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setGenerateInterviewModal({ isOpen: false, loading: false, candidate: null, questions: [], error: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}08)`, border: `1px solid ${primaryColor}30` }}>
                  <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">AI Interview Questions</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{generateInterviewModal.candidate?.name || 'Candidate'}</p>
                </div>
              </div>
              <button onClick={() => setGenerateInterviewModal({ isOpen: false, loading: false, candidate: null, questions: [], error: null })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 bg-gray-50/50 dark:bg-gray-900/50">
              {generateInterviewModal.loading && (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div className="w-12 h-12 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}20`, borderTopColor: primaryColor }} />
                  <p className="text-sm text-gray-400 font-medium">Generating interview questions…</p>
                </div>
              )}

              {generateInterviewModal.error && (
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  </div>
                  <p className="text-sm text-red-500 font-medium">{generateInterviewModal.error}</p>
                </div>
              )}

              {generateInterviewModal.questions.length > 0 && (
                <div className="space-y-3">
                  {generateInterviewModal.questions.map((question, idx) => (
                    <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: primaryColor }}>
                          {idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {typeof question === 'string'
                              ? question
                              : question?.question || question?.title || 'Untitled question'}
                          </p>
                          {question && typeof question === 'object' && (
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                              {question.source && <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{question.source}</span>}
                              {question.section && <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{question.section}</span>}
                              {question.difficulty && <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{question.difficulty}</span>}
                              {question.type && <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{question.type}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Meeting Analysis Modal — replaced by the shared <MeetingAnalysisModal/> mounted below. */}
      {false && meetingAnalysisModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, error: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}08)`, border: `1px solid ${primaryColor}30` }}>
                  <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Meeting Analysis</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{meetingAnalysisModal.candidate?.name || 'Candidate'}</p>
                </div>
              </div>
              <button onClick={() => setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, error: null })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 bg-gray-50/50 dark:bg-gray-900/50">
              {meetingAnalysisModal.loading && (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div className="w-12 h-12 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}20`, borderTopColor: primaryColor }} />
                  <p className="text-sm text-gray-400 font-medium">Loading meeting analysis…</p>
                </div>
              )}

              {meetingAnalysisModal.error && (
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" /></svg>
                  </div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">{meetingAnalysisModal.error}</p>
                </div>
              )}

              {meetingAnalysisModal.analysis && (() => {
                const analysis = meetingAnalysisModal.analysis || {};
                const summary = analysis.summary || analysis.analysis_summary || analysis.overall_summary || '';
                const recommendation = analysis.recommendation || analysis.decision || '';
                const strengths = Array.isArray(analysis.strengths)
                  ? analysis.strengths
                  : Array.isArray(analysis.pros)
                    ? analysis.pros
                    : [];
                const concerns = Array.isArray(analysis.concerns)
                  ? analysis.concerns
                  : Array.isArray(analysis.risks)
                    ? analysis.risks
                    : [];
                const keyPoints = Array.isArray(analysis.key_points)
                  ? analysis.key_points
                  : Array.isArray(analysis.highlights)
                    ? analysis.highlights
                    : [];

                const renderList = (items, bulletColor, keyPrefix) => (
                  <ul className="space-y-2">
                    {items.map((item, index) => (
                      <li key={`${keyPrefix}-${index}`} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                        <span className={`${bulletColor} mt-0.5`}>•</span>
                        <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                      </li>
                    ))}
                  </ul>
                );

                return (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm space-y-4">
                    {summary && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Summary</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{summary}</p>
                      </div>
                    )}

                    {recommendation && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Recommendation</h4>
                        <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                          {recommendation}
                        </span>
                      </div>
                    )}

                    {strengths.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Strengths</h4>
                        {renderList(strengths, 'text-green-500', 'strength')}
                      </div>
                    )}

                    {concerns.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Concerns</h4>
                        {renderList(concerns, 'text-red-500', 'concern')}
                      </div>
                    )}

                    {keyPoints.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Key Points</h4>
                        {renderList(keyPoints, 'text-gray-400', 'point')}
                      </div>
                    )}

                    {!summary && !recommendation && strengths.length === 0 && concerns.length === 0 && keyPoints.length === 0 && (
                      <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{JSON.stringify(analysis, null, 2)}</pre>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      </>
      )}
      {/* Shared interview result / meeting analysis modal */}
      <MeetingAnalysisModal
        open={meetingAnalysisModal.isOpen}
        onClose={() => { meetingReportRequestRef.current += 1; setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, aiResult: null, error: null }); }}
        candidate={meetingAnalysisModal.candidate}
        aiResult={meetingAnalysisModal.aiResult}
        scheduledInterviewId={meetingAnalysisModal.scheduledInterviewId}
        analysis={meetingAnalysisModal.analysis}
        loading={meetingAnalysisModal.loading}
        error={meetingAnalysisModal.error}
        onReject={() => setRejectCandidate(meetingAnalysisModal.candidate)}
      />

      <RejectCandidateModal
        open={!!rejectCandidate}
        candidate={rejectCandidate}
        searchId={rejectCandidate?._searchId || rejectCandidate?.search_id || rejectCandidate?.searchId || automationSearchId || ''}
        sessionId={rejectCandidate?.session_id || rejectCandidate?.sessionId || automationSessionId || selectedJobId || sessionId || ''}
        onClose={() => setRejectCandidate(null)}
        onRejected={() => {
          handleCandidateRejected(rejectCandidate);
          setRejectCandidate(null);
          setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, aiResult: null, error: null });
        }}
      />

      <BulkReplyDraftModal
        isOpen={bulkReplyDraftModal.isOpen}
        drafts={bulkReplyDraftModal.drafts}
        onClose={() => setBulkReplyDraftModal({ isOpen: false, drafts: [] })}
        onSent={(sentItems) => {
          const sentNames = (sentItems || []).map((item) => item.candidateName || item.candidate_email || item.candidate?.name).filter(Boolean);
          if (sentNames.length > 0) {
            showToast(`Reply sent to ${sentNames.join(', ')}`, 'success', 3000);
          }
        }}
      />

      <JDScoreConfigModal
        open={jdCriteriaModalOpen}
        jdId={jdCriteriaModalContext.jdId || automationSessionId || selectedJobId || sessionId}
        jobTitle={jdCriteriaModalContext.jobTitle || currentInterviewJobTitle}
        showCancelButton={false}
        onClose={() => setJdCriteriaModalOpen(false)}
        onSaved={() => {
          setCandidatesRenderToken((value) => value + 1);
          setSourcingRefreshToken((value) => value + 1);
        }}
      />

      {scorecardEditorOpen && <InterviewScorecardModal
        key={`${scorecardEditorSessionId}-${scorecardSelectedSetupId ?? 'draft'}`}
        sessionId={scorecardEditorSessionId}
        initialSetupId={scorecardSelectedSetupId}
        view={scorecardSelectedSetupId == null ? 'draft' : 'saved'}
        onClose={() => setScorecardEditorOpen(false)}
        onSaved={(saved) => {
          setScorecardEditorOpen(false);
          const savedSessionId = saved.session_id;
          if (saved.review_message_id && savedSessionId) {
            const metadata = {
              type: saved.setup_step === 'setup_review' ? 'lifecycle_setup_review' : 'lifecycle_setup_collect',
              source: 'scorecard_editor',
            };
            const reviewEntry = {
              id: saved.review_message_id,
              sender: 'agent',
              message: normalizeAssistantChatContent(saved.review_message, metadata, 'lifecycle'),
              timestamp: saved.review_message_created_at,
              sequence_number: saved.review_message_sequence,
              metadata,
            };
            updateSessionState(savedSessionId, (state) => {
              const history = Array.isArray(state.chat.chatHistory) ? state.chat.chatHistory : [];
              if (!history.some((entry) => entry.id === reviewEntry.id)) {
                state.chat.chatHistory = [...history, reviewEntry];
              }
              return state;
            });
            if (currentViewedSessionRef.current === savedSessionId) {
              setChatHistory((history) => history.some((entry) => entry.id === reviewEntry.id)
                ? history : [...history, reviewEntry]);
            }
          }
          if (currentViewedSessionRef.current === savedSessionId) {
            setScorecardSetupStep(saved.setup_step || '');
            setScorecardSetupSessionId(saved.setup_step ? savedSessionId : '');
          }
          if (currentViewedSessionRef.current === savedSessionId) {
            setScorecardSavedSummary(`Scorecard saved: ${saved.rows.length} topics, ${saved.total_weight}% total.`);
            showToast(`Scorecard saved: ${saved.rows.length} topics, ${saved.total_weight}% total.`, 'success', 3000);
          }
        }}
      />}

      {/* Global Toast Notification */}
      <Toast
        message={toastState.message}
        type={toastState.type}
        visible={toastState.visible}
        duration={toastState.duration || 5000}
        onClose={() => setToastState((prev) => ({ ...prev, visible: false }))}
      />

      {/* Quick Profile Modal — clean card for Job Posting tab */}
      {quickProfileCandidate && (() => {
        const qc = quickProfileCandidate;
        const matchColor = Number(qc.match_percentage) >= 80 ? '#22C55E' : Number(qc.match_percentage) >= 60 ? '#F59E0B' : '#EF4444';
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setQuickProfileCandidate(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden m-4" onClick={(e) => e.stopPropagation()}>
            {/* Header with name + close */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{qc.name || 'Candidate'}</h3>
                {qc.title && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{qc.title}{qc.company ? ` at ${qc.company}` : ''}</p>}
              </div>
              <button onClick={() => setQuickProfileCandidate(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 flex-shrink-0 ml-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[65vh]">
              {/* Status row */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                  {qc.funnel_stage || qc.status || 'Sourced'}
                </span>
                {qc.match_percentage && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-sm font-bold" style={{ color: matchColor }} title="Resume/JD match score. The AI interview result score is shown inside Interview Report.">{qc.match_percentage}% resume match</span>
                    <ScoreDetailsInfo details={qc.score_details || qc.scoreDetails} candidateName={qc.name || qc.candidate_name || qc.full_name} title="Resume Match Details" />
                  </span>
                )}
                {(qc.doc_id || qc.cv_filename)
                  ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Resume</span>
                  : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">No Resume</span>
                }
              </div>

              {/* Contact info */}
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                {qc.email && <div className="flex items-center gap-2"><svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span className="truncate">{qc.email}</span></div>}
                {qc.phone && <div className="flex items-center gap-2"><svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg><span>{qc.phone}</span></div>}
                {qc.location && <div className="flex items-center gap-2"><svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg><span>{qc.location}</span></div>}
                {qc.experience_years && <div className="flex items-center gap-2"><svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span>{qc.experience_years} years experience</span></div>}
                {qc.linkedin_profile_url && <div className="flex items-center gap-2"><svg className="w-4 h-4 flex-shrink-0" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg><a href={qc.linkedin_profile_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">LinkedIn Profile</a></div>}
              </div>

              {/* Skills */}
              {(qc.skills || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {qc.skills.map((sk, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">{sk}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Info banner */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  For full profile with email timeline, notes, and interview scheduling, switch to the <strong>Overview</strong> tab.
                </p>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Undo Toast (archive or delete) */}
      {undoToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-gray-900 dark:bg-gray-700 text-white rounded-xl shadow-lg text-sm animate-in slide-in-from-bottom-4">
          <span>"{undoToast.title}" {undoToast.type === 'delete' ? 'deleted' : 'archived'}.</span>
          <button
            onClick={handleUndoAction}
            className="font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
